import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import OUI_DATA from './data/oui.json';
import PROTOCOLS from './data/protocols.json';
import OID_DETAILS from './data/oid_details.json';
import { 
  Network, Mail, Github, Linkedin, Code, Terminal, Globe, 
  BookOpen, Zap, Download, Copy, Check, 
  Cpu, Shield, Activity, Hash, Lock, Router, Cable, Battery, 
  FileText, Search, Database, Layers, MapPin, BarChart, Calculator, 
  Divide, HardDrive, Key, Radio, ShieldCheck, Camera, AlertCircle,
  Maximize2, Minimize2, Plus, Trash2, Settings, Server,
  Cloud, ShieldOff, Globe as GlobeIcon,
  RefreshCw, ChevronDown, ChevronUp, X, Tag,
  Home, Menu
} from 'lucide-react';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-6">
          <div className="bg-gray-950 border border-gray-800 rounded-2xl p-8 max-w-md text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-red-400 mb-2">Tool Error</h2>
            <p className="text-gray-300 mb-4">
              Something went wrong with this tool. Please try again or refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-gray-800 hover:bg-gray-600 rounded-full font-semibold transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Validated Input Component - Moved outside to prevent re-renders
const ValidatedInput = React.memo(({ 
  value, 
  onChange, 
  validate, 
  errorMessage = 'Invalid input',
  className = '',
  placeholder = '',
  type = 'text',
  ...props 
}) => {
  const [isValid, setIsValid] = useState(true);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsValid(validate(newValue));
  };
  
  const handleBlur = () => {
    setTouched(true);
    setIsValid(validate(value));
  };
  
  return (
    <div className="relative">
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        className={`w-full bg-black border ${isValid ? 'border-gray-800' : 'border-red-500'} rounded-xl px-4 py-3 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600 ${className}`}
        placeholder={placeholder}
        {...props}
      />
      {touched && !isValid && (
        <>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
          <div className="text-xs text-red-400 mt-1">{errorMessage}</div>
        </>
      )}
    </div>
  );
});

// Copy Button Component with clipboard fallback
const CopyButton = React.memo(({ text, itemKey, className = '' }) => {
  const [localCopied, setLocalCopied] = useState(false);
  
  const handleClick = () => {
    if (!text || typeof text !== 'string') {
      console.error('Invalid text for clipboard');
      return;
    }

        // Fallback for older browsers or environments without clipboard API
    const copyToClipboard = (textToCopy) => {
      // Method 1: Modern clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(textToCopy);
      }
      
      // Method 2: Fallback using textarea
      return new Promise((resolve, reject) => {
        try {
          const textArea = document.createElement('textarea');
          textArea.value = textToCopy;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
              const success = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (success) {
            resolve();
          } else {
            reject(new Error('Copy command failed'));
          }
        } catch (err) {
          reject(err);
        }
      });
    };

    copyToClipboard(text)
      .then(() => {
        setLocalCopied(true);
        setTimeout(() => setLocalCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        // Last resort - show text in alert
        alert(`Copy this text: ${text}`);
      });
  };
  
  return (
    <button
      onClick={handleClick}
      className={`relative group px-3 py-1 rounded-lg bg-gray-800 hover:bg-gray-600 transition-all ${className}`}
      aria-label={`Copy ${text} to clipboard`}
    >
      {localCopied ? (
        <Check className="w-4 h-4 text-green-400" />
      ) : (
        <Copy className="w-4 h-4 text-gray-400" />
      )}
    </button>
  );
});

// Config Preview Component
const ConfigPreview = React.memo(({ config, title }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const lines = config.split('\n');
  const previewLines = isExpanded ? lines : lines.slice(0, 20);
  
        return (
    <div className="border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex justify-between items-center bg-gray-950 px-4 py-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-300">{title}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-2 py-1 rounded-lg bg-gray-800 hover:bg-gray-600 text-xs text-gray-300"
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </button>
          <CopyButton text={config} itemKey={`${title}-config`} />
        </div>
      </div>
      <div className="bg-black p-4">
        <pre className="font-mono text-sm text-gray-300 overflow-x-auto">
          {previewLines.join('\n')}
          {!isExpanded && lines.length > 20 && (
            <div className="text-gray-500 italic mt-2">... {lines.length - 20} more lines</div>
          )}
        </pre>
      </div>
    </div>
  );
});

// Interface Config Component
const InterfaceConfig = ({ iface, index, onUpdate, onRemove, deviceType, isLast }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  return (
    <div className={`bg-black/60 rounded-xl p-4 border ${isLast ? 'border-gray-800' : 'border-gray-600'} mb-3`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-gray-800 p-2 rounded-lg">
            <Server className="w-4 h-4 text-gray-300" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-300">Interface {index + 1}</h4>
            <div className="text-sm text-gray-500">{iface.name}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="px-3 py-1 rounded-lg bg-gray-800 hover:bg-gray-600 text-sm text-gray-300"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={onRemove}
            className="px-3 py-1 rounded-lg bg-red-900/30 hover:bg-red-800/30 text-sm text-red-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Interface Name</label>
          <input
            value={iface.name}
            onChange={(e) => onUpdate('name', e.target.value)}
            className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
            placeholder="GigabitEthernet0/0"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">IP Address</label>
          <input
            value={iface.ip}
            onChange={(e) => onUpdate('ip', e.target.value)}
            className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
            placeholder="192.168.1.1"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Subnet Mask</label>
          <input
            value={iface.mask}
            onChange={(e) => onUpdate('mask', e.target.value)}
            className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
            placeholder="255.255.255.0"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Description</label>
          <input
            value={iface.description}
            onChange={(e) => onUpdate('description', e.target.value)}
            className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
            placeholder="WAN Interface"
          />
        </div>
      </div>
      
      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-gray-800">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {deviceType === 'switch' && (
              <>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Switchport Mode</label>
                  <select
                    value={iface.mode}
                    onChange={(e) => onUpdate('mode', e.target.value)}
                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
                  >
                    <option value="access">Access</option>
                    <option value="trunk">Trunk</option>
                    <option value="routed">Routed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">VLAN ID</label>
                  <input
                    type="number"
                    value={iface.vlan}
                    onChange={(e) => onUpdate('vlan', e.target.value)}
                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
                    min="1"
                    max="4094"
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Voice VLAN</label>
                  <input
                    type="number"
                    value={iface.voiceVlan || ''}
                    onChange={(e) => onUpdate('voiceVlan', e.target.value)}
                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
                    min="1"
                    max="4094"
                    placeholder="Optional"
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">MTU</label>
              <input
                type="number"
                value={iface.mtu || '1500'}
                onChange={(e) => onUpdate('mtu', e.target.value)}
                className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
                min="68"
                max="9216"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Duplex</label>
              <select
                value={iface.duplex || 'auto'}
                onChange={(e) => onUpdate('duplex', e.target.value)}
                className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
              >
                <option value="auto">Auto</option>
                <option value="full">Full</option>
                <option value="half">Half</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Speed</label>
              <select
                value={iface.speed || 'auto'}
                onChange={(e) => onUpdate('speed', e.target.value)}
                className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100"
              >
                <option value="auto">Auto</option>
                <option value="10">10 Mbps</option>
                <option value="100">100 Mbps</option>
                <option value="1000">1 Gbps</option>
                <option value="10000">10 Gbps</option>
              </select>
            </div>
          </div>
          
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={iface.enableCdp || false}
                onChange={(e) => onUpdate('enableCdp', e.target.checked)}
                className="rounded border-gray-600 bg-gray-800"
                id={`cdp-${iface.id}`}
              />
              <label htmlFor={`cdp-${iface.id}`} className="text-sm text-gray-300">
                Enable CDP
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={iface.enableLldp || false}
                onChange={(e) => onUpdate('enableLldp', e.target.checked)}
                className="rounded border-gray-600 bg-gray-800"
                id={`lldp-${iface.id}`}
              />
              <label htmlFor={`lldp-${iface.id}`} className="text-sm text-gray-300">
                Enable LLDP
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={iface.enablePortSecurity || false}
                onChange={(e) => onUpdate('enablePortSecurity', e.target.checked)}
                className="rounded border-gray-600 bg-gray-800"
                id={`portsec-${iface.id}`}
              />
              <label htmlFor={`portsec-${iface.id}`} className="text-sm text-gray-300">
                Port Security
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={iface.enableStormControl || false}
                onChange={(e) => onUpdate('enableStormControl', e.target.checked)}
                className="rounded border-gray-600 bg-gray-800"
                id={`storm-${iface.id}`}
              />
              <label htmlFor={`storm-${iface.id}`} className="text-sm text-gray-300">
                Storm Control
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Security Feature Toggle Component
const SecurityFeatureToggle = ({ feature, enabled, onToggle, description }) => (
  <div className="flex items-center justify-between p-3 bg-black/60 rounded-lg border border-gray-800">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${enabled ? 'bg-green-900/30' : 'bg-gray-800'}`}>
        {enabled ? <ShieldCheck className="w-4 h-4 text-green-400" /> : <ShieldOff className="w-4 h-4 text-gray-400" />}
      </div>
      <div>
        <div className="font-medium text-gray-300">{feature}</div>
        <div className="text-xs text-gray-500">{description}</div>
      </div>
    </div>
    <button
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full ${enabled ? 'bg-green-600' : 'bg-gray-800'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </div>
);

// Typewriter Component
const TypewriterComponent = () => {
  const [text, setText] = useState('');
  const [titleIndex, setTitleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  
  const titles = useMemo(() => [
    'Network Engineer',
    'System Administrator',
    'Cybersecurity Enthusiast',
    'Cloud Architect',
    'DevOps Practitioner',
    'Network Automation Specialist',
    'Infrastructure Engineer',
    'IT Security Analyst'
  ], []);
  
  useEffect(() => {
    const currentTitle = titles[titleIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting && text !== currentTitle) {
        // Typing
        setText(currentTitle.substring(0, text.length + 1));
        setTypingSpeed(100);
      } else if (isDeleting && text !== '') {
        // Deleting
        setText(currentTitle.substring(0, text.length - 1));
        setTypingSpeed(50);
      } else if (!isDeleting && text === currentTitle) {
        // Finished typing, wait then start deleting
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && text === '') {
        // Finished deleting, move to next title
        setIsDeleting(false);
        setTitleIndex((prev) => (prev + 1) % titles.length);
        setTypingSpeed(500);
      }
    }, typingSpeed);
    
    return () => clearTimeout(timeout);
  }, [text, titleIndex, isDeleting, typingSpeed, titles]);
  
  return (
    <div className="inline-flex items-center">
      <span className="text-gray-300">{text}</span>
      <span className="ml-1 w-1 h-8 bg-gray-400 animate-pulse"></span>
    </div>
  );
};

// Main Portfolio Component
export default function NetworkEngineerPortfolio() {
  const [activeSection, setActiveSection] = useState(() => {
    try { return localStorage.getItem('activeSection') || 'home'; } catch (e) { return 'home'; }
  });
  const [activeCategory, setActiveCategory] = useState(() => {
    try { return localStorage.getItem('activeCategory') || 'all'; } catch (e) { return 'all'; }
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTool, setActiveTool] = useState(() => {
    try {
      return localStorage.getItem('activeTool') || 'subnet';
    } catch (e) {
      return 'subnet';
    }
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('activeTool', activeTool);
    } catch (e) {
      // ignore storage errors (e.g., private mode)
    }
  }, [activeTool]);

  useEffect(() => {
    try {
      localStorage.setItem('activeSection', activeSection);
      localStorage.setItem('activeCategory', activeCategory);
    } catch (e) {
      // ignore storage errors
    }
  }, [activeSection, activeCategory]);

  const toolButtons = useMemo(() => [
    // IP & Subnetting
    { id: 'subnet', label: 'Subnet Calculator', category: 'ip', icon: Divide },
    { id: 'vlsm', label: 'VLSM Calculator', category: 'ip', icon: Calculator },
    { id: 'binary', label: 'Binary Converter', category: 'ip', icon: Hash },
    { id: 'summarize', label: 'Route Summarization', category: 'ip', icon: MapPin },
    { id: 'wildcard', label: 'Wildcard Mask', category: 'ip', icon: ShieldCheck },
    { id: 'ipv6', label: 'IPv6 Calculator', category: 'ip', icon: Cpu },
    
    // Security & VPN
    { id: 'acl', label: 'ACL Generator', category: 'security', icon: Lock },
    { id: 'password', label: 'Password Tools', category: 'security', icon: Key },
    { id: 'hash', label: 'Hash Generator', category: 'security', icon: Hash },
    { id: 'vpn', label: 'VPN Config', category: 'security', icon: Shield },
    { id: 'ssl', label: 'TLS/SSL Checker', category: 'security', icon: ShieldCheck },
    
    // DNS & Domains
    { id: 'dns', label: 'DNS Lookup', category: 'dns', icon: Globe },
    { id: 'whois', label: 'WHOIS Lookup', category: 'dns', icon: Search },
    
    // Lookups & Reference
    { id: 'ports', label: 'Port Reference', category: 'lookup', icon: Database },
    { id: 'mac', label: 'MAC Lookup', category: 'lookup', icon: Cpu },
    { id: 'protocol', label: 'Protocol Decoder', category: 'lookup', icon: FileText },
    { id: 'oid', label: 'SNMP OID Lookup', category: 'lookup', icon: Search },
    { id: 'geolocation', label: 'IP Geolocation', category: 'lookup', icon: MapPin },
    
    // Conversion & Encoding
    { id: 'hexascii', label: 'Hex/ASCII Converter', category: 'convert', icon: Code },
    
    // Design & Capacity
    { id: 'bandwidth', label: 'Bandwidth Calculator', category: 'design', icon: Zap },
    { id: 'cabling', label: 'Cabling Calculator', category: 'design', icon: Cable },
    { id: 'poe', label: 'PoE Calculator', category: 'design', icon: Battery },
    { id: 'qos', label: 'QoS Calculator', category: 'design', icon: BarChart },
    { id: 'mtu', label: 'MTU Calculator', category: 'design', icon: HardDrive },
    
    // Cisco Config & Routing
    { id: 'metrics', label: 'Routing Metrics', category: 'cisco', icon: Router },
    { id: 'bgp', label: 'BGP Tools', category: 'cisco', icon: Radio },
    { id: 'config', label: 'Config Generator', category: 'cisco', icon: FileText },
    { id: 'stp', label: 'STP Calculator', category: 'cisco', icon: Network },
    { id: 'nat', label: 'NAT Configuration', category: 'cisco', icon: Router },
    
    // Monitoring & Logs
    { id: 'logs', label: 'Log Parser', category: 'monitoring', icon: Camera },
  ], []);

  // Enhanced validation functions
  const validateIP = useCallback((ip) => {
    if (!ip || typeof ip !== 'string') return false;
    
    const pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!pattern.test(ip)) return false;
    
    const octets = ip.split('.');
    if (octets.length !== 4) return false;
    
    return octets.every(octet => {
      const num = parseInt(octet, 10);
      return !isNaN(num) && num >= 0 && num <= 255 && octet === num.toString();
    });
  }, []);

  const validateCIDR = useCallback((cidr) => {
    if (!cidr && cidr !== 0) return false;
    const num = parseInt(cidr, 10);
    return !isNaN(num) && num >= 0 && num <= 32;
  }, []);

  const validateMAC = useCallback((mac) => {
    if (!mac || typeof mac !== 'string') return false;
    
    const cleaned = mac.trim();
    const patterns = [
      /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/,
      /^([0-9A-Fa-f]{4}\.){2}([0-9A-Fa-f]{4})$/,
      /^[0-9A-Fa-f]{12}$/
    ];
    
    return patterns.some(pattern => pattern.test(cleaned));
  }, []);

  const validateIPv6 = useCallback((ip) => {
    if (!ip || typeof ip !== 'string') return false;
    
    // Remove any leading/trailing whitespace and convert to lowercase
    const addr = ip.trim().toLowerCase();
    
    // Check for empty string
    if (addr === '') return false;
    
    // Check for double colon more than once
    if ((addr.match(/::/g) || []).length > 1) return false;
    
    // Check for triple or more colons in a row
    if (/:::+/.test(addr)) return false;
    
    // If address starts or ends with single colon (not double)
    if (/^:[^:]/.test(addr) || /[^:]:$/.test(addr)) return false;
    
    // Split by double colon to handle compressed form
    const parts = addr.split('::');
    
    if (parts.length === 1) {
      // No compression - must be 8 groups
      const groups = parts[0].split(':');
      if (groups.length !== 8) return false;
      
      return groups.every(group => {
        if (group === '') return false;
        if (group.length > 4) return false;
        return /^[0-9a-f]{1,4}$/.test(group);
      });
    } else if (parts.length === 2) {
      // Has compression
      const left = parts[0].split(':').filter(x => x !== '');
      const right = parts[1].split(':').filter(x => x !== '');
      const total = left.length + right.length;
      
      if (total > 7) return false; // Can't have more than 7 groups when compressed
      
      // Check all groups
      const allGroups = [...left, ...right];
      return allGroups.every(group => {
        if (group === '') return false;
        if (group.length > 4) return false;
        return /^[0-9a-f]{1,4}$/.test(group);
      });
    }
    
    return false;
  }, []);

  // ==============================
  // TOOL STATES
  // ==============================

  // 1. Subnetting
  const [ipAddress, setIpAddress] = useState('192.168.1.0');
  const [cidr, setCidr] = useState('24');
  const [subnetResults, setSubnetResults] = useState(null);
  const [subnetError, setSubnetError] = useState('');

  // 2. Binary Converter
  const [decimalIP, setDecimalIP] = useState('192.168.1.1');
  const [binaryResult, setBinaryResult] = useState(null);
  const [binaryError, setBinaryError] = useState('');

  // 3. VLSM
  const [vlsmNetwork, setVlsmNetwork] = useState('10.0.0.0');
  const [vlsmCidr, setVlsmCidr] = useState('16');
  const [vlsmRequirements, setVlsmRequirements] = useState([
    { id: 1, name: 'HR Department', hosts: 50 },
    { id: 2, name: 'IT Department', hosts: 30 },
    { id: 3, name: 'Sales Department', hosts: 100 }
  ]);
  const [vlsmResults, setVlsmResults] = useState(null);
  const [vlsmError, setVlsmError] = useState('');

  // 4. MAC lookup
  const [macAddress, setMacAddress] = useState('00:1A:2B:3C:4D:5E');
  const [macResult, setMacResult] = useState(null);
  const [macError, setMacError] = useState('');

  // Use static OUI dataset imported from src/data/oui.json
  // Normalize imported OUI dataset: ensure keys are in format XX:XX:XX uppercase
  const OUI_MAP = useMemo(() => {
    const map = {};
    try {
      Object.entries(OUI_DATA).forEach(([rawKey, vendor]) => {
        if (!rawKey || typeof rawKey !== 'string') return;
        const cleaned = rawKey.toUpperCase().replace(/[^0-9A-F]/g, '');
        if (cleaned.length < 6) return;
        const oui = cleaned.slice(0, 6).match(/.{2}/g).join(':');
        map[oui] = vendor;
      });
    } catch (err) {
      // Keep map empty on parse error
    }
    return map;
  }, [/* static import, won't change */]);

  const OUI_SAMPLE = OUI_MAP;

  // 5. Port Reference
  const [portSearch, setPortSearch] = useState('');
  const commonPorts = useMemo(() => [
    // Web Services
    { port: 7, protocol: 'Echo', description: 'Echo Protocol' },
    { port: 20, protocol: 'FTP', description: 'File Transfer Protocol (Data)' },
    { port: 21, protocol: 'FTP', description: 'File Transfer Protocol (Control)' },
    { port: 22, protocol: 'SSH', description: 'Secure Shell' },
    { port: 23, protocol: 'Telnet', description: 'Telnet Protocol' },
    { port: 25, protocol: 'SMTP', description: 'Simple Mail Transfer Protocol' },
    { port: 53, protocol: 'DNS', description: 'Domain Name System' },
    { port: 67, protocol: 'DHCP', description: 'DHCP Server' },
    { port: 68, protocol: 'DHCP', description: 'DHCP Client' },
    { port: 69, protocol: 'TFTP', description: 'Trivial File Transfer Protocol' },
    { port: 70, protocol: 'Gopher', description: 'Gopher Protocol' },
    { port: 79, protocol: 'Finger', description: 'Finger Protocol' },
    { port: 80, protocol: 'HTTP', description: 'Hypertext Transfer Protocol' },
    { port: 88, protocol: 'Kerberos', description: 'Kerberos Authentication' },
    { port: 110, protocol: 'POP3', description: 'Post Office Protocol v3' },
    { port: 113, protocol: 'Ident', description: 'Identification Protocol' },
    { port: 119, protocol: 'NNTP', description: 'Network News Transfer Protocol' },
    { port: 123, protocol: 'NTP', description: 'Network Time Protocol' },
    { port: 135, protocol: 'RPC', description: 'Microsoft RPC' },
    { port: 137, protocol: 'NetBIOS', description: 'NetBIOS Name Service' },
    { port: 138, protocol: 'NetBIOS', description: 'NetBIOS Datagram Service' },
    { port: 139, protocol: 'NetBIOS', description: 'NetBIOS Session Service' },
    { port: 143, protocol: 'IMAP', description: 'Internet Message Access Protocol' },
    { port: 161, protocol: 'SNMP', description: 'Simple Network Management Protocol' },
    { port: 162, protocol: 'SNMP', description: 'SNMP Trap' },
    { port: 179, protocol: 'BGP', description: 'Border Gateway Protocol' },
    { port: 194, protocol: 'IRC', description: 'Internet Relay Chat' },
    { port: 220, protocol: 'IMAP3', description: 'Interactive Mail Access Protocol v3' },
    { port: 389, protocol: 'LDAP', description: 'Lightweight Directory Access Protocol' },
    { port: 443, protocol: 'HTTPS', description: 'HTTP Secure' },
    { port: 445, protocol: 'SMB', description: 'Server Message Block' },
    { port: 464, protocol: 'Kerberos', description: 'Kerberos Password Change' },
    { port: 465, protocol: 'SMTPS', description: 'SMTP over SSL' },
    { port: 514, protocol: 'Syslog', description: 'System Logging' },
    { port: 515, protocol: 'LPD', description: 'Line Printer Daemon' },
    { port: 587, protocol: 'SMTP', description: 'SMTP Submission' },
    { port: 593, protocol: 'HTTP-RPC', description: 'HTTP RPC Ep Map' },
    { port: 636, protocol: 'LDAPS', description: 'LDAP over SSL' },
    { port: 993, protocol: 'IMAPS', description: 'IMAP over SSL' },
    { port: 995, protocol: 'POP3S', description: 'POP3 over SSL' },
    { port: 1025, protocol: 'Microsoft RPC', description: 'Microsoft RPC' },
    { port: 1026, protocol: 'Windows Messenger', description: 'Windows Messenger' },
    { port: 1027, protocol: 'IIS', description: 'IIS' },
    { port: 1028, protocol: 'IIS', description: 'IIS' },
    { port: 1029, protocol: 'IIS', description: 'IIS' },
    { port: 1030, protocol: 'IIS', description: 'IIS' },
    { port: 1194, protocol: 'OpenVPN', description: 'OpenVPN' },
    { port: 1352, protocol: 'Lotus Notes', description: 'Lotus Notes' },
    { port: 1433, protocol: 'MSSQL', description: 'Microsoft SQL Server' },
    { port: 1434, protocol: 'MSSQL', description: 'Microsoft SQL Monitor' },
    { port: 1521, protocol: 'Oracle', description: 'Oracle Database' },
    { port: 1526, protocol: 'Oracle', description: 'Oracle Listener' },
    { port: 1583, protocol: 'Pervasive SQL', description: 'Pervasive SQL' },
    { port: 1701, protocol: 'L2TP', description: 'Layer 2 Tunneling Protocol' },
    { port: 1723, protocol: 'PPTP', description: 'Point-to-Point Tunneling' },
    { port: 1812, protocol: 'RADIUS', description: 'RADIUS Authentication' },
    { port: 1813, protocol: 'RADIUS', description: 'RADIUS Accounting' },
    { port: 1900, protocol: 'UPnP', description: 'Universal Plug and Play' },
    { port: 2000, protocol: 'Cisco SCCP', description: 'Cisco Skinny' },
    { port: 2049, protocol: 'NFS', description: 'Network File System' },
    { port: 2082, protocol: 'cPanel', description: 'cPanel' },
    { port: 2083, protocol: 'cPanel', description: 'cPanel SSL' },
    { port: 2086, protocol: 'WHM', description: 'Web Host Manager' },
    { port: 2087, protocol: 'WHM', description: 'WHM SSL' },
    { port: 2095, protocol: 'cPanel', description: 'Webmail' },
    { port: 2096, protocol: 'cPanel', description: 'Webmail SSL' },
    { port: 2100, protocol: 'Oracle XDB', description: 'Oracle XDB FTP' },
    { port: 2222, protocol: 'SSH', description: 'SSH Alternative' },
    { port: 2375, protocol: 'Docker', description: 'Docker REST API' },
    { port: 2376, protocol: 'Docker', description: 'Docker REST API SSL' },
    { port: 2483, protocol: 'Oracle', description: 'Oracle DB Listener' },
    { port: 2484, protocol: 'Oracle', description: 'Oracle DB SSL' },
    { port: 2638, protocol: 'Sybase', description: 'Sybase' },
    { port: 3000, protocol: 'Node.js', description: 'Node.js Development' },
    { port: 3050, protocol: 'Firebird', description: 'Firebird Database' },
    { port: 3128, protocol: 'Squid', description: 'Squid Proxy' },
    { port: 3260, protocol: 'iSCSI', description: 'iSCSI Target' },
    { port: 3268, protocol: 'MS iSCSI', description: 'Microsoft iSCSI' },
    { port: 3269, protocol: 'MS iSCSI', description: 'Microsoft iSCSI SSL' },
    { port: 3306, protocol: 'MySQL', description: 'MySQL Database' },
    { port: 3389, protocol: 'RDP', description: 'Remote Desktop Protocol' },
    { port: 3690, protocol: 'SVN', description: 'Subversion' },
    { port: 4000, protocol: 'ICQ', description: 'ICQ' },
    { port: 4045, protocol: 'Cisco', description: 'Cisco VPN Client' },
    { port: 4190, protocol: 'Sieve', description: 'ManageSieve' },
    { port: 4333, protocol: 'mSQL', description: 'mSQL' },
    { port: 4443, protocol: 'HTTPS', description: 'HTTPS Alternative' },
    { port: 4500, protocol: 'IPsec', description: 'IPsec NAT-T' },
    { port: 4569, protocol: 'Interbase', description: 'Interbase Database' },
    { port: 4672, protocol: 'eMule', description: 'eMule' },
    { port: 4899, protocol: 'Radmin', description: 'Radmin Remote Admin' },
    { port: 5000, protocol: 'UPnP', description: 'UPnP' },
    { port: 5001, protocol: 'iperf', description: 'iperf' },
    { port: 5004, protocol: 'RTP', description: 'RTP Data' },
    { port: 5005, protocol: 'RTP', description: 'RTP Control' },
    { port: 5060, protocol: 'SIP', description: 'Session Initiation Protocol' },
    { port: 5061, protocol: 'SIP', description: 'SIP over TLS' },
    { port: 5093, protocol: 'SPSS', description: 'SPSS' },
    { port: 5100, protocol: 'Yahoo Messenger', description: 'Yahoo Messenger' },
    { port: 5190, protocol: 'AIM', description: 'AOL Instant Messenger' },
    { port: 5222, protocol: 'XMPP', description: 'XMPP Client Connection' },
    { port: 5223, protocol: 'XMPP', description: 'XMPP Client SSL' },
    { port: 5269, protocol: 'XMPP', description: 'XMPP Server Connection' },
    { port: 5280, protocol: 'XMPP', description: 'XMPP BOSH' },
    { port: 5298, protocol: 'XMPP', description: 'XMPP File Transfer' },
    { port: 5353, protocol: 'mDNS', description: 'Multicast DNS' },
    { port: 5355, protocol: 'LLMNR', description: 'Link-Local Multicast Name Resolution' },
    { port: 5432, protocol: 'PostgreSQL', description: 'PostgreSQL Database' },
    { port: 5480, protocol: 'AFP', description: 'Apple Filing Protocol' },
    { port: 5500, protocol: 'VNC', description: 'VNC Server' },
    { port: 5555, protocol: 'Android Debug', description: 'Android Debug Bridge' },
    { port: 5631, protocol: 'PCAnywhere', description: 'pcAnywhere' },
    { port: 5632, protocol: 'PCAnywhere', description: 'pcAnywhere SSL' },
    { port: 5800, protocol: 'VNC', description: 'VNC HTTP' },
    { port: 5900, protocol: 'VNC', description: 'Virtual Network Computing' },
    { port: 5984, protocol: 'CouchDB', description: 'CouchDB' },
    { port: 5985, protocol: 'WinRM', description: 'Windows Remote Management' },
    { port: 5986, protocol: 'WinRM', description: 'Windows Remote Management SSL' },
    { port: 6000, protocol: 'X11', description: 'X Window System' },
    { port: 6379, protocol: 'Redis', description: 'Redis Database' },
    { port: 6443, protocol: 'Kubernetes', description: 'Kubernetes API Server' },
    { port: 6667, protocol: 'IRC', description: 'IRC' },
    { port: 6881, protocol: 'BitTorrent', description: 'BitTorrent' },
    { port: 6969, protocol: 'BitTorrent', description: 'BitTorrent Tracker' },
    { port: 7000, protocol: 'Cassandra', description: 'Apache Cassandra' },
    { port: 7001, protocol: 'Cassandra', description: 'Cassandra SSL' },
    { port: 7077, protocol: 'Spark', description: 'Apache Spark' },
    { port: 7199, protocol: 'Cassandra', description: 'Cassandra JMX' },
    { port: 7474, protocol: 'Neo4j', description: 'Neo4j Database' },
    { port: 7547, protocol: 'CPE WAN', description: 'CableLabs CPE WAN Management' },
    { port: 8000, protocol: 'HTTP', description: 'HTTP Alternative' },
    { port: 8008, protocol: 'HTTP', description: 'HTTP Alternative' },
    { port: 8009, protocol: 'AJP', description: 'Apache JServ Protocol' },
    { port: 8080, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 8081, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 8088, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 8090, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 8123, protocol: 'Polipo', description: 'Polipo Proxy' },
    { port: 8161, protocol: 'ActiveMQ', description: 'Apache ActiveMQ' },
    { port: 8181, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 8443, protocol: 'HTTPS-Alt', description: 'HTTPS Alternate' },
    { port: 8500, protocol: 'Consul', description: 'HashiCorp Consul' },
    { port: 8686, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 873, protocol: 'rsync', description: 'rsync' },
    { port: 8888, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 9000, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 9001, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 9042, protocol: 'Cassandra', description: 'Cassandra CQL' },
    { port: 9090, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 9092, protocol: 'Kafka', description: 'Apache Kafka' },
    { port: 9200, protocol: 'Elasticsearch', description: 'Elasticsearch' },
    { port: 9300, protocol: 'Elasticsearch', description: 'Elasticsearch Transport' },
    { port: 9418, protocol: 'Git', description: 'Git' },
    { port: 9443, protocol: 'HTTPS-Alt', description: 'HTTPS Alternate' },
    { port: 9600, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 9800, protocol: 'WebDAV', description: 'WebDAV' },
    { port: 9999, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 10000, protocol: 'Webmin', description: 'Webmin Administration' },
    { port: 10050, protocol: 'Zabbix', description: 'Zabbix Agent' },
    { port: 10051, protocol: 'Zabbix', description: 'Zabbix Server' },
    { port: 10101, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 11211, protocol: 'Memcached', description: 'Memcached' },
    { port: 11371, protocol: 'OpenPGP', description: 'OpenPGP HTTP Keyserver' },
    { port: 12000, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 12345, protocol: 'NetBus', description: 'NetBus' },
    { port: 13782, protocol: 'NetBackup', description: 'Veritas NetBackup' },
    { port: 15000, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 16080, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 19888, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 20000, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 24800, protocol: 'Synergy', description: 'Synergy' },
    { port: 27017, protocol: 'MongoDB', description: 'MongoDB' },
    { port: 27018, protocol: 'MongoDB', description: 'MongoDB Web Status' },
    { port: 27019, protocol: 'MongoDB', description: 'MongoDB Configuration' },
    { port: 28017, protocol: 'MongoDB', description: 'MongoDB HTTP' },
    { port: 32768, protocol: 'Filenet', description: 'Filenet' },
    { port: 49152, protocol: 'UPnP', description: 'UPnP' },
    { port: 50000, protocol: 'SAP', description: 'SAP' },
    { port: 50070, protocol: 'HDFS', description: 'Hadoop HDFS NameNode' },
    { port: 50075, protocol: 'HDFS', description: 'Hadoop HDFS DataNode' },
    { port: 50090, protocol: 'HDFS', description: 'Hadoop HDFS Secondary NameNode' },
    { port: 60000, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 60010, protocol: 'HTTP-Alt', description: 'HTTP Alternate' },
    { port: 61616, protocol: 'ActiveMQ', description: 'ActiveMQ STOMP' },
    { port: 61617, protocol: 'ActiveMQ', description: 'ActiveMQ OpenWire' },
    { port: 61618, protocol: 'ActiveMQ', description: 'ActiveMQ WebSocket' },
    { port: 61619, protocol: 'ActiveMQ', description: 'ActiveMQ MQTT' },
    { port: 61620, protocol: 'ActiveMQ', description: 'ActiveMQ AMQP' },
    { port: 61621, protocol: 'ActiveMQ', description: 'ActiveMQ STOMP SSL' },
    { port: 61622, protocol: 'ActiveMQ', description: 'ActiveMQ OpenWire SSL' },
    { port: 61623, protocol: 'ActiveMQ', description: 'ActiveMQ WebSocket SSL' },
    { port: 61624, protocol: 'ActiveMQ', description: 'ActiveMQ MQTT SSL' },
    { port: 61625, protocol: 'ActiveMQ', description: 'ActiveMQ AMQP SSL' },
    { port: 65535, protocol: 'Reserved', description: 'Reserved Port' }
  ], []);

  // 6. Wildcard Mask
  const [wildcardIP, setWildcardIP] = useState('255.255.255.0');
  const [wildcardResult, setWildcardResult] = useState(null);
  const [wildcardError, setWildcardError] = useState('');

  // 7. Route Summarization
  const [summarizeIPs, setSummarizeIPs] = useState(['192.168.1.0/24', '192.168.2.0/24', '192.168.3.0/24']);
  const [summarizeResult, setSummarizeResult] = useState(null);
  const [summarizeError, setSummarizeError] = useState('');

  // 8. Bandwidth Calculator
  const [fileSize, setFileSize] = useState('100');
  const [sizeUnit, setSizeUnit] = useState('MB');
  const [bandwidth, setBandwidth] = useState('10');
  const [bandwidthUnit, setBandwidthUnit] = useState('Mbps');
  const [bandwidthResult, setBandwidthResult] = useState(null);
  const [bandwidthError, setBandwidthError] = useState('');

  // 9. Protocol Decoder
  const [protocolCode, setProtocolCode] = useState('6');
  const [protocolResult, setProtocolResult] = useState(null);
  // Load protocol registry from static file (IANA) and normalize
  const PROTOCOL_MAP = useMemo(() => {
    const map = {};
    try {
      Object.entries(PROTOCOLS).forEach(([key, val]) => {
        const k = String(key).trim();
        if (!k) return;
        map[k] = val;
      });
    } catch (err) {}
    return map;
  }, [/* static import */]);

  const protocolDatabase = PROTOCOL_MAP;

  const [hashType, setHashType] = useState('sha256');
  const [hashText, setHashText] = useState('');
  const [hashResult, setHashResult] = useState(null);
  const [hashError, setHashError] = useState('');

  // 12. Password Tools
  const [passwordText, setPasswordText] = useState('cisco');
  const [passwordType, setPasswordType] = useState('type7');
  const [passwordResult, setPasswordResult] = useState(null);
  const [passwordError, setPasswordError] = useState('');

  // 13. Cabling Calculator
  const [cableType, setCableType] = useState('cat6');
  const [cableLength, setCableLength] = useState(100);
  const [cablingResult, setCablingResult] = useState(null);
  const [cablingError, setCablingError] = useState('');

  // 14. Log Parser
  const [logText, setLogText] = useState(`%LINK-3-UPDOWN: Interface GigabitEthernet0/1, changed state to down
%LINK-5-CHANGED: Interface GigabitEthernet0/1, changed state to administratively down
%SYS-5-CONFIG_I: Configured from console by admin on vty0 (192.168.1.100)
%SEC-6-IPACCESSLOGP: list 100 denied tcp 192.168.1.200(1024) -> 10.0.0.1(80), 1 packet
%SYS-6-LOGGINGHOST_STARTSTOP: Logging to host 192.168.1.50 port 514 started - CLI initiated
%LINEPROTO-5-UPDOWN: Line protocol on Interface GigabitEthernet0/1, changed state to down
%AAA-4-AAA_AUTHENTICATION_FAILURE: Authentication failed for user admin from 192.168.1.150
%DHCP-6-ADDRESS_ASSIGN: Interface GigabitEthernet0/2 assigned DHCP address 192.168.1.101, mask 255.255.255.0
%OSPF-5-ADJCHG: Process 1, Nbr 192.168.1.2 on GigabitEthernet0/3 from LOADING to FULL, Loading Done
%BGP-5-ADJCHANGE: neighbor 10.0.0.2 Up
%HSRP-5-STATECHANGE: GigabitEthernet0/4 Grp 1 state Standby -> Active
%EIGRP-5-NBRCHANGE: EIGRP-IPv4 100: Neighbor 192.168.1.3 (GigabitEthernet0/5) is up: new adjacency
%CDP-4-NVLANMISMATCH: Native VLAN mismatch detected on GigabitEthernet0/6 (1/100)
%STP-6-PORT_STATE: Port GigabitEthernet0/7 instance 1 moving from blocking to forwarding
%VLAN-6-VTP_DOMAIN_NAME: VTP domain name set to "CORP_DOMAIN"
%ACL-6-ACL_APPLIED: ACL "BLOCK_HTTP" applied to interface GigabitEthernet0/8
%NAT-5-ADDRESS_CREATED: Dynamic mapping created for 192.168.1.100 -> 203.0.113.1
%IP-4-DUPADDR: Duplicate address 192.168.1.100 on GigabitEthernet0/9, sourced by 0011.2233.4455
%TCP-6-BADAUTH: No MD5 digest from 10.0.0.2(179) to 192.168.1.1(11027) tableid - 0
%UDP-6-BADAUTH: No MD5 digest from 10.0.0.3(49) to 192.168.1.1(520) tableid - 0
%ICMP-3-INVALIDCODE: Invalid ICMP code from 192.168.1.200 to 192.168.1.1
%IP-3-INVALIDLEN: Invalid IP length from 192.168.1.201 to 192.168.1.1
%TCP-3-SYNC_TIMEOUT: TCP SYN timeout from 192.168.1.202 to 192.168.1.1
%UDP-3-PORT_UNREACH: UDP port unreachable from 192.168.1.203 to 192.168.1.1
%SEC-6-IPACCESSLOGDP: list 100 denied icmp 192.168.1.204 -> 192.168.1.1 (8/0), 1 packet
%SEC-6-IPACCESSLOGNP: list 100 denied 47 192.168.1.205 -> 192.168.1.1, 1 packet
%SEC-6-IPACCESSLOGSP: list 100 denied tcp 192.168.1.206(22) -> 192.168.1.1(22), 1 packet
%SYS-3-CPUHOG: Task ran for 2345 msec, more than 2000 msec, process = IP Input
%SYS-3-MEMORY_LOW: Memory utilization reached 95%
%ENV-3-FAN_FAILED: Fan 1 in power supply A has failed
%ENV-3-POWER_FAILED: Power supply B has failed
%ENV-3-TEMP_HIGH: Temperature sensor 1 reading 75C, threshold 70C
%SYS-5-RESTART: System restarted by power-on
%SYS-5-SCHEDULED_RELOAD: Reload requested by admin on console
%SYS-6-CLOCKUPDATE: System clock updated from NTP server 192.168.1.50
%NTP-6-PEER_REACHABLE: NTP peer 192.168.1.51 is now reachable
%NTP-3-PEER_UNREACHABLE: NTP peer 192.168.1.52 is now unreachable
%SNMP-5-COLDSTART: SNMP agent on host 192.168.1.1 is undergoing a cold start
%SNMP-6-TRAP_SENT: SNMP trap sent to 192.168.1.50
%MPLS-5-LDP_SESSION_UP: LDP session with neighbor 192.168.1.3 is up
%MPLS-3-LDP_SESSION_DOWN: LDP session with neighbor 192.168.1.4 is down
%MSDP-5-PEER_UP: MSDP peer 192.168.1.5 is up
%MSDP-3-PEER_DOWN: MSDP peer 192.168.1.6 is down
%PIM-5-NBRCHG: neighbor 192.168.1.7 UP on interface GigabitEthernet0/10
%PIM-3-NBRCHG: neighbor 192.168.1.8 DOWN on interface GigabitEthernet0/11
%IGMP-6-QUERY_SENT: IGMP query sent on GigabitEthernet0/12
%IGMP-3-LEAVE_RCVD: IGMP leave received on GigabitEthernet0/13
%QOS-6-POLICY_APPLIED: QoS policy "VOICE_QOS" applied to interface GigabitEthernet0/14
%QOS-3-POLICY_FAILED: QoS policy application failed on GigabitEthernet0/15
%VPN-5-TUNNEL_UP: IPSEC tunnel to 10.0.0.2 is up
%VPN-3-TUNNEL_DOWN: IPSEC tunnel to 10.0.0.3 is down
%CRYPTO-4-IKMP_NO_SA: IKE message from 10.0.0.4 has no SA
%CRYPTO-6-IKMP_SA_ESTABLISHED: IKE SA established with 10.0.0.5
%SSH-5-ENABLED: SSH 2.0 has been enabled
%SSH-4-LOGIN_FAILED: Login failed for user admin from 192.168.1.100
%SSH-5-LOGIN_SUCCESS: Login successful for user admin from 192.168.1.100
%RADIUS-4-RADIUS_DEAD: RADIUS server 192.168.1.50:1812,1813 is not responding
%RADIUS-5-RADIUS_ALIVE: RADIUS server 192.168.1.50:1812,1813 is responding again
%TACACS-4-TACACS_DEAD: TACACS server 192.168.1.51:49 is not responding
%TACACS-5-TACACS_ALIVE: TACACS server 192.168.1.51:49 is responding again
%IPSEC-5-ESP_SETUP_FAILURE: ESP setup failed for peer 10.0.0.6
%IPSEC-6-ESP_SETUP_SUCCESS: ESP setup successful for peer 10.0.0.7
%IKE-3-INVALID_COOKIE: IKE packet from 10.0.0.8 has invalid cookie
%IKE-5-IKE_SA_ESTABLISHED: IKE SA established for peer 10.0.0.9
%PKI-3-CERT_REVOKED: Certificate with serial number 1234567890ABCDEF has been revoked
%PKI-5-CERT_VALIDATED: Certificate validation successful for peer 10.0.0.10
%PKI-3-CRL_UPDATE_FAILED: Certificate Revocation List update failed
%PKI-5-CRL_UPDATE_SUCCESS: Certificate Revocation List updated successfully
%SYS-6-PRIVILEGE: User admin privilege level changed from 1 to 15
%SYS-4-PRIVILEGE_VIOLATION: Privilege violation - user admin attempted command 'configure terminal'
%ARCHIVE-5-CONFIG_RESTORE: Configuration restored from running-config
%ARCHIVE-3-CONFIG_SAVE_FAILED: Configuration save to startup-config failed
%ARCHIVE-5-CONFIG_SAVE_SUCCESS: Configuration saved to startup-config successfully
%FLASH-5-RW_MOUNTED: Flash card in slot 0 mounted as read-write
%FLASH-3-RW_FAILED: Flash card in slot 0 mount failed
%USB-5-DEVICE_ATTACHED: USB device attached to port 0
%USB-3-DEVICE_DETACHED: USB device detached from port 0
%ETHERNET-5-LINK_UP: Ethernet link up on GigabitEthernet0/16
%ETHERNET-3-LINK_DOWN: Ethernet link down on GigabitEthernet0/17
%FIB-5-FIB_TABLE_ADD: FIB table add for prefix 192.168.1.0/24
%FIB-3-FIB_TABLE_DELETE: FIB table delete for prefix 10.0.0.0/8
%ADJ-5-ADD: Adjacency added for 192.168.1.100 on GigabitEthernet0/18
%ADJ-3-DELETE: Adjacency deleted for 192.168.1.101 on GigabitEthernet0/19
%ARP-5-ARP_ADD: ARP entry added for 192.168.1.102 on GigabitEthernet0/20
%ARP-3-ARP_DELETE: ARP entry deleted for 192.168.1.103 on GigabitEthernet0/21
%DNS-5-QUERY_SUCCESS: DNS query for example.com succeeded
%DNS-3-QUERY_FAILED: DNS query for badexample.com failed
%DHCP-5-LEASE_SUCCESS: DHCP lease obtained for interface GigabitEthernet0/22
%DHCP-3-LEASE_FAILED: DHCP lease failed for interface GigabitEthernet0/23
%TFTP-5-TRANSFER_SUCCESS: TFTP transfer of config.txt completed successfully
%TFTP-3-TRANSFER_FAILED: TFTP transfer of config.txt failed
%HTTP-5-CLIENT_SUCCESS: HTTP client request to 192.168.1.50 succeeded
%HTTP-3-CLIENT_FAILED: HTTP client request to 192.168.1.51 failed
%HTTPS-5-SSL_SUCCESS: SSL handshake with 192.168.1.52 succeeded
%HTTPS-3-SSL_FAILED: SSL handshake with 192.168.1.53 failed
%IP-5-ROUTE_ADD: Route added to 172.16.0.0/16 via 192.168.1.1
%IP-3-ROUTE_DELETE: Route deleted to 172.16.0.0/16 via 192.168.1.1
%OSPF-5-HELLO_RCVD: OSPF hello received from 192.168.1.2 on GigabitEthernet0/24
%OSPF-3-HELLO_TIMEOUT: OSPF hello timeout from 192.168.1.3 on GigabitEthernet0/25
%BGP-5-KEEPALIVE_RCVD: BGP keepalive received from 10.0.0.2
%BGP-3-KEEPALIVE_TIMEOUT: BGP keepalive timeout from 10.0.0.3
%EIGRP-5-HELLO_RCVD: EIGRP hello received from 192.168.1.4 on GigabitEthernet0/26
%EIGRP-3-HELLO_TIMEOUT: EIGRP hello timeout from 192.168.1.5 on GigabitEthernet0/27
%RIP-5-UPDATE_RCVD: RIP update received from 192.168.1.6 on GigabitEthernet0/28
%RIP-3-UPDATE_TIMEOUT: RIP update timeout from 192.168.1.7 on GigabitEthernet0/29
%ISIS-5-ADJ_UP: ISIS adjacency to 192.168.1.8 up on GigabitEthernet0/30
%ISIS-3-ADJ_DOWN: ISIS adjacency to 192.168.1.9 down on GigabitEthernet0/31
%MPLS-5-TE_TUNNEL_UP: MPLS TE tunnel 100 to 192.168.1.10 is up
%MPLS-3-TE_TUNNEL_DOWN: MPLS TE tunnel 101 to 192.168.1.11 is down
%LDP-5-SESSION_UP: LDP session to 192.168.1.12 is up
%LDP-3-SESSION_DOWN: LDP session to 192.168.1.13 is down
%RSVP-5-PATH_UP: RSVP path to 192.168.1.14 is up
%RSVP-3-PATH_DOWN: RSVP path to 192.168.1.15 is down
%MSDP-5-SA_RCVD: MSDP SA received from 192.168.1.16
%MSDP-3-SA_TIMEOUT: MSDP SA timeout from 192.168.1.17
%PIM-5-JOIN_RCVD: PIM join received on GigabitEthernet0/32
%PIM-3-PRUNE_RCVD: PIM prune received on GigabitEthernet0/33
%IGMP-5-REPORT_RCVD: IGMP report received on GigabitEthernet0/34
%IGMP-3-LEAVE_RCVD: IGMP leave received on GigabitEthernet0/35
%DVMRP-5-NBR_UP: DVMRP neighbor 192.168.1.18 up on GigabitEthernet0/36
%DVMRP-3-NBR_DOWN: DVMRP neighbor 192.168.1.19 down on GigabitEthernet0/37
%HSRP-5-STATECHANGE: GigabitEthernet0/38 Grp 2 state Active -> Speak
%HSRP-3-STATECHANGE: GigabitEthernet0/39 Grp 3 state Standby -> Init
%VRRP-5-STATECHANGE: GigabitEthernet0/40 Grp 4 state Master -> Backup
%VRRP-3-STATECHANGE: GigabitEthernet0/41 Grp 5 state Backup -> Init
%GLBP-5-STATECHANGE: GigabitEthernet0/42 Grp 6 state Active -> Listen
%GLBP-3-STATECHANGE: GigabitEthernet0/43 Grp 7 state Standby -> Init
%SYS-5-RELOAD: Reload requested for reason: reload command
%SYS-3-RELOAD_FAILED: Reload failed due to configuration error
%SYS-6-BOOT_START: System booting with image c2900-universalk9-mz.SPA.154-3.M3.bin
%SYS-5-BOOT_COMPLETE: System boot completed successfully
%SYS-3-BOOT_FAILED: System boot failed - insufficient memory
%IOS-5-UPGRADE_START: IOS upgrade started from tftp://192.168.1.50/c2900-universalk9-mz.SPA.155-3.M4a.bin
%IOS-5-UPGRADE_SUCCESS: IOS upgrade completed successfully
%IOS-3-UPGRADE_FAILED: IOS upgrade failed - file not found
%CONFIG-5-CHANGE: Configuration changed by user admin
%CONFIG-3-CHANGE_FAILED: Configuration change failed - invalid command
%IF-5-UP: Interface GigabitEthernet0/44, changed state to up
%IF-3-DOWN: Interface GigabitEthernet0/45, changed state to down
%IF-6-SPEED_CHANGE: Interface GigabitEthernet0/46 speed changed to 1000 Mbps
%IF-4-DUPLEX_CHANGE: Interface GigabitEthernet0/47 duplex changed to full
%IF-3-ENCAPSULATION_FAILED: Interface GigabitEthernet0/48 encapsulation failed
%IF-5-MTU_CHANGE: Interface GigabitEthernet0/49 MTU changed to 1500
%IF-3-BANDWIDTH_CHANGE: Interface GigabitEthernet0/50 bandwidth changed to 1000000 Kbps
%IF-5-DESCRIPTION_CHANGE: Interface GigabitEthernet0/51 description changed to "Uplink to Core"
%IF-3-DESCRIPTION_FAILED: Interface GigabitEthernet0/52 description change failed
%IF-5-IP_CHANGE: Interface GigabitEthernet0/53 IP address changed to 192.168.1.1/24
%IF-3-IP_FAILED: Interface GigabitEthernet0/54 IP address change failed - duplicate address
%IF-5-MASK_CHANGE: Interface GigabitEthernet0/55 subnet mask changed to 255.255.255.0
%IF-3-MASK_FAILED: Interface GigabitEthernet0/56 subnet mask change failed
%IF-5-VLAN_CHANGE: Interface GigabitEthernet0/57 VLAN changed to 100
%IF-3-VLAN_FAILED: Interface GigabitEthernet0/58 VLAN change failed - VLAN not configured
%IF-5-TRUNK_CHANGE: Interface GigabitEthernet0/59 trunk mode changed to on
%IF-3-TRUNK_FAILED: Interface GigabitEthernet0/60 trunk mode change failed
%IF-5-CHANNEL_CHANGE: Interface GigabitEthernet0/61 added to port-channel 1
%IF-3-CHANNEL_FAILED: Interface GigabitEthernet0/62 channel group change failed
%IF-5-POE_CHANGE: Interface GigabitEthernet0/63 PoE mode changed to auto
%IF-3-POE_FAILED: Interface GigabitEthernet0/64 PoE mode change failed
%IF-5-STORM_CHANGE: Interface GigabitEthernet0/65 storm control changed to 10%
%IF-3-STORM_FAILED: Interface GigabitEthernet0/66 storm control change failed
%IF-5-QOS_CHANGE: Interface GigabitEthernet0/67 QoS policy changed to VOICE_QOS
%IF-3-QOS_FAILED: Interface GigabitEthernet0/68 QoS policy change failed
%IF-5-ACL_CHANGE: Interface GigabitEthernet0/69 ACL changed to BLOCK_HTTP
%IF-3-ACL_FAILED: Interface GigabitEthernet0/70 ACL change failed
%IF-5-NAT_CHANGE: Interface GigabitEthernet0/71 NAT mode changed to inside
%IF-3-NAT_FAILED: Interface GigabitEthernet0/72 NAT mode change failed
%IF-5-DHCP_CHANGE: Interface GigabitEthernet0/73 DHCP mode changed to client
%IF-3-DHCP_FAILED: Interface GigabitEthernet0/74 DHCP mode change failed
%IF-5-SSH_CHANGE: Interface GigabitEthernet0/75 SSH mode changed to enabled
%IF-3-SSH_FAILED: Interface GigabitEthernet0/76 SSH mode change failed
%IF-5-SNMP_CHANGE: Interface GigabitEthernet0/77 SNMP mode changed to enabled
%IF-3-SNMP_FAILED: Interface GigabitEthernet0/78 SNMP mode change failed
%IF-5-NTP_CHANGE: Interface GigabitEthernet0/79 NTP mode changed to enabled
%IF-3-NTP_FAILED: Interface GigabitEthernet0/80 NTP mode change failed
%IF-5-SYSLOG_CHANGE: Interface GigabitEthernet0/81 syslog mode changed to enabled
%IF-3-SYSLOG_FAILED: Interface GigabitEthernet0/82 syslog mode change failed
%IF-5-TACACS_CHANGE: Interface GigabitEthernet0/83 TACACS mode changed to enabled
%IF-3-TACACS_FAILED: Interface GigabitEthernet0/84 TACACS mode change failed
%IF-5-RADIUS_CHANGE: Interface GigabitEthernet0/85 RADIUS mode changed to enabled
%IF-3-RADIUS_FAILED: Interface GigabitEthernet0/86 RADIUS mode change failed
%IF-5-LDAP_CHANGE: Interface GigabitEthernet0/87 LDAP mode changed to enabled
%IF-3-LDAP_FAILED: Interface GigabitEthernet0/88 LDAP mode change failed
%IF-5-KERBEROS_CHANGE: Interface GigabitEthernet0/89 KERBEROS mode changed to enabled
%IF-3-KERBEROS_FAILED: Interface GigabitEthernet0/90 KERBEROS mode change failed
%IF-5-CERT_CHANGE: Interface GigabitEthernet0/91 certificate mode changed to enabled
%IF-3-CERT_FAILED: Interface GigabitEthernet0/92 certificate mode change failed
%IF-5-VPN_CHANGE: Interface GigabitEthernet0/93 VPN mode changed to enabled
%IF-3-VPN_FAILED: Interface GigabitEthernet0/94 VPN mode change failed
%IF-5-FIREWALL_CHANGE: Interface GigabitEthernet0/95 firewall mode changed to enabled
%IF-3-FIREWALL_FAILED: Interface GigabitEthernet0/96 firewall mode change failed
%IF-5-IDS_CHANGE: Interface GigabitEthernet0/97 IDS mode changed to enabled
%IF-3-IDS_FAILED: Interface GigabitEthernet0/98 IDS mode change failed
%IF-5-IPS_CHANGE: Interface GigabitEthernet0/99 IPS mode changed to enabled
%IF-3-IPS_FAILED: Interface GigabitEthernet0/100 IPS mode change failed

<30>Jan 15 08:30:15 router-1 sshd[1234]: Failed password for invalid user admin from 192.168.1.100 port 22 ssh2
<30>Jan 15 08:31:20 firewall-1 kernel: DROP IN=eth0 OUT= MAC=00:11:22:33:44:55:66:77:88:99:aa:bb SRC=10.0.0.1 DST=192.168.1.1 LEN=60 TOS=0x00 PREC=0x00 TTL=64 ID=12345 DF PROTO=TCP SPT=44567 DPT=80 WINDOW=29200 RES=0x00 SYN URGP=0
<30>Jan 15 08:32:05 web-server-1 nginx: 192.168.1.100 - - [15/Jan/2024:08:32:05 +0000] "GET /admin HTTP/1.1" 404 162 "-" "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
<30>Jan 15 08:33:12 db-server-1 mysqld: 2024-01-15T08:33:12.123456Z 1234 Query SELECT * FROM users WHERE id = 1
<30>Jan 15 08:34:28 cloud-instance-1 systemd: Started Session 1234 of user ubuntu.
<30>Jan 15 08:35:45 container-host-1 dockerd: level=info msg="Container 1234567890ab started"
<30>Jan 15 08:36:33 monitoring-1 snmpd: Connection from UDP: [192.168.1.100]:161
<30>Jan 15 08:37:19 backup-server-1 rsync: building file list ... done
<30>Jan 15 08:38:07 cert-server-1 certbot: Certificate renewed successfully for example.com
<30>Jan 15 08:39:22 dns-server-1 named: client 192.168.1.100#53: query: example.com IN A
<30>Jan 15 08:40:15 mail-server-1 postfix: 1234567890: from=<user@example.com>, size=1234, nrcpt=1 (queue active)
<30>Jan 15 08:41:08 voip-server-1 asterisk: -- Registered SIP 'phone1' at 192.168.1.101:5060
<30>Jan 15 08:42:33 storage-1 iscsid: Connection established to 192.168.1.200:3260
<30>Jan 15 08:43:19 load-balancer-1 haproxy: 192.168.1.100:12345 [15/Jan/2024:08:43:19] frontend http-in~ backend/backend1/server1 0/0/1/123/456 200 1234 - - ---- 1/1/0/0/0 0/0 "GET /api HTTP/1.1"
<30>Jan 15 08:44:05 wireless-ap-1 hostapd: wlan0: STA 00:11:22:33:44:55 IEEE 802.11: associated
<30>Jan 15 08:45:12 firewall-2 ufw: [12345] BLOCK IN=eth0 OUT= MAC=00:11:22:33:44:55:66:77:88:99:aa:bb SRC=203.0.113.1 DST=192.168.1.1 LEN=60 TOS=0x00 PREC=0x00 TTL=64 ID=12345 DF PROTO=TCP SPT=44567 DPT=22 WINDOW=29200 RES=0x00 SYN URGP=0
<30>Jan 15 08:46:28 ids-sensor-1 snort: [1:1234:5] WEB-IIS cmd.exe access [Classification: Web Application Attack] [Priority: 1] {TCP} 203.0.113.1:80 -> 192.168.1.1:12345
<30>Jan 15 08:47:15 db-server-2 mongod: 2024-01-15T08:47:15.123Z I NETWORK [conn123] received client metadata from 192.168.1.100:27017 conn123: { driver: { name: "mongosh", version: "1.6.0" }, os: { type: "Linux", name: "Ubuntu", architecture: "x64", version: "20.04" } }
<30>Jan 15 08:48:33 app-server-1 tomcat: 15-Jan-2024 08:48:33.123 INFO [http-nio-8080-exec-1] org.apache.catalina.core.StandardWrapperValve.invoke Servlet.service() for servlet [dispatcher] in context with path [] threw exception [Request processing failed; nested exception is java.lang.NullPointerException] with root cause java.lang.NullPointerException
<30>Jan 15 08:49:19 cloud-lb-1 aws-elb: 2024-01-15T08:49:19.456Z elb-name 192.168.1.100:12345 10.0.0.1:80 0.000 0.001 0.000 200 200 123 456 "GET http://example.com:80/api HTTP/1.1" "Mozilla/5.0" - -
<30>Jan 15 08:50:45 container-orch-1 kubelet: I0115 08:50:45.789012 1234 kubelet.go:1234] Successfully pulled image "nginx:latest"
<30>Jan 15 08:51:22 perf-mon-1 collectd: 2024-01-15 08:51:22.123 (interval=10) dispatch_values: cpu-0/cpu-user=12.345000
<30>Jan 15 08:52:08 backup-2 rdiff-backup: 2024-01-15T08:52:08Z Successfully backed up 1234 files
<30>Jan 15 08:53:35 cert-manager-1 cert-manager: I0115 08:53:35.123456 1234 controller.go:123] Reconciled certificate "example-tls" in namespace "default"
<30>Jan 15 08:54:12 dns-resolver-1 unbound: [1234:0] info: 192.168.1.100 example.com. A IN
<30>Jan 15 08:55:28 email-gateway-1 sendmail: 123456789: from=user@example.com, size=1234, class=0, nrcpt=1, msgid=<20240115085528.123456@example.com>, proto=ESMTP, daemon=MTA, relay=mail.example.com [192.168.1.200]
<30>Jan 15 08:56:15 voip-gateway-1 freeswitch: 2024-01-15 08:56:15.123456 [INFO] switch_core_state_machine.c:1234 sofia/internal/1001@192.168.1.1:5060 State Change CS_EXECUTE -> CS_EXCHANGE_MEDIA
<30>Jan 15 08:57:42 storage-array-1 emc: 2024-01-15 08:57:42 INFO LUN 123 created successfully
<30>Jan 15 08:58:19 load-bal-2 nginx-upstream: 192.168.1.100 - - [15/Jan/2024:08:58:19 +0000] "GET /health HTTP/1.1" 200 2 "-" "-" upstream_response_time 0.001 upstream_connect_time 0.000 upstream_header_time 0.001`);
  const [logResult, setLogResult] = useState(null);
  const [logError, setLogError] = useState('');
  const [logFormat, setLogFormat] = useState('auto');
  const [logAnalysisLevel, setLogAnalysisLevel] = useState('detailed');

  // 15. QoS Calculator
  const [qosBandwidth, setQosBandwidth] = useState(100);
  const [qosPolicies, setQosPolicies] = useState([
    { id: 1, class: 'voice', percent: 20 },
    { id: 2, class: 'video', percent: 30 },
    { id: 3, class: 'data', percent: 50 }
  ]);
  const [qosResult, setQosResult] = useState(null);
  const [qosError, setQosError] = useState('');

  // 16. IPv6 Calculator
  const [ipv6Address, setIpv6Address] = useState('2001:0db8:85a3::8a2e:0370:7334');
  const [ipv6Prefix, setIpv6Prefix] = useState('64');
  const [ipv6Result, setIpv6Result] = useState(null);
  const [ipv6Error, setIpv6Error] = useState('');

  // 17. VPN Config Generator
  const [vpnType, setVpnType] = useState('ipsec-site-to-site');
  const [vpnPeerIP, setVpnPeerIP] = useState('203.0.113.1');
  const [vpnLocalNetwork, setVpnLocalNetwork] = useState('192.168.1.0/24');
  const [vpnRemoteNetwork, setVpnRemoteNetwork] = useState('10.0.0.0/24');
  const [vpnLocalInterface, setVpnLocalInterface] = useState('GigabitEthernet0/0');
  const [vpnIkeVersion, setVpnIkeVersion] = useState('ikev2');
  const [vpnEncryption, setVpnEncryption] = useState('aes256');
  const [vpnHash, setVpnHash] = useState('sha256');
  const [vpnDhGroup, setVpnDhGroup] = useState('14');
  const [vpnAuthMethod, setVpnAuthMethod] = useState('pre-shared');
  const [vpnPsk, setVpnPsk] = useState('');
  const [vpnFeatures, setVpnFeatures] = useState({
    enableDPD: true,
    enableNatTraversal: true,
    enablePfs: true,
    enableRouteInjection: true,
    enableMonitoring: true
  });
  const [vpnResult, setVpnResult] = useState(null);
  const [vpnError, setVpnError] = useState('');

  // 18. SNMP OID Lookup
  const [oidSearch, setOidSearch] = useState('');
  const [oidResult, setOidResult] = useState(null);
  const [oidError, setOidError] = useState('');

  // 19. DNS Lookup
  const [dnsHostname, setDnsHostname] = useState('google.com');
  const [dnsRecordType, setDnsRecordType] = useState('A');
  const [dnsResult, setDnsResult] = useState(null);
  const [dnsError, setDnsError] = useState('');
  const [dnsLoading, setDnsLoading] = useState(false);

  // 20. Hex/ASCII Converter
  const [hexAsciiInput, setHexAsciiInput] = useState('Hello World');
  const [hexAsciiMode, setHexAsciiMode] = useState('ascii-to-hex');
  const [hexAsciiResult, setHexAsciiResult] = useState(null);
  const [hexAsciiError, setHexAsciiError] = useState('');
  const [hexAsciiHexFormat, setHexAsciiHexFormat] = useState('lowercase');

  // 21. TLS/SSL Checker
  const [sslDomain, setSslDomain] = useState('google.com');
  const [sslResult, setSslResult] = useState(null);
  const [sslError, setSslError] = useState('');
  const [sslLoading, setSslLoading] = useState(false);

  // 22. IP Geolocation
  const [geoIP, setGeoIP] = useState('8.8.8.8');
  const [geoResult, setGeoResult] = useState(null);
  const [geoError, setGeoError] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);

  // 23. WHOIS Lookup
  const [whoisDomain, setWhoisDomain] = useState('google.com');
  const [whoisResult, setWhoisResult] = useState(null);
  const [whoisError, setWhoisError] = useState('');
  const [whoisLoading, setWhoisLoading] = useState(false);

  // 24. NAT Configuration Generator
  const [natType, setNatType] = useState('static');
  const [natInsideLocal, setNatInsideLocal] = useState('192.168.1.10');
  const [natInsideGlobal, setNatInsideGlobal] = useState('203.0.113.10');
  const [natOutsideLocal, setNatOutsideLocal] = useState('');
  const [natOutsideGlobal, setNatOutsideGlobal] = useState('');
  const [natAcl, setNatAcl] = useState('10');
  const [natPool, setNatPool] = useState('POOL1');
  const [natPoolStart, setNatPoolStart] = useState('203.0.113.50');
  const [natPoolEnd, setNatPoolEnd] = useState('203.0.113.100');
  const [natPoolMask, setNatPoolMask] = useState('255.255.255.0');
  const [natInsideInt, setNatInsideInt] = useState('Ethernet0/0');
  const [natOutsideInt, setNatOutsideInt] = useState('Ethernet0/1');
  const [natPlatform, setNatPlatform] = useState('ios');
  const [natConfig, setNatConfig] = useState('');
  const [natInsideSubnet, setNatInsideSubnet] = useState('192.168.1.0');
  const [natInsideWildcard, setNatInsideWildcard] = useState('0.0.0.255');
  const [natEnableExempt, setNatEnableExempt] = useState(false);
  const [natExemptDest, setNatExemptDest] = useState('10.0.0.0');
  const [natExemptDestWildcard, setNatExemptDestWildcard] = useState('0.255.255.255');
  const [natEnableStaticPat, setNatEnableStaticPat] = useState(false);
  const [natStaticProtocol, setNatStaticProtocol] = useState('tcp');
  const [natStaticInsidePort, setNatStaticInsidePort] = useState('80');
  const [natStaticGlobalPort, setNatStaticGlobalPort] = useState('8080');
  const [natEnableTwiceNat, setNatEnableTwiceNat] = useState(false);
  const [natTwiceSrcLocal, setNatTwiceSrcLocal] = useState('192.168.1.10');
  const [natTwiceSrcGlobal, setNatTwiceSrcGlobal] = useState('203.0.113.10');
  const [natTwiceDstLocal, setNatTwiceDstLocal] = useState('10.0.0.10');
  const [natTwiceDstGlobal, setNatTwiceDstGlobal] = useState('198.51.100.10');
  const [natEnableTimeouts, setNatEnableTimeouts] = useState(false);
  const [natTimeout, setNatTimeout] = useState('86400');
  const [natTcpTimeout, setNatTcpTimeout] = useState('86400');
  const [natUdpTimeout, setNatUdpTimeout] = useState('300');
  const [natEnableLogging, setNatEnableLogging] = useState(false);
  const [natRouteMap, setNatRouteMap] = useState('');
  const [natVrf, setNatVrf] = useState('');

  // Use only the curated OID_DETAILS from oid_details.json
  const OID_MAP = useMemo(() => {
    const map = {};
    Object.entries(OID_DETAILS).forEach(([oid, details]) => {
      map[oid] = details.name ? `${details.name}${details.description ? ' - ' + details.description : ''}` : oid;
    });
    return map;
  }, []);

  // DNS Lookup function
  const performDNSLookup = useCallback(async () => {
    if (!dnsHostname.trim()) {
      setDnsError('Please enter a hostname');
      return;
    }

    setDnsLoading(true);
    setDnsError('');
    setDnsResult(null);

    try {
      // Using Google's public DNS-over-HTTPS API (CORS-friendly)
      const response = await fetch(
        `https://dns.google/resolve?name=${encodeURIComponent(dnsHostname)}&type=${dnsRecordType}`,
        { headers: { 'Accept': 'application/json' } }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}: DNS query failed`);

      const data = await response.json();

      if (!data.Answer || data.Answer.length === 0) {
        setDnsError(`No ${dnsRecordType} records found for ${dnsHostname}`);
        setDnsLoading(false);
        return;
      }

      const records = data.Answer.map(record => ({
        type: record.type === 1 ? 'A' : record.type === 28 ? 'AAAA' : record.type === 5 ? 'CNAME' : record.type === 15 ? 'MX' : record.type === 16 ? 'TXT' : record.type === 2 ? 'NS' : record.type === 6 ? 'SOA' : `TYPE${record.type}`,
        name: record.name,
        data: record.data,
        ttl: record.TTL
      }));

      setDnsResult({
        hostname: dnsHostname,
        recordType: dnsRecordType,
        records,
        timestamp: new Date().toLocaleString(),
        totalRecords: records.length
      });
    } catch (err) {
      setDnsError(`Error: ${err.message}. Check hostname and try again.`);
    } finally {
      setDnsLoading(false);
    }
  }, [dnsHostname, dnsRecordType]);

  // Hex/ASCII Converter functions
  const performHexAsciiConversion = useCallback(() => {
    setHexAsciiError('');
    setHexAsciiResult(null);

    if (!hexAsciiInput.trim()) {
      setHexAsciiError('Please enter input');
      return;
    }

    try {
      const encodeUtf8 = (value) => new TextEncoder().encode(value);
      const decodeUtf8 = (bytes) => new TextDecoder('utf-8', { fatal: false }).decode(bytes);
      const bytesToHex = (bytes) => {
        const joined = bytes
          .map(byte => byte.toString(16).padStart(2, '0'))
          .join(hexAsciiHexFormat === 'uppercase' ? ' ' : '');
        return hexAsciiHexFormat === 'uppercase' ? joined.toUpperCase() : joined.toLowerCase();
      };
      const bytesToBinary = (bytes) => bytes
        .map(byte => byte.toString(2).padStart(8, '0'))
        .join(' ');
      const base64FromBytes = (bytes) => {
        let binary = '';
        bytes.forEach(byte => { binary += String.fromCharCode(byte); });
        return btoa(binary);
      };
      const bytesFromBase64 = (value) => {
        const binary = atob(value);
        return Uint8Array.from(binary, (char) => char.charCodeAt(0));
      };

      let hex = '';
      let ascii = '';
      let base64 = '';
      let url = '';
      let binary = '';

      if (hexAsciiMode === 'ascii-to-hex') {
        // ASCII/UTF-8 to Hex
        const bytes = encodeUtf8(hexAsciiInput);
        hex = bytesToHex(Array.from(bytes));
        ascii = hexAsciiInput;
        base64 = base64FromBytes(bytes);
        url = encodeURIComponent(hexAsciiInput);
        binary = bytesToBinary(Array.from(bytes));
      } else if (hexAsciiMode === 'hex-to-ascii') {
        // Hex to ASCII/UTF-8
        const cleaned = hexAsciiInput.replace(/0x/gi, '').replace(/[^0-9a-fA-F]/g, '');
        if (!/^[0-9a-fA-F]*$/.test(cleaned)) throw new Error('Invalid hex input');
        if (cleaned.length % 2 !== 0) throw new Error('Hex string must have even length');
        const bytes = cleaned
          .match(/.{1,2}/g)
          .map(byte => parseInt(byte, 16));
        ascii = decodeUtf8(Uint8Array.from(bytes));
        hex = bytesToHex(bytes);
        base64 = base64FromBytes(Uint8Array.from(bytes));
        url = encodeURIComponent(ascii);
        binary = bytesToBinary(bytes);
      } else if (hexAsciiMode === 'base64-decode') {
        // Base64 to ASCII/UTF-8
        const bytes = bytesFromBase64(hexAsciiInput.trim());
        ascii = decodeUtf8(bytes);
        hex = bytesToHex(Array.from(bytes));
        base64 = hexAsciiInput.trim();
        url = encodeURIComponent(ascii);
        binary = bytesToBinary(Array.from(bytes));
      } else if (hexAsciiMode === 'base64-encode') {
        // ASCII/UTF-8 to Base64
        const bytes = encodeUtf8(hexAsciiInput);
        base64 = base64FromBytes(bytes);
        ascii = hexAsciiInput;
        hex = bytesToHex(Array.from(bytes));
        url = encodeURIComponent(ascii);
        binary = bytesToBinary(Array.from(bytes));
      } else if (hexAsciiMode === 'url-decode') {
        // URL decode to ASCII/UTF-8
        ascii = decodeURIComponent(hexAsciiInput.trim());
        const bytes = encodeUtf8(ascii);
        hex = bytesToHex(Array.from(bytes));
        base64 = base64FromBytes(bytes);
        url = hexAsciiInput.trim();
        binary = bytesToBinary(Array.from(bytes));
      }

      const byteCount = typeof TextEncoder !== 'undefined'
        ? new TextEncoder().encode(ascii).length
        : new Blob([ascii]).size;
      const graphemeCount = typeof Intl !== 'undefined' && Intl.Segmenter
        ? Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(ascii)).length
        : Array.from(ascii).length;

      setHexAsciiResult({
        hex,
        ascii,
        base64,
        url,
        binary,
        charCount: ascii.length,
        graphemeCount,
        byteCount
      });
    } catch (err) {
      setHexAsciiError(`Conversion error: ${err.message}`);
    }
  }, [hexAsciiInput, hexAsciiMode, hexAsciiHexFormat]);

  // TLS/SSL Checker function using Certspotter API (Certificate Transparency)
  const performSSLCheck = useCallback(async () => {
    const rawHost = sslDomain.trim();
    if (!rawHost) {
      setSslError('Please enter a domain');
      return;
    }

    const host = rawHost.replace(/^https?:\/\//i, '').replace(/\/.*/, '');
    setSslLoading(true);
    setSslError('');
    setSslResult(null);

    try {
      // Using Certspotter API - free, CORS-friendly Certificate Transparency API
      const apiUrl = `https://api.certspotter.com/v1/issuances?domain=${encodeURIComponent(host)}&include_subdomains=true&expand=dns_names&expand=issuer`;
      
      const response = await fetch(apiUrl, { 
        headers: { 
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const certData = await response.json();
      
      if (!Array.isArray(certData) || certData.length === 0) {
        setSslResult({
          host,
          status: 'NO_DATA',
          statusMessage: 'No certificates found',
          protocols: ['TLS 1.2', 'TLS 1.3'],
          certChain: [],
          issues: ['No HTTPS certificates found in Certificate Transparency logs.']
        });
        setSslLoading(false);
        return;
      }

      // Filter for most recent valid (non-expired) certificates
      const now = new Date();
      const validCerts = certData.filter(cert => {
        if (!cert.not_after) return false;
        try {
          const expiryDate = new Date(cert.not_after);
          return expiryDate > now; // Only non-expired
        } catch (e) {
          return false;
        }
      }).sort((a, b) => {
        // Sort by not_after date (latest expiry first)
        try {
          const dateA = new Date(a.not_after || 0).getTime();
          const dateB = new Date(b.not_after || 0).getTime();
          return dateB - dateA;
        } catch (e) {
          return 0;
        }
      }).slice(0, 5);

      // If no valid certs, show most recent ones
      const displayCerts = validCerts.length > 0 ? validCerts : certData.slice(0, 5);

      const certChain = displayCerts.map((cert) => ({
        subject: (cert.dns_names && cert.dns_names[0]) || host || 'Unknown',
        issuer: (cert.issuer && cert.issuer.name) || 'Unknown',
        notBefore: cert.not_before ? new Date(cert.not_before).toLocaleString() : 'Unknown',
        notAfter: cert.not_after ? new Date(cert.not_after).toLocaleString() : 'Unknown',
        sigAlg: cert.signature_algorithm || 'Unknown'
      }));

      const issues = [];
      
      if (validCerts.length > 0) {
        const latestCert = validCerts[0];
        if (latestCert && latestCert.not_after) {
          const expiryDate = new Date(latestCert.not_after);
          const diffMs = expiryDate.getTime() - Date.now();
          const expiresInDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
          
          if (expiresInDays <= 30) {
            issues.push(`⚠ Certificate expires in ${expiresInDays} days`);
          } else {
            issues.push(`✓ Certificate valid for ${expiresInDays} days`);
          }
        }
        issues.push(`✓ Found ${validCerts.length} valid certificates`);
      } else {
        issues.push(`⚠ Found ${certData.length} certificate records (all expired)`);
      }
      issues.push('✓ HTTPS support detected');
      issues.push('(Data from Certificate Transparency logs)');

      setSslResult({
        host,
        status: validCerts.length > 0 ? 'VALID' : 'EXPIRED',
        statusMessage: validCerts.length > 0 
          ? `Valid SSL certificates found for ${host}`
          : `Certificates exist but expired for ${host}`,
        protocols: ['TLS 1.2', 'TLS 1.3'],
        certChain,
        issues
      });
    } catch (err) {
      setSslError(`Error: ${err.message}. Try a different domain.`);
    } finally {
      setSslLoading(false);
    }
  }, [sslDomain]);

  // IP Geolocation function using ip-api.com
  const performGeolocation = useCallback(async () => {
    const ip = geoIP.trim();
    if (!ip) {
      setGeoError('Please enter an IP address');
      return;
    }

    setGeoLoading(true);
    setGeoError('');
    setGeoResult(null);

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000); // 12 second timeout

      // Using ipinfo.io - accurate ISP and organization data
      const response = await fetch(`https://ipinfo.io/${encodeURIComponent(ip)}/json`, {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      
      clearTimeout(timeout);

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data.ip) {
        setGeoError('No data found for this IP address.');
        setGeoLoading(false);
        return;
      }

      setGeoResult({
        ip: data.ip,
        country: data.country || 'Unknown',
        region: data.region || 'Unknown',
        city: data.city || 'Unknown',
        latitude: data.loc ? parseFloat(data.loc.split(',')[0]) : 0,
        longitude: data.loc ? parseFloat(data.loc.split(',')[1]) : 0,
        timezone: data.timezone || 'Unknown',
        isp: data.org || 'Unknown',
        org: data.org || 'Unknown',
        asn: data.asn || 'Unknown',
        mobile: false,
        proxy: false,
        hosting: false
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        setGeoError('Request timed out. API may be slow, try again.');
      } else {
        setGeoError(`Error: ${err.message}`);
      }
    } finally {
      setGeoLoading(false);
    }
  }, [geoIP]);

  // NAT Configuration Generator function
  const generateNatConfig = useCallback(() => {
    let config = '';
    const vrfSuffix = natVrf ? ` vrf ${natVrf}` : '';
    const useExtendedAcl = natEnableExempt;
    const iosAclConfig = useExtendedAcl
      ? `ip access-list extended ${natAcl}\n  deny ip ${natInsideSubnet} ${natInsideWildcard} ${natExemptDest} ${natExemptDestWildcard}\n  permit ip ${natInsideSubnet} ${natInsideWildcard} any\n  exit\n`
      : `ip access-list standard ${natAcl}\n  permit ${natInsideSubnet} ${natInsideWildcard}\n  exit\n`;
    const xrAclConfig = useExtendedAcl
      ? `ipv4 access-list ${natAcl}\n  deny ipv4 ${natInsideSubnet} ${natInsideWildcard} ${natExemptDest} ${natExemptDestWildcard}\n  permit ipv4 ${natInsideSubnet} ${natInsideWildcard} any\n  exit\n`
      : `ipv4 access-list ${natAcl}\n  permit ipv4 ${natInsideSubnet} ${natInsideWildcard} any\n  exit\n`;
    const routeMapConfig = natRouteMap
      ? `route-map ${natRouteMap} permit 10\n  match ip address ${natAcl}\n  exit\n`
      : '';

    if (natPlatform === 'ios') {
      // Cisco IOS (Traditional)
      config += `! NAT Configuration - Cisco IOS\n`;
      config += `!\n`;
      config += `! Configure inside interface\n`;
      config += `interface ${natInsideInt}\n`;
      config += `  ip nat inside\n`;
      config += `  exit\n`;
      config += `!\n`;
      config += `! Configure outside interface\n`;
      config += `interface ${natOutsideInt}\n`;
      config += `  ip nat outside\n`;
      config += `  exit\n`;
      config += `!\n`;

      if (natType === 'static') {
        config += `! Static NAT Configuration\n`;
        config += `ip nat inside source static ${natInsideLocal} ${natInsideGlobal}${vrfSuffix}\n`;
        if (natEnableStaticPat) {
          config += `ip nat inside source static ${natStaticProtocol} ${natInsideLocal} ${natStaticInsidePort} ${natInsideGlobal} ${natStaticGlobalPort}${vrfSuffix}\n`;
        }
        if (natEnableTwiceNat) {
          config += `ip nat inside source static ${natTwiceSrcLocal} ${natTwiceSrcGlobal} destination ${natTwiceDstLocal} ${natTwiceDstGlobal}\n`;
        }
        if (natOutsideLocal && natOutsideGlobal) {
          config += `ip nat outside source static ${natOutsideGlobal} ${natOutsideLocal}\n`;
        }
      } else if (natType === 'dynamic') {
        config += `! Dynamic NAT Configuration\n`;
        config += `ip nat pool ${natPool} ${natPoolStart} ${natPoolEnd} netmask ${natPoolMask}\n`;
        config += iosAclConfig;
        config += routeMapConfig;
        if (natRouteMap) {
          config += `ip nat inside source route-map ${natRouteMap} pool ${natPool}${vrfSuffix}\n`;
        } else {
          config += `ip nat inside source list ${natAcl} pool ${natPool}${vrfSuffix}\n`;
        }
      } else if (natType === 'pat') {
        config += `! PAT (Port Address Translation) Configuration\n`;
        config += iosAclConfig;
        config += routeMapConfig;
        if (natRouteMap) {
          config += `ip nat inside source route-map ${natRouteMap} interface ${natOutsideInt} overload${vrfSuffix}\n`;
        } else {
          config += `ip nat inside source list ${natAcl} interface ${natOutsideInt} overload${vrfSuffix}\n`;
        }
      }

      if (natEnableTimeouts) {
        config += `! NAT Translation Timeouts\n`;
        config += `ip nat translation timeout ${natTimeout}\n`;
        config += `ip nat translation tcp-timeout ${natTcpTimeout}\n`;
        config += `ip nat translation udp-timeout ${natUdpTimeout}\n`;
      }

      if (natEnableLogging) {
        config += `! NAT Logging\n`;
        config += `ip nat log translations syslog\n`;
      }

      config += `!\n`;
      config += `! Verify NAT configuration\n`;
      config += `! show ip nat translations\n`;
      config += `! show ip nat statistics\n`;
      config += `! debug ip nat\n`;

    } else if (natPlatform === 'iosxe') {
      // Cisco IOS XE
      config += `! NAT Configuration - Cisco IOS XE\n`;
      config += `!\n`;
      config += `! Configure inside interface\n`;
      config += `interface ${natInsideInt}\n`;
      config += `  ip nat inside\n`;
      config += `  exit\n`;
      config += `!\n`;
      config += `! Configure outside interface\n`;
      config += `interface ${natOutsideInt}\n`;
      config += `  ip nat outside\n`;
      config += `  exit\n`;
      config += `!\n`;

      if (natType === 'static') {
        config += `! Static NAT Configuration\n`;
        config += `ip nat inside source static ${natInsideLocal} ${natInsideGlobal}${vrfSuffix}\n`;
        if (natEnableStaticPat) {
          config += `ip nat inside source static ${natStaticProtocol} ${natInsideLocal} ${natStaticInsidePort} ${natInsideGlobal} ${natStaticGlobalPort}${vrfSuffix}\n`;
        }
        if (natEnableTwiceNat) {
          config += `ip nat inside source static ${natTwiceSrcLocal} ${natTwiceSrcGlobal} destination ${natTwiceDstLocal} ${natTwiceDstGlobal}\n`;
        }
        if (natOutsideLocal && natOutsideGlobal) {
          config += `ip nat outside source static ${natOutsideGlobal} ${natOutsideLocal}\n`;
        }
      } else if (natType === 'dynamic') {
        config += `! Dynamic NAT Configuration\n`;
        config += `ip nat pool ${natPool} ${natPoolStart} ${natPoolEnd} netmask ${natPoolMask}\n`;
        config += iosAclConfig;
        config += routeMapConfig;
        if (natRouteMap) {
          config += `ip nat inside source route-map ${natRouteMap} pool ${natPool}${vrfSuffix}\n`;
        } else {
          config += `ip nat inside source list ${natAcl} pool ${natPool}${vrfSuffix}\n`;
        }
      } else if (natType === 'pat') {
        config += `! PAT (Port Address Translation) Configuration\n`;
        config += iosAclConfig;
        config += routeMapConfig;
        if (natRouteMap) {
          config += `ip nat inside source route-map ${natRouteMap} interface ${natOutsideInt} overload${vrfSuffix}\n`;
        } else {
          config += `ip nat inside source list ${natAcl} interface ${natOutsideInt} overload${vrfSuffix}\n`;
        }
      }

      if (natEnableTimeouts) {
        config += `! NAT Translation Timeouts\n`;
        config += `ip nat translation timeout ${natTimeout}\n`;
        config += `ip nat translation tcp-timeout ${natTcpTimeout}\n`;
        config += `ip nat translation udp-timeout ${natUdpTimeout}\n`;
      }

      if (natEnableLogging) {
        config += `! NAT Logging\n`;
        config += `ip nat log translations syslog\n`;
      }

      config += `!\n`;
      config += `! Verify NAT configuration\n`;
      config += `! show ip nat translations\n`;
      config += `! show ip nat statistics\n`;

    } else if (natPlatform === 'nxos') {
      // Cisco NX-OS (Nexus)
      config += `! NAT Configuration - Cisco NX-OS\n`;
      config += `!\n`;
      config += `! Enable feature nat\n`;
      config += `feature nat\n`;
      config += `!\n`;
      config += `! Configure inside interface\n`;
      config += `interface ${natInsideInt}\n`;
      config += `  nat inside\n`;
      config += `  exit\n`;
      config += `!\n`;
      config += `! Configure outside interface\n`;
      config += `interface ${natOutsideInt}\n`;
      config += `  nat outside\n`;
      config += `  exit\n`;
      config += `!\n`;

      if (natType === 'static') {
        config += `! Static NAT Configuration\n`;
        config += `ip nat inside source static ${natInsideLocal} ${natInsideGlobal}\n`;
        if (natEnableStaticPat) {
          config += `ip nat inside source static ${natStaticProtocol} ${natInsideLocal} ${natStaticInsidePort} ${natInsideGlobal} ${natStaticGlobalPort}\n`;
        }
      } else if (natType === 'dynamic') {
        config += `! Dynamic NAT Configuration\n`;
        config += `ip nat pool ${natPool} ${natPoolStart} ${natPoolEnd} netmask ${natPoolMask}\n`;
        config += iosAclConfig;
        config += `ip nat inside source list ${natAcl} pool ${natPool}\n`;
      } else if (natType === 'pat') {
        config += `! PAT (Port Address Translation) Configuration\n`;
        config += iosAclConfig;
        config += `ip nat inside source list ${natAcl} interface ${natOutsideInt} overload\n`;
      }

      config += `!\n`;
      config += `! Verify NAT configuration\n`;
      config += `! show ip nat translations\n`;
      config += `! show ip nat statistics\n`;

    } else if (natPlatform === 'iosxr') {
      // Cisco IOS XR (ASR9k, CRS)
      config += `! NAT Configuration - Cisco IOS XR\n`;
      config += `!\n`;
      config += `! Configure inside interface\n`;
      config += `interface ${natInsideInt}\n`;
      config += `  nat inside\n`;
      config += `  exit\n`;
      config += `!\n`;
      config += `! Configure outside interface\n`;
      config += `interface ${natOutsideInt}\n`;
      config += `  nat outside\n`;
      config += `  exit\n`;
      config += `!\n`;

      if (natType === 'static') {
        config += `! Static NAT Configuration\n`;
        config += `router nat\n`;
        config += `  inside source static ${natInsideLocal} ${natInsideGlobal}\n`;
        config += `  exit\n`;
      } else if (natType === 'dynamic') {
        config += `! Dynamic NAT Configuration\n`;
        config += `ip nat pool ${natPool} ${natPoolStart} ${natPoolEnd} netmask ${natPoolMask}\n`;
        config += xrAclConfig;
        config += `router nat\n`;
        config += `  inside source list ${natAcl} pool ${natPool}\n`;
        config += `  exit\n`;
      } else if (natType === 'pat') {
        config += `! PAT Configuration\n`;
        config += xrAclConfig;
        config += `router nat\n`;
        config += `  inside source list ${natAcl} interface ${natOutsideInt} overload\n`;
        config += `  exit\n`;
      }

      config += `!\n`;
      config += `! Verify NAT configuration\n`;
      config += `! show nat translations\n`;
      config += `! show nat statistics\n`;
    }

    setNatConfig(config);
  }, [natType, natPlatform, natInsideLocal, natInsideGlobal, natOutsideLocal, natOutsideGlobal, natAcl, natPool, natPoolStart, natPoolEnd, natPoolMask, natInsideInt, natOutsideInt, natInsideSubnet, natInsideWildcard, natEnableExempt, natExemptDest, natExemptDestWildcard, natEnableStaticPat, natStaticProtocol, natStaticInsidePort, natStaticGlobalPort, natEnableTwiceNat, natTwiceSrcLocal, natTwiceSrcGlobal, natTwiceDstLocal, natTwiceDstGlobal, natEnableTimeouts, natTimeout, natTcpTimeout, natUdpTimeout, natEnableLogging, natRouteMap, natVrf]);

  // WHOIS Lookup function
  const performWhoisLookup = useCallback(async () => {
    const domain = whoisDomain.trim().replace(/^https?:\/\//i, '').replace(/\/.*/, '');
    if (!domain) {
      setWhoisError('Please enter a domain name');
      return;
    }

    setWhoisLoading(true);
    setWhoisError('');
    setWhoisResult(null);

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      // Use RDAP (Registration Data Access Protocol) - no API key needed
      let rdapUrl = `https://rdap.org/domain/${encodeURIComponent(domain)}`;
      
      const response = await fetch(rdapUrl, {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      
      clearTimeout(timeout);

      if (!response.ok) {
        throw new Error(`Failed to fetch WHOIS data (${response.status})`);
      }

      const data = await response.json();

      if (!data || !data.ldhName) {
        setWhoisError('No WHOIS data found for this domain');
        setWhoisLoading(false);
        return;
      }

      // Parse RDAP response
      const registrar = data.entities?.find(e => e.roles?.includes('registrar'));
      const registrant = data.entities?.find(e => e.roles?.includes('registrant'));
      
      setWhoisResult({
        domain: data.ldhName || domain,
        registrar: registrar?.vcardArray?.[1]?.find(v => v[0] === 'fn')?.[3] || registrar?.handle || 'Unknown',
        registrant: registrant?.vcardArray?.[1]?.find(v => v[0] === 'org')?.[3] || 
                    registrant?.vcardArray?.[1]?.find(v => v[0] === 'fn')?.[3] || 'Unknown',
        createdDate: data.events?.find(e => e.eventAction === 'registration')?.eventDate || 'Unknown',
        expiryDate: data.events?.find(e => e.eventAction === 'expiration')?.eventDate || 'Unknown',
        updatedDate: data.events?.find(e => e.eventAction === 'last changed')?.eventDate || 
                     data.events?.find(e => e.eventAction === 'last update of RDAP database')?.eventDate || 'Unknown',
        status: data.status || [],
        nameservers: data.nameservers?.map(ns => ns.ldhName) || [],
        dnssec: data.secureDNS?.delegationSigned ? 'Signed' : 'Unsigned',
        rawData: ''
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        setWhoisError('Request timed out. Try again.');
      } else {
        setWhoisError(`Error: ${err.message}`);
      }
    } finally {
      setWhoisLoading(false);
    }
  }, [whoisDomain]);

  const commonOIDs = OID_MAP;

  // Filtered OIDs for table view

  // 19. PoE Calculator
  const [poeDeviceCount, setPoeDeviceCount] = useState(5);
  const [poeDeviceType, setPoeDeviceType] = useState('ip-camera');
  const [poeSwitchPower, setPoeSwitchPower] = useState(370);
  const [poeResult, setPoeResult] = useState(null);
  const [poeError, setPoeError] = useState('');

  // 20. MTU Calculator
  const [mtuSize, setMtuSize] = useState(1500);
  const [mtuProtocol, setMtuProtocol] = useState('tcp');
  const [mtuFeatures, setMtuFeatures] = useState({
    ipv6: false,
    vlan: false,
    dot1q: true, // Single tag by default
    mpls: false,
    mplsLabels: 1
  });
  const [mtuResult, setMtuResult] = useState(null);
  const [mtuError, setMtuError] = useState('');

  // 21. Routing Metrics Calculator - FIXED
  const [metricBandwidth, setMetricBandwidth] = useState(100);
  const [metricDelay, setMetricDelay] = useState(100);
  const [metricLoad, setMetricLoad] = useState(1);
  const [metricReliability, setMetricReliability] = useState(255);
  const [metricResult, setMetricResult] = useState(null);
  const [metricError, setMetricError] = useState('');

  // 22. BGP Tools
  const [bgpAsn, setBgpAsn] = useState('64512');
  const [bgpRouterId, setBgpRouterId] = useState('1.1.1.1');
  const [bgpNeighbors, setBgpNeighbors] = useState([{ ip: '192.168.1.1', asn: '64513', description: 'Peer Router' }]);
  const [bgpNetworks, setBgpNetworks] = useState([{ network: '192.168.0.0', mask: '255.255.0.0' }]);
  const [bgpCommunities, setBgpCommunities] = useState([{ name: 'LOCAL', value: '64512:100' }]);
  const [bgpRouteMaps, setBgpRouteMaps] = useState([{ name: 'RM-IN', action: 'permit', sequence: 10 }]);
  const [bgpPrefixLists, setBgpPrefixLists] = useState([{ name: 'PL-IN', action: 'permit', network: '0.0.0.0/0' }]);
  const [bgpFeatures, setBgpFeatures] = useState({
    enableRouteReflector: false,
    enableConfederation: false,
    enableAggregation: false,
    enableIPv6: false,
    enableSecurity: true,
    enableCommunities: true,
    enableRouteMaps: false,
    enablePrefixLists: false
  });
  const [bgpConfederationId, setBgpConfederationId] = useState('64500');
  const [bgpConfederationMembers, setBgpConfederationMembers] = useState(['64512', '64513']);
  const [bgpResult, setBgpResult] = useState(null);
  const [bgpError, setBgpError] = useState('');

  // STP Calculator
  const [stpLinkSpeed, setStpLinkSpeed] = useState('1000');
  const [stpPathCost, setStpPathCost] = useState('');
  const [stpHopCount, setStpHopCount] = useState('1');
  const [stpBridgePriority, setStpBridgePriority] = useState('32768');
  const [stpMacAddress, setStpMacAddress] = useState('00:00:00:00:00:01');
  const [stpResult, setStpResult] = useState(null);

  // 23. ENHANCED Config Generator
  const [configDevice, setConfigDevice] = useState('router');
  const [configHostname, setConfigHostname] = useState('Router1');
  const [configDomain, setConfigDomain] = useState('network.lab');
  const [configTimezone, setConfigTimezone] = useState('UTC-5');
  const [configRoutingProtocol, setConfigRoutingProtocol] = useState('ospf');
  const [configOspfArea, setConfigOspfArea] = useState('0');
  const [configBgpAsn, setConfigBgpAsn] = useState('65001');
  const [configRedundancyProtocol, setConfigRedundancyProtocol] = useState('none');
  const [configVirtualIp, setConfigVirtualIp] = useState('192.168.1.254');
  const [configVlanId, setConfigVlanId] = useState('10');
  const [configVlanName, setConfigVlanName] = useState('USERS');
  const [configAdditionalVlans, setConfigAdditionalVlans] = useState([
    { id: 2, vlanId: '20', name: 'SERVERS', ip: '10.0.20.1', mask: '255.255.255.0' },
    { id: 3, vlanId: '30', name: 'MANAGEMENT', ip: '10.0.30.1', mask: '255.255.255.0' },
    { id: 99, vlanId: '99', name: 'NATIVE', ip: '', mask: '' }
  ]);
  const [configSecurityFeatures, setConfigSecurityFeatures] = useState({
    enableSSH: true,
    enableHTTPS: true,
    enableACLs: true,
    enablePortSecurity: true,
    enableStormControl: false,
    enableDot1x: false,
    enableLogging: true,
    enableNTP: true,
    enableSNMP: true,
    enableNetFlow: false,
    enableVTP: false,
    enableEtherChannel: false,
    enableDHCP: false,
    enableQoS: false,
    enableMulticast: false,
    enableVPN: false,
    // New advanced features
    enableNAT: false,
    enableIPv6: false,
    enableMPLS: false,
    enableGRE: false,
    enableDMVPN: false,
    enableIPSLAs: false,
    enableEEM: false,
    // Switch-specific advanced features
    enableMSTP: false,
    enablePrivateVLAN: false,
    enableDHCPSnooping: false,
    enableDAI: false,
    enableIPSourceGuard: false,
    enableBPDUGuard: false,
    enableRootGuard: false,
    enableLoopGuard: false,
    enableUDLD: false,
    enableFlexLinks: false
  });
  const [configInterfaces, setConfigInterfaces] = useState([
    { 
      id: 1, 
      name: 'GigabitEthernet0/0', 
      ip: '192.168.1.1', 
      mask: '255.255.255.0', 
      description: 'WAN Interface', 
      vlan: '1', 
      mode: 'routed',
      mtu: '1500',
      duplex: 'auto',
      speed: 'auto',
      enableCdp: true,
      enableLldp: true,
      enablePortSecurity: false,
      enableStormControl: false,
      voiceVlan: ''
    }
  ]);
  const [configResult, setConfigResult] = useState(null);
  const [configError, setConfigError] = useState('');

  // ==============================
  // HELPER FUNCTIONS
  // ==============================

  const ipToInt = useCallback((ip) => {
    try {
      return ip.split('.').reduce((acc, octet) => (acc << 8) + Number(octet), 0) >>> 0;
    } catch (error) {
      return 0;
    }
  }, []);

  const intToIp = useCallback((int) => {
    try {
      return [24, 16, 8, 0].map(shift => ((int >>> shift) & 255).toString()).join('.');
    } catch (error) {
      return '0.0.0.0';
    }
  }, []);

  const maskFromCidr = useCallback((cidr) => {
    try {
      return cidr === 0 ? 0 : (~0 << (32 - cidr)) >>> 0;
    } catch (error) {
      return 0;
    }
  }, []);

  const maskToDot = useCallback((maskInt) => intToIp(maskInt), [intToIp]);

  const generateRandomMAC = useCallback(() => {
    const hex = '0123456789ABCDEF';
    let mac = '';
    for (let i = 0; i < 6; i++) {
      mac += hex[Math.floor(Math.random() * 16)] + hex[Math.floor(Math.random() * 16)];
      if (i < 5) mac += ':';
    }
    return mac;
  }, []);

  const generatePassword = useCallback((length = 12, level = 'strong') => {
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    let chars = '';
    if (level === 'weak') {
      chars = lower + numbers;
    } else if (level === 'medium') {
      chars = lower + upper + numbers;
    } else {
      chars = lower + upper + numbers + symbols;
    }
    
    let password = '';
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    
    for (let i = 0; i < length; i++) {
      password += chars[array[i] % chars.length];
    }
    
    return password;
  }, []);

  // Helper function for large number multiplication by 2
  const multiplyByTwo = (numStr) => {
    let result = '';
    let carry = 0;
    for (let i = numStr.length - 1; i >= 0; i--) {
      const digit = parseInt(numStr[i]);
      const product = digit * 2 + carry;
      result = (product % 10) + result;
      carry = Math.floor(product / 10);
    }
    if (carry > 0) result = carry + result;
    return result;
  };

  // Helper function to subtract 1 from large number string
  const subtractOne = (numStr) => {
    const digits = numStr.split('');
    let borrow = 1;
    for (let i = digits.length - 1; i >= 0; i--) {
      const digit = parseInt(digits[i]);
      if (borrow > 0) {
        if (digit > 0) {
          digits[i] = (digit - 1).toString();
          borrow = 0;
        } else {
          digits[i] = '9';
          // borrow remains 1
        }
      }
    }
    // Remove leading zeros
    let result = digits.join('').replace(/^0+/, '');
    return result || '0';
  };

  // Helper function to format large numbers with commas
  const formatLargeNumber = (numStr) => {
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Password analysis functions
  const analyzePasswordStrength = useCallback((password) => {
    const length = password.length;
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSymbols = /[^a-zA-Z0-9]/.test(password);
    
    // Calculate entropy
    let charSet = 0;
    if (hasLower) charSet += 26;
    if (hasUpper) charSet += 26;
    if (hasNumbers) charSet += 10;
    if (hasSymbols) charSet += 32;
    
    const entropy = charSet > 0 ? Math.log2(charSet) * length : 0;
    
    // Determine strength
    let strength = 'Very Weak';
    let score = 0;
    let color = 'text-red-400';
    
    if (length >= 8) score += 1;
    if (length >= 12) score += 1;
    if (hasLower) score += 1;
    if (hasUpper) score += 1;
    if (hasNumbers) score += 1;
    if (hasSymbols) score += 1;
    
    if (score >= 6) {
      strength = 'Very Strong';
      color = 'text-green-400';
    } else if (score >= 5) {
      strength = 'Strong';
      color = 'text-green-300';
    } else if (score >= 4) {
      strength = 'Medium';
      color = 'text-yellow-400';
    } else if (score >= 3) {
      strength = 'Weak';
      color = 'text-orange-400';
    }
    
    return {
      length,
      hasLower,
      hasUpper,
      hasNumbers,
      hasSymbols,
      entropy: Math.round(entropy * 100) / 100,
      strength,
      score,
      color,
      suggestions: []
    };
  }, []);

  const estimateCrackingTime = useCallback((password) => {
    const analysis = analyzePasswordStrength(password);
    const entropy = analysis.entropy;
    
    // Calculate combinations
    const combinations = Math.pow(2, entropy);
    
    // Estimate cracking speeds (attempts per second)
    const speeds = {
      'Online Attack (Basic)': 1000, // 1k attempts/sec
      'Online Attack (Advanced)': 1000000, // 1M attempts/sec
      'Offline Attack (Fast Hash)': 1000000000, // 1B attempts/sec
      'Offline Attack (Slow Hash)': 1000000, // 1M attempts/sec
      'GPU Attack': 10000000000, // 10B attempts/sec
      'Supercomputer': 1000000000000 // 1T attempts/sec
    };
    
    const times = {};
    Object.entries(speeds).forEach(([method, speed]) => {
      const seconds = combinations / speed;
      times[method] = formatTime(seconds);
    });
    
    return {
      password,
      entropy: analysis.entropy,
      combinations: formatLargeNumber(Math.round(combinations).toString()),
      strength: analysis.strength,
      times
    };
  }, [analyzePasswordStrength]);

  const formatTime = (seconds) => {
    if (seconds < 1) return 'Instant';
    if (seconds < 60) return `${Math.round(seconds)} seconds`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} minutes`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)} hours`;
    if (seconds < 31536000) return `${Math.round(seconds / 86400)} days`;
    if (seconds < 31536000000) return `${Math.round(seconds / 31536000)} years`;
    return `${Math.round(seconds / 31536000000)} centuries`;
  };

  const generatePasswordPolicy = useCallback(() => {
    const policies = [
      {
        name: 'Basic Security',
        minLength: 8,
        requireUpper: false,
        requireLower: true,
        requireNumbers: true,
        requireSymbols: false,
        maxAge: 90,
        history: 5
      },
      {
        name: 'Standard Security',
        minLength: 12,
        requireUpper: true,
        requireLower: true,
        requireNumbers: true,
        requireSymbols: false,
        maxAge: 60,
        history: 10
      },
      {
        name: 'High Security',
        minLength: 16,
        requireUpper: true,
        requireLower: true,
        requireNumbers: true,
        requireSymbols: true,
        maxAge: 30,
        history: 24
      },
      {
        name: 'Critical Security',
        minLength: 20,
        requireUpper: true,
        requireLower: true,
        requireNumbers: true,
        requireSymbols: true,
        maxAge: 7,
        history: 50
      }
    ];
    
    return policies[Math.floor(Math.random() * policies.length)];
  }, []);

  // IPv6 Helper Functions
  const expandIPv6 = useCallback((ip) => {
    // Remove any leading/trailing whitespace
    ip = ip.trim();
    
    // Handle IPv4-mapped IPv6 addresses (but convert to proper IPv6)
    if (ip.includes('.') && ip.includes('::ffff:')) {
      // IPv4-mapped IPv6 like ::ffff:192.168.1.1
      const ipv4Part = ip.split('::ffff:')[1];
      const ipv4Bytes = ipv4Part.split('.').map(x => parseInt(x, 10));
      const ipv6Bytes = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 255, 255, ...ipv4Bytes];
      return ipv6Bytes.map(b => b.toString(16).padStart(2, '0')).join('').match(/.{4}/g).join(':');
    }
    
    // Split by double colon
    const parts = ip.split('::');
    
    if (parts.length === 1) {
      // No compression
      const groups = parts[0].split(':');
      // Ensure 8 groups, pad with zeros
      while (groups.length < 8) {
        groups.push('0');
      }
      return groups.map(g => g.padStart(4, '0')).join(':');
    } else {
      // Has compression
      const left = parts[0].split(':').filter(x => x !== '');
      const right = parts[1].split(':').filter(x => x !== '');
      const zerosNeeded = 8 - (left.length + right.length);
      const zeros = Array(zerosNeeded).fill('0000');
      return [...left, ...zeros, ...right].map(g => g.padStart(4, '0')).join(':');
    }
  }, []);

  const compressIPv6 = useCallback((ip) => {
    const groups = ip.split(':');
    
    // Find the longest consecutive sequence of zeros
    let bestStart = -1;
    let bestLength = 0;
    let currentStart = -1;
    let currentLength = 0;
    
    for (let i = 0; i < groups.length; i++) {
      if (groups[i] === '0000' || groups[i] === '0') {
        if (currentStart === -1) {
          currentStart = i;
          currentLength = 1;
        } else {
          currentLength++;
        }
      } else {
        if (currentLength > bestLength) {
          bestLength = currentLength;
          bestStart = currentStart;
        }
        currentStart = -1;
        currentLength = 0;
      }
    }
    
    // Check if the last sequence is the longest
    if (currentLength > bestLength) {
      bestLength = currentLength;
      bestStart = currentStart;
    }
    
    // Compress the longest zero sequence
    if (bestLength > 1) {
      const compressed = [
        ...groups.slice(0, bestStart),
        '',
        ...groups.slice(bestStart + bestLength)
      ].join(':');
      
      // Handle edge cases
      if (compressed.startsWith(':')) return ':' + compressed.slice(1);
      if (compressed.endsWith(':')) return compressed.slice(0, -1) + ':';
      if (compressed === '') return '::';
      
      return compressed.replace(':::', '::').replace(':::', '::');
    }
    
    return ip;
  }, []);

  const showError = (setErrorFn, message) => {
    setErrorFn(message);
    setTimeout(() => setErrorFn(''), 5000);
  };

  // Helper functions for subnet calculation
  const getNetworkClass = useCallback((ipInt) => {
    const firstOctet = (ipInt >>> 24) & 0xff;
    if (firstOctet < 128) return 'A';
    if (firstOctet < 192) return 'B';
    if (firstOctet < 224) return 'C';
    if (firstOctet < 240) return 'D (Multicast)';
    return 'E (Experimental)';
  }, []);

  const isPrivateIP = useCallback((ipInt) => {
    const ip = intToIp(ipInt);
    const privateRanges = [
      { start: '10.0.0.0', end: '10.255.255.255', name: 'RFC1918 Class A' },
      { start: '172.16.0.0', end: '172.31.255.255', name: 'RFC1918 Class B' },
      { start: '192.168.0.0', end: '192.168.255.255', name: 'RFC1918 Class C' },
      { start: '169.254.0.0', end: '169.254.255.255', name: 'Link-local' },
      { start: '127.0.0.0', end: '127.255.255.255', name: 'Loopback' }
    ];
    
    const ipNum = ipToInt(ip);
    for (const range of privateRanges) {
      const startNum = ipToInt(range.start);
      const endNum = ipToInt(range.end);
      if (ipNum >= startNum && ipNum <= endNum) {
        return range.name;
      }
    }
    return 'Public';
  }, [intToIp, ipToInt]);

  // ==============================
  // TOOL CALCULATION FUNCTIONS
  // ==============================

  // 1. Subnet calculation
  const calculateSubnet = useCallback(() => {
    try {
      setSubnetError('');
      
      const cidrNum = parseInt(cidr, 10);
      
      if (!validateCIDR(cidrNum)) {
        showError(setSubnetError, 'CIDR must be a number between 0 and 32');
        return;
      }
      
      if (!validateIP(ipAddress)) {
        showError(setSubnetError, 'Please enter a valid IP address');
        return;
      }

      const ipInt = ipToInt(ipAddress);
      const maskInt = maskFromCidr(cidrNum);
      const networkInt = ipInt & maskInt;
      const broadcastInt = networkInt | (~maskInt >>> 0);
      
      const totalHosts = cidrNum <= 30 ? (broadcastInt - networkInt + 1) >>> 0 : (cidrNum === 31 ? 2 : 1);
      const usableHosts = cidrNum <= 30 ? (totalHosts > 2 ? totalHosts - 2 : 0) : (cidrNum === 31 ? 2 : 1);

      const firstHostInt = usableHosts > 0 ? networkInt + 1 : networkInt;
      const lastHostInt = usableHosts > 0 ? broadcastInt - 1 : broadcastInt;

      const networkClass = getNetworkClass(ipInt);
      const privateRange = isPrivateIP(ipInt);
      
      setSubnetResults({
        network: intToIp(networkInt),
        broadcast: intToIp(broadcastInt),
        mask: maskToDot(maskInt),
        firstHost: intToIp(firstHostInt),
        lastHost: intToIp(lastHostInt),
        totalHosts,
        usableHosts,
        cidr: cidrNum,
        networkClass,
        privateRange,
        ipRange: `${intToIp(networkInt)} - ${intToIp(broadcastInt)}`,
        subnetBits: 32 - cidrNum
      });
    } catch (error) {
      showError(setSubnetError, `Error calculating subnet: ${error.message}`);
    }
  }, [ipAddress, cidr, validateCIDR, validateIP, ipToInt, maskFromCidr, intToIp, maskToDot, getNetworkClass, isPrivateIP]);

  // 2. Binary converter - FIXED alignment
  const calculateBinary = useCallback(() => {
    try {
      setBinaryError('');
      
      if (!validateIP(decimalIP)) {
        showError(setBinaryError, 'Please enter a valid IP address');
        return;
      }
      
      const binary = decimalIP.split('.').map(p => {
        const num = parseInt(p, 10);
        return num.toString(2).padStart(8, '0');
      }).join('.');

      const hex = decimalIP.split('.').map(p => {
        const num = parseInt(p, 10);
        return num.toString(16).toUpperCase().padStart(2, '0');
      }).join('.');
      
      // Calculate total 32-bit binary
      const ipInt = ipToInt(decimalIP);
      const totalBinary = ipInt.toString(2).padStart(32, '0');
      
      setBinaryResult({
        binary,
        hex,
        decimal: decimalIP,
        totalBinary,
        octets: decimalIP.split('.').map((p, i) => ({
          decimal: p,
          binary: parseInt(p, 10).toString(2).padStart(8, '0'),
          hex: parseInt(p, 10).toString(16).toUpperCase().padStart(2, '0')
        }))
      });
    } catch (error) {
      showError(setBinaryError, `Error converting to binary: ${error.message}`);
    }
  }, [decimalIP, validateIP, ipToInt]);

  // 3. Wildcard mask calc
  const calculateWildcard = useCallback(() => {
    try {
      setWildcardError('');
      
      if (!validateIP(wildcardIP)) {
        showError(setWildcardError, 'Invalid subnet mask');
        return;
      }
      
      const wildcardParts = wildcardIP.split('.').map(p => 255 - parseInt(p, 10));
      const wildcard = wildcardParts.join('.');
      
      const subnetParts = wildcardParts.map(p => 255 - p);
      const subnet = subnetParts.join('.');
      
      const binaryMask = subnetParts.map(p => 
        parseInt(p, 10).toString(2).padStart(8, '0')
      ).join('');
      const cidr = binaryMask.split('1').length - 1;
      
      setWildcardResult({
        wildcard,
        subnet,
        cidr,
        binary: subnetParts.map(p => 
          parseInt(p, 10).toString(2).padStart(8, '0')
        ).join('.')
      });
    } catch (error) {
      showError(setWildcardError, `Error calculating wildcard: ${error.message}`);
    }
  }, [wildcardIP, validateIP]);

  // 4. MAC lookup
  const lookupMac = useCallback(() => {
    try {
      setMacError('');

      if (!validateMAC(macAddress)) {
        showError(setMacError, 'Invalid MAC address format');
        return;
      }

      const cleaned = macAddress.toUpperCase().replace(/[^0-9A-F]/g, '');
      if (cleaned.length < 6) {
        showError(setMacError, 'MAC address too short');
        return;
      }

      // Build OUI in normalized form XX:XX:XX
      const oui = cleaned.slice(0, 6).match(/.{2}/g).join(':');
      const vendor = (OUI_SAMPLE && OUI_SAMPLE[oui]) ? OUI_SAMPLE[oui] : 'Unknown Vendor';

      // Format MAC as XX:XX:XX:XX:XX:XX
      const formattedMAC = cleaned.match(/.{2}/g).join(':');

      const firstOctet = parseInt(cleaned.slice(0, 2), 16);
      const isMulticast = (firstOctet & 0x01) === 1;
      const isLocal = (firstOctet & 0x02) === 2;

      setMacResult({
        oui,
        vendor,
        formattedMAC,
        isMulticast,
        isLocal,
        type: isMulticast ? 'Multicast' : 'Unicast',
        scope: isLocal ? 'Locally Administered' : 'Universally Administered'
      });
    } catch (error) {
      showError(setMacError, `Error looking up MAC: ${error.message}`);
    }
  }, [macAddress, validateMAC, OUI_SAMPLE]);

  // 5. VLSM calculation
  const calculateVLSM = useCallback(() => {
    try {
      setVlsmError('');
      
      const baseCidr = parseInt(vlsmCidr, 10);
      if (!validateCIDR(baseCidr)) {
        showError(setVlsmError, 'Base CIDR must be between 0 and 32');
        return;
      }
      
      if (!validateIP(vlsmNetwork)) {
        showError(setVlsmError, 'Invalid base network address');
        return;
      }

      const netInt = ipToInt(vlsmNetwork);
      const baseMask = maskFromCidr(baseCidr);
      const networkStart = netInt & baseMask;
      const availableAddresses = (2 ** (32 - baseCidr));

      const reqs = [...vlsmRequirements]
        .filter(r => r.hosts > 0 && r.name.trim())
        .sort((a, b) => b.hosts - a.hosts);

      if (reqs.length === 0) {
        showError(setVlsmError, 'Please add requirements');
        return;
      }

      let currentAddress = networkStart;
      const results = [];
      let totalAllocated = 0;

      for (const r of reqs) {
        const neededHosts = r.hosts + 2;
        const bitsForHosts = Math.ceil(Math.log2(neededHosts));
        const newPrefix = 32 - bitsForHosts;
        const blockSize = 2 ** bitsForHosts;
        
        if (currentAddress % blockSize !== 0) {
          currentAddress = (currentAddress + blockSize) & ~(blockSize - 1);
        }
        
        const blockEnd = (currentAddress + blockSize - 1) >>> 0;
        const baseNetworkEnd = (networkStart + availableAddresses - 1) >>> 0;
        
        if (blockEnd > baseNetworkEnd) {
          showError(setVlsmError, `Not enough space for "${r.name}"`);
          return;
        }

        const maskInt = maskFromCidr(newPrefix);
        const network = currentAddress;
        const broadcast = blockEnd;
        const usableHosts = blockSize > 2 ? blockSize - 2 : (blockSize === 1 ? 1 : 0);

        results.push({
          id: r.id,
          name: r.name,
          hosts: r.hosts,
          network: intToIp(network),
          cidr: newPrefix,
          mask: maskToDot(maskInt),
          broadcast: intToIp(broadcast),
          usableHosts,
          firstUsable: blockSize > 2 ? intToIp(network + 1) : intToIp(network),
          lastUsable: blockSize > 2 ? intToIp(broadcast - 1) : intToIp(broadcast),
          blockSize
        });

        currentAddress = (broadcast + 1) >>> 0;
        totalAllocated += blockSize;
      }

      const utilization = ((totalAllocated / availableAddresses) * 100).toFixed(2);
      const wasted = availableAddresses - totalAllocated;
      
      setVlsmResults({
        allocations: results,
        totalUsed: totalAllocated,
        available: availableAddresses,
        utilization,
        wasted,
        efficiency: ((totalAllocated / (totalAllocated + wasted)) * 100).toFixed(2),
        baseNetwork: intToIp(networkStart),
        baseCIDR: baseCidr
      });
    } catch (error) {
      showError(setVlsmError, `Error calculating VLSM: ${error.message}`);
    }
  }, [vlsmNetwork, vlsmCidr, vlsmRequirements, validateCIDR, validateIP, ipToInt, maskFromCidr, intToIp, maskToDot]);

  // 6. Route Summarization
  const calculateSummarization = useCallback(() => {
    try {
      setSummarizeError('');
      
      const validIPs = summarizeIPs.filter(ip => {
        const parts = ip.split('/');
        return parts.length === 2 && validateIP(parts[0]) && validateCIDR(parseInt(parts[1], 10));
      });

      if (validIPs.length < 2) {
        showError(setSummarizeError, 'Please enter valid IP ranges');
        return;
      }

      const ipRanges = validIPs.map(ip => {
        const [address, prefix] = ip.split('/');
        const prefixNum = parseInt(prefix, 10);
        const mask = maskFromCidr(prefixNum);
        const network = ipToInt(address) & mask;
        const broadcast = network | (~mask >>> 0);
        return { 
          network, 
          broadcast, 
          prefix: prefixNum,
          original: ip,
          networkIP: intToIp(network)
        };
      });

      const minNetwork = Math.min(...ipRanges.map(r => r.network));
      const maxBroadcast = Math.max(...ipRanges.map(r => r.broadcast));

      let commonPrefix = 32;
      for (let i = 0; i < ipRanges.length; i++) {
        for (let j = i + 1; j < ipRanges.length; j++) {
          const xor = ipRanges[i].network ^ ipRanges[j].network;
          if (xor === 0) continue;
          
          const leadingZeros = 32 - Math.floor(Math.log2(xor)) - 1;
          if (leadingZeros < commonPrefix) {
            commonPrefix = leadingZeros;
          }
        }
      }

      while (commonPrefix > 0) {
        const summaryMask = maskFromCidr(commonPrefix);
        const summaryNetwork = minNetwork & summaryMask;
        const summaryBroadcast = summaryNetwork | (~summaryMask >>> 0);
        
        if (summaryNetwork <= minNetwork && summaryBroadcast >= maxBroadcast) {
          break;
        }
        commonPrefix--;
      }

      const summaryNetwork = minNetwork & maskFromCidr(commonPrefix);
      const summaryBroadcast = summaryNetwork | (~maskFromCidr(commonPrefix) >>> 0);
      
      const sortedRanges = [...ipRanges].sort((a, b) => a.network - b.network);
      let hasGaps = false;
      for (let i = 1; i < sortedRanges.length; i++) {
        if (sortedRanges[i].network > sortedRanges[i-1].broadcast + 1) {
          hasGaps = true;
          break;
        }
      }

      setSummarizeResult({
        summary: `${intToIp(summaryNetwork)}/${commonPrefix}`,
        originalCount: ipRanges.length,
        mask: maskToDot(maskFromCidr(commonPrefix)),
        network: intToIp(summaryNetwork),
        broadcast: intToIp(summaryBroadcast),
        totalAddresses: 2 ** (32 - commonPrefix),
        hasGaps,
        efficiency: ((ipRanges.length * 100) / (2 ** (32 - commonPrefix))).toFixed(2)
      });
    } catch (error) {
      showError(setSummarizeError, `Error calculating summarization: ${error.message}`);
    }
  }, [summarizeIPs, validateIP, validateCIDR, maskFromCidr, ipToInt, intToIp, maskToDot]);

  // 7. Bandwidth Calculator
  const calculateBandwidth = useCallback(() => {
    try {
      setBandwidthError('');
      
      const fileSizeNum = parseFloat(fileSize);
      const bandwidthNum = parseFloat(bandwidth);
      
      if (fileSizeNum <= 0 || bandwidthNum <= 0) {
        showError(setBandwidthError, 'File size and bandwidth must be greater than 0');
        return;
      }

      let fileSizeBits;
      switch (sizeUnit) {
        case 'GB': fileSizeBits = fileSizeNum * 8 * 1024 * 1024 * 1024; break;
        case 'MB': fileSizeBits = fileSizeNum * 8 * 1024 * 1024; break;
        case 'KB': fileSizeBits = fileSizeNum * 8 * 1024; break;
        default: fileSizeBits = fileSizeNum * 8;
      }

      let bandwidthBitsPerSec;
      switch (bandwidthUnit) {
        case 'Gbps': bandwidthBitsPerSec = bandwidthNum * 1000000000; break;
        case 'Mbps': bandwidthBitsPerSec = bandwidthNum * 1000000; break;
        case 'Kbps': bandwidthBitsPerSec = bandwidthNum * 1000; break;
        default: bandwidthBitsPerSec = bandwidthNum;
      }

      const timeSeconds = fileSizeBits / bandwidthBitsPerSec;
      
      let timeFormatted;
      if (timeSeconds < 0.001) {
        timeFormatted = `${(timeSeconds * 1000000).toFixed(2)} μs`;
      } else if (timeSeconds < 1) {
        timeFormatted = `${(timeSeconds * 1000).toFixed(2)} ms`;
      } else if (timeSeconds < 60) {
        timeFormatted = `${timeSeconds.toFixed(2)} seconds`;
      } else if (timeSeconds < 3600) {
        const minutes = Math.floor(timeSeconds / 60);
        const seconds = timeSeconds % 60;
        timeFormatted = `${minutes}m ${seconds.toFixed(2)}s`;
      } else {
        const hours = Math.floor(timeSeconds / 3600);
        const minutes = Math.floor((timeSeconds % 3600) / 60);
        const seconds = timeSeconds % 60;
        timeFormatted = `${hours}h ${minutes}m ${seconds.toFixed(2)}s`;
      }

      const effectiveSpeed = (fileSizeBits / 1000000 / timeSeconds);
      const packetSize = 1500 * 8;
      const overheadPerPacket = 40 * 8;
      const packets = Math.ceil(fileSizeBits / packetSize);
      const totalWithOverhead = fileSizeBits + (packets * overheadPerPacket);
      const timeWithOverhead = totalWithOverhead / bandwidthBitsPerSec;
      const overheadPercentage = ((timeWithOverhead - timeSeconds) / timeSeconds * 100).toFixed(1);

      setBandwidthResult({
        time: timeFormatted,
        mbps: effectiveSpeed.toFixed(2),
        rawSeconds: timeSeconds,
        packets,
        overhead: overheadPercentage,
        timeWithOverhead: timeWithOverhead.toFixed(2) + 's'
      });
    } catch (error) {
      showError(setBandwidthError, `Error calculating bandwidth: ${error.message}`);
    }
  }, [fileSize, sizeUnit, bandwidth, bandwidthUnit]);

  // 8. Protocol Decoder
  const decodeProtocol = useCallback(() => {
    try {
      const raw = protocolDatabase[protocolCode];
      const result = raw || 'Unknown protocol number';
      const [name, description] = (result || '').split(' - ');

      const details = {
        'TCP': { portBased: true, connectionOriented: true, reliable: true },
        'UDP': { portBased: true, connectionOriented: false, reliable: false },
        'ICMP': { portBased: false, connectionOriented: false, reliable: false },
        'IGMP': { portBased: false, connectionOriented: false, reliable: false },
        'GRE': { portBased: false, connectionOriented: false, reliable: false },
        'ESP': { portBased: false, connectionOriented: false, reliable: true },
        'AH': { portBased: false, connectionOriented: false, reliable: true },
        'OSPF': { portBased: false, connectionOriented: true, reliable: true },
        'EIGRP': { portBased: false, connectionOriented: true, reliable: true }
      };

      const ianaUrl = `https://www.iana.org/assignments/protocol-numbers/protocol-numbers.xhtml`;

      setProtocolResult({
        number: protocolCode,
        name: name || result,
        description: description || result,
        hex: '0x' + parseInt(protocolCode, 10).toString(16).toUpperCase().padStart(2, '0'),
        binary: parseInt(protocolCode, 10).toString(2).padStart(8, '0'),
        details: details[name?.split(' ')[0]] || {},
        source: raw ? 'IANA' : 'Local',
        ianaUrl,
        registryRaw: raw || ''
      });
    } catch (error) {
      console.error('Error decoding protocol:', error);
    }
  }, [protocolCode, protocolDatabase]);

  // 9. ACL Generator
  const [aclSource, setAclSource] = useState('any');
  const [aclDest, setAclDest] = useState('any');
  const [aclProtocol, setAclProtocol] = useState('ip');
  const [aclAction, setAclAction] = useState('permit');
  const [aclPort, setAclPort] = useState('any');
  const [aclResult, setAclResult] = useState(null);
  const [aclError, setAclError] = useState('');

  const generateACL = useCallback(() => {
    try {
      setAclError('');

      if (!aclSource.trim() || !aclDest.trim()) {
        showError(setAclError, 'Source and destination are required');
        return;
      }

      // Validate IP addresses/networks
      const validateNetwork = (network) => {
        if (network === 'any') return true;
        const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
        if (!ipRegex.test(network)) return false;
        const parts = network.split('/')[0].split('.');
        return parts.every(part => parseInt(part) >= 0 && parseInt(part) <= 255);
      };

      if (!validateNetwork(aclSource) || !validateNetwork(aclDest)) {
        showError(setAclError, 'Invalid IP address or network format');
        return;
      }

      // Generate ACL number (1-99 for standard, 100-199 for extended)
      const aclNumber = aclProtocol === 'ip' ? Math.floor(Math.random() * 99) + 1 : Math.floor(Math.random() * 99) + 100;

      let ciscoStandard = '';
      let ciscoExtended = '';
      let juniper = '';
      let explanation = '';

      if (aclProtocol === 'ip') {
        // Standard ACL (source only)
        ciscoStandard = `access-list ${aclNumber} ${aclAction} ${aclSource}`;
        ciscoExtended = `ip access-list standard ACL-${aclNumber}\n ${aclAction} ${aclSource}`;
        juniper = `set firewall family inet filter ACL-${aclNumber} term TERM-1 from source-address ${aclSource}\nset firewall family inet filter ACL-${aclNumber} term TERM-1 then ${aclAction}`;
        explanation = `${aclAction.toUpperCase()} all IP traffic from ${aclSource}`;
      } else {
        // Extended ACL
        const portClause = aclPort !== 'any' ? ` eq ${aclPort}` : '';
        ciscoStandard = `access-list ${aclNumber} ${aclAction} ${aclProtocol} ${aclSource} ${aclDest}${portClause}`;
        ciscoExtended = `ip access-list extended ACL-${aclNumber}\n ${aclAction} ${aclProtocol} ${aclSource} ${aclDest}${portClause}`;
        juniper = `set firewall family inet filter ACL-${aclNumber} term TERM-1 from protocol ${aclProtocol}\nset firewall family inet filter ACL-${aclNumber} term TERM-1 from source-address ${aclSource}\nset firewall family inet filter ACL-${aclNumber} term TERM-1 from destination-address ${aclDest}`;
        if (aclPort !== 'any') {
          juniper += `\nset firewall family inet filter ACL-${aclNumber} term TERM-1 from destination-port ${aclPort}`;
        }
        juniper += `\nset firewall family inet filter ACL-${aclNumber} term TERM-1 then ${aclAction}`;
        explanation = `${aclAction.toUpperCase()} ${aclProtocol.toUpperCase()} traffic from ${aclSource} to ${aclDest}${portClause ? ` on port ${aclPort}` : ''}`;
      }

      // Add implicit deny for extended ACLs
      if (aclProtocol !== 'ip') {
        ciscoExtended += '\n deny ip any any';
        juniper += `\nset firewall family inet filter ACL-${aclNumber} term TERM-99 then deny`;
      }

      setAclResult({
        ciscoStandard,
        ciscoExtended,
        juniper,
        explanation,
        aclNumber,
        aclType: aclProtocol === 'ip' ? 'Standard' : 'Extended'
      });
    } catch (error) {
      showError(setAclError, `Error generating ACL: ${error.message}`);
    }
  }, [aclSource, aclDest, aclProtocol, aclAction, aclPort]);

  // Fallback hash implementations for deprecated algorithms
  const generateMD5 = async (input) => {
    // MD5 implementation based on RSA Data Security, Inc. MD5 Message-Digest Algorithm
    function md5(input) {
      function addUnsigned(a, b) {
        const lsw = (a & 0xFFFF) + (b & 0xFFFF);
        const msw = (a >> 16) + (b >> 16) + (lsw >> 16);
        return (msw << 16) | (lsw & 0xFFFF);
      }

      function leftRotate(value, shift) {
        return (value << shift) | (value >>> (32 - shift));
      }

      function F(x, y, z) { return (x & y) | (~x & z); }
      function G(x, y, z) { return (x & z) | (y & ~z); }
      function H(x, y, z) { return x ^ y ^ z; }
      function I(x, y, z) { return y ^ (x | ~z); }

      function FF(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(F(b, c, d), x), ac));
        return addUnsigned(leftRotate(a, s), b);
      }

      function GG(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(G(b, c, d), x), ac));
        return addUnsigned(leftRotate(a, s), b);
      }

      function HH(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(H(b, c, d), x), ac));
        return addUnsigned(leftRotate(a, s), b);
      }

      function II(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(I(b, c, d), x), ac));
        return addUnsigned(leftRotate(a, s), b);
      }

      function convertToWordArray(input) {
        const messageLength = input.length;
        const numberOfWordsTemp1 = messageLength + 8;
        const numberOfWordsTemp2 = (numberOfWordsTemp1 - (numberOfWordsTemp1 % 64)) / 64;
        const numberOfWords = (numberOfWordsTemp2 + 1) * 16;
        const wordArray = new Array(numberOfWords - 1);

        for (let i = 0; i < numberOfWords - 1; i++) {
          wordArray[i] = 0;
        }

        let bytePosition = 0;
        let byteCount = 0;

        while (byteCount < messageLength) {
          const count = (byteCount - (byteCount % 4)) / 4;
          bytePosition = (byteCount % 4) * 8;
          wordArray[count] = (wordArray[count] | (input.charCodeAt(byteCount) << bytePosition));
          byteCount++;
        }

        const count2 = (byteCount - (byteCount % 4)) / 4;
        bytePosition = (byteCount % 4) * 8;
        wordArray[count2] = wordArray[count2] | (0x80 << bytePosition);
        wordArray[numberOfWords - 2] = messageLength << 3;
        wordArray[numberOfWords - 1] = messageLength >>> 29;

        return wordArray;
      }

      function wordToHex(value) {
        let hex = '';
        for (let i = 0; i <= 3; i++) {
          const byte = (value >>> (i * 8)) & 255;
          const char = '0' + byte.toString(16);
          hex += char.slice(-2);
        }
        return hex;
      }

      const x = convertToWordArray(input);
      let a = 0x67452301;
      let b = 0xEFCDAB89;
      let c = 0x98BADCFE;
      let d = 0x10325476;

      for (let i = 0; i < x.length; i += 16) {
        const AA = a, BB = b, CC = c, DD = d;

        a = FF(a, b, c, d, x[i + 0], 7, 0xD76AA478);
        d = FF(d, a, b, c, x[i + 1], 12, 0xE8C7B756);
        c = FF(c, d, a, b, x[i + 2], 17, 0x242070DB);
        b = FF(b, c, d, a, x[i + 3], 22, 0xC1BDCEEE);
        a = FF(a, b, c, d, x[i + 4], 7, 0xF57C0FAF);
        d = FF(d, a, b, c, x[i + 5], 12, 0x4787C62A);
        c = FF(c, d, a, b, x[i + 6], 17, 0xA8304613);
        b = FF(b, c, d, a, x[i + 7], 22, 0xFD469501);
        a = FF(a, b, c, d, x[i + 8], 7, 0x698098D8);
        d = FF(d, a, b, c, x[i + 9], 12, 0x8B44F7AF);
        c = FF(c, d, a, b, x[i + 10], 17, 0xFFFF5BB1);
        b = FF(b, c, d, a, x[i + 11], 22, 0x895CD7BE);
        a = FF(a, b, c, d, x[i + 12], 7, 0x6B901122);
        d = FF(d, a, b, c, x[i + 13], 12, 0xFD987193);
        c = FF(c, d, a, b, x[i + 14], 17, 0xA679438E);
        b = FF(b, c, d, a, x[i + 15], 22, 0x49B40821);

        a = GG(a, b, c, d, x[i + 1], 5, 0xF61E2562);
        d = GG(d, a, b, c, x[i + 6], 9, 0xC040B340);
        c = GG(c, d, a, b, x[i + 11], 14, 0x265E5A51);
        b = GG(b, c, d, a, x[i + 0], 20, 0xE9B6C7AA);
        a = GG(a, b, c, d, x[i + 5], 5, 0xD62F105D);
        d = GG(d, a, b, c, x[i + 10], 9, 0x02441453);
        c = GG(c, d, a, b, x[i + 15], 14, 0xD8A1E681);
        b = GG(b, c, d, a, x[i + 4], 20, 0xE7D3FBC8);
        a = GG(a, b, c, d, x[i + 9], 5, 0x21E1CDE6);
        d = GG(d, a, b, c, x[i + 14], 9, 0xC33707D6);
        c = GG(c, d, a, b, x[i + 3], 14, 0xF4D50D87);
        b = GG(b, c, d, a, x[i + 8], 20, 0x455A14ED);
        a = GG(a, b, c, d, x[i + 13], 5, 0xA9E3E905);
        d = GG(d, a, b, c, x[i + 2], 9, 0xFCEFA3F8);
        c = GG(c, d, a, b, x[i + 7], 14, 0x676F02D9);
        b = GG(b, c, d, a, x[i + 12], 20, 0x8D2A4C8A);

        a = HH(a, b, c, d, x[i + 5], 4, 0xFFFA3942);
        d = HH(d, a, b, c, x[i + 8], 11, 0x8771F681);
        c = HH(c, d, a, b, x[i + 11], 16, 0x6D9D6122);
        b = HH(b, c, d, a, x[i + 14], 23, 0xFDE5380C);
        a = HH(a, b, c, d, x[i + 1], 4, 0xA4BEEA44);
        d = HH(d, a, b, c, x[i + 4], 11, 0x4BDECFA9);
        c = HH(c, d, a, b, x[i + 7], 16, 0xF6BB4B60);
        b = HH(b, c, d, a, x[i + 10], 23, 0xBEBFBC70);
        a = HH(a, b, c, d, x[i + 13], 4, 0x289B7EC6);
        d = HH(d, a, b, c, x[i + 0], 11, 0xEAA127FA);
        c = HH(c, d, a, b, x[i + 3], 16, 0xD4EF3085);
        b = HH(b, c, d, a, x[i + 6], 23, 0x04881D05);
        a = HH(a, b, c, d, x[i + 9], 4, 0xD9D4D039);
        d = HH(d, a, b, c, x[i + 12], 11, 0xE6DB99E5);
        c = HH(c, d, a, b, x[i + 15], 16, 0x1FA27CF8);
        b = HH(b, c, d, a, x[i + 2], 23, 0xC4AC5665);

        a = II(a, b, c, d, x[i + 0], 6, 0xF4292244);
        d = II(d, a, b, c, x[i + 7], 10, 0x432AFF97);
        c = II(c, d, a, b, x[i + 14], 15, 0xAB9423A7);
        b = II(b, c, d, a, x[i + 5], 21, 0xFC93A039);
        a = II(a, b, c, d, x[i + 12], 6, 0x655B59C3);
        d = II(d, a, b, c, x[i + 3], 10, 0x8F0CCC92);
        c = II(c, d, a, b, x[i + 10], 15, 0xFFEFF47D);
        b = II(b, c, d, a, x[i + 1], 21, 0x85845DD1);
        a = II(a, b, c, d, x[i + 8], 6, 0x6FA87E4F);
        d = II(d, a, b, c, x[i + 15], 10, 0xFE2CE6E0);
        c = II(c, d, a, b, x[i + 6], 15, 0xA3014314);
        b = II(b, c, d, a, x[i + 13], 21, 0x4E0811A1);
        a = II(a, b, c, d, x[i + 4], 6, 0xF7537E82);
        d = II(d, a, b, c, x[i + 11], 10, 0xBD3AF235);
        c = II(c, d, a, b, x[i + 2], 15, 0x2AD7D2BB);
        b = II(b, c, d, a, x[i + 9], 21, 0xEB86D391);

        a = addUnsigned(a, AA);
        b = addUnsigned(b, BB);
        c = addUnsigned(c, CC);
        d = addUnsigned(d, DD);
      }

      return wordToHex(a) + wordToHex(b) + wordToHex(c) + wordToHex(d);
    }

    return md5(input);
  };

  const generateSHA1 = async (input) => {
    // Real SHA-1 implementation
    // SHA-1 produces a 160-bit hash (40 hex characters)
    function sha1(input) {
      // Convert string to array of bytes
      const bytes = [];
      for (let i = 0; i < input.length; i++) {
        const code = input.charCodeAt(i);
        if (code < 0x80) {
          bytes.push(code);
        } else if (code < 0x800) {
          bytes.push(0xc0 | (code >> 6));
          bytes.push(0x80 | (code & 0x3f));
        } else if (code < 0xd800 || code >= 0xe000) {
          bytes.push(0xe0 | (code >> 12));
          bytes.push(0x80 | ((code >> 6) & 0x3f));
          bytes.push(0x80 | (code & 0x3f));
        } else {
          // Surrogate pair
          i++;
          const hi = code;
          const lo = input.charCodeAt(i);
          const codePoint = 0x10000 + (((hi & 0x3ff) << 10) | (lo & 0x3ff));
          bytes.push(0xf0 | (codePoint >> 18));
          bytes.push(0x80 | ((codePoint >> 12) & 0x3f));
          bytes.push(0x80 | ((codePoint >> 6) & 0x3f));
          bytes.push(0x80 | (codePoint & 0x3f));
        }
      }

      // Initialize SHA-1 state
      let h0 = 0x67452301;
      let h1 = 0xEFCDAB89;
      let h2 = 0x98BADCFE;
      let h3 = 0x10325476;
      let h4 = 0xC3D2E1F0;

      // Pre-processing: add padding bits
      const msgLen = bytes.length;
      bytes.push(0x80); // Add '1' bit

      // Pad to 448 bits mod 512
      while ((bytes.length % 64) !== 56) {
        bytes.push(0);
      }

      // Append original length as 64-bit big-endian integer
      const lengthBits = msgLen * 8;
      for (let i = 7; i >= 0; i--) {
        bytes.push((lengthBits >>> (i * 8)) & 0xff);
      }

      // Helper function
      function leftRotate(value, amount) {
        return ((value << amount) | (value >>> (32 - amount))) >>> 0;
      }

      // Process message in 512-bit chunks
      for (let offset = 0; offset < bytes.length; offset += 64) {
        const w = [];
        for (let i = 0; i < 16; i++) {
          const j = offset + i * 4;
          w[i] = (bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3];
        }

        for (let i = 16; i < 80; i++) {
          w[i] = leftRotate(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
        }

        let a = h0, b = h1, c = h2, d = h3, e = h4;

        for (let i = 0; i < 80; i++) {
          let f, k;
          if (i < 20) {
            f = (b & c) | (~b & d);
            k = 0x5A827999;
          } else if (i < 40) {
            f = b ^ c ^ d;
            k = 0x6ED9EBA1;
          } else if (i < 60) {
            f = (b & c) | (b & d) | (c & d);
            k = 0x8F1BBCDC;
          } else {
            f = b ^ c ^ d;
            k = 0xCA62C1D6;
          }

          const temp = (leftRotate(a, 5) + f + e + k + w[i]) >>> 0;
          e = d;
          d = c;
          c = leftRotate(b, 30);
          b = a;
          a = temp;
        }

        h0 = (h0 + a) >>> 0;
        h1 = (h1 + b) >>> 0;
        h2 = (h2 + c) >>> 0;
        h3 = (h3 + d) >>> 0;
        h4 = (h4 + e) >>> 0;
      }

      // Produce the final hash value as a 160-bit number (big-endian)
      function toHex(n) {
        return ((n < 0 ? n + 0x100000000 : n).toString(16).padStart(8, '0'));
      }

      return toHex(h0) + toHex(h1) + toHex(h2) + toHex(h3) + toHex(h4);
    }

    return sha1(input);
  };

  // 9. Hash Generator - Using real crypto APIs
  const generateHash = useCallback(async () => {
    try {
      setHashError('');
      
      if (!hashText.trim()) {
        showError(setHashError, 'Please enter text to hash');
        return;
      }

      const encoder = new TextEncoder();
      const data = encoder.encode(hashText);
      
      let hashBuffer, hash, length, algorithm;
      
      switch(hashType) {
        case 'md5':
          // MD5 is not available in Web Crypto API for security reasons
          // Using a fallback implementation for educational purposes only
          hash = await generateMD5(hashText);
          length = 128;
          algorithm = 'MD5 (Message-Digest Algorithm) - DEPRECATED';
          break;
          
        case 'sha1':
          // SHA-1 is not available in Web Crypto API for security reasons
          // Using a fallback implementation for educational purposes only
          hash = await generateSHA1(hashText);
          length = 160;
          algorithm = 'SHA-1 (Secure Hash Algorithm 1) - DEPRECATED';
          break;
          
        case 'sha256':
          hashBuffer = await crypto.subtle.digest('SHA-256', data);
          hash = Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
          length = 256;
          algorithm = 'SHA-256 (Secure Hash Algorithm 256-bit)';
          break;
          
        case 'sha384':
          hashBuffer = await crypto.subtle.digest('SHA-384', data);
          hash = Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
          length = 384;
          algorithm = 'SHA-384 (Secure Hash Algorithm 384-bit)';
          break;
          
        case 'sha512':
          hashBuffer = await crypto.subtle.digest('SHA-512', data);
          hash = Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
          length = 512;
          algorithm = 'SHA-512 (Secure Hash Algorithm 512-bit)';
          break;
          
        default:
          hashBuffer = await crypto.subtle.digest('SHA-256', data);
          hash = Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
          length = 256;
          algorithm = 'SHA-256';
      }
      
      // Calculate entropy more accurately
      const charSet = new Set(hashText.split(''));
      const uniqueChars = charSet.size;
      const hasLower = /[a-z]/.test(hashText);
      const hasUpper = /[A-Z]/.test(hashText);
      const hasNumbers = /\d/.test(hashText);
      const hasSymbols = /[^a-zA-Z0-9]/.test(hashText);
      
      let entropy = Math.log2(uniqueChars) * hashText.length;
      if (hasLower) entropy += 26;
      if (hasUpper) entropy += 26;
      if (hasNumbers) entropy += 10;
      if (hasSymbols) entropy += 32;
      
      const strength = entropy > 100 ? 'Very Strong' : 
                      entropy > 80 ? 'Strong' : 
                      entropy > 60 ? 'Medium' : 
                      entropy > 40 ? 'Weak' : 'Very Weak';
      
      setHashResult({
        type: hashType.toUpperCase(),
        hash: hash.toUpperCase(),
        input: hashText,
        length,
        algorithm,
        strength,
        entropy,
        timestamp: new Date().toISOString(),
        note: (hashType === 'md5' || hashType === 'sha1') ? 
          '⚠️ This algorithm is deprecated and should not be used for security purposes.' : 
          '✅ This is a cryptographically secure hash function.'
      });
    } catch (error) {
      showError(setHashError, `Error generating hash: ${error.message}`);
    }
  }, [hashText, hashType]);

  // 11. Password Tools
  const processPassword = useCallback(() => {
    try {
      setPasswordError('');
      
      if (passwordType === 'type7') {
        if (!passwordText.trim()) {
          showError(setPasswordError, 'Please enter a password');
          return;
        }
        
        // Real Cisco Type 7 encryption using Vigenère cipher with correct algorithm
        const key = "dsfd;kfoA,.iyewrkldJKDHSUBsgvca69834ncxv9873254k;fg87kmf";
        const salt = 0; // Fixed salt for consistent output
        let encrypted = salt.toString(16).toUpperCase().padStart(2, '0');
        for (let i = 0; i < passwordText.length; i++) {
          const charCode = passwordText.charCodeAt(i);
          const keyChar = key.charCodeAt(i % key.length);
          const encryptedChar = charCode ^ keyChar;
          encrypted += encryptedChar.toString(16).toUpperCase().padStart(2, '0');
        }

        // For demonstration, also show the decoded version
        let decoded = '';
        for (let i = 0; i < passwordText.length; i++) {
          const encryptedChar = parseInt(encrypted.substr(2 + i * 2, 2), 16);
          const keyChar = key.charCodeAt(i % key.length);
          const charCode = encryptedChar ^ keyChar;
          decoded += String.fromCharCode(charCode);
        }
        
        setPasswordResult({
          type: 'Cisco Type 7',
          encrypted: encrypted,
          original: passwordText,
          note: '⚠️ Cisco Type 7 is easily reversible and should not be used for security. This is for educational purposes only.',
          strength: 'Weak (Reversible)',
          decoded: decoded
        });
      } else if (passwordType === 'analyzer') {
        if (!passwordText.trim()) {
          showError(setPasswordError, 'Please enter a password to analyze');
          return;
        }
        
        const analysis = analyzePasswordStrength(passwordText);
        
        // Generate suggestions
        const suggestions = [];
        if (analysis.length < 8) suggestions.push('Increase length to at least 8 characters');
        if (analysis.length < 12) suggestions.push('Consider 12+ characters for better security');
        if (!analysis.hasLower) suggestions.push('Add lowercase letters');
        if (!analysis.hasUpper) suggestions.push('Add uppercase letters');
        if (!analysis.hasNumbers) suggestions.push('Add numbers');
        if (!analysis.hasSymbols) suggestions.push('Add special characters');
        if (analysis.entropy < 50) suggestions.push('Use a more complex password');
        
        setPasswordResult({
          type: 'Password Analysis',
          password: passwordText,
          analysis: analysis,
          suggestions: suggestions,
          score: analysis.score,
          strength: analysis.strength,
          color: analysis.color
        });
      } else if (passwordType === 'cracker') {
        if (!passwordText.trim()) {
          showError(setPasswordError, 'Please enter a password to test');
          return;
        }
        
        const estimate = estimateCrackingTime(passwordText);
        
        setPasswordResult({
          type: 'Cracking Time Estimate',
          password: passwordText,
          estimate: estimate,
          note: '⚠️ These are rough estimates. Actual cracking time depends on many factors including hardware, algorithms, and techniques used.'
        });
      } else if (passwordType === 'policy') {
        const policy = generatePasswordPolicy();
        
        setPasswordResult({
          type: 'Password Policy',
          policy: policy,
          description: `Generated ${policy.name} password policy suitable for ${policy.name.toLowerCase().replace(' security', '')} environments.`
        });
      } else if (passwordType === 'generator') {
        // Password generator
        const strongPass = generatePassword(16, 'strong');
        const mediumPass = generatePassword(12, 'medium');
        const weakPass = generatePassword(8, 'weak');
        
        const hasLower = /[a-z]/.test(strongPass);
        const hasUpper = /[A-Z]/.test(strongPass);
        const hasNumbers = /\d/.test(strongPass);
        const hasSymbols = /[!@#$%^&*()_+\-=[\]{};':",.<>?]/.test(strongPass);
        const length = strongPass.length;
        
        let strengthScore = 0;
        if (length >= 12) strengthScore += 2;
        if (hasLower) strengthScore += 1;
        if (hasUpper) strengthScore += 1;
        if (hasNumbers) strengthScore += 1;
        if (hasSymbols) strengthScore += 1;
        
        const strength = strengthScore >= 5 ? 'Very Strong' :
                        strengthScore >= 4 ? 'Strong' :
                        strengthScore >= 3 ? 'Medium' : 'Weak';
        
        setPasswordResult({
          type: 'Strong Password Generator',
          password: strongPass,
          alternatives: {
            medium: mediumPass,
            weak: weakPass
          },
          strength,
          length,
          hasLower,
          hasUpper,
          hasNumbers,
          hasSymbols,
          score: strengthScore,
          recommendation: 'Use the "Very Strong" password for critical systems'
        });
      }
    } catch (error) {
      showError(setPasswordError, `Error processing password: ${error.message}`);
    }
  }, [passwordText, passwordType, generatePassword, analyzePasswordStrength, estimateCrackingTime, generatePasswordPolicy]);

  // 12. Cabling Calculator
  const calculateCabling = useCallback(() => {
    try {
      setCablingError('');
      
      if (cableLength <= 0 || cableLength > 200) {
        showError(setCablingError, 'Cable length must be between 1 and 200 meters');
        return;
      }

      const standards = {
        cat5: { 
          speed: '100 Mbps', 
          distance: 100, 
          frequency: 100,
          grade: 'Obsolete',
          cost: '$'
        },
        cat5e: { 
          speed: '1 Gbps', 
          distance: 100, 
          frequency: 100,
          grade: 'Standard',
          cost: '$$'
        },
        cat6: { 
          speed: '1 Gbps', 
          distance: 100, 
          frequency: 250,
          grade: 'Recommended',
          cost: '$$$'
        },
        cat6a: { 
          speed: '10 Gbps', 
          distance: 100, 
          frequency: 500,
          grade: 'Premium',
          cost: '$$$$'
        },
        cat7: { 
          speed: '10 Gbps', 
          distance: 100, 
          frequency: 600,
          grade: 'Enterprise',
          cost: '$$$$$'
        }
      };

      const standard = standards[cableType] || standards.cat6;
      const maxDistance = standard.distance;
      const status = cableLength <= maxDistance ? 'Within spec ✅' : 'Exceeds maximum distance ❌';
      const performance = cableLength <= maxDistance ? 
        'Optimal performance expected' : 
        'Potential signal degradation';
      
      const attenuation = (cableLength * 0.2).toFixed(2);
      const crosstalk = (20 - (cableLength * 0.1)).toFixed(2);
      
      setCablingResult({
        type: cableType.toUpperCase(),
        maxSpeed: standard.speed,
        maxDistance: `${maxDistance} meters`,
        yourLength: `${cableLength} meters`,
        status,
        performance,
        attenuation: `${attenuation} dB`,
        crosstalk: `${crosstalk > 0 ? crosstalk : '0'} dB`,
        recommendation: cableLength > maxDistance ? 
          'Consider fiber optic or shorter cable' : 
          'Good - within specifications',
        grade: standard.grade,
        cost: standard.cost,
        frequency: `${standard.frequency} MHz`
      });
    } catch (error) {
      showError(setCablingError, `Error calculating cabling: ${error.message}`);
    }
  }, [cableType, cableLength]);

  // 13. Log Parser
  // 13. Enhanced Log Parser
  const parseLog = useCallback(() => {
    try {
      setLogError('');
      
      const logs = logText.trim();
      if (!logs) {
        showError(setLogError, 'Please enter log entries');
        return;
      }

      const lines = logs.split('\n').filter(line => line.trim());
      const parsedEntries = [];
      const severityCounts = { ERROR: 0, WARNING: 0, CRITICAL: 0, INFO: 0, DEBUG: 0, ALERT: 0, EMERGENCY: 0, NOTICE: 0 };
      const patternCounts = {};
      let parsedLines = 0;

      // Comprehensive pattern definitions
      const patterns = [
        // Network Interface Events
        {
          regex: /(?:interface|eth|gi|fa|te|se|po|vl|lo)\s*(\S+).*(?:down|up|changed state|administratively|line protocol)/i,
          type: 'INTERFACE_STATE_CHANGE',
          description: 'Interface state or protocol change',
          category: 'network',
          extract: (match, line) => {
            const interfaceName = match[1];
            const isUp = /up/i.test(line);
            const isDown = /down/i.test(line);
            return [
              { key: 'Interface', value: interfaceName },
              { key: 'State', value: isUp ? 'UP' : isDown ? 'DOWN' : 'CHANGED' }
            ];
          }
        },
        // Configuration Changes
        {
          regex: /(?:configured|configuration|config).*(?:console|vty|tftp|memory|running|startup)/i,
          type: 'CONFIG_CHANGE',
          description: 'Configuration change or save event',
          category: 'config',
          extract: (match, line) => {
            const source = line.match(/(?:from|by)\s+(\S+)/i)?.[1] || 'unknown';
            return [{ key: 'Source', value: source }];
          }
        },
        // Authentication Events
        {
          regex: /(?:authentication|auth|login|aaa).*(?:failed|success|succeeded|denied|accepted)/i,
          type: 'AUTHENTICATION_EVENT',
          description: 'Authentication success or failure',
          category: 'security',
          extract: (match, line) => {
            const success = /success|succeeded|accepted/i.test(line);
            const user = line.match(/(?:user|for)\s+(\S+)/i)?.[1] || 'unknown';
            const ip = line.match(/(\d+\.\d+\.\d+\.\d+)/)?.[0];
            return [
              { key: 'User', value: user },
              { key: 'Result', value: success ? 'SUCCESS' : 'FAILED' },
              ...(ip ? [{ key: 'Source IP', value: ip }] : [])
            ];
          }
        },
        // Security Violations
        {
          regex: /(?:security|sec|access|acl|firewall).*(?:violation|attack|denied|blocked|intrusion)/i,
          type: 'SECURITY_VIOLATION',
          description: 'Security violation or access denial',
          category: 'security',
          extract: (match, line) => {
            const protocol = line.match(/(?:tcp|udp|icmp)/i)?.[0]?.toUpperCase();
            const ips = line.match(/(\d+\.\d+\.\d+\.\d+)/g) || [];
            const ports = line.match(/(?:port|dport|sport)\s+(\d+)/gi) || [];
            return [
              ...(protocol ? [{ key: 'Protocol', value: protocol }] : []),
              ...(ips.length >= 2 ? [
                { key: 'Source', value: `${ips[0]}${ports[0] ? ':' + ports[0].match(/(\d+)/)?.[1] : ''}` },
                { key: 'Destination', value: `${ips[1]}${ports[1] ? ':' + ports[1].match(/(\d+)/)?.[1] : ''}` }
              ] : [])
            ];
          }
        },
        // DHCP Events
        {
          regex: /(?:dhcp|ip.*assign).*(?:address|lease)/i,
          type: 'DHCP_EVENT',
          description: 'DHCP address assignment or lease event',
          category: 'network',
          extract: (match, line) => {
            const ip = line.match(/(\d+\.\d+\.\d+\.\d+)/)?.[0];
            const mac = line.match(/([0-9a-f]{2}:){5}[0-9a-f]{2}/i)?.[0];
            return [
              ...(ip ? [{ key: 'IP Address', value: ip }] : []),
              ...(mac ? [{ key: 'MAC Address', value: mac.toUpperCase() }] : [])
            ];
          }
        },
        // Routing Protocol Events
        {
          regex: /(?:ospf|bgp|eigrp|rip|isis).*(?:adj|neighbor|route|update)/i,
          type: 'ROUTING_EVENT',
          description: 'Routing protocol adjacency or route change',
          category: 'routing',
          extract: (match, line) => {
            const protocol = line.match(/(ospf|bgp|eigrp|rip|isis)/i)?.[0]?.toUpperCase();
            const state = line.match(/(?:from|to)\s+(\w+)/i)?.[1];
            const neighbor = line.match(/(\d+\.\d+\.\d+\.\d+)/)?.[0];
            return [
              ...(protocol ? [{ key: 'Protocol', value: protocol }] : []),
              ...(state ? [{ key: 'State Change', value: state }] : []),
              ...(neighbor ? [{ key: 'Neighbor', value: neighbor }] : [])
            ];
          }
        },
        // System Resource Events
        {
          regex: /(?:memory|cpu|buffer|disk).*(?:low|high|full|critical|warning)/i,
          type: 'RESOURCE_EVENT',
          description: 'System resource utilization alert',
          category: 'system',
          extract: (match, line) => {
            const resource = line.match(/(memory|cpu|buffer|disk)/i)?.[0];
            const level = line.match(/(low|high|full|critical|warning)/i)?.[0];
            const percentage = line.match(/(\d+)%/)?.[1];
            return [
              ...(resource ? [{ key: 'Resource', value: resource.toUpperCase() }] : []),
              ...(level ? [{ key: 'Level', value: level.toUpperCase() }] : []),
              ...(percentage ? [{ key: 'Usage', value: `${percentage}%` }] : [])
            ];
          }
        },
        // Hardware Events
        {
          regex: /(?:power|fan|temperature|voltage|hardware).*(?:fail|error|warning)/i,
          type: 'HARDWARE_EVENT',
          description: 'Hardware failure or warning',
          category: 'hardware',
          extract: (match, line) => {
            const component = line.match(/(power|fan|temperature|voltage|hardware)/i)?.[0];
            return [
              ...(component ? [{ key: 'Component', value: component.toUpperCase() }] : [])
            ];
          }
        },
        // VPN/IPsec Events
        {
          regex: /(?:vpn|ipsec|ike|isakmp).*(?:up|down|established|failed)/i,
          type: 'VPN_EVENT',
          description: 'VPN or IPsec tunnel event',
          category: 'security',
          extract: (match, line) => {
            const isUp = /up|established/i.test(line);
            const tunnel = line.match(/(?:tunnel|peer)\s+(\S+)/i)?.[1];
            return [
              { key: 'Status', value: isUp ? 'ESTABLISHED' : 'FAILED' },
              ...(tunnel ? [{ key: 'Tunnel/Peer', value: tunnel }] : [])
            ];
          }
        },
        // NTP/Syslog Events
        {
          regex: /(?:ntp|syslog|logging).*(?:sync|started|stopped|host)/i,
          type: 'SYSTEM_SERVICE_EVENT',
          description: 'System service status change',
          category: 'system',
          extract: (match, line) => {
            const service = line.match(/(ntp|syslog|logging)/i)?.[0]?.toUpperCase();
            const action = line.match(/(sync|started|stopped)/i)?.[0];
            const host = line.match(/host\s+(\S+)/i)?.[1];
            return [
              ...(service ? [{ key: 'Service', value: service }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(host ? [{ key: 'Host', value: host }] : [])
            ];
          }
        },
        // Wireless/WiFi Events
        {
          regex: /(?:wifi|wireless|wlan|ap|client).*(?:connect|disconnect|associate|deauthenticate|roam)/i,
          type: 'WIRELESS_EVENT',
          description: 'Wireless network client events',
          category: 'wireless',
          extract: (match, line) => {
            const action = line.match(/(connect|disconnect|associate|deauthenticate|roam)/i)?.[0];
            const mac = line.match(/([0-9a-f]{2}:){5}[0-9a-f]{2}/i)?.[0];
            const ssid = line.match(/(?:ssid|network)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(mac ? [{ key: 'MAC Address', value: mac.toUpperCase() }] : []),
              ...(ssid ? [{ key: 'SSID', value: ssid }] : [])
            ];
          }
        },
        // Firewall Events
        {
          regex: /(?:firewall|fw|iptables|ufw).*(?:drop|reject|accept|block|allow)/i,
          type: 'FIREWALL_EVENT',
          description: 'Firewall rule action',
          category: 'security',
          extract: (match, line) => {
            const action = line.match(/(drop|reject|accept|block|allow)/i)?.[0];
            const chain = line.match(/(?:chain|table)\s+(\w+)/i)?.[1];
            const ips = line.match(/(\d+\.\d+\.\d+\.\d+)/g) || [];
            return [
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(chain ? [{ key: 'Chain/Table', value: chain.toUpperCase() }] : []),
              ...(ips.length >= 2 ? [
                { key: 'Source IP', value: ips[0] },
                { key: 'Destination IP', value: ips[1] }
              ] : [])
            ];
          }
        },
        // IDS/IPS Events
        {
          regex: /(?:ids|ips|snort|suricata).*(?:alert|attack|signature|threat|malware)/i,
          type: 'IDS_IPS_EVENT',
          description: 'Intrusion detection/prevention system alert',
          category: 'security',
          extract: (match, line) => {
            const signature = line.match(/(?:signature|sid)\s+(\d+)/i)?.[1];
            const severity = line.match(/(?:priority|severity)\s+(\d+)/i)?.[1];
            const classification = line.match(/(?:classification|category)\s+["']?([^"'\n]+)["']?/i)?.[1];
            return [
              ...(signature ? [{ key: 'Signature ID', value: signature }] : []),
              ...(severity ? [{ key: 'Severity', value: severity }] : []),
              ...(classification ? [{ key: 'Classification', value: classification }] : [])
            ];
          }
        },
        // Database Events
        {
          regex: /(?:database|db|mysql|postgres|oracle|sql).*(?:connect|disconnect|query|error|timeout)/i,
          type: 'DATABASE_EVENT',
          description: 'Database connection or query event',
          category: 'database',
          extract: (match, line) => {
            const dbType = line.match(/(mysql|postgres|oracle|mongodb|redis)/i)?.[0];
            const action = line.match(/(connect|disconnect|query|error|timeout)/i)?.[0];
            const user = line.match(/(?:user|username)\s+(\w+)/i)?.[1];
            return [
              ...(dbType ? [{ key: 'Database Type', value: dbType.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(user ? [{ key: 'User', value: user }] : [])
            ];
          }
        },
        // Application Server Events
        {
          regex: /(?:apache|nginx|tomcat|jboss|websphere|iis).*(?:start|stop|restart|error|timeout)/i,
          type: 'APPLICATION_SERVER_EVENT',
          description: 'Web/application server status event',
          category: 'application',
          extract: (match, line) => {
            const server = line.match(/(apache|nginx|tomcat|jboss|websphere|iis)/i)?.[0];
            const action = line.match(/(start|stop|restart|error|timeout)/i)?.[0];
            const statusCode = line.match(/(?:status|code)\s+(\d+)/i)?.[1];
            return [
              ...(server ? [{ key: 'Server Type', value: server.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(statusCode ? [{ key: 'Status Code', value: statusCode }] : [])
            ];
          }
        },
        // Cloud Service Events
        {
          regex: /(?:aws|azure|gcp|cloud).*(?:instance|vm|bucket|object|api).*(?:create|delete|start|stop|error)/i,
          type: 'CLOUD_SERVICE_EVENT',
          description: 'Cloud service resource event',
          category: 'cloud',
          extract: (match, line) => {
            const provider = line.match(/(aws|azure|gcp)/i)?.[0];
            const resource = line.match(/(instance|vm|bucket|object|api|function|lambda)/i)?.[0];
            const action = line.match(/(create|delete|start|stop|error|scale)/i)?.[0];
            return [
              ...(provider ? [{ key: 'Cloud Provider', value: provider.toUpperCase() }] : []),
              ...(resource ? [{ key: 'Resource Type', value: resource.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : [])
            ];
          }
        },
        // Container/Docker Events
        {
          regex: /(?:docker|container|podman|kubernetes|k8s).*(?:start|stop|create|pull|push|error)/i,
          type: 'CONTAINER_EVENT',
          description: 'Container orchestration event',
          category: 'container',
          extract: (match, line) => {
            const platform = line.match(/(docker|podman|kubernetes|k8s)/i)?.[0];
            const action = line.match(/(start|stop|create|pull|push|error|crash)/i)?.[0];
            const image = line.match(/(?:image|repo)\s+([^:\s]+)/i)?.[1];
            return [
              ...(platform ? [{ key: 'Platform', value: platform.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(image ? [{ key: 'Image/Repo', value: image }] : [])
            ];
          }
        },
        // Performance Monitoring Events
        {
          regex: /(?:performance|perf|monitor|threshold|alert).*(?:exceed|breach|violation|high|low)/i,
          type: 'PERFORMANCE_EVENT',
          description: 'Performance threshold violation',
          category: 'monitoring',
          extract: (match, line) => {
            const metric = line.match(/(?:cpu|memory|disk|network|latency|response)\s+time?/i)?.[0];
            const threshold = line.match(/(\d+(?:\.\d+)?)%?/i)?.[0];
            const current = line.match(/(?:current|actual)\s+[:=]\s*(\d+(?:\.\d+)?)/i)?.[1];
            return [
              ...(metric ? [{ key: 'Metric', value: metric.toUpperCase() }] : []),
              ...(threshold ? [{ key: 'Threshold', value: threshold }] : []),
              ...(current ? [{ key: 'Current Value', value: current }] : [])
            ];
          }
        },
        // Backup/Restore Events
        {
          regex: /(?:backup|restore|archive).*(?:start|complete|fail|success|error)/i,
          type: 'BACKUP_EVENT',
          description: 'Backup or restore operation event',
          category: 'backup',
          extract: (match, line) => {
            const operation = line.match(/(backup|restore|archive)/i)?.[0];
            const status = line.match(/(start|complete|fail|success|error)/i)?.[0];
            const size = line.match(/(\d+(?:\.\d+)?)\s*(?:gb|mb|kb|b)/i)?.[0];
            return [
              ...(operation ? [{ key: 'Operation', value: operation.toUpperCase() }] : []),
              ...(status ? [{ key: 'Status', value: status.toUpperCase() }] : []),
              ...(size ? [{ key: 'Size', value: size.toUpperCase() }] : [])
            ];
          }
        },
        // Certificate/SSL Events
        {
          regex: /(?:certificate|cert|ssl|tls).*(?:expire|invalid|revoke|renew|error)/i,
          type: 'CERTIFICATE_EVENT',
          description: 'SSL/TLS certificate event',
          category: 'security',
          extract: (match, line) => {
            const action = line.match(/(expire|invalid|revoke|renew|error)/i)?.[0];
            const domain = line.match(/(?:cn|domain|subject)\s*[:=]\s*([^,\s]+)/i)?.[1];
            const issuer = line.match(/(?:issuer|ca)\s*[:=]\s*([^,\n]+)/i)?.[1];
            return [
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(domain ? [{ key: 'Domain/CN', value: domain }] : []),
              ...(issuer ? [{ key: 'Issuer', value: issuer }] : [])
            ];
          }
        },
        // DNS Events
        {
          regex: /(?:dns|bind|named).*(?:query|resolve|nxdomain|timeout|error)/i,
          type: 'DNS_EVENT',
          description: 'DNS resolution or server event',
          category: 'network',
          extract: (match, line) => {
            const query = line.match(/(?:query|lookup)\s+([^:\s]+)/i)?.[1];
            const result = line.match(/(nxdomain|timeout|error|success)/i)?.[0];
            const server = line.match(/(\d+\.\d+\.\d+\.\d+):\d+/i)?.[0];
            return [
              ...(query ? [{ key: 'Query', value: query }] : []),
              ...(result ? [{ key: 'Result', value: result.toUpperCase() }] : []),
              ...(server ? [{ key: 'DNS Server', value: server }] : [])
            ];
          }
        },
        // Email/SMTP Events
        {
          regex: /(?:smtp|email|mail|postfix|sendmail).*(?:send|receive|bounce|spam|relay)/i,
          type: 'EMAIL_EVENT',
          description: 'Email/SMTP server event',
          category: 'email',
          extract: (match, line) => {
            const action = line.match(/(send|receive|bounce|spam|relay)/i)?.[0];
            const from = line.match(/(?:from|sender)\s*[:=]\s*([^\s,]+)/i)?.[1];
            const to = line.match(/(?:to|recipient)\s*[:=]\s*([^\s,]+)/i)?.[1];
            return [
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(from ? [{ key: 'From', value: from }] : []),
              ...(to ? [{ key: 'To', value: to }] : [])
            ];
          }
        },
        // VoIP/SIP Events
        {
          regex: /(?:sip|voip|asterisk|freepbx).*(?:call|register|invite|bye|cancel)/i,
          type: 'VOIP_EVENT',
          description: 'VoIP/SIP call signaling event',
          category: 'voip',
          extract: (match, line) => {
            const method = line.match(/(invite|bye|cancel|register|ack)/i)?.[0];
            const code = line.match(/(?:status|code)\s+(\d+)/i)?.[1];
            const endpoint = line.match(/(\d+@\S+)/i)?.[0];
            return [
              ...(method ? [{ key: 'SIP Method', value: method.toUpperCase() }] : []),
              ...(code ? [{ key: 'Response Code', value: code }] : []),
              ...(endpoint ? [{ key: 'Endpoint', value: endpoint }] : [])
            ];
          }
        },
        // Storage/SAN Events
        {
          regex: /(?:storage|san|iscsi|fiber|raid|lun).*(?:fail|error|degrade|rebuild)/i,
          type: 'STORAGE_EVENT',
          description: 'Storage/SAN system event',
          category: 'storage',
          extract: (match, line) => {
            const component = line.match(/(storage|san|iscsi|fiber|raid|lun)/i)?.[0];
            const status = line.match(/(fail|error|degrade|rebuild|offline|online)/i)?.[0];
            const device = line.match(/(?:device|disk|lun)\s+(\w+)/i)?.[1];
            return [
              ...(component ? [{ key: 'Component', value: component.toUpperCase() }] : []),
              ...(status ? [{ key: 'Status', value: status.toUpperCase() }] : []),
              ...(device ? [{ key: 'Device', value: device }] : [])
            ];
          }
        },
        // Load Balancer Events
        {
          regex: /(?:load.?balanc|lb|haproxy|nginx|aws.?elb).*(?:up|down|fail|health|check)/i,
          type: 'LOAD_BALANCER_EVENT',
          description: 'Load balancer health check or status event',
          category: 'loadbalancer',
          extract: (match, line) => {
            const backend = line.match(/(?:backend|server|pool)\s+(\S+)/i)?.[1];
            const status = line.match(/(up|down|fail|health|check)/i)?.[0];
            const reason = line.match(/(?:reason|cause)\s+["']?([^"'\n]+)["']?/i)?.[1];
            return [
              ...(backend ? [{ key: 'Backend/Server', value: backend }] : []),
              ...(status ? [{ key: 'Status', value: status.toUpperCase() }] : []),
              ...(reason ? [{ key: 'Reason', value: reason }] : [])
            ];
          }
        },
        // Network Monitoring Events (SNMP/NetFlow)
        {
          regex: /(?:snmp|netflow|sflow|ipfix).*(?:trap|flow|export|poll)/i,
          type: 'NETWORK_MONITORING_EVENT',
          description: 'SNMP traps, NetFlow, or network monitoring events',
          category: 'monitoring',
          extract: (match, line) => {
            const protocol = line.match(/(snmp|netflow|sflow|ipfix)/i)?.[0];
            const action = line.match(/(trap|flow|export|poll)/i)?.[0];
            const oid = line.match(/(?:oid|object)\s+([0-9.]+)/i)?.[1];
            return [
              ...(protocol ? [{ key: 'Protocol', value: protocol.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(oid ? [{ key: 'OID', value: oid }] : [])
            ];
          }
        },
        // Authentication Server Events (RADIUS/TACACS+)
        {
          regex: /(?:radius|tacacs|ldap|kerberos).*(?:auth|access|reject|accept|bind)/i,
          type: 'AUTH_SERVER_EVENT',
          description: 'Authentication server protocol events',
          category: 'security',
          extract: (match, line) => {
            const protocol = line.match(/(radius|tacacs|ldap|kerberos)/i)?.[0];
            const result = line.match(/(auth|access|reject|accept|bind)/i)?.[0];
            const user = line.match(/(?:user|username)\s+([^,\s]+)/i)?.[1];
            return [
              ...(protocol ? [{ key: 'Protocol', value: protocol.toUpperCase() }] : []),
              ...(result ? [{ key: 'Result', value: result.toUpperCase() }] : []),
              ...(user ? [{ key: 'User', value: user }] : [])
            ];
          }
        },
        // Endpoint Security Events
        {
          regex: /(?:antivirus|edr|dlp|endpoint|malware|virus).*(?:detect|block|quarantine|scan)/i,
          type: 'ENDPOINT_SECURITY_EVENT',
          description: 'Endpoint protection and security events',
          category: 'security',
          extract: (match, line) => {
            const type = line.match(/(antivirus|edr|dlp|endpoint|malware|virus)/i)?.[0];
            const action = line.match(/(detect|block|quarantine|scan)/i)?.[0];
            const threat = line.match(/(?:threat|signature)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(type ? [{ key: 'Security Type', value: type.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(threat ? [{ key: 'Threat', value: threat }] : [])
            ];
          }
        },
        // Web Application Firewall Events
        {
          regex: /(?:waf|modsecurity|cloudflare|akamai).*(?:block|alert|attack|xss|sql|injection)/i,
          type: 'WAF_EVENT',
          description: 'Web Application Firewall security events',
          category: 'security',
          extract: (match, line) => {
            const waf = line.match(/(waf|modsecurity|cloudflare|akamai)/i)?.[0];
            const attack = line.match(/(xss|sql|injection|attack)/i)?.[0];
            const rule = line.match(/(?:rule|id)\s+(\d+)/i)?.[1];
            return [
              ...(waf ? [{ key: 'WAF System', value: waf.toUpperCase() }] : []),
              ...(attack ? [{ key: 'Attack Type', value: attack.toUpperCase() }] : []),
              ...(rule ? [{ key: 'Rule ID', value: rule }] : [])
            ];
          }
        },
        // API Gateway Events
        {
          regex: /(?:api.?gateway|kong|apigee|aws.?api).*(?:rate.?limit|throttle|auth|request)/i,
          type: 'API_GATEWAY_EVENT',
          description: 'API Gateway and rate limiting events',
          category: 'application',
          extract: (match, line) => {
            const gateway = line.match(/(kong|apigee|aws.?api)/i)?.[0];
            const action = line.match(/(rate.?limit|throttle|auth|request)/i)?.[0];
            const endpoint = line.match(/(?:endpoint|path)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(gateway ? [{ key: 'Gateway', value: gateway.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(endpoint ? [{ key: 'Endpoint', value: endpoint }] : [])
            ];
          }
        },
        // Virtualization Events
        {
          regex: /(?:vmware|hyper.?v|kvm|virtualbox|xen).*(?:vm|guest|host|migrate|snapshot)/i,
          type: 'VIRTUALIZATION_EVENT',
          description: 'Virtualization platform events',
          category: 'virtualization',
          extract: (match, line) => {
            const platform = line.match(/(vmware|hyper.?v|kvm|virtualbox|xen)/i)?.[0];
            const action = line.match(/(migrate|snapshot|start|stop|create|delete)/i)?.[0];
            const vm = line.match(/(?:vm|guest)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(platform ? [{ key: 'Platform', value: platform.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(vm ? [{ key: 'VM/Guest', value: vm }] : [])
            ];
          }
        },
        // Configuration Management Events
        {
          regex: /(?:ansible|puppet|chef|salt).*(?:playbook|manifest|recipe|run|apply)/i,
          type: 'CONFIG_MANAGEMENT_EVENT',
          description: 'Configuration management tool events',
          category: 'automation',
          extract: (match, line) => {
            const tool = line.match(/(ansible|puppet|chef|salt)/i)?.[0];
            const type = line.match(/(playbook|manifest|recipe|run|apply)/i)?.[0];
            const target = line.match(/(?:host|target)\s+([^,\s]+)/i)?.[1];
            return [
              ...(tool ? [{ key: 'Tool', value: tool.toUpperCase() }] : []),
              ...(type ? [{ key: 'Type', value: type.toUpperCase() }] : []),
              ...(target ? [{ key: 'Target', value: target }] : [])
            ];
          }
        },
        // CI/CD Pipeline Events
        {
          regex: /(?:jenkins|gitlab|github|circleci|travis).*(?:build|deploy|pipeline|job|stage)/i,
          type: 'CI_CD_EVENT',
          description: 'Continuous Integration/Deployment events',
          category: 'devops',
          extract: (match, line) => {
            const platform = line.match(/(jenkins|gitlab|github|circleci|travis)/i)?.[0];
            const action = line.match(/(build|deploy|pipeline|job|stage)/i)?.[0];
            const status = line.match(/(success|fail|error|pass|failed)/i)?.[0];
            return [
              ...(platform ? [{ key: 'Platform', value: platform.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(status ? [{ key: 'Status', value: status.toUpperCase() }] : [])
            ];
          }
        },
        // Message Queue Events
        {
          regex: /(?:rabbitmq|kafka|redis|activemq).*(?:queue|topic|publish|consume|message)/i,
          type: 'MESSAGE_QUEUE_EVENT',
          description: 'Message queue and pub/sub events',
          category: 'messaging',
          extract: (match, line) => {
            const system = line.match(/(rabbitmq|kafka|redis|activemq)/i)?.[0];
            const action = line.match(/(publish|consume|enqueue|dequeue)/i)?.[0];
            const queue = line.match(/(?:queue|topic)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(system ? [{ key: 'Message System', value: system.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(queue ? [{ key: 'Queue/Topic', value: queue }] : [])
            ];
          }
        },
        // Time Synchronization Events
        {
          regex: /(?:ntp|ptp|chrony|timesync).*(?:sync|offset|stratum|peer)/i,
          type: 'TIME_SYNC_EVENT',
          description: 'Time synchronization protocol events',
          category: 'system',
          extract: (match, line) => {
            const protocol = line.match(/(ntp|ptp|chrony)/i)?.[0];
            const status = line.match(/(sync|offset|stratum|peer)/i)?.[0];
            const offset = line.match(/([+-]?\d+(?:\.\d+)?)\s*(?:ms|us|ns)/i)?.[0];
            return [
              ...(protocol ? [{ key: 'Protocol', value: protocol.toUpperCase() }] : []),
              ...(status ? [{ key: 'Status', value: status.toUpperCase() }] : []),
              ...(offset ? [{ key: 'Offset', value: offset }] : [])
            ];
          }
        },
        // Power Management Events
        {
          regex: /(?:ups|pdu|power|battery).*(?:fail|low|critical|on.?battery|restore)/i,
          type: 'POWER_MANAGEMENT_EVENT',
          description: 'Power supply and UPS events',
          category: 'infrastructure',
          extract: (match, line) => {
            const device = line.match(/(ups|pdu|power|battery)/i)?.[0];
            const status = line.match(/(fail|low|critical|on.?battery|restore)/i)?.[0];
            const load = line.match(/(\d+(?:\.\d+)?)%/i)?.[0];
            return [
              ...(device ? [{ key: 'Device', value: device.toUpperCase() }] : []),
              ...(status ? [{ key: 'Status', value: status.toUpperCase() }] : []),
              ...(load ? [{ key: 'Load', value: load }] : [])
            ];
          }
        },
        // Environmental Monitoring Events
        {
          regex: /(?:temperature|humidity|smoke|sensor|environmental).*(?:high|low|critical|alarm)/i,
          type: 'ENVIRONMENTAL_EVENT',
          description: 'Environmental monitoring and sensor events',
          category: 'infrastructure',
          extract: (match, line) => {
            const sensor = line.match(/(temperature|humidity|smoke|sensor)/i)?.[0];
            const level = line.match(/(high|low|critical|alarm)/i)?.[0];
            const value = line.match(/(\d+(?:\.\d+)?)\s*(?:°?[CF]|%)/i)?.[0];
            return [
              ...(sensor ? [{ key: 'Sensor', value: sensor.toUpperCase() }] : []),
              ...(level ? [{ key: 'Level', value: level.toUpperCase() }] : []),
              ...(value ? [{ key: 'Value', value: value }] : [])
            ];
          }
        },
        // Access Control Events
        {
          regex: /(?:door|badge|access|lock|unlock|entry|exit).*(?:grant|deny|alarm|tamper)/i,
          type: 'ACCESS_CONTROL_EVENT',
          description: 'Physical access control and security events',
          category: 'security',
          extract: (match, line) => {
            const action = line.match(/(grant|deny|alarm|tamper|lock|unlock)/i)?.[0];
            const badge = line.match(/(?:badge|card|id)\s+([^,\s]+)/i)?.[1];
            const door = line.match(/(?:door|gate|zone)\s+([^,\s]+)/i)?.[1];
            return [
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(badge ? [{ key: 'Badge/ID', value: badge }] : []),
              ...(door ? [{ key: 'Door/Zone', value: door }] : [])
            ];
          }
        },
        // Print Server Events
        {
          regex: /(?:print|printer|queue|job).*(?:start|complete|fail|jam|out.?of.?paper)/i,
          type: 'PRINT_SERVER_EVENT',
          description: 'Print server and printer events',
          category: 'peripheral',
          extract: (match, line) => {
            const action = line.match(/(start|complete|fail|jam|out.?of.?paper)/i)?.[0];
            const printer = line.match(/(?:printer|device)\s+([^,\s]+)/i)?.[1];
            const job = line.match(/(?:job|document)\s+([^,\s]+)/i)?.[1];
            return [
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(printer ? [{ key: 'Printer', value: printer }] : []),
              ...(job ? [{ key: 'Job/Document', value: job }] : [])
            ];
          }
        },
        // File Server Events
        {
          regex: /(?:smb|nfs|ftp|cifs).*(?:connect|disconnect|transfer|access|permission)/i,
          type: 'FILE_SERVER_EVENT',
          description: 'File server and network share events',
          category: 'storage',
          extract: (match, line) => {
            const protocol = line.match(/(smb|nfs|ftp|cifs)/i)?.[0];
            const action = line.match(/(connect|disconnect|transfer|access|permission)/i)?.[0];
            const file = line.match(/(?:file|path)\s+["']?([^"'\n]+)["']?/i)?.[1];
            return [
              ...(protocol ? [{ key: 'Protocol', value: protocol.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(file ? [{ key: 'File/Path', value: file }] : [])
            ];
          }
        },
        // Version Control Events
        {
          regex: /(?:git|svn|mercurial|hg).*(?:commit|push|pull|merge|branch|tag)/i,
          type: 'VERSION_CONTROL_EVENT',
          description: 'Version control system events',
          category: 'development',
          extract: (match, line) => {
            const vcs = line.match(/(git|svn|mercurial|hg)/i)?.[0];
            const action = line.match(/(commit|push|pull|merge|branch|tag)/i)?.[0];
            const repo = line.match(/(?:repo|repository)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(vcs ? [{ key: 'VCS', value: vcs.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(repo ? [{ key: 'Repository', value: repo }] : [])
            ];
          }
        },
        // Package Management Events
        {
          regex: /(?:apt|yum|dnf|pacman|brew|npm|pip|gem).*(?:install|update|remove|upgrade)/i,
          type: 'PACKAGE_MANAGEMENT_EVENT',
          description: 'Package manager installation and update events',
          category: 'system',
          extract: (match, line) => {
            const manager = line.match(/(apt|yum|dnf|pacman|brew|npm|pip|gem)/i)?.[0];
            const action = line.match(/(install|update|remove|upgrade)/i)?.[0];
            const pkgName = line.match(/(?:package|pkg)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(manager ? [{ key: 'Package Manager', value: manager.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(pkgName ? [{ key: 'Package', value: pkgName }] : [])
            ];
          }
        },
        // Service Mesh Events
        {
          regex: /(?:istio|linkerd|consul|envoy).*(?:service|endpoint|route|policy)/i,
          type: 'SERVICE_MESH_EVENT',
          description: 'Service mesh and service discovery events',
          category: 'networking',
          extract: (match, line) => {
            const mesh = line.match(/(istio|linkerd|consul|envoy)/i)?.[0];
            const component = line.match(/(service|endpoint|route|policy)/i)?.[0];
            const service = line.match(/(?:service.?name|svc)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(mesh ? [{ key: 'Service Mesh', value: mesh.toUpperCase() }] : []),
              ...(component ? [{ key: 'Component', value: component.toUpperCase() }] : []),
              ...(service ? [{ key: 'Service', value: service }] : [])
            ];
          }
        },
        // IoT Device Events
        {
          regex: /(?:iot|sensor|actuator|smart|device).*(?:connect|disconnect|reading|command)/i,
          type: 'IOT_DEVICE_EVENT',
          description: 'Internet of Things device events',
          category: 'iot',
          extract: (match, line) => {
            const device = line.match(/(sensor|actuator|smart|device)/i)?.[0];
            const action = line.match(/(connect|disconnect|reading|command)/i)?.[0];
            const value = line.match(/(\d+(?:\.\d+)?)\s*(?:°?[CF]|%|lux|db|v)/i)?.[0];
            return [
              ...(device ? [{ key: 'Device Type', value: device.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(value ? [{ key: 'Value', value: value }] : [])
            ];
          }
        },
        // Telecom Events
        {
          regex: /(?:pbx|sip.?trunk|cellular|gsm|lte|5g).*(?:call|register|handover|signal)/i,
          type: 'TELECOM_EVENT',
          description: 'Telecommunications and cellular network events',
          category: 'telecom',
          extract: (match, line) => {
            const system = line.match(/(pbx|sip.?trunk|cellular|gsm|lte|5g)/i)?.[0];
            const action = line.match(/(call|register|handover|signal)/i)?.[0];
            const number = line.match(/(\+?\d{10,})/)?.[0];
            return [
              ...(system ? [{ key: 'Telecom System', value: system.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(number ? [{ key: 'Phone Number', value: number }] : [])
            ];
          }
        },
        // Industrial Control Events
        {
          regex: /(?:scada|plc|ics|dcs|hmi).*(?:alarm|fault|control|setpoint)/i,
          type: 'INDUSTRIAL_CONTROL_EVENT',
          description: 'SCADA, PLC, and industrial control system events',
          category: 'industrial',
          extract: (match, line) => {
            const system = line.match(/(scada|plc|ics|dcs|hmi)/i)?.[0];
            const event = line.match(/(alarm|fault|control|setpoint)/i)?.[0];
            const tag = line.match(/(?:tag|point)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(system ? [{ key: 'Control System', value: system.toUpperCase() }] : []),
              ...(event ? [{ key: 'Event', value: event.toUpperCase() }] : []),
              ...(tag ? [{ key: 'Tag/Point', value: tag }] : [])
            ];
          }
        },
        // Financial Services Events
        {
          regex: /(?:payment|transaction|card|fraud|compliance).*(?:process|decline|approve|chargeback)/i,
          type: 'FINANCIAL_EVENT',
          description: 'Payment processing and financial transaction events',
          category: 'financial',
          extract: (match, line) => {
            const type = line.match(/(payment|transaction|card|fraud|compliance)/i)?.[0];
            const action = line.match(/(process|decline|approve|chargeback)/i)?.[0];
            const amount = line.match(/\$?(\d+(?:\.\d{2})?)/)?.[0];
            return [
              ...(type ? [{ key: 'Transaction Type', value: type.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(amount ? [{ key: 'Amount', value: amount }] : [])
            ];
          }
        },
        // Healthcare Events
        {
          regex: /(?:hl7|dicom|medical|patient|ehr).*(?:message|image|record|transfer)/i,
          type: 'HEALTHCARE_EVENT',
          description: 'Healthcare and medical system events',
          category: 'healthcare',
          extract: (match, line) => {
            const standard = line.match(/(hl7|dicom|ehr)/i)?.[0];
            const action = line.match(/(message|image|record|transfer)/i)?.[0];
            const patient = line.match(/(?:patient|mrn)\s+([^,\s]+)/i)?.[1];
            return [
              ...(standard ? [{ key: 'Standard', value: standard.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(patient ? [{ key: 'Patient/MRN', value: patient }] : [])
            ];
          }
        },
        // CDN Events
        {
          regex: /(?:cdn|cloudflare|akamai|fastly|cloudfront).*(?:cache|purge|hit|miss|origin)/i,
          type: 'CDN_EVENT',
          description: 'Content Delivery Network events',
          category: 'cdn',
          extract: (match, line) => {
            const provider = line.match(/(cloudflare|akamai|fastly|cloudfront)/i)?.[0];
            const action = line.match(/(cache|purge|hit|miss|origin)/i)?.[0];
            const url = line.match(/(?:url|path)\s+["']?([^"'\s]+)["']?/i)?.[1];
            return [
              ...(provider ? [{ key: 'CDN Provider', value: provider.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(url ? [{ key: 'URL/Path', value: url }] : [])
            ];
          }
        },
        // Blockchain Events
        {
          regex: /(?:bitcoin|ethereum|blockchain|crypto).*(?:block|transaction|mine|sync|wallet)/i,
          type: 'BLOCKCHAIN_EVENT',
          description: 'Blockchain and cryptocurrency node events',
          category: 'blockchain',
          extract: (match, line) => {
            const crypto = line.match(/(bitcoin|ethereum)/i)?.[0];
            const action = line.match(/(block|transaction|mine|sync|wallet)/i)?.[0];
            const hash = line.match(/([a-f0-9]{64})/i)?.[0];
            return [
              ...(crypto ? [{ key: 'Cryptocurrency', value: crypto.toUpperCase() }] : []),
              ...(action ? [{ key: 'Action', value: action.toUpperCase() }] : []),
              ...(hash ? [{ key: 'Block/Hash', value: hash }] : [])
            ];
          }
        }
      ];

      // Parse each line
      lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        let severity = 'INFO';
        let component = 'UNKNOWN';
        let message = trimmedLine;
        let facility = 'UNKNOWN';
        let mnemonic = 'UNKNOWN';
        let timestamp = null;
        let detectedPattern = null;
        let details = [];

        // Parse different log formats based on selection
        if (logFormat === 'auto' || logFormat === 'cisco') {
          // Cisco IOS format: %FACILITY-SEVERITY-MNEMONIC: message
          const ciscoMatch = trimmedLine.match(/%(\w+)-(\d)-(\w+):\s*(.*)/);
          if (ciscoMatch) {
            facility = ciscoMatch[1];
            const severityNum = parseInt(ciscoMatch[2]);
            mnemonic = ciscoMatch[3];
            message = ciscoMatch[4];
            
            const severityMap = {
              0: 'EMERGENCY',
              1: 'ALERT', 
              2: 'CRITICAL',
              3: 'ERROR',
              4: 'WARNING',
              5: 'NOTIFICATION',
              6: 'INFORMATIONAL',
              7: 'DEBUGGING'
            };
            severity = severityMap[severityNum] || 'UNKNOWN';
            component = facility;
          }
        }

        if (logFormat === 'auto' || logFormat === 'syslog') {
          // Standard syslog format: <PRI>timestamp host message
          const syslogMatch = trimmedLine.match(/<(\d+)>(?:\s*(.+?)\s+(\S+)\s+)?(.*)/);
          if (syslogMatch && !facility.includes('LINK')) { // Avoid false positives
            const pri = parseInt(syslogMatch[1]);
            timestamp = syslogMatch[2];
            component = syslogMatch[3] || 'UNKNOWN';
            message = syslogMatch[4];
            
            const severityNum = pri & 0x7;
            const severityMap = {
              0: 'EMERGENCY',
              1: 'ALERT',
              2: 'CRITICAL', 
              3: 'ERROR',
              4: 'WARNING',
              5: 'NOTICE',
              6: 'INFO',
              7: 'DEBUG'
            };
            severity = severityMap[severityNum] || 'UNKNOWN';
          }
        }

        if (logFormat === 'auto' || logFormat === 'syslog5424') {
          // RFC 5424 format
          const rfc5424Match = trimmedLine.match(/<(\d+)>(\d+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(.+)/);
          if (rfc5424Match) {
            const pri = parseInt(rfc5424Match[1]);
            timestamp = rfc5424Match[2];
            component = rfc5424Match[4];
            message = rfc5424Match[7];
            
            const severityNum = pri & 0x7;
            const severityMap = {
              0: 'EMERGENCY',
              1: 'ALERT',
              2: 'CRITICAL',
              3: 'ERROR',
              4: 'WARNING',
              5: 'NOTICE',
              6: 'INFO',
              7: 'DEBUG'
            };
            severity = severityMap[severityNum] || 'UNKNOWN';
          }
        }

        // Pattern detection
        if (logAnalysisLevel !== 'basic') {
          for (const pattern of patterns) {
            if (pattern.regex.test(trimmedLine)) {
              detectedPattern = pattern;
              if (logAnalysisLevel === 'comprehensive') {
                details = pattern.extract ? pattern.extract(pattern.regex.exec(trimmedLine), trimmedLine) : [];
              }
              patternCounts[pattern.type] = (patternCounts[pattern.type] || 0) + 1;
              break;
            }
          }
        }

        // Update severity counts
        severityCounts[severity] = (severityCounts[severity] || 0) + 1;
        parsedLines++;

        parsedEntries.push({
          lineNumber: index + 1,
          original: trimmedLine,
          severity,
          component,
          message,
          facility,
          mnemonic,
          timestamp,
          pattern: detectedPattern,
          details
        });
      });

      // Generate pattern summary
      const patternSummary = Object.entries(patternCounts)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);

      setLogResult({
        totalLines: lines.length,
        parsedLines,
        entries: parsedEntries,
        severityCounts,
        uniquePatterns: patternSummary.length,
        patternSummary
      });
    } catch (error) {
      showError(setLogError, `Error parsing logs: ${error.message}`);
    }
  }, [logText, logFormat, logAnalysisLevel]);

  // 14. QoS Calculator
  const calculateQoS = useCallback(() => {
    try {
      setQosError('');
      
      if (qosBandwidth <= 0) {
        showError(setQosError, 'Bandwidth must be greater than 0');
        return;
      }

      const totalPercent = qosPolicies.reduce((sum, p) => sum + p.percent, 0);
      if (totalPercent > 100) {
        showError(setQosError, 'Total percentage cannot exceed 100%');
        return;
      }

      const allocations = qosPolicies.map(policy => ({
        id: policy.id,
        class: policy.class,
        percent: policy.percent,
        bandwidth: (qosBandwidth * policy.percent / 100).toFixed(2),
        priority: policy.class === 'voice' ? 'High' : 
                 policy.class === 'video' ? 'Medium' : 'Low',
        dscp: policy.class === 'voice' ? 'EF (46)' :
              policy.class === 'video' ? 'AF41 (34)' : 'AF11 (10)',
        queue: policy.class === 'voice' ? 'LLQ' :
               policy.class === 'video' ? 'CBWFQ' : 'Default'
      }));

      const remaining = (qosBandwidth - allocations.reduce((sum, a) => sum + parseFloat(a.bandwidth), 0)).toFixed(2);
      
      const burstCalculations = allocations.map(a => ({
        class: a.class,
        normalBurst: (parseFloat(a.bandwidth) * 0.25).toFixed(2),
        excessBurst: (parseFloat(a.bandwidth) * 0.5).toFixed(2),
        cir: a.bandwidth
      }));

      setQosResult({
        totalBandwidth: `${qosBandwidth} Mbps`,
        allocations,
        remaining,
        totalPercent,
        burstCalculations,
        recommendation: remaining < 0 ? 
          'Reduce bandwidth allocations' : 
          `Allocation complete. ${remaining} Mbps remaining.`
      });
    } catch (error) {
      showError(setQosError, `Error calculating QoS: ${error.message}`);
    }
  }, [qosBandwidth, qosPolicies]);

  // 15. IPv6 Calculator - FIXED
  const calculateIPv6 = useCallback(() => {
    try {
      setIpv6Error('');
      
      if (!validateIPv6(ipv6Address)) {
        showError(setIpv6Error, 'Please enter a valid IPv6 address');
        return;
      }

      const prefix = parseInt(ipv6Prefix, 10);
      if (isNaN(prefix) || prefix < 0 || prefix > 128) {
        showError(setIpv6Error, 'Prefix must be between 0 and 128');
        return;
      }

      // Expand and normalize the IPv6 address
      const expanded = expandIPv6(ipv6Address);
      const compressed = compressIPv6(expanded);
      
      // Calculate subnet size (handle large numbers as strings)
      const hostBits = 128 - prefix;
      let subnetSize = '1';
      for (let i = 0; i < hostBits; i++) {
        subnetSize = multiplyByTwo(subnetSize);
      }
      
      // Calculate network address properly
      const groups = expanded.split(':');
      const networkGroups = [];
      
      // Process each 16-bit group
      for (let i = 0; i < 8; i++) {
        const groupValue = parseInt(groups[i], 16);
        const bitsInGroup = Math.min(16, Math.max(0, prefix - (i * 16)));
        
        if (bitsInGroup === 16) {
          // Full group is network part
          networkGroups.push(groups[i]);
        } else if (bitsInGroup > 0) {
          // Partial group - mask the bits
          const mask = (0xFFFF << (16 - bitsInGroup)) & 0xFFFF;
          const maskedValue = groupValue & mask;
          networkGroups.push(maskedValue.toString(16).padStart(4, '0'));
        } else {
          // Host part - set to 0
          networkGroups.push('0000');
        }
      }
      
      // Compress the network address
      const networkAddress = compressIPv6(networkGroups.join(':'));
      
      // Determine address type
      let addressType = 'Global Unicast';
      let scope = 'Global';
      
      if (expanded.startsWith('fe80:')) {
        addressType = 'Link-local';
        scope = 'Link';
      } else if (expanded.startsWith('fc00:') || expanded.startsWith('fd00:')) {
        addressType = 'Unique Local';
        scope = 'Site';
      } else if (expanded.startsWith('ff')) {
        addressType = 'Multicast';
        scope = 'Variable';
      } else if (expanded.startsWith('2001:db8:')) {
        addressType = 'Documentation';
        scope = 'Test';
      } else if (expanded.startsWith('::ffff:')) {
        addressType = 'IPv4-mapped';
        scope = 'Special';
      } else if (expanded === '::1') {
        addressType = 'Loopback';
        scope = 'Interface';
      }
      
      setIpv6Result({
        address: ipv6Address,
        expanded: expanded,
        compressed: compressed,
        prefix,
        subnetSize: formatLargeNumber(subnetSize),
        hostCount: prefix < 128 ? formatLargeNumber(subtractOne(subnetSize)) : '1', // IPv6 has no broadcast
        addressType,
        scope,
        network: `${networkAddress}/${prefix}`,
        bitsForHosts: 128 - prefix,
        bitsForNetwork: prefix,
        note: `IPv6 /${prefix} provides ${formatLargeNumber(subnetSize)} total addresses${prefix < 128 ? ` (${formatLargeNumber(subtractOne(subnetSize))} usable hosts)` : ' (single host)'}`
      });
    } catch (error) {
      showError(setIpv6Error, `Error calculating IPv6: ${error.message}`);
    }
  }, [ipv6Address, ipv6Prefix, validateIPv6, expandIPv6, compressIPv6]);

  // 17. VPN Config Generator
  const generateVPNConfig = useCallback(() => {
    try {
      setVpnError('');

      // Validation
      if (!validateIP(vpnPeerIP)) {
        showError(setVpnError, 'Invalid peer IP address');
        return;
      }

      const validateNetwork = (network) => {
        const parts = network.split('/');
        if (parts.length !== 2) return false;
        if (!validateIP(parts[0])) return false;
        const mask = parseInt(parts[1]);
        return mask >= 8 && mask <= 32;
      };

      if (!validateNetwork(vpnLocalNetwork) || !validateNetwork(vpnRemoteNetwork)) {
        showError(setVpnError, 'Invalid network format. Use CIDR notation (e.g., 192.168.1.0/24)');
        return;
      }

      if (vpnAuthMethod === 'pre-shared' && !vpnPsk.trim()) {
        setVpnPsk(generatePassword(24, 'strong'));
      }

      let config = '';
      let typeName = '';
      let description = '';
      let protocols = '';

      // Generate pre-shared key if not provided
      const psk = vpnAuthMethod === 'pre-shared' ? (vpnPsk || generatePassword(24, 'strong')) : '';

      if (vpnType === 'ipsec-site-to-site') {
        typeName = 'IPSec Site-to-Site VPN';
        description = 'Policy-based IPSec VPN tunnel between two sites';
        protocols = `${vpnIkeVersion.toUpperCase()}, ESP-${vpnEncryption.toUpperCase()}-${vpnHash.toUpperCase()}`;

        config = `! IPSec Site-to-Site VPN Configuration
! Peer: ${vpnPeerIP}
! Local Network: ${vpnLocalNetwork}
! Remote Network: ${vpnRemoteNetwork}
! IKE Version: ${vpnIkeVersion.toUpperCase()}
! Generated: ${new Date().toLocaleString()}
!
`;

        // IKEv2 Configuration
        if (vpnIkeVersion === 'ikev2') {
          config += `crypto ikev2 proposal IKEV2-PROPOSAL
 encryption ${vpnEncryption}
 integrity ${vpnHash}
 group ${vpnDhGroup}
!
crypto ikev2 policy IKEV2-POLICY
 proposal IKEV2-PROPOSAL
!
crypto ikev2 keyring KEYRING-1
 peer ${vpnPeerIP}
  address ${vpnPeerIP}
  ${vpnAuthMethod === 'pre-shared' ? `pre-shared-key ${psk}` : 'identity address ' + vpnPeerIP}
!
crypto ikev2 profile IKEV2-PROFILE
 match address local interface ${vpnLocalInterface}
 match identity remote address ${vpnPeerIP} 255.255.255.255
 authentication remote pre-share
 authentication local pre-share
 keyring local KEYRING-1
 lifetime 86400
${vpnFeatures.enableDPD ? ' dpd 10 2 on-demand' : ''}
${vpnFeatures.enableNatTraversal ? ' nat keepalive 10' : ''}
!
`;
        } else {
          // IKEv1 Configuration
          config += `crypto isakmp policy 10
 encryption ${vpnEncryption}
 hash ${vpnHash}
 authentication pre-share
 group ${vpnDhGroup}
 lifetime 86400
!
crypto isakmp key ${psk} address ${vpnPeerIP}
${vpnFeatures.enableNatTraversal ? 'crypto isakmp nat-traversal 30' : ''}
!
`;
        }

        // IPSec Transform Set
        config += `crypto ipsec transform-set TRANSFORM-SET esp-${vpnEncryption} esp-${vpnHash}-hmac
 mode tunnel
${vpnFeatures.enablePfs ? `crypto ipsec security-association pfs group${vpnDhGroup}` : ''}
!
`;

        // Crypto Map (IKEv1) or IPSec Profile (IKEv2)
        if (vpnIkeVersion === 'ikev1') {
          config += `crypto map VPN-MAP 10 ipsec-isakmp
 set peer ${vpnPeerIP}
 set transform-set TRANSFORM-SET
 match address VPN-ACL
${vpnFeatures.enablePfs ? ` set pfs group${vpnDhGroup}` : ''}
!
`;
        } else {
          config += `crypto ipsec profile IPSEC-PROFILE
 set transform-set TRANSFORM-SET
 set ikev2-profile IKEV2-PROFILE
${vpnFeatures.enablePfs ? ` set pfs group${vpnDhGroup}` : ''}
!
`;
        }

        // Access List for interesting traffic
        config += `ip access-list extended VPN-ACL
 permit ip ${vpnLocalNetwork.replace('/', ' ')} ${vpnRemoteNetwork.replace('/', ' ')}
!
`;

        // Apply crypto map to interface
        if (vpnIkeVersion === 'ikev1') {
          config += `interface ${vpnLocalInterface}
 crypto map VPN-MAP
!
`;
        }

        // Static routes for VPN traffic
        if (vpnFeatures.enableRouteInjection) {
          // Convert CIDR to subnet mask
          const cidrToMask = (cidr) => {
            const mask = [];
            for (let i = 0; i < 4; i++) {
              const bits = Math.min(8, Math.max(0, cidr - i * 8));
              mask.push((255 << (8 - bits)) & 255);
            }
            return mask.join('.');
          };
          
          const remoteNetwork = vpnRemoteNetwork.split('/');
          const subnetMask = cidrToMask(parseInt(remoteNetwork[1]));
          
          config += `ip route ${remoteNetwork[0]} ${subnetMask} ${vpnPeerIP}
!
`;
        }

      } else if (vpnType === 'gre-ipsec') {
        typeName = 'GRE over IPSec VPN';
        description = 'GRE tunnel protected by IPSec encryption';
        protocols = `GRE, ${vpnIkeVersion.toUpperCase()}, ESP-${vpnEncryption.toUpperCase()}-${vpnHash.toUpperCase()}`;

        // Generate tunnel IP addresses
        const tunnelLocalIP = '10.255.255.1';
        const tunnelRemoteIP = '10.255.255.2';
        const tunnelMask = '255.255.255.252';

        config = `! GRE over IPSec VPN Configuration
! Peer: ${vpnPeerIP}
! Local Network: ${vpnLocalNetwork}
! Remote Network: ${vpnRemoteNetwork}
! Tunnel IPs: ${tunnelLocalIP} <-> ${tunnelRemoteIP}
! Generated: ${new Date().toLocaleString()}
!
`;

        // IKE Configuration (same as site-to-site)
        if (vpnIkeVersion === 'ikev2') {
          config += `crypto ikev2 proposal IKEV2-PROPOSAL
 encryption ${vpnEncryption}
 integrity ${vpnHash}
 group ${vpnDhGroup}
!
crypto ikev2 policy IKEV2-POLICY
 proposal IKEV2-PROPOSAL
!
crypto ikev2 keyring KEYRING-1
 peer ${vpnPeerIP}
  address ${vpnPeerIP}
  pre-shared-key ${psk}
!
crypto ikev2 profile IKEV2-PROFILE
 match address local interface ${vpnLocalInterface}
 match identity remote address ${vpnPeerIP} 255.255.255.255
 authentication remote pre-share
 authentication local pre-share
 keyring local KEYRING-1
 lifetime 86400
${vpnFeatures.enableDPD ? ' dpd 10 2 on-demand' : ''}
!
`;
        } else {
          config += `crypto isakmp policy 10
 encryption ${vpnEncryption}
 hash ${vpnHash}
 authentication pre-share
 group ${vpnDhGroup}
 lifetime 86400
!
crypto isakmp key ${psk} address ${vpnPeerIP}
!
`;
        }

        // IPSec Transform Set (Transport mode for GRE)
        config += `crypto ipsec transform-set GRE-TRANSFORM esp-${vpnEncryption} esp-${vpnHash}-hmac
 mode transport
!
`;

        // IPSec Profile (IKE version specific)
        if (vpnIkeVersion === 'ikev2') {
          config += `crypto ipsec profile GRE-PROFILE
 set transform-set GRE-TRANSFORM
 set ikev2-profile IKEV2-PROFILE
!
`;
        } else {
          config += `crypto ipsec profile GRE-PROFILE
 set transform-set GRE-TRANSFORM
!
crypto map GRE-MAP 10 ipsec-isakmp
 set peer ${vpnPeerIP}
 set transform-set GRE-TRANSFORM
 match address GRE-ACL
!
`;
        }

        // GRE Tunnel Interface
        config += `interface Tunnel0
 description GRE Tunnel to ${vpnPeerIP}
 ip address ${tunnelLocalIP} ${tunnelMask}
 ip mtu 1400
 ip tcp adjust-mss 1360
 tunnel source ${vpnLocalInterface}
 tunnel destination ${vpnPeerIP}
 tunnel mode gre ip
`;

        if (vpnIkeVersion === 'ikev2') {
          config += ` tunnel protection ipsec profile GRE-PROFILE
!
`;
        } else {
          config += ` tunnel protection ipsec profile GRE-PROFILE
!
interface ${vpnLocalInterface}
 crypto map GRE-MAP
!
`;
        }

        // GRE ACL for IKEv1 crypto map
        if (vpnIkeVersion === 'ikev1') {
          config += `ip access-list extended GRE-ACL
 permit gre any host ${vpnPeerIP}
!
`;
        }

        // Routing
        config += `router ospf 1
 network ${tunnelLocalIP} 0.0.0.3 area 0
 network ${vpnLocalNetwork.replace('/', ' ')} area 0
!
ip route 0.0.0.0 0.0.0.0 Tunnel0 250
!
`;

      } else if (vpnType === 'dmvpn') {
        typeName = 'DMVPN (Dynamic Multipoint VPN)';
        description = 'Hub-and-spoke VPN with dynamic spoke-to-spoke tunnels';
        protocols = `GRE, NHRP, ${vpnIkeVersion.toUpperCase()}, ESP-${vpnEncryption.toUpperCase()}-${vpnHash.toUpperCase()}`;

        config = `! DMVPN Hub Configuration
! Hub IP: ${vpnPeerIP}
! Local Network: ${vpnLocalNetwork}
! Generated: ${new Date().toLocaleString()}
!
crypto ikev2 proposal IKEV2-PROPOSAL
 encryption ${vpnEncryption}
 integrity ${vpnHash}
 group ${vpnDhGroup}
!
crypto ikev2 policy IKEV2-POLICY
 proposal IKEV2-PROPOSAL
!
crypto ikev2 keyring DMVPN-KEYRING
 peer DMVPN-SPOKES
  address 0.0.0.0 0.0.0.0
  pre-shared-key ${psk}
!
crypto ikev2 profile DMVPN-IKEV2
 match identity remote address 0.0.0.0
 authentication remote pre-share
 authentication local pre-share
 keyring local DMVPN-KEYRING
 lifetime 86400
 dpd 10 2 on-demand
!
crypto ipsec transform-set DMVPN-TRANSFORM esp-${vpnEncryption} esp-${vpnHash}-hmac
 mode transport
!
crypto ipsec profile DMVPN-PROFILE
 set transform-set DMVPN-TRANSFORM
 set ikev2-profile DMVPN-IKEV2
!
interface Tunnel0
 description DMVPN Hub
 ip address 10.0.0.1 255.255.255.0
 ip mtu 1400
 ip nhrp map multicast dynamic
 ip nhrp network-id 1
 ip nhrp holdtime 300
 ip nhrp redirect
 ip tcp adjust-mss 1360
 tunnel source ${vpnLocalInterface}
 tunnel mode gre multipoint
 tunnel key 1
 tunnel protection ipsec profile DMVPN-PROFILE
!
router eigrp 100
 network 10.0.0.0 0.0.0.255
 network ${vpnLocalNetwork.replace('/', ' ')}
 no auto-summary
!
`;

      } else if (vpnType === 'flexvpn') {
        typeName = 'FlexVPN (IKEv2-based VPN)';
        description = 'Modern IKEv2-based VPN with advanced features';
        protocols = `IKEv2, ESP-${vpnEncryption.toUpperCase()}-${vpnHash.toUpperCase()}, FlexVPN`;

        config = `! FlexVPN Configuration
! Peer: ${vpnPeerIP}
! Local Network: ${vpnLocalNetwork}
! Remote Network: ${vpnRemoteNetwork}
! Generated: ${new Date().toLocaleString()}
!
crypto ikev2 authorization policy FLEXVPN-AUTH
 route set interface
 route set access-list FLEXVPN-ROUTES
!
crypto ikev2 proposal FLEXVPN-PROPOSAL
 encryption ${vpnEncryption}
 integrity ${vpnHash}
 group ${vpnDhGroup}
!
crypto ikev2 policy FLEXVPN-POLICY
 proposal FLEXVPN-PROPOSAL
!
crypto ikev2 keyring FLEXVPN-KEYRING
 peer ${vpnPeerIP}
  address ${vpnPeerIP}
  pre-shared-key ${psk}
!
crypto ikev2 profile FLEXVPN-PROFILE
 match address local interface ${vpnLocalInterface}
 match identity remote address ${vpnPeerIP} 255.255.255.255
 authentication remote pre-share
 authentication local pre-share
 keyring local FLEXVPN-KEYRING
 aaa authorization group psk list default FLEXVPN-AUTH
 virtual-template 1
 lifetime 86400
 dpd 10 2 on-demand
!
crypto ipsec transform-set FLEXVPN-TRANSFORM esp-${vpnEncryption} esp-${vpnHash}-hmac
 mode tunnel
!
crypto ipsec profile FLEXVPN-IPSEC
 set transform-set FLEXVPN-TRANSFORM
 set ikev2-profile FLEXVPN-PROFILE
 set reverse-route
!
interface Virtual-Template1 type tunnel
 ip unnumbered ${vpnLocalInterface}
 ip mtu 1400
 ip tcp adjust-mss 1360
 tunnel mode ipsec ipv4
 tunnel protection ipsec profile FLEXVPN-IPSEC
!
ip access-list extended FLEXVPN-ROUTES
 permit ip ${vpnLocalNetwork.replace('/', ' ')} ${vpnRemoteNetwork.replace('/', ' ')}
!
router ospf 1
 network ${vpnLocalNetwork.replace('/', ' ')} area 0
!
`;
      }

      // Monitoring and troubleshooting commands
      let monitoringConfig = '';
      if (vpnFeatures.enableMonitoring) {
        monitoringConfig = `
! VPN Monitoring and Troubleshooting Commands
!
! Show VPN status:
! show crypto ikev2 sa
! show crypto ipsec sa
! show crypto session
!
! Debug commands:
! debug crypto ikev2 protocol
! debug crypto ipsec
! debug crypto isakmp
!
! Clear VPN sessions:
! clear crypto ikev2 sa
! clear crypto ipsec sa
! clear crypto session
!
! Verify connectivity:
! ping ${vpnRemoteNetwork.split('/')[0]}
! traceroute ${vpnRemoteNetwork.split('/')[0]}
`;
      }

      setVpnResult({
        type: typeName,
        description,
        config: config + monitoringConfig,
        peerIP: vpnPeerIP,
        localNetwork: vpnLocalNetwork,
        remoteNetwork: vpnRemoteNetwork,
        protocols,
        ikeVersion: vpnIkeVersion.toUpperCase(),
        encryption: vpnEncryption.toUpperCase(),
        hash: vpnHash.toUpperCase(),
        dhGroup: `Group ${vpnDhGroup}`,
        authMethod: vpnAuthMethod,
        preSharedKey: psk,
        features: vpnFeatures
      });
    } catch (error) {
      showError(setVpnError, `Error generating VPN config: ${error.message}`);
    }
  }, [vpnType, vpnPeerIP, vpnLocalNetwork, vpnRemoteNetwork, vpnLocalInterface, vpnIkeVersion, vpnEncryption, vpnHash, vpnDhGroup, vpnAuthMethod, vpnPsk, vpnFeatures, validateIP, generatePassword]);

  // 17. SNMP OID Lookup
  const lookupOID = useCallback(() => {
    try {
      const oid = oidSearch.trim();
      if (!oid) {
        showError(setOidError, 'Please enter an OID');
        return;
      }

      const result = commonOIDs[oid] || null;
      const details = OID_DETAILS[oid] || null;

      // Preferred: authoritative details, fallback to commonOIDs
      const name = details?.name || (result ? result.split(' - ')[0] : 'Unknown OID');
      const description = details?.description || (result ? (result.split(' - ')[1] || '') : 'No description available');
      const type = details?.module || (oid.includes('1.3.6.1.4.1.9') ? 'Cisco Enterprise' : oid.includes('1.3.6.1.2.1.2') ? 'Interface MIB' : oid.includes('1.3.6.1.2.1.4') ? 'IP MIB' : oid.includes('1.3.6.1.2.1.25') ? 'Host Resources MIB' : 'Standard MIB');
      const access = details?.access || 'read-only';

      const parts = oid.split('.');
      const parentOID = parts.slice(0, -1).join('.');

      setOidResult({
        oid,
        name,
        description,
        type,
        access,
        parent: parentOID || 'None',
        fullPath: oid.split('.').map((p, i) => `${parts.slice(0, i + 1).join('.')}`),
        syntax: details?.syntax || null,
        module: details?.module || null,
        source: details?.source || (result ? 'Local' : 'Unknown')
      });
    } catch (error) {
      showError(setOidError, `Error looking up OID: ${error.message}`);
    }
  }, [oidSearch, commonOIDs]);

  // 18. PoE Calculator
  const calculatePoE = useCallback(() => {
    try {
      setPoeError('');
      
      if (poeDeviceCount <= 0) {
        showError(setPoeError, 'Number of devices must be greater than 0');
        return;
      }

      if (poeSwitchPower <= 0) {
        showError(setPoeError, 'Switch power must be greater than 0');
        return;
      }

      const devicePowerMap = {
        'ip-camera': { power: 7.5, class: 'Class 3', standard: '802.3af' },
        'wireless-ap': { power: 12.5, class: 'Class 3', standard: '802.3af' },
        'voip-phone': { power: 7, class: 'Class 2', standard: '802.3af' },
        'ptz-camera': { power: 20, class: 'Class 4', standard: '802.3at' },
        'switch': { power: 15, class: 'Class 4', standard: '802.3at' },
        'access-point': { power: 30, class: 'Class 6', standard: '802.3bt' }
      };

      const deviceInfo = devicePowerMap[poeDeviceType] || devicePowerMap['ip-camera'];
      const powerPerDevice = deviceInfo.power;
      const totalPower = powerPerDevice * poeDeviceCount;
      const powerAvailable = poeSwitchPower;
      const remainingPower = powerAvailable - totalPower;
      
      const status = remainingPower >= 0 ? '✅ Sufficient Power' : '❌ Insufficient Power';
      const efficiency = ((totalPower / powerAvailable) * 100).toFixed(1);
      
      const budget80Percent = powerAvailable * 0.8;
      const withinBudget = totalPower <= budget80Percent;
      
      const voltageDrop = (100 * 0.02).toFixed(2);
      const effectivePower = (powerPerDevice - (voltageDrop * 0.1)).toFixed(2);
      
      setPoeResult({
        deviceCount: poeDeviceCount,
        deviceType: poeDeviceType,
        powerPerDevice: `${powerPerDevice}W`,
        totalPower: `${totalPower.toFixed(1)}W`,
        switchPower: `${powerAvailable}W`,
        remainingPower: `${remainingPower.toFixed(1)}W`,
        status,
        efficiency: `${efficiency}%`,
        withinBudget,
        poeClass: deviceInfo.class,
        poeStandard: deviceInfo.standard,
        voltageDrop: `${voltageDrop}V`,
        effectivePower: `${effectivePower}W per device`,
        recommendation: remainingPower < 0 ? 
          'Upgrade PoE switch or reduce devices' : 
          withinBudget ? 'Configuration optimal' : 'Consider larger power supply'
      });
    } catch (error) {
      showError(setPoeError, `Error calculating PoE: ${error.message}`);
    }
  }, [poeDeviceCount, poeDeviceType, poeSwitchPower]);

  // 19. MTU Calculator
  const calculateMTU = useCallback(() => {
    try {
      setMtuError('');
      
      if (mtuSize < 68 || mtuSize > 65535) {
        showError(setMtuError, 'MTU must be between 68 and 65535 bytes');
        return;
      }

      // Enhanced protocol overheads with IPv4/IPv6 support
      const overheads = {
        'tcp': { 
          ipv4: { header: 20, options: 0, total: 40 },
          ipv6: { header: 20, options: 0, total: 60 } // IPv6 + TCP
        },
        'udp': { 
          ipv4: { header: 8, options: 0, total: 28 },
          ipv6: { header: 8, options: 0, total: 48 }
        },
        'icmp': { 
          ipv4: { header: 8, options: 0, total: 28 },
          ipv6: { header: 8, options: 0, total: 48 }
        },
        'gre': { 
          ipv4: { header: 4, options: 0, total: 24 },
          ipv6: { header: 4, options: 0, total: 44 }
        },
        'ipsec-esp': {
          ipv4: { header: 50, options: 0, total: 70 }, // ESP + auth
          ipv6: { header: 50, options: 0, total: 90 }
        },
        'vxlan': {
          ipv4: { header: 50, options: 0, total: 70 }, // Outer + VXLAN
          ipv6: { header: 70, options: 0, total: 110 }
        },
        'mpls': {
          ipv4: { header: 4, options: 0, total: 24 }, // Single label
          ipv6: { header: 4, options: 0, total: 44 }
        }
      };

      const isIPv6 = mtuProtocol.includes('ipv6') || mtuFeatures.ipv6;
      const baseProtocol = mtuProtocol.replace('-ipv6', '').replace('ipv6-', '');
      const protocolInfo = overheads[baseProtocol] || overheads.tcp;
      const versionInfo = isIPv6 ? protocolInfo.ipv6 : protocolInfo.ipv4;
      
      let overhead = versionInfo.total;
      
      // Add VLAN overhead
      if (mtuFeatures.vlan) {
        overhead += mtuFeatures.dot1q ? 4 : 8; // Single or double tag
      }
      
      // Add MPLS labels
      if (mtuFeatures.mpls) {
        overhead += mtuFeatures.mplsLabels * 4;
      }
      
      const payload = mtuSize - overhead;
      const efficiency = ((payload / mtuSize) * 100).toFixed(1);
      
      const maxSegmentSize = payload - (baseProtocol === 'tcp' ? 12 : 0);
      const fragments = Math.ceil(1500 / maxSegmentSize);
      
      // Enhanced recommendations
      let recommendation = '';
      if (mtuSize < 1500) {
        recommendation = 'Consider increasing MTU to 1500 for better efficiency';
      } else if (mtuSize > 1500 && mtuSize <= 9000) {
        recommendation = 'Jumbo frames enabled. Ensure all devices support jumbo frames';
      } else if (mtuSize > 9000) {
        recommendation = 'Ultra-large frames. Verify hardware compatibility';
      } else {
        recommendation = 'Standard MTU size. Good for general networking';
      }
      
      const pmtud = mtuSize > 1500 ? 
        'Path MTU Discovery recommended for large frames' : 
        'Standard MTU, PMTUD not critical';

      // Calculate throughput (theoretical max)
      const throughputMbps = ((payload * 8) / 1000000).toFixed(2);
      
      // Protocol breakdown
      const breakdown = {
        ip: isIPv6 ? 40 : 20,
        transport: versionInfo.header,
        encapsulation: (mtuFeatures.vlan ? (mtuFeatures.dot1q ? 4 : 8) : 0) + (mtuFeatures.mpls ? mtuFeatures.mplsLabels * 4 : 0)
      };

      setMtuResult({
        mtu: mtuSize,
        protocol: baseProtocol.toUpperCase(),
        ipVersion: isIPv6 ? 'IPv6' : 'IPv4',
        overhead,
        payload,
        efficiency: `${efficiency}%`,
        maxSegmentSize,
        fragments: fragments > 1 ? fragments : 1,
        recommendation,
        pmtud,
        throughput: `${throughputMbps} Mbps`,
        breakdown,
        features: mtuFeatures
      });
    } catch (error) {
      showError(setMtuError, `Error calculating MTU: ${error.message}`);
    }
  }, [mtuSize, mtuProtocol, mtuFeatures]);

  // 20. Routing Metrics Calculator - FIXED calculations
  const calculateMetrics = useCallback(() => {
    try {
      setMetricError('');
      
      if (metricBandwidth <= 0 || metricDelay < 0 || metricLoad <= 0 || metricReliability <= 0) {
        showError(setMetricError, 'All metrics must be positive values');
        return;
      }

      // EIGRP Metric Calculation (simplified)
      // Formula: metric = 256 * ((K1 * bandwidth) + (K2 * bandwidth)/(256 - load) + (K3 * delay))
      // Using default K values: K1=1, K2=0, K3=1, K4=0, K5=0
      
      // Convert bandwidth from Mbps to Kbps (EIGRP uses Kbps)
      const bandwidthKbps = metricBandwidth * 1000;
      
      // EIGRP uses lowest bandwidth in path (simplified)
      const scaledBandwidth = 10000000 / Math.min(bandwidthKbps, 10000000);
      
      // Delay is in tens of microseconds
      const scaledDelay = metricDelay / 10;
      
      // EIGRP metric
      const eigrpMetric = Math.round(256 * (scaledBandwidth + scaledDelay));
      
      // OSPF Cost Calculation
      // Cost = Reference Bandwidth / Interface Bandwidth
      const referenceBandwidth = 100; // 100 Mbps default
      let ospfCost = Math.round(referenceBandwidth / metricBandwidth);
      if (ospfCost < 1) ospfCost = 1;
      
      // Determine route quality
      let quality = '';
      let color = '';
      if (eigrpMetric < 1000000) {
        quality = 'Excellent Route';
        color = 'text-green-400';
      } else if (eigrpMetric < 5000000) {
        quality = 'Good Route';
        color = 'text-green-300';
      } else if (eigrpMetric < 20000000) {
        quality = 'Average Route';
        color = 'text-yellow-400';
      } else {
        quality = 'Poor Route';
        color = 'text-red-400';
      }
      
      setMetricResult({
        bandwidth: `${metricBandwidth} Mbps`,
        delay: `${metricDelay} μs`,
        load: `${((metricLoad / 255) * 100).toFixed(1)}%`,
        reliability: `${((metricReliability / 255) * 100).toFixed(1)}%`,
        eigrpMetric: eigrpMetric.toLocaleString(),
        ospfCost: ospfCost.toLocaleString(),
        quality,
        color,
        note: 'EIGRP uses K1=1, K2=0, K3=1. OSPF uses reference bandwidth of 100 Mbps.'
      });
    } catch (error) {
      showError(setMetricError, `Error calculating metrics: ${error.message}`);
    }
  }, [metricBandwidth, metricDelay, metricLoad, metricReliability]);

  // STP Calculator
  const calculateSTP = useCallback(() => {
    try {
      // STP Path Cost Standards (IEEE 802.1D-2004 and RSTP)
      const pathCosts = {
        '10': 100,      // 10 Mbps
        '100': 19,      // 100 Mbps  
        '1000': 4,      // 1 Gbps
        '10000': 2,     // 10 Gbps
        '40000': 1,     // 40 Gbps
        '100000': 1     // 100 Gbps
      };

      // Validate inputs
      const priority = parseInt(stpBridgePriority);
      if (isNaN(priority) || priority < 0 || priority > 61440 || priority % 4096 !== 0) {
        throw new Error('Bridge priority must be between 0-61440 and a multiple of 4096');
      }

      // Validate MAC address
      const cleanMac = stpMacAddress.replace(/[:.-]/g, '');
      if (!/^[0-9A-Fa-f]{12}$/.test(cleanMac)) {
        throw new Error('Invalid MAC address format');
      }

      // Calculate path cost
      let pathCost = stpPathCost ? parseInt(stpPathCost) : pathCosts[stpLinkSpeed];
      if (!pathCost || pathCost < 1) {
        pathCost = pathCosts[stpLinkSpeed] || 4;
      }

      const hopCount = Math.min(32, Math.max(1, parseInt(stpHopCount, 10) || 1));
      const perHopCost = pathCost;
      const totalPathCost = perHopCost * hopCount;

      // Get link speed description
      const linkSpeedMap = {
        '10': '10 Mbps',
        '100': '100 Mbps',
        '1000': '1 Gbps',
        '10000': '10 Gbps',
        '40000': '40 Gbps',
        '100000': '100 Gbps'
      };
      const linkSpeed = linkSpeedMap[stpLinkSpeed] || '1 Gbps';

      // Format MAC address for bridge ID (XX:XX:XX:XX:XX:XX format)
      const formattedMac = cleanMac.match(/.{2}/g).join(':').toUpperCase();
      
      // Calculate Bridge ID (IEEE 802.1D: Priority [16 bits] + MAC Address [48 bits])
      // Bridge ID format: priority:mac (where priority is in range 0-65535 in 4096 increments, lower is better)
      const bridgeId = `${priority.toString().padStart(5, '0')}:${formattedMac}`;
      
      // Calculate port costs and priorities
      const rootPortCost = totalPathCost;
      const designatedPortCost = perHopCost;
      
      // Determine bridge role based on priority
      let bridgeRole = 'Regular Bridge';
      if (priority === 0) bridgeRole = 'Root Bridge';
      else if (priority <= 4096) bridgeRole = 'Primary Root Bridge Candidate';
      else if (priority <= 8192) bridgeRole = 'Secondary Root Bridge';
      else if (priority <= 12288) bridgeRole = 'Tertiary Root Bridge';

      // Calculate STP timers (default values)
      const helloTime = 2; // seconds
      const maxAge = 20; // seconds  
      const forwardDelay = 15; // seconds

      setStpResult({
        pathCost,
        linkSpeed,
        priority,
        bridgeId,
        formattedMac,
        rootPortCost,
        designatedPortCost,
        totalPathCost,
        hopCount,
        perHopCost,
        bridgeRole,
        helloTime,
        maxAge,
        forwardDelay
      });
    } catch (error) {
      console.error('STP calculation error:', error);
      // You could add error state here if needed
      setStpResult(null);
    }
  }, [stpLinkSpeed, stpPathCost, stpBridgePriority, stpMacAddress, stpHopCount]);

  // BGP Helper Functions
  const addBgpNeighbor = useCallback(() => {
    setBgpNeighbors([...bgpNeighbors, { ip: '', asn: '', description: '' }]);
  }, [bgpNeighbors]);

  const removeBgpNeighbor = useCallback((index) => {
    setBgpNeighbors(bgpNeighbors.filter((_, i) => i !== index));
  }, [bgpNeighbors]);

  const updateBgpNeighbor = useCallback((index, field, value) => {
    const updated = [...bgpNeighbors];
    updated[index] = { ...updated[index], [field]: value };
    setBgpNeighbors(updated);
  }, [bgpNeighbors]);

  const addBgpNetwork = useCallback(() => {
    setBgpNetworks([...bgpNetworks, { network: '', mask: '' }]);
  }, [bgpNetworks]);

  const removeBgpNetwork = useCallback((index) => {
    setBgpNetworks(bgpNetworks.filter((_, i) => i !== index));
  }, [bgpNetworks]);

  const updateBgpNetwork = useCallback((index, field, value) => {
    const updated = [...bgpNetworks];
    updated[index] = { ...updated[index], [field]: value };
    setBgpNetworks(updated);
  }, [bgpNetworks]);

  const addBgpCommunity = useCallback(() => {
    setBgpCommunities([...bgpCommunities, { name: '', value: '' }]);
  }, [bgpCommunities]);

  const removeBgpCommunity = useCallback((index) => {
    setBgpCommunities(bgpCommunities.filter((_, i) => i !== index));
  }, [bgpCommunities]);

  const updateBgpCommunity = useCallback((index, field, value) => {
    const updated = [...bgpCommunities];
    updated[index] = { ...updated[index], [field]: value };
    setBgpCommunities(updated);
  }, [bgpCommunities]);

  // 22. BGP Tools
  const generateBGPConfig = useCallback(() => {
    try {
      setBgpError('');

      const asn = parseInt(bgpAsn, 10);
      if (isNaN(asn) || asn < 1 || asn > 4294967295) {
        showError(setBgpError, 'ASN must be between 1 and 4294967295');
        return;
      }

      if (!validateIP(bgpRouterId)) {
        showError(setBgpError, 'Invalid router ID (must be valid IP address)');
        return;
      }

      // Validate neighbors
      for (const neighbor of bgpNeighbors) {
        if (!validateIP(neighbor.ip)) {
          showError(setBgpError, `Invalid neighbor IP: ${neighbor.ip}`);
          return;
        }
        const neighborAsn = parseInt(neighbor.asn, 10);
        if (isNaN(neighborAsn) || neighborAsn < 1 || neighborAsn > 4294967295) {
          showError(setBgpError, `Invalid neighbor ASN: ${neighbor.asn}`);
          return;
        }
      }

      let config = `! BGP Configuration
! Local AS: ${asn}
! Router ID: ${bgpRouterId}
! Generated: ${new Date().toLocaleString()}
!
router bgp ${asn}
 bgp router-id ${bgpRouterId}
 bgp log-neighbor-changes
 no bgp default ipv4-unicast
`;

      // Confederation configuration
      if (bgpFeatures.enableConfederation) {
        config += ` bgp confederation identifier ${bgpConfederationId}\n`;
        bgpConfederationMembers.forEach(member => {
          config += ` bgp confederation peers ${member}\n`;
        });
      }

      // Route reflector configuration
      if (bgpFeatures.enableRouteReflector) {
        config += ` bgp cluster-id ${bgpRouterId}\n`;
      }

      // Communities
      if (bgpFeatures.enableCommunities) {
        config += ` bgp bestpath community all\n`;
      }

      config += `!\n`;

      // Neighbor configurations
      bgpNeighbors.forEach(neighbor => {
        const neighborAsn = parseInt(neighbor.asn, 10);
        const isIBGP = asn === neighborAsn;
        const isEBGP = asn !== neighborAsn;

        config += ` neighbor ${neighbor.ip} remote-as ${neighbor.asn}\n`;
        if (neighbor.description) {
          config += ` neighbor ${neighbor.ip} description ${neighbor.description}\n`;
        }
        config += ` neighbor ${neighbor.ip} update-source Loopback0\n`;

        if (isEBGP) {
          config += ` neighbor ${neighbor.ip} ebgp-multihop 2\n`;
        }

        if (bgpFeatures.enableSecurity) {
          config += ` neighbor ${neighbor.ip} password ${generatePassword(12, 'strong')}\n`;
        }

        config += ` neighbor ${neighbor.ip} timers 30 90\n`;

        if (bgpFeatures.enableRouteReflector && isIBGP) {
          config += ` neighbor ${neighbor.ip} route-reflector-client\n`;
        }

        if (bgpFeatures.enableCommunities) {
          config += ` neighbor ${neighbor.ip} send-community both\n`;
        }

        if (bgpFeatures.enableRouteMaps) {
          config += ` neighbor ${neighbor.ip} route-map RM-IN in\n`;
          config += ` neighbor ${neighbor.ip} route-map RM-OUT out\n`;
        }

        if (bgpFeatures.enablePrefixLists) {
          config += ` neighbor ${neighbor.ip} prefix-list PL-IN in\n`;
          config += ` neighbor ${neighbor.ip} prefix-list PL-OUT out\n`;
        }

        config += `!\n`;
      });

      // Address family IPv4
      config += ` address-family ipv4\n`;
      bgpNeighbors.forEach(neighbor => {
        config += `  neighbor ${neighbor.ip} activate\n`;
        if (parseInt(neighbor.asn, 10) === asn) {
          config += `  neighbor ${neighbor.ip} next-hop-self\n`;
        }
        config += `  neighbor ${neighbor.ip} soft-reconfiguration inbound\n`;
      });

      // Networks
      bgpNetworks.forEach(network => {
        config += `  network ${network.network} mask ${network.mask}\n`;
      });

      // Aggregation
      if (bgpFeatures.enableAggregation) {
        bgpNetworks.forEach(network => {
          const parts = network.network.split('/');
          if (parts.length === 2) {
            const prefixLen = parseInt(parts[1]);
            if (prefixLen < 24) {
              config += `  aggregate-address ${network.network} summary-only\n`;
            }
          }
        });
      }

      config += ` exit-address-family\n!\n`;

      // IPv6 address family
      if (bgpFeatures.enableIPv6) {
        config += ` address-family ipv6\n`;
        bgpNeighbors.forEach(neighbor => {
          config += `  neighbor ${neighbor.ip} activate\n`;
          config += `  neighbor ${neighbor.ip} soft-reconfiguration inbound\n`;
        });
        config += ` exit-address-family\n!\n`;
      }

      // Route maps
      if (bgpFeatures.enableRouteMaps) {
        bgpRouteMaps.forEach(rm => {
          config += `route-map ${rm.name} ${rm.action} ${rm.sequence}\n`;
          config += ` match ip address prefix-list PL-${rm.name}\n`;
          config += ` set community ${bgpCommunities[0]?.value || '64512:100'}\n`;
          config += `!\n`;
        });
      }

      // Prefix lists
      if (bgpFeatures.enablePrefixLists) {
        bgpPrefixLists.forEach(pl => {
          config += `ip prefix-list ${pl.name} seq ${pl.sequence || 5} ${pl.action} ${pl.network}\n`;
        });
        config += `!\n`;
      }

      // Communities
      if (bgpFeatures.enableCommunities) {
        bgpCommunities.forEach(comm => {
          config += `ip community-list standard ${comm.name} permit ${comm.value}\n`;
        });
        config += `!\n`;
      }

      setBgpResult({
        asn: asn,
        routerId: bgpRouterId,
        neighbors: bgpNeighbors.length,
        networks: bgpNetworks.length,
        config,
        features: bgpFeatures,
        confederation: bgpFeatures.enableConfederation ? {
          id: bgpConfederationId,
          members: bgpConfederationMembers
        } : null
      });
    } catch (error) {
      showError(setBgpError, `Error generating BGP config: ${error.message}`);
    }
  }, [bgpAsn, bgpRouterId, bgpNeighbors, bgpNetworks, bgpCommunities, bgpRouteMaps, bgpPrefixLists, bgpFeatures, bgpConfederationId, bgpConfederationMembers, validateIP, generatePassword]);

  // 22. ENHANCED Config Generator
  const generateConfig = useCallback(() => {
    try {
      setConfigError('');
      
      if (!configHostname.trim()) {
        showError(setConfigError, 'Hostname is required');
        return;
      }

      if (configInterfaces.length === 0) {
        showError(setConfigError, 'At least one interface is required');
        return;
      }

      // Generate random passwords
      const enableSecret = generatePassword(16, 'strong');
      const consolePassword = generatePassword(12, 'medium');
      const vtyPassword = generatePassword(12, 'medium');
      
      // Generate timestamp
      const timestamp = new Date().toLocaleString();
      const date = new Date().toISOString().split('T')[0];
      
      let config = '';
      
      if (configDevice === 'router') {
        // Router Configuration
        config = `! Cisco IOS Router Configuration
! Hostname: ${configHostname}
! Domain: ${configDomain}
! Generated: ${timestamp}
! IOS Version: 15.X
! Security Level: HIGH

version 15.7
service timestamps debug datetime msec localtime show-timezone
service timestamps log datetime msec localtime show-timezone
service password-encryption
service sequence-numbers
service tcp-keepalives-in
service tcp-keepalives-out
no service pad
!
hostname ${configHostname}
!
boot-start-marker
boot-end-marker
!
${configSecurityFeatures.enableDot1x ? `aaa new-model
!
aaa authentication login default local
aaa authentication enable default enable
aaa authorization exec default local
aaa authorization network default local
!` : 'no aaa new-model\n!'}
!
no ip domain lookup
ip domain name ${configDomain}
ip name-server 8.8.8.8
ip name-server 8.8.4.4
!
${configSecurityFeatures.enableSSH ? `crypto key generate rsa general-keys modulus 2048
ip ssh version 2
ip ssh time-out 60
ip ssh authentication-retries 3
ip scp server enable
!` : ''}
login block-for 120 attempts 3 within 60
login on-failure log
login on-success log
!
no ip bootp server
no ip source-route
no ip gratuitous-arps
!
ip cef
!
${configSecurityFeatures.enableIPv6 ? 'ipv6 unicast-routing\nipv6 cef\n!' : 'no ipv6 cef\n!'}
!
${configSecurityFeatures.enableMulticast ? 'ip multicast-routing\n!' : ''}
multilink bundle-name authenticated
!
spanning-tree mode ${configDevice === 'router' ? 'pvst' : 'rapid-pvst'}
spanning-tree extend system-id
${configSecurityFeatures.enableBPDUGuard ? 'spanning-tree portfast bpduguard default\nspanning-tree portfast default\n!' : '!'}
!
${configSecurityFeatures.enableNTP ? `clock timezone ${configTimezone.replace('UTC', 'EST')} ${configTimezone.includes('-') ? configTimezone.split('-')[1] : '0'}
ntp authentication-key 1 md5 ${generatePassword(8, 'medium')} 7
ntp authenticate
ntp trusted-key 1
ntp server 132.163.97.4 key 1 prefer
ntp server 129.6.15.28 key 1
ntp update-calendar
!` : '!'}
redundancy
!
${configSecurityFeatures.enableLogging ? `logging buffered 51200 informational
logging console critical
logging monitor informational
logging trap notifications
logging facility local7
logging source-interface ${configInterfaces[0]?.name || 'GigabitEthernet0/0'}
${configInterfaces[0]?.ip ? `logging host ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.254` : ''}
!` : '!'}
${configSecurityFeatures.enableSNMP ? `snmp-server community ${generatePassword(8, 'medium')} RO 99
snmp-server community ${generatePassword(8, 'medium')} RW 99
snmp-server location "Network Operations Center"
snmp-server contact "Network Admin <admin@${configDomain}>"
snmp-server enable traps snmp authentication linkdown linkup coldstart warmstart
snmp-server enable traps config
snmp-server enable traps syslog
!
ip access-list standard 99
 permit 10.0.0.0 0.255.255.255
 deny any log
!` : '!'}
username admin privilege 15 secret ${enableSecret}
username operator privilege 1 secret ${generatePassword(12, 'medium')}
!
${configSecurityFeatures.enableNetFlow ? `flow exporter NETFLOW-EXPORTER
 destination ${configInterfaces[0]?.ip ? configInterfaces[0].ip.split('.').slice(0, 3).join('.') + '.253' : '192.168.1.253'}
 source ${configInterfaces[0]?.name || 'GigabitEthernet0/0'}
 transport udp 2055
 export-protocol netflow-v9
!
flow monitor NETFLOW-MONITOR
 exporter NETFLOW-EXPORTER
 cache timeout inactive 10
 cache timeout active 60
 record netflow ipv4 original-input
!` : ''}
!
${configInterfaces.map(iface => {
  return `interface ${iface.name}
 description ${iface.description}
 ip address ${iface.ip} ${iface.mask}
 ${iface.mtu && iface.mtu !== '1500' ? `ip mtu ${iface.mtu}` : ''}
 ${iface.enableStormControl ? `storm-control broadcast level 10.00\n storm-control action shutdown` : ''}
 ${iface.speed && iface.speed !== 'auto' ? `speed ${iface.speed}` : 'negotiation auto'}
 ${iface.duplex && iface.duplex !== 'auto' ? `duplex ${iface.duplex}` : ''}
 ${iface.enableCdp ? 'cdp enable' : 'no cdp enable'}
 ${iface.enableLldp ? 'lldp transmit\n lldp receive' : 'no lldp transmit\n no lldp receive'}
 ${configSecurityFeatures.enableNetFlow ? 'ip flow monitor NETFLOW-MONITOR input\n ip flow monitor NETFLOW-MONITOR output' : ''}
 no shutdown
!`;
}).join('\n')}
!
${(() => {
  let routingConfig = '';
  
  if (configRoutingProtocol === 'ospf') {
    routingConfig = `router ospf 1
 router-id ${configInterfaces[0].ip}
 auto-cost reference-bandwidth 10000
 network ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.0 0.0.0.255 area ${configOspfArea}
 passive-interface default
 no passive-interface ${configInterfaces[0].name}
 log-adjacency-changes detail
!`;
  } else if (configRoutingProtocol === 'eigrp') {
    routingConfig = `router eigrp 100
 eigrp router-id ${configInterfaces[0].ip}
 network ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.0 0.0.0.255
 passive-interface default
 no passive-interface ${configInterfaces[0].name}
 eigrp log-neighbor-changes
!`;
  } else if (configRoutingProtocol === 'bgp') {
    routingConfig = `router bgp ${configBgpAsn}
 bgp router-id ${configInterfaces[0].ip}
 bgp log-neighbor-changes
 bgp graceful-restart
 neighbor 192.168.1.2 remote-as 65002
 neighbor 192.168.1.2 description Upstream Provider
 !
 address-family ipv4
 network ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.0 mask 255.255.255.0
 neighbor 192.168.1.2 activate
 exit-address-family
!`;
  } else if (configRoutingProtocol === 'rip') {
    routingConfig = `router rip
 version 2
 network ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.0
 no auto-summary
 passive-interface default
 no passive-interface ${configInterfaces[0].name}
!`;
  } else {
    routingConfig = `ip route 0.0.0.0 0.0.0.0 ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.254
!
`;
  }
  
  // Add redundancy protocol configuration
  if (configRedundancyProtocol === 'hsrp') {
    routingConfig += `interface ${configInterfaces[0].name}
 standby version 2
 standby 1 ip ${configVirtualIp}
 standby 1 priority 110
 standby 1 preempt
 standby 1 preempt delay minimum 30
!`;
  } else if (configRedundancyProtocol === 'glbp') {
    routingConfig += `interface ${configInterfaces[0].name}
 glbp 1 ip ${configVirtualIp}
 glbp 1 priority 110
 glbp 1 preempt
 glbp 1 authentication md5 key-string cisco123
 glbp 1 load-balancing round-robin
!`;
  } else if (configRedundancyProtocol === 'vrrp') {
    routingConfig += `interface ${configInterfaces[0].name}
 vrrp 1 ip ${configVirtualIp}
 vrrp 1 priority 110
 vrrp 1 preempt
 vrrp 1 timers advertise 1
!`;
  }
  
  return routingConfig;
})()}
${configSecurityFeatures.enableMulticast ? `ip multicast-routing
!
interface ${configInterfaces[0].name}
 ip pim sparse-mode
!
` : ''}
${configSecurityFeatures.enableVPN ? `crypto isakmp policy 10
 encr aes 256
 authentication pre-share
 group 14
 hash sha256
 lifetime 86400
!
crypto isakmp key cisco123 address 0.0.0.0
!
crypto ipsec transform-set ESP-AES256-SHA256 esp-aes 256 esp-sha256-hmac
 mode tunnel
!
crypto map VPN-MAP 10 ipsec-isakmp
 set peer 203.0.113.1
 set transform-set ESP-AES256-SHA256
 set pfs group14
 match address VPN-TRAFFIC
!
interface ${configInterfaces[0].name}
 crypto map VPN-MAP
!
ip access-list extended VPN-TRAFFIC
 permit ip 192.168.0.0 0.0.255.255 10.0.0.0 0.255.255.255
!
` : ''}
${configSecurityFeatures.enableNAT ? `ip nat inside source list NAT-ACL interface ${configInterfaces[0].name} overload
!
ip access-list standard NAT-ACL
 permit 192.168.0.0 0.0.255.255
!
interface ${configInterfaces[0].name}
 ip nat outside
!
${configInterfaces.slice(1).map(iface => `interface ${iface.name}
 ip nat inside
!`).join('\n')}
` : ''}
${configSecurityFeatures.enableIPv6 ? `ipv6 unicast-routing
ipv6 cef
!
interface ${configInterfaces[0].name}
 ipv6 address 2001:db8::1/64
 ipv6 enable
!
${configInterfaces.slice(1).map((iface, idx) => `interface ${iface.name}
 ipv6 address 2001:db8:${(idx + 2).toString(16)}::1/64
 ipv6 enable
!`).join('\n')}
ipv6 route ::/0 2001:db8::254
!
` : ''}
${configSecurityFeatures.enableMPLS ? `mpls label protocol ldp
mpls ldp router-id ${configInterfaces[0].ip} force
!
interface ${configInterfaces[0].name}
 mpls ip
!
${configInterfaces.slice(1).map(iface => `interface ${iface.name}
 mpls ip
!`).join('\n')}` : ''}
${configSecurityFeatures.enableGRE ? `interface Tunnel0
 description GRE Tunnel to Remote Site
 ip address 10.0.0.1 255.255.255.252
 tunnel source ${configInterfaces[0].ip}
 tunnel destination 203.0.113.2
 ip mtu 1400
 ip tcp adjust-mss 1360
!
ip route 192.168.2.0 255.255.255.0 Tunnel0
!
` : ''}
${configSecurityFeatures.enableDMVPN ? `crypto isakmp policy 20
 encr aes 256
 authentication pre-share
 group 14
 hash sha256
crypto isakmp key dmvpnkey address 0.0.0.0
!
crypto ipsec transform-set DMVPN-TRANSFORM esp-aes 256 esp-sha256-hmac
 mode transport
!
crypto ipsec profile DMVPN-PROFILE
 set transform-set DMVPN-TRANSFORM
 set pfs group14
!
interface Tunnel1
 description DMVPN Tunnel
 ip address 10.0.1.1 255.255.255.0
 no ip redirects
 no ip split-horizon eigrp 100
 ip mtu 1400
 ip nhrp map multicast dynamic
 ip nhrp network-id 1
 ip nhrp holdtime 300
 ip nhrp authentication dmvpnkey
 ip tcp adjust-mss 1360
 tunnel source ${configInterfaces[0].name}
 tunnel mode gre multipoint
 tunnel key 100
 tunnel protection ipsec profile DMVPN-PROFILE
!
` : ''}
${configSecurityFeatures.enableIPSLAs ? `!
ip sla 1
 icmp-echo 8.8.8.8 source-interface ${configInterfaces[0]?.name || 'GigabitEthernet0/0'}
 threshold 1000
 timeout 1000
 frequency 30
ip sla schedule 1 life forever start-time now
!
track 1 ip sla 1 reachability
!
ip route 0.0.0.0 0.0.0.0 ${configInterfaces[0]?.ip ? configInterfaces[0].ip.split('.').slice(0, 3).join('.') + '.254' : '192.168.1.254'} track 1
!` : ''}
${configSecurityFeatures.enableEEM ? `!
event manager applet INTERFACE-DOWN-ALERT
 event syslog pattern "Interface ${configInterfaces[0]?.name || 'GigabitEthernet0/0'}, changed state to down"
 action 1.0 syslog msg "CRITICAL: Interface ${configInterfaces[0]?.name || 'GigabitEthernet0/0'} is down - EEM Alert"
 action 2.0 mail server "${configDomain}" to "admin@${configDomain}" from "router@${configDomain}" subject "Interface Down Alert" body "Interface ${configInterfaces[0]?.name || 'GigabitEthernet0/0'} has gone down"
!` : ''}
${configSecurityFeatures.enableDHCP ? `!
ip dhcp excluded-address ${configInterfaces[0]?.ip ? configInterfaces[0].ip.split('.').slice(0, 3).join('.') + '.1 ' + configInterfaces[0].ip.split('.').slice(0, 3).join('.') + '.10' : '192.168.1.1 192.168.1.10'}
!
ip dhcp pool LAN-POOL
 network ${configInterfaces[0]?.ip ? configInterfaces[0].ip.split('.').slice(0, 3).join('.') + '.0 255.255.255.0' : '192.168.1.0 255.255.255.0'}
 default-router ${configInterfaces[0]?.ip || '192.168.1.1'}
 dns-server 8.8.8.8 8.8.4.4
 domain-name ${configDomain}
 lease 7
!` : ''}
${configSecurityFeatures.enableQoS ? `!
class-map match-all VOICE
 match ip dscp ef
class-map match-all VIDEO
 match ip dscp af41
class-map match-all CRITICAL-DATA
 match ip dscp af31
!
policy-map WAN-OUT
 class VOICE
  priority percent 30
  set ip dscp ef
 class VIDEO
  bandwidth percent 30
  set ip dscp af41
 class CRITICAL-DATA
  bandwidth percent 20
 class class-default
  bandwidth percent 20
  fair-queue
  random-detect
!
interface ${configInterfaces[0]?.name || 'GigabitEthernet0/0'}
 service-policy output WAN-OUT
!` : ''}
${configSecurityFeatures.enableACLs ? `!
ip access-list extended OUTSIDE-IN
 remark Permit established connections
 permit tcp any any established
 remark Permit ICMP
 permit icmp any any echo-reply
 permit icmp any any unreachable
 permit icmp any any time-exceeded
 remark Permit DNS
 permit udp any eq 53 any
 remark Permit HTTP/HTTPS
 permit tcp any any eq 80
 permit tcp any any eq 443
 remark Deny and log everything else
 deny ip any any log
!
ip access-list extended INSIDE-OUT
 remark Deny private addresses from leaving
 deny ip 10.0.0.0 0.255.255.255 any
 deny ip 172.16.0.0 0.15.255.255 any
 deny ip 192.168.0.0 0.0.255.255 any
 remark Permit all other outbound traffic
 permit ip any any
!
interface ${configInterfaces[0]?.name || 'GigabitEthernet0/0'}
 ip access-group OUTSIDE-IN in
!` : ''}
!
${configSecurityFeatures.enableHTTPS ? `ip http server
ip http secure-server
ip http authentication local
ip http timeout-policy idle 60 life 86400 requests 10000
!` : 'no ip http server\nno ip http secure-server\n!'}
!
control-plane
 service-policy input CONTROL-PLANE-POLICY
!
banner motd ^C
================================================================================
UNAUTHORIZED ACCESS TO THIS DEVICE IS PROHIBITED
You must have explicit, authorized permission to access or configure this device.
Unauthorized attempts and actions to access or use this system may result in 
civil and/or criminal penalties. All activities performed on this device are 
logged and monitored.
================================================================================
^C
!
line con 0
 exec-timeout 15 0
 password ${consolePassword}
 login local
 logging synchronous
 transport output none
line aux 0
 exec-timeout 15 0
 password ${consolePassword}
 login local
 logging synchronous
 transport output none
 no exec
line vty 0 4
 access-class 99 in
 exec-timeout 15 0
 password ${vtyPassword}
 login local
 logging synchronous
 transport input ssh
line vty 5 15
 access-class 99 in
 exec-timeout 15 0
 password ${vtyPassword}
 login local
 transport input ssh
!
${configSecurityFeatures.enableACLs ? `!
! Control Plane Protection
ip access-list extended CONTROL-PLANE-POLICY
 remark Permit BGP
 permit tcp any any eq 179
 remark Permit OSPF
 permit ospf any any
 remark Permit EIGRP
 permit eigrp any any
 remark Permit SSH
 permit tcp any any eq 22
 remark Permit SNMP
 permit udp any any eq 161
 remark Rate limit ICMP
 permit icmp any any
 remark Deny all other traffic to control plane
 deny ip any any
!` : ''}
!
end`;
      } else {
        // Switch Configuration
        config = `! Switch Configuration
! Hostname: ${configHostname}
! Domain: ${configDomain}
! Generated: ${timestamp}
! Version: 1.0
! Security Level: HIGH

version 15.2
service timestamps debug datetime msec
service timestamps log datetime msec
service password-encryption
service sequence-numbers
!
hostname ${configHostname}
!
boot-start-marker
boot-end-marker
!
no aaa new-model
!
no ip domain lookup
ip domain name ${configDomain}
ip name-server 8.8.8.8
ip name-server 8.8.4.4
!
login block-for 120 attempts 3 within 60
!
${configSecurityFeatures.enableNTP ? `clock timezone UTC -5
clock summer-time EDT recurring
ntp server 132.163.97.4 prefer
ntp server 129.6.15.28
ntp update-calendar
!` : ''}
${configSecurityFeatures.enableLogging ? `logging buffered 16384 informational
logging console informational
logging monitor informational
logging trap debugging
logging source-interface Vlan${configVlanId}
logging 192.168.1.254
!` : ''}
${configSecurityFeatures.enableSNMP ? `snmp-server community public RO
snmp-server community private RW
snmp-server location "Data Center"
snmp-server contact "Network Admin <admin@${configDomain}>"
snmp-server enable traps snmp authentication linkdown linkup coldstart warmstart
!` : ''}
!
system mtu routing 1500
!
spanning-tree mode rapid-pvst
spanning-tree extend system-id
spanning-tree vlan 1-4094 priority 32768
!
vlan internal allocation policy ascending
!
${configSecurityFeatures.enableVTP ? `vtp domain ${configDomain}
vtp password cisco123
vtp version 2
vtp mode server
!` : ''}
vlan ${configVlanId}
 name ${configVlanName}
!
${configAdditionalVlans.map(vlan => vlan.name ? `vlan ${vlan.vlanId}
 name ${vlan.name}
!` : '').join('\n')}
!
${configSecurityFeatures.enableEtherChannel ? `interface Port-channel1
 description EtherChannel Link Aggregation
 switchport mode trunk
 switchport trunk native vlan 99
 switchport trunk allowed vlan ${configVlanId},${configAdditionalVlans.map(v => v.vlanId).filter(v => v).join(',')}
!
interface range GigabitEthernet0/1-2
 description EtherChannel Member Ports
 channel-group 1 mode desirable
 switchport mode trunk
 switchport trunk native vlan 99
 switchport trunk allowed vlan ${configVlanId},${configAdditionalVlans.map(v => v.vlanId).filter(v => v).join(',')}
!
` : ''}
${configInterfaces.map(iface => {
  const interfaceConfig = [`interface ${iface.name}`];
  interfaceConfig.push(` description ${iface.description}`);
  
  if (iface.mode === 'access') {
    interfaceConfig.push(` switchport mode access`);
    interfaceConfig.push(` switchport access vlan ${iface.vlan || configVlanId}`);
    if (iface.voiceVlan) {
      interfaceConfig.push(` switchport voice vlan ${iface.voiceVlan}`);
    }
    interfaceConfig.push(` switchport nonegotiate`);
    if (configSecurityFeatures.enablePortSecurity && iface.enablePortSecurity) {
      interfaceConfig.push(` switchport port-security`);
      interfaceConfig.push(` switchport port-security maximum 3`);
      interfaceConfig.push(` switchport port-security violation restrict`);
      interfaceConfig.push(` switchport port-security aging time 2`);
    }
  } else if (iface.mode === 'trunk') {
    interfaceConfig.push(` switchport mode trunk`);
    interfaceConfig.push(` switchport trunk native vlan 99`);
    const allowedVlans = [configVlanId, ...configAdditionalVlans.map(v => v.vlanId).filter(v => v)].join(',');
    interfaceConfig.push(` switchport trunk allowed vlan ${allowedVlans}`);
    interfaceConfig.push(` switchport nonegotiate`);
  }
  
  if (iface.mtu) interfaceConfig.push(` mtu ${iface.mtu}`);
  if (iface.speed && iface.speed !== 'auto') interfaceConfig.push(` speed ${iface.speed}`);
  if (iface.duplex && iface.duplex !== 'auto') interfaceConfig.push(` duplex ${iface.duplex}`);
  if (iface.enableCdp) interfaceConfig.push(` cdp enable`);
  if (iface.enableLldp) interfaceConfig.push(` lldp transmit`);
  if (iface.enableLldp) interfaceConfig.push(` lldp receive`);
  if (configSecurityFeatures.enableStormControl && iface.enableStormControl) {
    interfaceConfig.push(` storm-control broadcast level 80.00`);
    interfaceConfig.push(` storm-control multicast level 80.00`);
    interfaceConfig.push(` storm-control unicast level 80.00`);
  }
  
  interfaceConfig.push(` no shutdown`);
  interfaceConfig.push(`!`);
  
  return interfaceConfig.join('\n');
}).join('\n')}
!
interface Vlan${configVlanId}
 description ${configVlanName} VLAN
 ip address ${configInterfaces[0].ip} ${configInterfaces[0].mask}
 no shutdown
!
${configAdditionalVlans.filter(v => v.ip && v.mask).map(v => `interface Vlan${v.vlanId}
 description ${v.name} VLAN
 ip address ${v.ip} ${v.mask}
 no shutdown
!`).join('\n')}
!
${configDevice === 'switch' && configInterfaces.some(iface => iface.mode === 'routed') ? `ip routing
!
` : ''}ip default-gateway ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.254
!
${configSecurityFeatures.enableDHCP ? `ip dhcp excluded-address ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.1 ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.10
!
ip dhcp pool VLAN${configVlanId}
 network ${configInterfaces[0].ip.split('.').slice(0, 3).join('.')}.0 255.255.255.0
 default-router ${configInterfaces[0].ip}
 dns-server 8.8.8.8 8.8.4.4
 lease 7
!
${configAdditionalVlans.filter(v => v.ip && v.mask).map((vlan, index) => 
  vlan.ip ? `ip dhcp pool VLAN${vlan.vlanId}
 network ${vlan.ip.split('.').slice(0, 3).join('.')}.0 255.255.255.0
 default-router ${vlan.ip}
 dns-server 8.8.8.8 8.8.4.4
 lease 7
!
` : '').join('')}
` : ''}
${configSecurityFeatures.enableQoS ? `mls qos
!
class-map match-all VOICE
 match ip dscp ef
!
class-map match-all VIDEO  
 match ip dscp af41
!
policy-map QOS-POLICY
 class VOICE
  trust dscp
  priority-queue out
 class VIDEO
  trust dscp
  bandwidth percent 30
 class class-default
  trust dscp
!
interface range GigabitEthernet0/1-24
 service-policy input QOS-POLICY
!
` : ''}
${configSecurityFeatures.enableSSH ? `ip ssh version 2
ip ssh authentication-retries 3
ip ssh time-out 60
!
username admin privilege 15 secret 0 ${enableSecret}
!
line vty 0 15
 transport input ssh
 login local
 exec-timeout 30 0
!
` : ''}
${configSecurityFeatures.enableACL ? `ip access-list extended MANAGEMENT-ACL
 permit tcp 192.168.1.0 0.0.0.255 any eq 22
 permit tcp 192.168.1.0 0.0.0.255 any eq 23
 permit tcp 192.168.1.0 0.0.0.255 any eq 80
 permit tcp 192.168.1.0 0.0.0.255 any eq 443
 deny ip any any log
!
line vty 0 15
 access-class MANAGEMENT-ACL in
!
` : ''}
${configSecurityFeatures.enableNTP ? `ntp server 0.pool.ntp.org
ntp server 1.pool.ntp.org
ntp server 2.pool.ntp.org
!
clock timezone UTC 0
!
` : ''}
${configSecurityFeatures.enableLogging ? `logging buffered 4096
logging console informational
logging monitor informational
logging trap informational
logging host 192.168.1.100
!
snmp-server community public RO
snmp-server community private RW
snmp-server location "Data Center"
snmp-server contact "Network Admin <admin@company.com>"
!
` : ''}
spanning-tree mode rapid-pvst
spanning-tree vlan ${configVlanId} priority 24576
!
${configSecurityFeatures.enableMSTP ? `spanning-tree mode mst
spanning-tree mst configuration
 name ${configHostname}
 revision 1
 instance 1 vlan ${configVlanId}${configAdditionalVlans.map(v => `,${v.vlanId}`).join('')}
!
spanning-tree mst 1 priority 24576
!
` : ''}
${configSecurityFeatures.enablePrivateVLAN ? `vlan ${configVlanId}
 private-vlan primary
!
vlan 100
 private-vlan isolated
!
vlan 101
 private-vlan community
!
vlan ${configVlanId}
 private-vlan association 100,101
!
interface GigabitEthernet0/1
 switchport mode private-vlan host
 switchport private-vlan host-association ${configVlanId} 100
!
` : ''}
${configSecurityFeatures.enableDHCPSnooping ? `ip dhcp snooping
ip dhcp snooping vlan ${configVlanId}${configAdditionalVlans.map(v => `,${v.vlanId}`).join('')}
no ip dhcp snooping information option
ip dhcp snooping database flash:dhcp-snooping.db
!
interface range GigabitEthernet0/1-24
 ip dhcp snooping trust
!
` : ''}
${configSecurityFeatures.enableDAI ? `ip arp inspection vlan ${configVlanId}${configAdditionalVlans.map(v => `,${v.vlanId}`).join('')}
!
interface range GigabitEthernet0/1-24
 ip arp inspection trust
!
` : ''}
${configSecurityFeatures.enableIPSourceGuard ? `interface range GigabitEthernet0/1-24
 ip verify source port-security
!
` : ''}
${configSecurityFeatures.enableBPDUGuard ? `interface range GigabitEthernet0/1-24
 spanning-tree bpduguard enable
!
` : ''}
${configSecurityFeatures.enableRootGuard ? `interface GigabitEthernet0/1
 spanning-tree guard root
!
` : ''}
${configSecurityFeatures.enableLoopGuard ? `interface range GigabitEthernet0/1-24
 spanning-tree guard loop
!
` : ''}
${configSecurityFeatures.enableUDLD ? `interface range GigabitEthernet0/1-24
 udld port aggressive
!
` : ''}
${configSecurityFeatures.enableFlexLinks ? `interface GigabitEthernet0/1
 switchport backup interface GigabitEthernet0/2
!
interface GigabitEthernet0/2
 switchport backup interface GigabitEthernet0/1
!
` : ''}
${configSecurityFeatures.enableVTP ? `vtp domain ${configHostname}
vtp mode server
vtp version 2
vtp password ${enableSecret}
!
` : ''}
end
${configSecurityFeatures.enableSSH ? `ip ssh version 2
ip ssh time-out 60
ip ssh authentication-retries 3
crypto key generate rsa modulus 2048
!` : ''}
${configSecurityFeatures.enableHTTPS ? `ip http secure-server
ip http authentication local
!` : ''}
${configSecurityFeatures.enableDot1x ? `aaa new-model
aaa authentication dot1x default group radius
aaa authorization network default group radius
!
dot1x system-auth-control
!
interface range GigabitEthernet1/0/1-24
 authentication port-control auto
 dot1x pae authenticator
!` : ''}
line con 0
 exec-timeout 30 0
 password ${consolePassword}
 login local
 logging synchronous
 transport output none
 stopbits 1
line aux 0
 exec-timeout 30 0
 password ${consolePassword}
 login local
 logging synchronous
 transport output none
 stopbits 1
line vty 0 15
 exec-timeout 30 0
 password ${vtyPassword}
 login local
 transport input ssh
!
end`;
      }

      const configFiles = {
        main: config,
        backup: `! Backup Configuration
! Device: ${configDevice.toUpperCase()}
! Hostname: ${configHostname}
! Date: ${date}
!
${config.split('\n').filter(line => !line.startsWith('!')).join('\n')}`,
        
        security: `! Security Configuration Summary
! Device: ${configHostname}
! Generated: ${timestamp}
!
${Object.entries(configSecurityFeatures)
  .filter(([_, enabled]) => enabled)
  .map(([feature]) => {
    const featureNames = {
      enableSSH: 'SSH Access',
      enableHTTPS: 'HTTPS Management',
      enableACLs: 'Access Control Lists',
      enablePortSecurity: 'Port Security',
      enableStormControl: 'Storm Control',
      enableDot1x: '802.1X Authentication',
      enableLogging: 'Syslog Logging',
      enableNTP: 'NTP Time Synchronization',
      enableSNMP: 'SNMP Monitoring',
      enableNetFlow: 'NetFlow Export',
      enableVTP: 'VTP',
      enableEtherChannel: 'EtherChannel',
      enableDHCP: 'DHCP Server',
      enableQoS: 'QoS',
      enableMulticast: 'Multicast Routing',
      enableVPN: 'VPN Support',
      enableNAT: 'NAT Configuration',
      enableIPv6: 'IPv6 Support',
      enableMPLS: 'MPLS Configuration',
      enableGRE: 'GRE Tunnels',
      enableDMVPN: 'DMVPN',
      enableIPSLAs: 'IP SLA',
      enableEEM: 'EEM Scripts',
      enableMSTP: 'MSTP',
      enablePrivateVLAN: 'Private VLANs',
      enableDHCPSnooping: 'DHCP Snooping',
      enableDAI: 'Dynamic ARP Inspection',
      enableIPSourceGuard: 'IP Source Guard',
      enableBPDUGuard: 'BPDU Guard',
      enableRootGuard: 'Root Guard',
      enableLoopGuard: 'Loop Guard',
      enableUDLD: 'UDLD',
      enableFlexLinks: 'Flex Links'
    };
    return `✓ ${featureNames[feature]}`;
  }).join('\n')}
!
! Generated Passwords:
! Enable Secret: ${enableSecret}
! Console Password: ${consolePassword}
! VTY Password: ${vtyPassword}
${configSecurityFeatures.enableSSH ? `! SSH Key Generated` : ''}`,
        
        interfaces: `! Interface Configuration Summary
! Device: ${configHostname}
! Generated: ${timestamp}
!
${configInterfaces.map(iface => {
  return `Interface: ${iface.name}
  Description: ${iface.description}
  IP Address: ${iface.ip} ${iface.mask}
  Mode: ${iface.mode}
  ${iface.mode === 'access' ? `Access VLAN: ${iface.vlan || configVlanId}` : ''}
  ${iface.voiceVlan ? `Voice VLAN: ${iface.voiceVlan}` : ''}
  MTU: ${iface.mtu || '1500'}
  Speed: ${iface.speed}
  Duplex: ${iface.duplex}
  CDP: ${iface.enableCdp ? 'Enabled' : 'Disabled'}
  LLDP: ${iface.enableLldp ? 'Enabled' : 'Disabled'}
  Port Security: ${iface.enablePortSecurity ? 'Enabled' : 'Disabled'}
  Storm Control: ${iface.enableStormControl ? 'Enabled' : 'Disabled'}
`;
}).join('\n')}`
      };

      setConfigResult({
        device: configDevice,
        hostname: configHostname,
        config: config,
        configFiles,
        interfaces: configInterfaces.length,
        enableSecret,
        consolePassword,
        vtyPassword,
        routingProtocol: configRoutingProtocol,
        vlanId: configVlanId,
        vlanName: configVlanName,
        timestamp,
        securityFeatures: Object.entries(configSecurityFeatures)
          .filter(([_, enabled]) => enabled)
          .map(([feature]) => feature.replace('enable', ''))
      });
    } catch (error) {
      showError(setConfigError, `Error generating config: ${error.message}`);
    }
  }, [configDevice, configHostname, configDomain, configRoutingProtocol, configOspfArea, 
      configVlanId, configVlanName, configAdditionalVlans, configSecurityFeatures, 
      configInterfaces, generatePassword, configBgpAsn, configRedundancyProtocol, configVirtualIp, configTimezone]);

  // ==============================
  // TOOL MANAGEMENT FUNCTIONS
  // ==============================

  const addVlsmRequirement = useCallback(() => {
    const newId = Math.max(...vlsmRequirements.map(r => r.id), 0) + 1;
    setVlsmRequirements([...vlsmRequirements, { 
      id: newId, 
      name: `Department ${newId}`, 
      hosts: 10 
    }]);
  }, [vlsmRequirements]);

  const updateVlsmRequirement = useCallback((id, field, value) => {
    setVlsmRequirements(vlsmRequirements.map(r => 
      r.id === id ? { ...r, [field]: field === 'hosts' ? parseInt(value) || 0 : value } : r
    ));
  }, [vlsmRequirements]);

  const removeVlsmRequirement = useCallback((id) => {
    setVlsmRequirements(vlsmRequirements.filter(r => r.id !== id));
  }, [vlsmRequirements]);

  const addConfigInterface = useCallback(() => {
    const newId = Math.max(...configInterfaces.map(i => i.id), 0) + 1;
    const interfaceNum = configInterfaces.length;
    const baseName = configDevice === 'router' ? 'GigabitEthernet' : 'FastEthernet';
    const slot = configDevice === 'router' ? '0' : '0/1';
    
    setConfigInterfaces([...configInterfaces, { 
      id: newId, 
      name: `${baseName}${slot}/${interfaceNum}`,
      ip: configDevice === 'router' ? '192.168.' + (interfaceNum + 1) + '.1' : '10.0.' + interfaceNum + '.1',
      mask: '255.255.255.0',
      description: configDevice === 'router' ? 'LAN Interface' : 'Access Interface',
      vlan: configVlanId,
      mode: configDevice === 'switch' ? 'access' : 'routed',
      mtu: '1500',
      duplex: 'auto',
      speed: 'auto',
      enableCdp: true,
      enableLldp: true,
      enablePortSecurity: configDevice === 'switch',
      enableStormControl: false,
      voiceVlan: ''
    }]);
  }, [configInterfaces, configDevice, configVlanId]);

  const updateConfigInterface = useCallback((id, field, value) => {
    setConfigInterfaces(configInterfaces.map(i => 
      i.id === id ? { ...i, [field]: value } : i
    ));
  }, [configInterfaces]);

  const removeConfigInterface = useCallback((id) => {
    setConfigInterfaces(configInterfaces.filter(i => i.id !== id));
  }, [configInterfaces]);

  const addQosPolicy = useCallback(() => {
    const newId = Math.max(...qosPolicies.map(p => p.id), 0) + 1;
    setQosPolicies([...qosPolicies, { 
      id: newId, 
      class: `class${newId}`, 
      percent: 10 
    }]);
  }, [qosPolicies]);

  const updateQosPolicy = useCallback((id, field, value) => {
    setQosPolicies(qosPolicies.map(p => 
      p.id === id ? { ...p, [field]: field === 'percent' ? parseInt(value) || 0 : value } : p
    ));
  }, [qosPolicies]);

  const removeQosPolicy = useCallback((id) => {
    setQosPolicies(qosPolicies.filter(p => p.id !== id));
  }, [qosPolicies]);

  const addSummarizeIP = useCallback(() => {
    setSummarizeIPs([...summarizeIPs, '']);
  }, [summarizeIPs]);

  const updateSummarizeIP = useCallback((index, value) => {
    const newIPs = [...summarizeIPs];
    newIPs[index] = value;
    setSummarizeIPs(newIPs);
  }, [summarizeIPs]);

  const removeSummarizeIP = useCallback((index) => {
    setSummarizeIPs(summarizeIPs.filter((_, i) => i !== index));
  }, [summarizeIPs]);

  // Enhanced Config Generator functions
  const addAdditionalVlan = useCallback(() => {
    const newId = Math.max(...configAdditionalVlans.map(v => v.id), 0) + 1;
    const vlanId = (parseInt(configAdditionalVlans[configAdditionalVlans.length - 1]?.vlanId) || 30) + 10;
    
    setConfigAdditionalVlans([...configAdditionalVlans, {
      id: newId,
      vlanId: vlanId.toString(),
      name: `VLAN_${vlanId}`,
      ip: `10.0.${vlanId}.1`,
      mask: '255.255.255.0'
    }]);
  }, [configAdditionalVlans]);

  const updateAdditionalVlan = useCallback((id, field, value) => {
    setConfigAdditionalVlans(configAdditionalVlans.map(v => 
      v.id === id ? { ...v, [field]: value } : v
    ));
  }, [configAdditionalVlans]);

  const removeAdditionalVlan = useCallback((id) => {
    setConfigAdditionalVlans(configAdditionalVlans.filter(v => v.id !== id));
  }, [configAdditionalVlans]);

  const toggleSecurityFeature = useCallback((feature) => {
    setConfigSecurityFeatures(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  }, []);

  // Filter ports
  const filteredPorts = useMemo(() => {
    const query = portSearch.trim().toLowerCase();
    if (!query) return commonPorts;
    
    return commonPorts.filter(p =>
      String(p.port).includes(query) ||
      p.protocol.toLowerCase().includes(query) ||
      p.description.toLowerCase().includes(query)
    );
  }, [portSearch, commonPorts]);

  // Filter OIDs
  const filteredOIDs = useMemo(() => {
    const query = oidSearch.trim().toLowerCase();
    if (!query) return Object.entries(commonOIDs);
    
    return Object.entries(commonOIDs).filter(([oid, desc]) =>
      oid.includes(query) || desc.toLowerCase().includes(query)
    );
  }, [oidSearch, commonOIDs]);

  // Tool categories
  const toolCategories = {
    all: { name: 'All Tools', icon: Globe, count: toolButtons.length },
    ip: { name: 'IP & Subnetting', icon: Cpu, count: toolButtons.filter(t => t.category === 'ip').length },
    security: { name: 'Security & VPN', icon: Shield, count: toolButtons.filter(t => t.category === 'security').length },
    dns: { name: 'DNS & Domains', icon: Globe, count: toolButtons.filter(t => t.category === 'dns').length },
    lookup: { name: 'Lookups & Reference', icon: BookOpen, count: toolButtons.filter(t => t.category === 'lookup').length },
    convert: { name: 'Conversion & Encoding', icon: Code, count: toolButtons.filter(t => t.category === 'convert').length },
    design: { name: 'Design & Capacity', icon: Layers, count: toolButtons.filter(t => t.category === 'design').length },
    cisco: { name: 'Cisco Config & Routing', icon: Activity, count: toolButtons.filter(t => t.category === 'cisco').length },
    monitoring: { name: 'Monitoring & Logs', icon: BarChart, count: toolButtons.filter(t => t.category === 'monitoring').length }
  };

  // Filter tools based on category and search
  const filteredTools = useMemo(() => {
    let tools = toolButtons;
    
    if (activeCategory !== 'all') {
      tools = tools.filter(tool => tool.category === activeCategory);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      tools = tools.filter(tool => 
        tool.label.toLowerCase().includes(term) ||
        tool.category.toLowerCase().includes(term)
      );
    }
    
    return tools;
  }, [activeCategory, searchTerm, toolButtons]);

  
  // Reset config generator
  const resetConfigGenerator = useCallback(() => {
    setConfigDevice('router');
    setConfigHostname('Router1');
    setConfigDomain('network.lab');
    setConfigTimezone('UTC-5');
    setConfigRoutingProtocol('ospf');
    setConfigOspfArea('0');
    setConfigVlanId('10');
    setConfigVlanName('USERS');
    setConfigAdditionalVlans([
      { id: 2, vlanId: '20', name: 'SERVERS', ip: '10.0.20.1', mask: '255.255.255.0' },
      { id: 3, vlanId: '30', name: 'MANAGEMENT', ip: '10.0.30.1', mask: '255.255.255.0' },
      { id: 99, vlanId: '99', name: 'NATIVE', ip: '', mask: '' }
    ]);
    setConfigSecurityFeatures({
      enableSSH: true,
      enableHTTPS: true,
      enableACLs: true,
      enablePortSecurity: true,
      enableStormControl: false,
      enableDot1x: false,
      enableLogging: true,
      enableNTP: true,
      enableSNMP: true,
      enableNetFlow: false
    });
    setConfigInterfaces([
      {
        id: 1,
        name: 'GigabitEthernet0/0',
        ip: '192.168.1.1',
        mask: '255.255.255.0',
        description: 'WAN Interface',
        vlan: '1',
        mode: 'routed',
        mtu: '1500',
        duplex: 'auto',
        speed: 'auto',
        enableCdp: true,
        enableLldp: true,
        enablePortSecurity: false,
        enableStormControl: false,
        voiceVlan: ''
      }
    ]);
    setConfigResult(null);
    setConfigError('');
  }, []);

  // Clear all tool data
  const clearAllToolData = useCallback(() => {
    if (!window.confirm('Are you sure you want to clear all tool data? This will reset the app to defaults.')) return;

    try {
      // Remove known app keys
      localStorage.removeItem('activeTool');
      localStorage.removeItem('activeSection');
      localStorage.removeItem('activeCategory');
      // If you prefer to clear all localStorage, uncomment the next line:
      // localStorage.clear();
    } catch (e) {
      // ignore storage errors
    }
    // Reset in-memory state for all tools to their initial defaults
    try {
      // Subnetting
      setIpAddress('192.168.1.0');
      setCidr('24');
      setSubnetResults(null);
      setSubnetError('');

      // Binary converter
      setDecimalIP('192.168.1.1');
      setBinaryResult(null);
      setBinaryError('');

      // VLSM
      setVlsmNetwork('10.0.0.0');
      setVlsmCidr('16');
      setVlsmRequirements([
        { id: 1, name: 'HR Department', hosts: 50 },
        { id: 2, name: 'IT Department', hosts: 30 },
        { id: 3, name: 'Sales Department', hosts: 100 }
      ]);
      setVlsmResults(null);
      setVlsmError('');

      // MAC lookup
      setMacAddress('00:1A:2B:3C:4D:5E');
      setMacResult(null);
      setMacError('');

      // Port/Reference
      setPortSearch('');

      // Wildcard / Summarize
      setWildcardIP('255.255.255.0');
      setWildcardResult(null);
      setWildcardError('');
      setSummarizeIPs(['192.168.1.0/24', '192.168.2.0/24', '192.168.3.0/24']);
      setSummarizeResult(null);
      setSummarizeError('');

      // Bandwidth
      setFileSize('100');
      setSizeUnit('MB');
      setBandwidth('10');
      setBandwidthUnit('Mbps');
      setBandwidthResult(null);
      setBandwidthError('');

      // Protocol / Hash / Password
      setProtocolCode('6');
      setProtocolResult(null);
      setHashType('sha256');
      setHashText('');
      setHashResult(null);
      setHashError('');
      setPasswordText('cisco');
      setPasswordType('type7');
      setPasswordResult(null);
      setPasswordError('');

      // Cabling
      setCableType('cat6');
      setCableLength(100);
      setCablingResult(null);
      setCablingError('');

      // Logs
      setLogText(`%LINK-3-UPDOWN: Interface GigabitEthernet0/1, changed state to down
%LINK-5-CHANGED: Interface GigabitEthernet0/1, changed state to administratively down
%SYS-5-CONFIG_I: Configured from console by admin on vty0 (192.168.1.100)`);
      setLogResult(null);
      setLogError('');
      setLogFormat('auto');
      setLogAnalysisLevel('detailed');

      // QoS
      setQosBandwidth(100);
      setQosPolicies([
        { id: 1, class: 'voice', percent: 20 },
        { id: 2, class: 'video', percent: 30 },
        { id: 3, class: 'data', percent: 50 }
      ]);
      setQosResult(null);
      setQosError('');

      // IPv6
      setIpv6Address('2001:0db8:85a3::8a2e:0370:7334');
      setIpv6Prefix('64');
      setIpv6Result(null);
      setIpv6Error('');

      // VPN
      setVpnType('ipsec-site-to-site');
      setVpnPeerIP('203.0.113.1');
      setVpnLocalNetwork('192.168.1.0/24');
      setVpnRemoteNetwork('10.0.0.0/24');
      setVpnLocalInterface('GigabitEthernet0/0');
      setVpnIkeVersion('ikev2');
      setVpnEncryption('aes256');
      setVpnHash('sha256');
      setVpnDhGroup('14');
      setVpnAuthMethod('pre-shared');
      setVpnPsk('');
      setVpnFeatures({
        enableDPD: true,
        enableNatTraversal: true,
        enablePfs: true,
        enableRouteInjection: true,
        enableMonitoring: true
      });
      setVpnResult(null);
      setVpnError('');

      // OID
      setOidSearch('');
      setOidResult(null);
      setOidError('');

      // PoE
      setPoeDeviceCount(5);
      setPoeDeviceType('ip-camera');
      setPoeSwitchPower(370);
      setPoeResult(null);
      setPoeError('');

      // MTU
      setMtuSize(1500);
      setMtuProtocol('tcp');
      setMtuFeatures({
        ipv6: false,
        vlan: false,
        dot1q: true,
        mpls: false,
        mplsLabels: 1
      });
      setMtuResult(null);
      setMtuError('');

      // Routing metrics
      setMetricBandwidth(100);
      setMetricDelay(100);
      setMetricLoad(1);
      setMetricReliability(255);
      setMetricResult(null);
      setMetricError('');

      // BGP
      setBgpAsn('64512');
      setBgpRouterId('1.1.1.1');
      setBgpNeighbors([{ ip: '192.168.1.1', asn: '64513', description: 'Peer Router' }]);
      setBgpNetworks([{ network: '192.168.0.0', mask: '255.255.0.0' }]);
      setBgpCommunities([{ name: 'LOCAL', value: '64512:100' }]);
      setBgpRouteMaps([{ name: 'RM-IN', action: 'permit', sequence: 10 }]);
      setBgpPrefixLists([{ name: 'PL-IN', action: 'permit', network: '0.0.0.0/0' }]);
      setBgpFeatures({
        enableRouteReflector: false,
        enableConfederation: false,
        enableAggregation: false,
        enableIPv6: false,
        enableSecurity: true,
        enableCommunities: true,
        enableRouteMaps: false,
        enablePrefixLists: false
      });
      setBgpConfederationId('64500');
      setBgpConfederationMembers(['64512', '64513']);
      setBgpResult(null);
      setBgpError('');

      // STP
      setStpLinkSpeed('1000');
      setStpPathCost('');
      setStpHopCount('1');
      setStpBridgePriority('32768');
      setStpMacAddress('00:00:00:00:00:01');
      setStpResult(null);

      // Reset enhanced config generator via helper
      if (typeof resetConfigGenerator === 'function') resetConfigGenerator();

      // Config results
      setConfigResult(null);
      setConfigError('');
    } catch (err) {
      // Ensure we don't break if any setter is missing
      console.warn('clearAllToolData: partial reset completed', err);
    }
  }, [resetConfigGenerator]);

  // ==============================
  // RENDER COMPONENT
  // ==============================

  return (
    <div className={`min-h-screen bg-black text-gray-100 overflow-x-hidden ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Background */}
      <div className="fixed inset-0 z-0 bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-black"></div>
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]"></div>
      </div>

      <div className="relative z-10">
        {/* Navigation */}
        {!isFullscreen && (
          <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/5">
            <div className="container mx-auto px-4 sm:px-6 py-3">
              <div className="flex justify-between items-center h-12">
                {/* Logo/Brand */}
                <div 
                  className="flex items-center cursor-pointer group relative"
                  onClick={() => {
                    try {
                      window.open('https://vaibhav.foo', '_blank', 'noopener,noreferrer');
                    } catch (err) {
                      // Fallback for environments where window.open is restricted
                      window.location.href = 'https://vaibhav.foo';
                    }
                    setActiveSection('home');
                  }}
                >
                  <div className="absolute inset-0 bg-white/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="relative w-10 h-10 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-xl shadow-white/5 ring-1 ring-white/10">
                    <span className="text-black font-black text-xl leading-none select-none">V</span>
                  </div>
                </div>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center gap-10">
                  {[
                    { id: 'home', label: 'Home', icon: Home },
                    { id: 'tools', label: 'Tools', icon: Activity }
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className="group relative flex items-center gap-3"
                    >
                      <div className={`p-2 rounded-xl transition-all duration-300 ${
                        activeSection === item.id 
                        ? 'bg-white text-black shadow-lg shadow-white/10 scale-110' 
                        : 'bg-white/5 text-gray-500 group-hover:bg-white/10 group-hover:text-gray-300'
                      }`}>
                        <item.icon className="w-4 h-4" />
                      </div>
                      <span className={`text-[12px] font-black tracking-widest uppercase transition-colors ${
                        activeSection === item.id ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'
                      }`}>
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-colors text-white"
                  aria-label="Toggle mobile menu"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>

              {/* Mobile Menu */}
              {isMobileMenuOpen && (
                <div className="md:hidden mt-3 pb-3 space-y-2 transition-all duration-300">
                  {[
                    { id: 'home', label: 'Home', icon: Home },
                    { id: 'tools', label: 'Tools', icon: Activity }
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveSection(item.id);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-5 py-4 rounded-2xl transition-all ${
                        activeSection === item.id
                          ? 'bg-white/10 text-white font-bold ring-1 ring-white/20'
                          : 'bg-white/5 text-gray-400 border border-white/5'
                      }`}
                    >
                      <item.icon className={`w-5 h-5 ${activeSection === item.id ? 'text-white' : 'text-gray-500'}`} />
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>
        )}
{activeSection === 'home' && !isFullscreen && (
  <section className="min-h-screen flex items-center justify-center px-4 sm:px-6 pt-20 pb-8">
    <div className="text-center max-w-5xl animate-fadeIn w-full">
      
      <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 text-gray-100 leading-tight">
       Hi! I'm Vaibhav Chamoli
      </h1>
      
      {/* Typewriter Animation Section */}
      <div className="mb-4">
        <div className="text-xl sm:text-2xl md:text-3xl text-gray-400 min-h-[2.5rem]">
          <TypewriterComponent />
        </div>
      </div>
      
      <p className="text-base sm:text-lg md:text-xl text-gray-500 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-2">

        Passionate about network automation, security, and infrastructure design.
      </p>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-16 px-4">
        <button 
          onClick={() => {
            setActiveSection('tools');
            setActiveTool('subnet');
          }}
          className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gray-950 hover:bg-gray-800 rounded-full font-semibold text-base sm:text-lg border border-gray-800 transition-all hover:scale-105 touch-manipulation"
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            <Terminal className="w-4 h-4 sm:w-5 sm:h-5" />
            Explore Tools
          </span>
        </button>
      </div>

      {/* Floating Skill Badges */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-4 px-2">
        {[
          { name: 'Cisco Networking', icon: Router, description: 'IOS/IOS XE, SD-WAN' },
          { name: 'Routing Protocols', icon: GlobeIcon, description: 'OSPF, EIGRP, BGP' },
          { name: 'Layer 2 Technologies', icon: Layers, description: 'VLANs, STP, EtherChannel' },
          { name: 'VPN & Security', icon: Shield, description: 'IPsec, DMVPN, ACLs' },
          { name: 'Linux Administration', icon: Terminal, description: 'System & Network' },
          { name: 'Cloud Networking', icon: Cloud, description: 'Azure, AWS, GCP' },
          { name: 'Automation', icon: Code, description: 'Python, Ansible' },
          { name: 'Monitoring', icon: Activity, description: 'Prometheus, Grafana' }
        ].map((skill, idx) => {
          const Icon = skill.icon;
          return (
            <div
              key={idx}
              className="group px-3 sm:px-4 md:px-6 py-2 sm:py-3 bg-black/60 backdrop-blur-sm rounded-full border border-gray-800 hover:border-gray-600 transition-all hover:scale-105 skill-card touch-manipulation min-w-0 flex-shrink-0"
              style={{ '--delay': `${idx * 0.8}s` }}
            >
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-gray-300 flex-shrink-0" />
                <div className="text-left min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-gray-300 group-hover:text-gray-100 block truncate">
                    {skill.name}
                  </span>
                  <span className="text-xs text-gray-500 group-hover:text-gray-400 block hidden sm:block">
                    {skill.description}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Social Links */}
      <div className="flex gap-4 sm:gap-6 justify-center mt-12 sm:mt-16">
        <a 
          href="https://github.com/VaibhavChamoli" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-12 h-12 sm:w-14 sm:h-14 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border border-gray-800 hover:bg-gray-800 hover:border-gray-600 transition-all hover:scale-105 touch-manipulation"
        >
          <Github className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 hover:text-gray-300" />
        </a>
        <a 
          href="https://www.linkedin.com/in/vaibhav-chamoli-240420309/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="w-12 h-12 sm:w-14 sm:h-14 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border border-gray-800 hover:bg-gray-800 hover:border-gray-600 transition-all hover:scale-105 touch-manipulation"
        >
          <Linkedin className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 hover:text-gray-300" />
        </a>
        <a 
          href="mailto:vaibhavx00@gmail.com"
          className="w-12 h-12 sm:w-14 sm:h-14 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border border-gray-800 hover:bg-gray-800 hover:border-gray-600 transition-all hover:scale-105 touch-manipulation"
        >
          <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 hover:text-gray-300" />
        </a>
      </div>
    </div>
  </section>
)}
        {/* Tools Section */}
        {activeSection === 'tools' && (
          <section className={`py-32 px-6 ${isFullscreen ? 'min-h-screen' : ''}`}>
            <div className="container mx-auto max-w-7xl">
              {/* Header */}
              {!isFullscreen && (
                <div className="text-center mb-16 animate-fadeIn">
                  <h2 className="text-5xl font-bold mb-4 text-gray-100">
                    Network Tools
                  </h2>
                  <p className="text-xl text-gray-500">30+ interactive tools for learning and practice</p>
                </div>
              )}

              {/* Tool Categories and Search */}
              {!isFullscreen && (
                <div className="mb-6 sm:mb-8">
                  <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide lg:flex-wrap lg:justify-center lg:overflow-x-visible lg:gap-3">
                    {Object.entries(toolCategories).map(([key, category]) => {
                      const Icon = category.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            setActiveCategory(key);
                            setSearchTerm('');
                          }}
                          className={`px-3 sm:px-4 py-2 rounded-full font-semibold transition-all flex items-center gap-1.5 sm:gap-2 flex-shrink-0 touch-manipulation ${
                            activeCategory === key 
                              ? 'bg-gray-800 text-gray-100 border border-gray-600' 
                              : 'bg-black text-gray-400 hover:bg-gray-950 hover:text-gray-300 border border-gray-800'
                          }`}
                        >
                          <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="text-sm">{category.name}</span>
                          <span className="text-xs bg-gray-950 px-1.5 py-0.5 rounded-full">
                            {category.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Search and Controls */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search tools by name or category..."
                        className="w-full bg-black border border-gray-800 rounded-xl pl-9 sm:pl-10 pr-4 py-3 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600 text-sm sm:text-base touch-manipulation"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Tool Grid */}
              {!isFullscreen && (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6 sm:mb-8">
                  {filteredTools.map(tool => {
                    const Icon = tool.icon;
                    return (
                      <button
                        key={tool.id}
                        onClick={() => setActiveTool(tool.id)}
                        className={`p-3 sm:p-4 rounded-xl border transition-all flex flex-col items-center justify-center gap-1.5 sm:gap-2 touch-manipulation min-h-[90px] sm:min-h-[120px] ${
                          activeTool === tool.id
                            ? 'bg-gray-800 border-gray-600 scale-105 ring-2 ring-gray-500'
                            : 'bg-black/60 border-gray-800 hover:bg-gray-950 hover:border-gray-600'
                        }`}
                      >
                        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-300 mb-1" />
                        <span className="text-xs sm:text-sm font-medium text-center leading-tight px-1">{tool.label}</span>
                        <div className="flex items-center gap-1 sm:gap-2 mt-1">
                          <span className="text-xs text-gray-500 capitalize bg-black/50 px-2 py-0.5 rounded-full">{tool.category}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Active Tool Display */}
              <div className={`bg-black/60 backdrop-blur-sm rounded-3xl border border-gray-800 animate-fadeIn ${isFullscreen ? 'min-h-screen p-4 sm:p-8' : 'p-4 sm:p-6 md:p-8'}`}>
                {/* Tool Header */}
                <div className="mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                      {(() => {
                        const tool = toolButtons.find(t => t.id === activeTool);
                        const Icon = tool?.icon || Globe;
                        return (
                          <>
                            <div className="bg-gray-800 p-2 sm:p-3 rounded-2xl border border-gray-600 flex-shrink-0">
                              <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-100 truncate">
                                {tool?.label || 'Select a Tool'}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-500 truncate">
                                {tool ? `Category: ${toolCategories[tool.category]?.name}` : 'Choose a tool from the grid above'}
                              </p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        className="p-2 bg-gray-800 hover:bg-gray-600 rounded-lg border border-gray-600 text-gray-300 touch-manipulation"
                      >
                        {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Error Display */}
                {(subnetError || binaryError || vlsmError || macError || wildcardError || 
                  summarizeError || bandwidthError || aclError || hashError || passwordError || 
                  cablingError || logError || qosError || ipv6Error || vpnError || 
                  poeError || mtuError || metricError || bgpError || configError || oidError) && (
                  <div className="mb-6 p-4 bg-red-900/20 border border-red-700 rounded-xl">
                    <div className="flex items-center gap-2 text-red-400">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-semibold">Error:</span>
                      <span>{subnetError || binaryError || vlsmError || macError || wildcardError || 
                            summarizeError || bandwidthError || aclError || hashError || passwordError || 
                            cablingError || logError || qosError || ipv6Error || vpnError || 
                            poeError || mtuError || metricError || bgpError || configError || oidError}</span>
                    </div>
                  </div>
                )}

                {/* ==============================
                    TOOL IMPLEMENTATIONS
                ============================== */}

                {/* Subnet Tool */}
                <ErrorBoundary>
                  {activeTool === 'subnet' && (
                    <div>
                      <div className="space-y-4 sm:space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">
                              IP Address
                            </label>
                            <ValidatedInput
                              value={ipAddress}
                              onChange={setIpAddress}
                              validate={validateIP}
                              errorMessage="Invalid IP address"
                              placeholder="Enter IP address"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">CIDR Notation</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <input
                                type="number"
                                value={cidr}
                                onChange={(e) => setCidr(e.target.value)}
                                min="0"
                                max="32"
                                className="flex-1 bg-black border border-gray-800 rounded-xl px-4 py-3 text-base sm:text-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600 touch-manipulation"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setCidr('24')}
                                  className="px-3 sm:px-4 py-2 bg-gray-800 hover:bg-gray-600 rounded-xl border border-gray-600 text-gray-300 text-sm sm:text-base touch-manipulation"
                                >
                                  /24
                                </button>
                                <button
                                  onClick={() => setCidr('16')}
                                  className="px-3 sm:px-4 py-2 bg-gray-800 hover:bg-gray-600 rounded-xl border border-gray-600 text-gray-300 text-sm sm:text-base touch-manipulation"
                                >
                                  /16
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                          <button
                            onClick={calculateSubnet}
                            className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100 transition-colors touch-manipulation text-base"
                          >
                            Calculate Subnet
                          </button>
                          <button
                            onClick={() => {
                              setIpAddress('192.168.1.0');
                              setCidr('24');
                              setSubnetResults(null);
                              setSubnetError('');
                            }}
                            className="px-4 sm:px-6 py-3 bg-gray-950 hover:bg-gray-800 rounded-xl border border-gray-800 text-gray-300 touch-manipulation text-sm sm:text-base"
                          >
                            Reset
                          </button>
                        </div>
                      </div>

                      {subnetResults && (
                        <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                              <div className="space-y-3 sm:space-y-4">
                                <div>
                                  <div className="text-sm text-gray-400">Network Address</div>
                                  <div className="text-lg sm:text-2xl font-bold text-gray-100 break-all">{subnetResults.network}/{subnetResults.cidr}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Subnet Mask</div>
                                  <div className="text-lg sm:text-2xl font-bold text-gray-100 break-all">{subnetResults.mask}</div>
                                </div>
                              </div>
                              <div className="space-y-3 sm:space-y-4">
                                <div>
                                  <div className="text-sm text-gray-400">IP Range</div>
                                  <div className="text-sm sm:text-lg font-mono text-gray-300 break-all">{subnetResults.ipRange}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Address Type</div>
                                  <div className={`text-base sm:text-lg font-semibold ${subnetResults.privateRange !== 'Public' ? 'text-yellow-400' : 'text-green-400'}`}>
                                    {subnetResults.privateRange}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                              <div className="flex justify-between items-start mb-3 sm:mb-4">
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm text-gray-400">First Usable Host</div>
                                  <div className="text-lg sm:text-2xl font-bold text-gray-100 break-all">{subnetResults.firstHost}</div>
                                </div>
                                <CopyButton text={subnetResults.firstHost} itemKey="subnet-first" />
                              </div>
                            </div>
                            <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                              <div className="flex justify-between items-start mb-3 sm:mb-4">
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm text-gray-400">Last Usable Host</div>
                                  <div className="text-lg sm:text-2xl font-bold text-gray-100 break-all">{subnetResults.lastHost}</div>
                                </div>
                                <CopyButton text={subnetResults.lastHost} itemKey="subnet-last" />
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                              <div className="flex justify-between items-start mb-3 sm:mb-4">
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm text-gray-400">Broadcast Address</div>
                                  <div className="text-lg sm:text-2xl font-bold text-gray-100 break-all">{subnetResults.broadcast}</div>
                                </div>
                                <CopyButton text={subnetResults.broadcast} itemKey="subnet-broadcast" />
                              </div>
                            </div>
                            <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                              <div className="space-y-3 sm:space-y-4">
                                <div>
                                  <div className="text-sm text-gray-400">Usable Hosts</div>
                                  <div className="text-lg sm:text-2xl font-bold text-gray-100">{subnetResults.usableHosts.toLocaleString()}</div>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Total Addresses:</span>
                                  <span className="text-gray-300">{subnetResults.totalHosts.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* VLSM Tool */}
                <ErrorBoundary>
                  {activeTool === 'vlsm' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-4 text-gray-100">VLSM Calculator</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Base Network</label>
                            <ValidatedInput
                              value={vlsmNetwork}
                              onChange={setVlsmNetwork}
                              validate={validateIP}
                              errorMessage="Invalid IP address"
                              placeholder="e.g., 10.0.0.0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Base CIDR</label>
                            <ValidatedInput
                              type="number"
                              value={vlsmCidr}
                              onChange={setVlsmCidr}
                              validate={(value) => validateCIDR(value) && parseInt(value) >= 1 && parseInt(value) <= 30}
                              errorMessage="CIDR must be between 1 and 30"
                              placeholder="e.g., 16"
                              min="1"
                              max="30"
                            />
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-300">Host Requirements</h4>
                          </div>
                          
                          <div className="space-y-3">
                            {vlsmRequirements.map((r) => (
                              <div key={r.id} className="flex gap-3 items-center">
                                <input
                                  value={r.name}
                                  onChange={(e) => updateVlsmRequirement(r.id, 'name', e.target.value)}
                                  className="flex-1 bg-black border border-gray-800 rounded-xl p-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  placeholder="Department name"
                                />
                                <input
                                  type="number"
                                  value={r.hosts}
                                  onChange={(e) => updateVlsmRequirement(r.id, 'hosts', parseInt(e.target.value) || 0)}
                                  className="w-32 bg-black border border-gray-800 rounded-xl p-3 text-center text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  min="1"
                                  placeholder="Hosts required"
                                />
                                <button
                                  onClick={() => removeVlsmRequirement(r.id)}
                                  className="px-4 py-3 rounded-xl bg-gray-800 hover:bg-gray-600 border border-gray-600 text-gray-300 flex items-center gap-2"
                                  disabled={vlsmRequirements.length <= 1}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={addVlsmRequirement}
                              className="flex-1 py-3 rounded-xl bg-gray-800 hover:bg-gray-600 border border-gray-600 text-gray-300 flex items-center justify-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Add Requirement
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculateVLSM}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate VLSM
                        </button>
                      </div>

                      {vlsmResults && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="flex justify-between items-center mb-4">
                              <div>
                                <div className="text-lg font-bold text-gray-100">VLSM Allocation Summary</div>
                                <div className="text-sm text-gray-500">
                                  Base Network: {vlsmResults.baseNetwork}/{vlsmResults.baseCIDR}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className={`text-xl font-bold ${parseFloat(vlsmResults.utilization) > 90 ? 'text-red-400' : 'text-green-400'}`}>
                                  {vlsmResults.utilization}% utilized
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {vlsmResults.allocations.map((r, i) => (
                            <div key={r.id} className="bg-black/50 p-6 rounded-xl border border-gray-800">
                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <div className="text-sm text-gray-500">
                                    {r.name} — {r.hosts.toLocaleString()} required hosts
                                  </div>
                                  <div className="text-xl font-bold text-gray-100">{r.network}/{r.cidr}</div>
                                </div>
                                <div className="flex gap-2">
                                  <CopyButton text={`${r.network}/${r.cidr}`} itemKey={`vlsm-${r.id}`} />
                                </div>
                              </div>
                              <div className="grid md:grid-cols-2 gap-4 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Subnet Mask:</span>
                                  <span className="font-mono text-gray-300">{r.mask}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Usable Range:</span>
                                  <span className="font-mono text-gray-300">{r.firstUsable} - {r.lastUsable}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Usable Hosts:</span>
                                  <span className="text-gray-300">{r.usableHosts.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Binary Converter - FIXED alignment */}
                <ErrorBoundary>
                  {activeTool === 'binary' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Binary Converter</h3>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">Decimal IP Address</label>
                          <ValidatedInput
                            value={decimalIP}
                            onChange={setDecimalIP}
                            validate={validateIP}
                            errorMessage="Invalid IP address"
                            placeholder="Enter IP address"
                          />
                        </div>
                        <div className="flex flex-col justify-end">
                          <button
                            onClick={calculateBinary}
                            className="py-3 px-6 rounded-xl bg-gray-800 hover:bg-gray-600 border border-gray-600 font-semibold text-gray-100"
                          >
                            Convert
                          </button>
                        </div>
                      </div>

                      {binaryResult && (
                        <div className="space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Decimal</div>
                                  <div className="text-xl font-mono font-bold text-gray-100 break-all">{binaryResult.decimal}</div>
                                </div>
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Binary</div>
                                  <div className="text-xl font-mono font-bold text-gray-100 break-all">{binaryResult.binary}</div>
                                </div>
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Hexadecimal</div>
                                  <div className="text-xl font-mono font-bold text-gray-100 break-all">{binaryResult.hex}</div>
                                </div>
                              </div>
                              
                              <div className="pt-4 border-t border-gray-800">
                                <div className="text-sm text-gray-400 mb-2">32-bit Binary Representation</div>
                                <div className="font-mono text-lg text-gray-300 break-all">
                                  {binaryResult.totalBinary}
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-4 gap-4">
                                {binaryResult.octets.map((octet, i) => (
                                  <div key={i} className="bg-black/50 p-4 rounded-xl border border-gray-800">
                                    <div className="text-center">
                                      <div className="text-sm text-gray-500">Octet {i + 1}</div>
                                      <div className="font-mono text-lg text-gray-100">{octet.decimal}</div>
                                      <div className="font-mono text-sm text-gray-400 mt-1">Bin: {octet.binary}</div>
                                      <div className="font-mono text-sm text-gray-400">Hex: {octet.hex}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* MAC Lookup */}
                <ErrorBoundary>
                  {activeTool === 'mac' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">MAC Address Lookup</h3>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">MAC Address</label>
                          <ValidatedInput
                            value={macAddress}
                            onChange={setMacAddress}
                            validate={validateMAC}
                            errorMessage="Invalid MAC address format"
                            placeholder="00:1A:2B:3C:4D:5E"
                          />
                        </div>
                        <div className="flex flex-col gap-3">
                          <button
                            onClick={lookupMac}
                            className="py-3 px-6 rounded-xl bg-gray-800 hover:bg-gray-600 border border-gray-600 font-semibold text-gray-100"
                          >
                            Lookup
                          </button>
                          <button
                            onClick={() => setMacAddress(generateRandomMAC())}
                            className="py-3 px-6 rounded-xl bg-gray-950 hover:bg-gray-800 border border-gray-800 text-gray-300"
                          >
                            Random MAC
                          </button>
                        </div>
                      </div>

                      {macResult && (
                        <div className="space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <div className="text-sm text-gray-400">Formatted MAC</div>
                                <div className="text-2xl font-mono font-bold text-gray-100">{macResult.formattedMAC}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-400">Vendor</div>
                                <div className="text-xl font-bold text-gray-100">{macResult.vendor}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Wildcard Mask */}
                <ErrorBoundary>
                  {activeTool === 'wildcard' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Wildcard Mask Calculator</h3>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">Subnet Mask</label>
                          <ValidatedInput
                            value={wildcardIP}
                            onChange={setWildcardIP}
                            validate={validateIP}
                            errorMessage="Invalid subnet mask"
                            placeholder="255.255.255.0"
                          />
                        </div>
                        <div className="flex flex-col justify-end">
                          <button
                            onClick={calculateWildcard}
                            className="py-3 px-6 rounded-xl bg-gray-800 hover:bg-gray-600 border border-gray-600 font-semibold text-gray-100"
                          >
                            Calculate
                          </button>
                        </div>
                      </div>

                      {wildcardResult && (
                        <div className="space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <div className="text-sm text-gray-400">Subnet Mask</div>
                                <div className="text-xl font-bold text-gray-100">{wildcardResult.subnet}</div>
                              </div>
                                <div>
                                  <div className="text-sm text-gray-400">Wildcard Mask</div>
                                  <div className="text-xl font-bold text-gray-100">{wildcardResult.wildcard}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">CIDR</div>
                                  <div className="text-xl font-bold text-gray-100">/{wildcardResult.cidr}</div>
                                </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Route Summarization */}
                <ErrorBoundary>
                  {activeTool === 'summarize' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Route Summarization</h3>

                      <div className="space-y-4 mb-6">
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-300">IP Ranges to Summarize</h4>
                          <div className="space-y-2">
                            {summarizeIPs.map((ip, index) => (
                              <div key={index} className="flex gap-2 items-center">
                                <input
                                  value={ip}
                                  onChange={(e) => updateSummarizeIP(index, e.target.value)}
                                  className="flex-1 bg-black border border-gray-800 rounded-xl p-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  placeholder="192.168.1.0/24"
                                />
                                <button
                                  onClick={() => removeSummarizeIP(index)}
                                  className="px-4 py-3 rounded-xl bg-gray-800 hover:bg-gray-600 border border-gray-600 text-gray-300"
                                  disabled={summarizeIPs.length <= 1}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                          <button
                            onClick={addSummarizeIP}
                            className="w-full py-3 rounded-xl bg-gray-950 hover:bg-gray-800 border border-gray-800 text-gray-300"
                          >
                            + Add IP Range
                          </button>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculateSummarization}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate Summary
                        </button>
                      </div>

                      {summarizeResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-4">
                              <div>
                                <div className="text-sm text-gray-400">Summary Route</div>
                                <div className="text-2xl font-bold text-gray-100">{summarizeResult.summary}</div>
                              </div>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <div className="text-sm text-gray-400">Subnet Mask</div>
                                  <div className="text-xl font-bold text-gray-100">{summarizeResult.mask}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Network Address</div>
                                  <div className="text-xl font-bold text-gray-100">{summarizeResult.network}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Total Addresses</div>
                                  <div className="text-xl font-bold text-gray-100">{summarizeResult.totalAddresses.toLocaleString()}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Efficiency</div>
                                  <div className="text-xl font-bold text-gray-100">{summarizeResult.efficiency}%</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Bandwidth Calculator */}
                <ErrorBoundary>
                  {activeTool === 'bandwidth' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Bandwidth Calculator</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">File Size</label>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={fileSize}
                                onChange={(e) => setFileSize(e.target.value)}
                                className="flex-1 bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                placeholder="100"
                              />
                              <select
                                value={sizeUnit}
                                onChange={(e) => setSizeUnit(e.target.value)}
                                className="bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="B">Bytes</option>
                                <option value="KB">KB</option>
                                <option value="MB">MB</option>
                                <option value="GB">GB</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Bandwidth</label>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={bandwidth}
                                onChange={(e) => setBandwidth(e.target.value)}
                                className="flex-1 bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                placeholder="10"
                              />
                              <select
                                value={bandwidthUnit}
                                onChange={(e) => setBandwidthUnit(e.target.value)}
                                className="bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="bps">bps</option>
                                <option value="Kbps">Kbps</option>
                                <option value="Mbps">Mbps</option>
                                <option value="Gbps">Gbps</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculateBandwidth}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate Transfer Time
                        </button>
                      </div>

                      {bandwidthResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-4">
                              <div>
                                <div className="text-sm text-gray-400">Transfer Time</div>
                                <div className="text-2xl font-bold text-gray-100">{bandwidthResult.time}</div>
                              </div>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <div className="text-sm text-gray-400">Effective Speed</div>
                                  <div className="text-xl font-bold text-gray-100">{bandwidthResult.mbps} Mbps</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Packets</div>
                                  <div className="text-xl font-bold text-gray-100">{bandwidthResult.packets}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Overhead</div>
                                  <div className="text-xl font-bold text-gray-100">{bandwidthResult.overhead}%</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Protocol Decoder */}
                <ErrorBoundary>
                  {activeTool === 'protocol' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Protocol Decoder</h3>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">Protocol Number</label>
                          <input
                            value={protocolCode}
                            onChange={(e) => setProtocolCode(e.target.value)}
                            className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            placeholder="6"
                          />
                        </div>
                        <div className="flex flex-col justify-end">
                          <button
                            onClick={decodeProtocol}
                            className="py-3 px-6 rounded-xl bg-gray-800 hover:bg-gray-600 border border-gray-600 font-semibold text-gray-100"
                          >
                            Decode Protocol
                          </button>
                        </div>
                      </div>

                      {protocolResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-4">
                              <div>
                                <div className="text-sm text-gray-400">Protocol</div>
                                <div className="text-2xl font-bold text-gray-100">{protocolResult.name}</div>
                                <div className="text-gray-500 mt-1">{protocolResult.description}</div>
                              </div>
                              <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                  <div className="text-sm text-gray-400">Decimal</div>
                                  <div className="text-xl font-bold text-gray-100 break-all">{protocolResult.number}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Hexadecimal</div>
                                  <div className="text-xl font-mono font-bold text-gray-100 break-all">{protocolResult.hex}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Binary</div>
                                  <div className="text-xl font-mono font-bold text-gray-100 break-all">{protocolResult.binary}</div>
                                </div>
                              </div>
                              {protocolResult.details && (
                                <div className="mt-4 pt-4 border-t border-gray-800">
                                  <div className="text-sm text-gray-400">Protocol Details</div>
                                  <div className="flex gap-4 mt-2">
                                    <div className="px-3 py-1 bg-gray-950 rounded-lg text-sm">
                                      {protocolResult.details.portBased ? 'Port-based' : 'Not port-based'}
                                    </div>
                                    <div className="px-3 py-1 bg-gray-950 rounded-lg text-sm">
                                      {protocolResult.details.connectionOriented ? 'Connection-oriented' : 'Connectionless'}
                                    </div>
                                    <div className="px-3 py-1 bg-gray-950 rounded-lg text-sm">
                                      {protocolResult.details.reliable ? 'Reliable' : 'Unreliable'}
                                    </div>
                                  </div>

                                  {/* Authoritative source */}
                                  <div className="mt-4">
                                    <div className="text-sm text-gray-400">Authoritative Source</div>
                                    <div className="flex items-center gap-3 mt-2">
                                      <div className="px-3 py-1 bg-gray-950 rounded-lg text-sm">
                                        {protocolResult.source || 'Unknown'}
                                      </div>
                                      {protocolResult.ianaUrl && (
                                        <a
                                          href={protocolResult.ianaUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm text-gray-300 hover:underline"
                                        >
                                          View IANA registry
                                        </a>
                                      )}
                                    </div>

                                    {protocolResult.registryRaw && (
                                      <div className="mt-2 text-sm text-gray-500">Registry entry: {protocolResult.registryRaw}</div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* ACL Generator */}
                <ErrorBoundary>
                  {activeTool === 'acl' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">ACL Generator</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Source Network/IP</label>
                            <ValidatedInput
                              value={aclSource}
                              onChange={setAclSource}
                              validate={(value) => {
                                if (value === 'any') return true;
                                const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
                                if (!ipRegex.test(value)) return false;
                                const parts = value.split('/')[0].split('.');
                                return parts.every(part => {
                                  const num = parseInt(part);
                                  return num >= 0 && num <= 255;
                                });
                              }}
                              errorMessage="Use format: 192.168.1.0/24 or 'any'"
                              placeholder="192.168.1.0/24"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Destination Network/IP</label>
                            <ValidatedInput
                              value={aclDest}
                              onChange={setAclDest}
                              validate={(value) => {
                                if (value === 'any') return true;
                                const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
                                if (!ipRegex.test(value)) return false;
                                const parts = value.split('/')[0].split('.');
                                return parts.every(part => {
                                  const num = parseInt(part);
                                  return num >= 0 && num <= 255;
                                });
                              }}
                              errorMessage="Use format: 10.0.0.0/8 or 'any'"
                              placeholder="any"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Protocol</label>
                            <select
                              value={aclProtocol}
                              onChange={(e) => setAclProtocol(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="ip">IP (Any Protocol)</option>
                              <option value="tcp">TCP</option>
                              <option value="udp">UDP</option>
                              <option value="icmp">ICMP</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Action</label>
                            <select
                              value={aclAction}
                              onChange={(e) => setAclAction(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="permit">Permit</option>
                              <option value="deny">Deny</option>
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-semibold mb-2 text-gray-300">
                              Port/Service {aclProtocol === 'ip' ? '(Not applicable for IP protocol)' : ''}
                            </label>
                            <select
                              value={aclPort}
                              onChange={(e) => setAclPort(e.target.value)}
                              disabled={aclProtocol === 'ip'}
                              className={`w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600 ${aclProtocol === 'ip' ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <option value="any">Any</option>
                              <optgroup label="Common TCP/UDP Ports">
                                <option value="22">SSH (22)</option>
                                <option value="23">Telnet (23)</option>
                                <option value="25">SMTP (25)</option>
                                <option value="53">DNS (53)</option>
                                <option value="80">HTTP (80)</option>
                                <option value="443">HTTPS (443)</option>
                                <option value="3389">RDP (3389)</option>
                              </optgroup>
                              <optgroup label="Custom Port">
                                <option value="custom">Custom Port...</option>
                              </optgroup>
                            </select>
                            {aclPort === 'custom' && (
                              <input
                                type="number"
                                min="1"
                                max="65535"
                                placeholder="Enter port number"
                                onChange={(e) => setAclPort(e.target.value)}
                                className="w-full mt-2 bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              />
                            )}
                          </div>
                        </div>

                        <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
                          <div className="text-sm text-yellow-400 font-semibold mb-2">ℹ️ ACL Types</div>
                          <div className="text-sm text-yellow-300 space-y-1">
                            <div><strong>Standard ACL:</strong> Filters only by source IP address (use IP protocol)</div>
                            <div><strong>Extended ACL:</strong> Filters by source, destination, protocol, and ports</div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={generateACL}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Generate ACL
                        </button>
                      </div>

                      {aclResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">ACL Type</div>
                                  <div className="text-xl font-bold text-gray-100">{aclResult.aclType}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">ACL Number</div>
                                  <div className="text-xl font-bold text-gray-100">{aclResult.aclNumber}</div>
                                </div>
                              </div>

                              <div>
                                <div className="text-sm text-gray-400">ACL Explanation</div>
                                <div className="text-lg text-gray-300">{aclResult.explanation}</div>
                              </div>

                              {aclResult.aclType === 'Standard' && (
                                <div>
                                  <div className="text-sm text-gray-400 mb-2">Cisco Standard ACL (Legacy)</div>
                                  <div className="font-mono bg-black p-4 rounded-lg border border-gray-800 text-sm whitespace-pre overflow-x-auto">
                                    {aclResult.ciscoStandard}
                                  </div>
                                  <CopyButton text={aclResult.ciscoStandard} itemKey="acl-cisco-standard" className="mt-2" />
                                </div>
                              )}

                              <div>
                                <div className="text-sm text-gray-400 mb-2">Cisco Extended ACL (Named)</div>
                                <div className="font-mono bg-black p-4 rounded-lg border border-gray-800 text-sm whitespace-pre overflow-x-auto">
                                  {aclResult.ciscoExtended}
                                </div>
                                <CopyButton text={aclResult.ciscoExtended} itemKey="acl-cisco-extended" className="mt-2" />
                              </div>

                              <div>
                                <div className="text-sm text-gray-400 mb-2">Juniper SRX ACL</div>
                                <div className="font-mono bg-black p-4 rounded-lg border border-gray-800 text-sm whitespace-pre overflow-x-auto">
                                  {aclResult.juniper}
                                </div>
                                <CopyButton text={aclResult.juniper} itemKey="acl-juniper" className="mt-2" />
                              </div>

                              <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                                <div className="text-sm text-blue-400 font-semibold mb-2">💡 Usage Notes</div>
                                <ul className="text-sm text-blue-300 space-y-1">
                                  <li>• Apply ACL to interface: <code className="bg-blue-900/50 px-1 rounded">ip access-group ACL-{aclResult.aclNumber} in/out</code></li>
                                  <li>• For Juniper: Apply filter to interface with <code className="bg-blue-900/50 px-1 rounded">set interfaces ge-0/0/0 unit 0 family inet filter input ACL-{aclResult.aclNumber}</code></li>
                                  <li>• Standard ACLs only filter by source IP, Extended ACLs filter by source, destination, protocol, and ports</li>
                                  <li>• Always add an implicit deny rule at the end (automatically included)</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Hash Generator */}
                <ErrorBoundary>
                  {activeTool === 'hash' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Hash Generator</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Text to Hash</label>
                            <input
                              value={hashText}
                              onChange={(e) => setHashText(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="Enter text"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Hash Algorithm</label>
                            <select
                              value={hashType}
                              onChange={(e) => setHashType(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="md5">MD5 (Deprecated)</option>
                              <option value="sha1">SHA-1 (Deprecated)</option>
                              <option value="sha256">SHA-256</option>
                              <option value="sha384">SHA-384</option>
                              <option value="sha512">SHA-512</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={generateHash}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Generate Hash
                        </button>
                      </div>

                      {hashResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-4">
                              <div>
                                <div className="text-sm text-gray-400">Hash Type</div>
                                <div className="text-2xl font-bold text-gray-100">{hashResult.type}</div>
                                <div className="text-gray-500 mt-1">{hashResult.algorithm}</div>
                              </div>
                              
                              <div>
                                <div className="text-sm text-gray-400">Generated Hash</div>
                                <div className="font-mono text-xl text-gray-300 break-all">{hashResult.hash}</div>
                                <CopyButton text={hashResult.hash} itemKey="hash-result" className="mt-2" />
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <div className="text-sm text-gray-400">Input Text</div>
                                  <div className="text-gray-300">{hashResult.input}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Strength</div>
                                  <div className={`font-bold ${
                                    hashResult.strength === 'Strong' ? 'text-green-400' : 
                                    hashResult.strength === 'Medium' ? 'text-yellow-400' : 'text-red-400'
                                  }`}>
                                    {hashResult.strength}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Hash Length</div>
                                  <div className="text-gray-300">{hashResult.length} bits</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Entropy</div>
                                  <div className="text-gray-300">{hashResult.entropy}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Password Tools */}
                <ErrorBoundary>
                  {activeTool === 'password' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Password Tools</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Password Type</label>
                            <select
                              value={passwordType}
                              onChange={(e) => setPasswordType(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="type7">Cisco Type 7 Encryption</option>
                              <option value="generator">Password Generator</option>
                              <option value="analyzer">Password Strength Analyzer</option>
                              <option value="cracker">Password Cracker Estimator</option>
                              <option value="policy">Password Policy Generator</option>
                            </select>
                          </div>
                          
                          {passwordType === 'type7' ? (
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Password to Encrypt</label>
                              <input
                                value={passwordText}
                                onChange={(e) => setPasswordText(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                placeholder="Enter password"
                              />
                            </div>
                          ) : passwordType === 'analyzer' ? (
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Password to Analyze</label>
                              <input
                                value={passwordText}
                                onChange={(e) => setPasswordText(e.target.value)}
                                type="password"
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                placeholder="Enter password to analyze"
                              />
                            </div>
                          ) : passwordType === 'cracker' ? (
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Password to Test</label>
                              <input
                                value={passwordText}
                                onChange={(e) => setPasswordText(e.target.value)}
                                type="password"
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                placeholder="Enter password to estimate cracking time"
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={processPassword}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          {passwordType === 'type7' ? 'Encrypt Password' : 
                           passwordType === 'generator' ? 'Generate Password' :
                           passwordType === 'analyzer' ? 'Analyze Password' :
                           passwordType === 'cracker' ? 'Estimate Cracking Time' :
                           'Generate Policy'}
                        </button>
                      </div>

                      {passwordResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              {passwordResult.type === 'Cisco Type 7' ? (
                                <>
                                  <div>
                                    <div className="text-sm text-gray-400">Original Password</div>
                                    <div className="text-xl font-bold text-gray-100">{passwordResult.original}</div>
                                  </div>
                                  
                                  <div>
                                    <div className="text-sm text-gray-400">Encrypted Password (Type 7)</div>
                                    <div className="font-mono text-xl text-gray-300">{passwordResult.encrypted}</div>
                                    <CopyButton text={passwordResult.encrypted} itemKey="password-encrypted" className="mt-2" />
                                  </div>
                                  
                                  <div className="p-4 bg-yellow-900/20 border border-yellow-700 rounded-xl">
                                    <div className="text-yellow-400 text-sm">{passwordResult.note}</div>
                                  </div>
                                </>
                              ) : passwordResult.type === 'Password Analysis' ? (
                                <>
                                  <div>
                                    <div className="text-sm text-gray-400">Password</div>
                                    <div className="font-mono text-xl text-gray-300">{passwordResult.password}</div>
                                  </div>
                                  
                                  <div className="grid md:grid-cols-3 gap-4">
                                    <div>
                                      <div className="text-sm text-gray-400">Strength</div>
                                      <div className={`text-xl font-bold ${passwordResult.color}`}>
                                        {passwordResult.strength}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-gray-400">Length</div>
                                      <div className="text-xl font-bold text-gray-100">{passwordResult.analysis.length} chars</div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-gray-400">Entropy</div>
                                      <div className="text-xl font-bold text-gray-100">{passwordResult.analysis.entropy} bits</div>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <div className="text-sm text-gray-400">Password Contains</div>
                                    <div className="flex flex-wrap gap-2">
                                      {passwordResult.analysis.hasLower && (
                                        <span className="px-3 py-1 bg-green-900/50 border border-green-700 rounded-lg text-sm text-green-300">Lowercase</span>
                                      )}
                                      {passwordResult.analysis.hasUpper && (
                                        <span className="px-3 py-1 bg-green-900/50 border border-green-700 rounded-lg text-sm text-green-300">Uppercase</span>
                                      )}
                                      {passwordResult.analysis.hasNumbers && (
                                        <span className="px-3 py-1 bg-green-900/50 border border-green-700 rounded-lg text-sm text-green-300">Numbers</span>
                                      )}
                                      {passwordResult.analysis.hasSymbols && (
                                        <span className="px-3 py-1 bg-green-900/50 border border-green-700 rounded-lg text-sm text-green-300">Symbols</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {passwordResult.suggestions.length > 0 && (
                                    <div className="p-4 bg-blue-900/20 border border-blue-700 rounded-xl">
                                      <div className="text-sm text-blue-400 mb-2">Suggestions for improvement:</div>
                                      <ul className="text-sm text-blue-300 space-y-1">
                                        {passwordResult.suggestions.map((suggestion, idx) => (
                                          <li key={idx}>• {suggestion}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </>
                              ) : passwordResult.type === 'Cracking Time Estimate' ? (
                                <>
                                  <div>
                                    <div className="text-sm text-gray-400">Password</div>
                                    <div className="font-mono text-xl text-gray-300">{passwordResult.password}</div>
                                  </div>
                                  
                                  <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                      <div className="text-sm text-gray-400">Password Strength</div>
                                      <div className={`text-xl font-bold ${passwordResult.estimate.strength === 'Very Strong' ? 'text-green-400' :
                                        passwordResult.estimate.strength === 'Strong' ? 'text-green-300' :
                                        passwordResult.estimate.strength === 'Medium' ? 'text-yellow-400' : 'text-red-400'}`}>
                                        {passwordResult.estimate.strength}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-gray-400">Entropy</div>
                                      <div className="text-xl font-bold text-gray-100">{passwordResult.estimate.entropy} bits</div>
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <div className="text-sm text-gray-400 mb-3">Estimated Cracking Times</div>
                                    <div className="space-y-2">
                                      {Object.entries(passwordResult.estimate.times).map(([method, time]) => (
                                        <div key={method} className="flex justify-between items-center p-3 bg-black/60 rounded-lg">
                                          <span className="text-gray-300 text-sm">{method}:</span>
                                          <span className="font-mono text-gray-100">{time}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  <div className="p-4 bg-yellow-900/20 border border-yellow-700 rounded-xl">
                                    <div className="text-yellow-400 text-sm">{passwordResult.note}</div>
                                  </div>
                                </>
                              ) : passwordResult.type === 'Password Policy' ? (
                                <>
                                  <div>
                                    <div className="text-sm text-gray-400">Policy Name</div>
                                    <div className="text-xl font-bold text-gray-100">{passwordResult.policy.name}</div>
                                    <div className="text-sm text-gray-500 mt-1">{passwordResult.description}</div>
                                  </div>
                                  
                                  <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                      <div className="text-sm text-gray-400 mb-2">Requirements</div>
                                      <div className="space-y-2">
                                        <div className="flex justify-between">
                                          <span className="text-gray-400">Minimum Length:</span>
                                          <span className="text-gray-100">{passwordResult.policy.minLength} characters</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-400">Require Uppercase:</span>
                                          <span className="text-gray-100">{passwordResult.policy.requireUpper ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-400">Require Lowercase:</span>
                                          <span className="text-gray-100">{passwordResult.policy.requireLower ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-400">Require Numbers:</span>
                                          <span className="text-gray-100">{passwordResult.policy.requireNumbers ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-400">Require Symbols:</span>
                                          <span className="text-gray-100">{passwordResult.policy.requireSymbols ? 'Yes' : 'No'}</span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <div className="text-sm text-gray-400 mb-2">Expiration & History</div>
                                      <div className="space-y-2">
                                        <div className="flex justify-between">
                                          <span className="text-gray-400">Max Age:</span>
                                          <span className="text-gray-100">{passwordResult.policy.maxAge} days</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-400">Password History:</span>
                                          <span className="text-gray-100">{passwordResult.policy.history} passwords</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <div className="text-sm text-gray-400">Generated Password</div>
                                    <div className="font-mono text-2xl font-bold text-gray-100">{passwordResult.password}</div>
                                    <CopyButton text={passwordResult.password} itemKey="password-generated" className="mt-2" />
                                  </div>
                                  
                                  <div className="grid md:grid-cols-3 gap-4">
                                    <div>
                                      <div className="text-sm text-gray-400">Strength</div>
                                      <div className={`text-xl font-bold ${
                                        passwordResult.strength === 'Very Strong' ? 'text-green-400' :
                                        passwordResult.strength === 'Strong' ? 'text-green-300' :
                                        passwordResult.strength === 'Medium' ? 'text-yellow-400' : 'text-red-400'
                                      }`}>
                                        {passwordResult.strength}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-gray-400">Length</div>
                                      <div className="text-xl font-bold text-gray-100">{passwordResult.length} chars</div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-gray-400">Score</div>
                                      <div className="text-xl font-bold text-gray-100">{passwordResult.score}/6</div>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <div className="text-sm text-gray-400">Password Contains</div>
                                    <div className="flex flex-wrap gap-2">
                                      {passwordResult.hasLower && (
                                        <span className="px-3 py-1 bg-gray-950 rounded-lg text-sm">Lowercase</span>
                                      )}
                                      {passwordResult.hasUpper && (
                                        <span className="px-3 py-1 bg-gray-950 rounded-lg text-sm">Uppercase</span>
                                      )}
                                      {passwordResult.hasNumbers && (
                                        <span className="px-3 py-1 bg-gray-950 rounded-lg text-sm">Numbers</span>
                                      )}
                                      {passwordResult.hasSymbols && (
                                        <span className="px-3 py-1 bg-gray-950 rounded-lg text-sm">Symbols</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <div className="text-sm text-gray-400 mb-2">Alternative Passwords</div>
                                    <div className="space-y-2">
                                      <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Medium strength:</span>
                                        <span className="font-mono text-gray-300">{passwordResult.alternatives.medium}</span>
                                        <CopyButton text={passwordResult.alternatives.medium} itemKey="password-medium" />
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Weak strength:</span>
                                        <span className="font-mono text-gray-300">{passwordResult.alternatives.weak}</span>
                                        <CopyButton text={passwordResult.alternatives.weak} itemKey="password-weak" />
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Cabling Calculator */}
                <ErrorBoundary>
                  {activeTool === 'cabling' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Cabling Calculator</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Cable Type</label>
                            <select
                              value={cableType}
                              onChange={(e) => setCableType(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="cat5">CAT5</option>
                              <option value="cat5e">CAT5e</option>
                              <option value="cat6">CAT6</option>
                              <option value="cat6a">CAT6a</option>
                              <option value="cat7">CAT7</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Cable Length (meters)</label>
                            <input
                              type="number"
                              value={cableLength}
                              onChange={(e) => setCableLength(parseInt(e.target.value) || 0)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="100"
                              min="1"
                              max="200"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculateCabling}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate
                        </button>
                      </div>

                      {cablingResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Cable Type</div>
                                  <div className="text-2xl font-bold text-gray-100">{cablingResult.type}</div>
                                  <div className="text-gray-500 mt-1">Grade: {cablingResult.grade} • Cost: {cablingResult.cost}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Maximum Speed</div>
                                  <div className="text-2xl font-bold text-gray-100">{cablingResult.maxSpeed}</div>
                                  <div className="text-gray-500 mt-1">Frequency: {cablingResult.frequency}</div>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Your Cable Length</div>
                                  <div className={`text-2xl font-bold ${
                                    cablingResult.status.includes('✅') ? 'text-green-400' : 'text-red-400'
                                  }`}>
                                    {cablingResult.yourLength}
                                  </div>
                                  <div className="text-gray-500 mt-1">{cablingResult.performance}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Maximum Distance</div>
                                  <div className="text-2xl font-bold text-gray-100">{cablingResult.maxDistance}</div>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Attenuation</div>
                                  <div className="text-xl font-bold text-gray-100">{cablingResult.attenuation}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Crosstalk</div>
                                  <div className="text-xl font-bold text-gray-100">{cablingResult.crosstalk}</div>
                                </div>
                              </div>
                              
                              <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                <div className="text-sm text-gray-400 mb-1">Recommendation</div>
                                <div className="text-gray-300">{cablingResult.recommendation}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Port Reference */}
                <ErrorBoundary>
                  {activeTool === 'ports' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Port Reference</h3>

                      <div className="mb-6">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            value={portSearch}
                            onChange={(e) => setPortSearch(e.target.value)}
                            placeholder="Search ports by number, protocol, or description..."
                            className="w-full bg-black border border-gray-800 rounded-xl pl-10 pr-4 py-3 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                          />
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-800">
                              <th className="text-left py-3 px-4 text-gray-400 font-semibold">Port</th>
                              <th className="text-left py-3 px-4 text-gray-400 font-semibold">Protocol</th>
                              <th className="text-left py-3 px-4 text-gray-400 font-semibold">Description</th>
                              <th className="text-left py-3 px-4 text-gray-400 font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredPorts.map((port, index) => (
                              <tr key={index} className="border-b border-gray-800 hover:bg-black/60">
                                <td className="py-3 px-4">
                                  <div className="font-mono text-gray-100">{port.port}</div>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="px-2 py-1 bg-gray-950 rounded text-xs">{port.protocol}</span>
                                </td>
                                <td className="py-3 px-4 text-gray-300">{port.description}</td>
                                <td className="py-3 px-4">
                                  <CopyButton text={port.port.toString()} itemKey={`port-${port.port}`} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-4 text-sm text-gray-500">
                        Showing {filteredPorts.length} of {commonPorts.length} ports
                      </div>
                    </div>
                  )}
                </ErrorBoundary>

                {/* Log Parser */}
                <ErrorBoundary>
                  {activeTool === 'logs' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Log Parser</h3>

                      <div className="space-y-6 mb-6">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">Log Entries</label>
                          <textarea
                            value={logText}
                            onChange={(e) => setLogText(e.target.value)}
                            rows="8"
                            className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            placeholder="Paste multiple log entries here (one per line)..."
                          />
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Log Format</label>
                            <select
                              value={logFormat}
                              onChange={(e) => setLogFormat(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="auto">Auto Detect</option>
                              <option value="cisco">Cisco IOS</option>
                              <option value="syslog">Syslog RFC 3164</option>
                              <option value="syslog5424">Syslog RFC 5424</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Analysis Level</label>
                            <select
                              value={logAnalysisLevel}
                              onChange={(e) => setLogAnalysisLevel(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="basic">Basic Analysis</option>
                              <option value="detailed">Detailed Analysis</option>
                              <option value="comprehensive">Comprehensive Analysis</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={parseLog}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Parse Logs
                        </button>
                        <button
                          onClick={() => setLogText('')}
                          className="px-6 py-3 bg-gray-950 hover:bg-gray-800 rounded-xl font-semibold border border-gray-800 text-gray-300"
                        >
                          Clear
                        </button>
                      </div>

                      {logResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-semibold text-gray-100">Analysis Summary</h4>
                              <div className="text-sm text-gray-400">
                                {logResult.totalLines} lines processed • {logResult.parsedLines} successfully parsed
                              </div>
                            </div>
                            
                            <div className="grid md:grid-cols-4 gap-4 mb-6">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-red-400">{logResult.severityCounts.ERROR || 0}</div>
                                <div className="text-xs text-gray-400">Errors</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-yellow-400">{logResult.severityCounts.WARNING || 0}</div>
                                <div className="text-xs text-gray-400">Warnings</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-blue-400">{logResult.severityCounts.INFO || 0}</div>
                                <div className="text-xs text-gray-400">Info</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-gray-400">{logResult.uniquePatterns}</div>
                                <div className="text-xs text-gray-400">Patterns</div>
                              </div>
                            </div>

                            {logResult.patternSummary && logResult.patternSummary.length > 0 && (
                              <div className="mb-6">
                                <h5 className="text-md font-semibold text-gray-300 mb-3">Detected Patterns</h5>
                                <div className="space-y-2">
                                  {logResult.patternSummary.map((pattern, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-black/60 rounded-lg">
                                      <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${
                                          pattern.type.includes('ERROR') || pattern.type.includes('CRITICAL') ? 'bg-red-500' :
                                          pattern.type.includes('WARNING') ? 'bg-yellow-500' :
                                          pattern.type.includes('AUTH') ? 'bg-orange-500' :
                                          pattern.type.includes('INTERFACE') ? 'bg-blue-500' :
                                          'bg-gray-500'
                                        }`}></div>
                                        <span className="text-sm font-medium text-gray-300">{pattern.type}</span>
                                      </div>
                                      <span className="text-sm text-gray-400">{pattern.count} occurrences</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-gray-100">Parsed Log Entries</h4>
                            {logResult.entries.map((entry, index) => (
                              <div key={index} className="bg-black/50 p-4 rounded-xl border border-gray-800">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                      entry.severity === 'ERROR' || entry.severity === 'CRITICAL' ? 'bg-red-900/30 text-red-400' :
                                      entry.severity === 'WARNING' ? 'bg-yellow-900/30 text-yellow-400' :
                                      entry.severity === 'INFO' || entry.severity === 'INFORMATIONAL' ? 'bg-blue-900/30 text-blue-400' :
                                      'bg-gray-800 text-gray-300'
                                    }`}>
                                      {entry.severity}
                                    </span>
                                    <span className="text-sm text-gray-400">{entry.component}</span>
                                    {entry.timestamp && (
                                      <span className="text-xs text-gray-500">{entry.timestamp}</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">Line {entry.lineNumber}</div>
                                </div>
                                
                                <div className="font-mono text-sm text-gray-300 bg-black p-3 rounded-lg mb-3">
                                  {entry.original}
                                </div>
                                
                                <div className="grid md:grid-cols-2 gap-4">
                                  <div>
                                    <div className="text-sm text-gray-400">Parsed Message</div>
                                    <div className="text-gray-300">{entry.message}</div>
                                  </div>
                                  
                                  {entry.pattern && (
                                    <div>
                                      <div className="text-sm text-gray-400">Pattern Detected</div>
                                      <div className="text-gray-300">{entry.pattern.type}</div>
                                      <div className="text-xs text-gray-500 mt-1">{entry.pattern.description}</div>
                                    </div>
                                  )}
                                </div>
                                
                                {entry.details && entry.details.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-800">
                                    <div className="text-sm text-gray-400 mb-2">Additional Details</div>
                                    <div className="space-y-1">
                                      {entry.details.map((detail, detailIndex) => (
                                        <div key={detailIndex} className="text-xs text-gray-500">
                                          <span className="font-medium">{detail.key}:</span> {detail.value}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* QoS Calculator */}
                <ErrorBoundary>
                  {activeTool === 'qos' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">QoS Calculator</h3>

                      <div className="space-y-6 mb-6">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">Total Bandwidth (Mbps)</label>
                          <input
                            type="number"
                            value={qosBandwidth}
                            onChange={(e) => setQosBandwidth(parseInt(e.target.value) || 0)}
                            className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            placeholder="100"
                            min="1"
                          />
                        </div>
                        
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-300">QoS Policies</h4>
                          </div>
                          
                          <div className="space-y-3">
                            {qosPolicies.map((p) => (
                              <div key={p.id} className="flex gap-3 items-center">
                                <input
                                  value={p.class}
                                  onChange={(e) => updateQosPolicy(p.id, 'class', e.target.value)}
                                  className="flex-1 bg-black border border-gray-800 rounded-xl p-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  placeholder="Traffic class"
                                />
                                <div className="relative w-32">
                                  <input
                                    type="number"
                                    value={p.percent}
                                    onChange={(e) => updateQosPolicy(p.id, 'percent', parseInt(e.target.value) || 0)}
                                    className="w-full bg-black border border-gray-800 rounded-xl p-3 pr-8 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    min="0"
                                    max="100"
                                    placeholder="%"
                                  />
                                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                                </div>
                                <button
                                  onClick={() => removeQosPolicy(p.id)}
                                  className="px-4 py-3 rounded-xl bg-gray-800 hover:bg-gray-600 border border-gray-600 text-gray-300"
                                  disabled={qosPolicies.length <= 1}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>

                          <button
                            onClick={addQosPolicy}
                            className="w-full py-3 rounded-xl bg-gray-950 hover:bg-gray-800 border border-gray-800 text-gray-300"
                          >
                            + Add Policy
                          </button>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculateQoS}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate QoS
                        </button>
                      </div>

                      {qosResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div>
                                <div className="text-sm text-gray-400">Total Bandwidth</div>
                                <div className="text-2xl font-bold text-gray-100">{qosResult.totalBandwidth}</div>
                              </div>
                              
                              <div className="space-y-4">
                                <div className="text-sm text-gray-400">Bandwidth Allocation</div>
                                {qosResult.allocations.map((a) => (
                                  <div key={a.id} className="space-y-2">
                                    <div className="flex justify-between items-center">
                                      <div>
                                        <span className="font-semibold text-gray-300">{a.class}</span>
                                        <span className="text-gray-500 ml-2">({a.priority})</span>
                                      </div>
                                      <div className="text-right">
                                        <div className="font-bold text-gray-100">{a.bandwidth} Mbps</div>
                                        <div className="text-sm text-gray-500">{a.percent}%</div>
                                      </div>
                                    </div>
                                    <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                                      <div
                                        className="bg-gray-600 h-full rounded-full"
                                        style={{ width: `${a.percent}%` }}
                                      ></div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                      <div>
                                        <span className="text-gray-500">DSCP:</span>
                                        <span className="text-gray-300 ml-2">{a.dscp}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Queue:</span>
                                        <span className="text-gray-300 ml-2">{a.queue}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                <div className="text-sm text-gray-400 mb-1">Summary</div>
                                <div className="text-gray-300">
                                  Total allocated: {qosPolicies.reduce((sum, p) => sum + p.percent, 0)}% • Remaining: {qosResult.remaining} Mbps
                                </div>
                                <div className="text-sm text-gray-500 mt-2">{qosResult.recommendation}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* IPv6 Calculator - FIXED */}
                <ErrorBoundary>
                  {activeTool === 'ipv6' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">IPv6 Calculator</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">IPv6 Address</label>
                            <ValidatedInput
                              value={ipv6Address}
                              onChange={setIpv6Address}
                              validate={validateIPv6}
                              errorMessage="Invalid IPv6 address (e.g., 2001:db8::1 or fe80::1)"
                              placeholder="2001:db8::1"
                            />
                            <div className="text-xs text-gray-500 mt-1">
                              Examples: 2001:db8::1, fe80::1, ::1 (loopback)
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Prefix Length</label>
                            <input
                              type="number"
                              value={ipv6Prefix}
                              onChange={(e) => setIpv6Prefix(e.target.value)}
                              min="0"
                              max="128"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="64"
                            />
                            <div className="text-xs text-gray-500 mt-1">
                              Common prefixes: /64 (standard), /48 (site), /32 (ISP)
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculateIPv6}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate
                        </button>
                      </div>

                      {ipv6Result && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">IPv6 Address</div>
                                  <div className="text-xl font-mono font-bold text-gray-100">{ipv6Result.address}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Compressed Form</div>
                                  <div className="text-xl font-mono font-bold text-gray-100">{ipv6Result.compressed}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Expanded Form</div>
                                  <div className="text-sm font-mono text-gray-300">{ipv6Result.expanded}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Address Type</div>
                                  <div className="text-xl font-bold text-gray-100">{ipv6Result.addressType}</div>
                                  <div className="text-sm text-gray-500">{ipv6Result.scope}</div>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Prefix Length</div>
                                  <div className="text-2xl font-bold text-gray-100">/{ipv6Result.prefix}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Network</div>
                                  <div className="text-xl font-mono text-gray-100">{ipv6Result.network}</div>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Subnet Size</div>
                                  <div className="text-2xl font-bold text-gray-100">{ipv6Result.subnetSize}</div>
                                  <div className="text-sm text-gray-500">total addresses</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Usable Hosts</div>
                                  <div className="text-2xl font-bold text-gray-100">{ipv6Result.hostCount}</div>
                                  <div className="text-sm text-gray-500">available hosts</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Network Bits</div>
                                  <div className="text-xl font-bold text-gray-100">{ipv6Result.bitsForNetwork}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Host Bits</div>
                                  <div className="text-xl font-bold text-gray-100">{ipv6Result.bitsForHosts}</div>
                                </div>
                              </div>
                              
                              <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                <div className="text-sm text-gray-400 mb-1">Note</div>
                                <div className="text-gray-300">{ipv6Result.note}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* VPN Config Generator */}
                <ErrorBoundary>
                  {activeTool === 'vpn' && (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-100">Advanced VPN Configuration Generator</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setVpnType('ipsec-site-to-site');
                              setVpnPeerIP('203.0.113.1');
                              setVpnLocalNetwork('192.168.1.0/24');
                              setVpnRemoteNetwork('10.0.0.0/24');
                              setVpnLocalInterface('GigabitEthernet0/0');
                              setVpnIkeVersion('ikev2');
                              setVpnEncryption('aes256');
                              setVpnHash('sha256');
                              setVpnDhGroup('14');
                              setVpnAuthMethod('pre-shared');
                              setVpnPsk('');
                              setVpnFeatures({
                                enableDPD: true,
                                enableNatTraversal: true,
                                enablePfs: true,
                                enableRouteInjection: true,
                                enableMonitoring: true
                              });
                              setVpnResult(null);
                              setVpnError('');
                            }}
                            className="px-4 py-2 bg-gray-800 hover:bg-gray-600 rounded-xl text-gray-300 text-sm flex items-center gap-2"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Reset
                          </button>
                        </div>
                      </div>

                      <div className="space-y-8">
                        {/* Basic VPN Configuration */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            Basic Configuration
                          </h4>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">VPN Type</label>
                              <select
                                value={vpnType}
                                onChange={(e) => setVpnType(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="ipsec-site-to-site">IPSec Site-to-Site</option>
                                <option value="gre-ipsec">GRE over IPSec</option>
                                <option value="dmvpn">DMVPN (Hub)</option>
                                <option value="flexvpn">FlexVPN</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">IKE Version</label>
                              <select
                                value={vpnIkeVersion}
                                onChange={(e) => setVpnIkeVersion(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="ikev1">IKEv1</option>
                                <option value="ikev2">IKEv2 (Recommended)</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Authentication</label>
                              <select
                                value={vpnAuthMethod}
                                onChange={(e) => setVpnAuthMethod(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="pre-shared">Pre-shared Key</option>
                                <option value="certificate">Certificate (RSA)</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Network Configuration */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Network className="w-5 h-5" />
                            Network Configuration
                          </h4>
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Peer IP Address</label>
                              <ValidatedInput
                                value={vpnPeerIP}
                                onChange={setVpnPeerIP}
                                validate={validateIP}
                                errorMessage="Invalid IP address"
                                placeholder="203.0.113.1"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Local Interface</label>
                              <input
                                value={vpnLocalInterface}
                                onChange={(e) => setVpnLocalInterface(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                placeholder="GigabitEthernet0/0"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Local Network</label>
                              <ValidatedInput
                                value={vpnLocalNetwork}
                                onChange={setVpnLocalNetwork}
                                validate={(value) => {
                                  const parts = value.split('/');
                                  if (parts.length !== 2) return false;
                                  if (!validateIP(parts[0])) return false;
                                  const mask = parseInt(parts[1]);
                                  return mask >= 8 && mask <= 32;
                                }}
                                errorMessage="Use CIDR notation (e.g., 192.168.1.0/24)"
                                placeholder="192.168.1.0/24"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Remote Network</label>
                              <ValidatedInput
                                value={vpnRemoteNetwork}
                                onChange={setVpnRemoteNetwork}
                                validate={(value) => {
                                  const parts = value.split('/');
                                  if (parts.length !== 2) return false;
                                  if (!validateIP(parts[0])) return false;
                                  const mask = parseInt(parts[1]);
                                  return mask >= 8 && mask <= 32;
                                }}
                                errorMessage="Use CIDR notation (e.g., 10.0.0.0/24)"
                                placeholder="10.0.0.0/24"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Security Parameters */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Lock className="w-5 h-5" />
                            Security Parameters
                          </h4>
                          <div className="grid md:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Encryption</label>
                              <select
                                value={vpnEncryption}
                                onChange={(e) => setVpnEncryption(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="aes128">AES-128</option>
                                <option value="aes192">AES-192</option>
                                <option value="aes256">AES-256 (Recommended)</option>
                                <option value="3des">3DES</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Hash/Integrity</label>
                              <select
                                value={vpnHash}
                                onChange={(e) => setVpnHash(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="sha256">SHA-256 (Recommended)</option>
                                <option value="sha384">SHA-384</option>
                                <option value="sha512">SHA-512</option>
                                <option value="md5">MD5</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">DH Group</label>
                              <select
                                value={vpnDhGroup}
                                onChange={(e) => setVpnDhGroup(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="2">Group 2 (1024-bit)</option>
                                <option value="5">Group 5 (1536-bit)</option>
                                <option value="14">Group 14 (2048-bit)</option>
                                <option value="15">Group 15 (3072-bit)</option>
                                <option value="16">Group 16 (4096-bit)</option>
                                <option value="19">Group 19 (256-bit ECP)</option>
                                <option value="20">Group 20 (384-bit ECP)</option>
                                <option value="21">Group 21 (521-bit ECP)</option>
                              </select>
                            </div>
                          </div>

                          {vpnAuthMethod === 'pre-shared' && (
                            <div className="mt-6">
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Pre-shared Key</label>
                              <div className="flex gap-2">
                                <input
                                  type="password"
                                  value={vpnPsk}
                                  onChange={(e) => setVpnPsk(e.target.value)}
                                  className="flex-1 bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  placeholder="Leave empty to auto-generate"
                                />
                                <button
                                  onClick={() => setVpnPsk(generatePassword(24, 'strong'))}
                                  className="px-4 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl text-gray-300"
                                  title="Generate strong password"
                                >
                                  🎲
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Advanced Features */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Advanced Features
                          </h4>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.entries(vpnFeatures).map(([feature, enabled]) => (
                              <label key={feature} className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={enabled}
                                  onChange={(e) => setVpnFeatures({...vpnFeatures, [feature]: e.target.checked})}
                                  className="w-4 h-4 text-gray-600 bg-black border-gray-800 rounded focus:ring-gray-600"
                                />
                                <span className="text-sm text-gray-300">
                                  {feature.replace('enable', '').replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Generate Button */}
                        <div className="flex gap-4">
                          <button
                            onClick={generateVPNConfig}
                            className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                          >
                            Generate VPN Configuration
                          </button>
                        </div>

                        {vpnError && (
                          <div className="bg-red-900/50 border border-red-700 rounded-xl p-4">
                            <div className="text-red-400 font-semibold">Error</div>
                            <div className="text-red-300 mt-1">{vpnError}</div>
                          </div>
                        )}

                        {/* Results */}
                        {vpnResult && (
                          <div className="space-y-6">
                            <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                              <h4 className="text-lg font-semibold text-gray-300 mb-4">VPN Configuration Summary</h4>
                              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">VPN Type</div>
                                  <div className="text-xl font-bold text-gray-100">{vpnResult.type}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">IKE Version</div>
                                  <div className="text-xl font-bold text-gray-100">{vpnResult.ikeVersion}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Peer IP</div>
                                  <div className="text-xl font-bold text-gray-100">{vpnResult.peerIP}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Encryption</div>
                                  <div className="text-xl font-bold text-gray-100">{vpnResult.encryption}</div>
                                </div>
                              </div>

                              <div className="mt-4">
                                <div className="text-sm text-gray-400">Description</div>
                                <div className="text-gray-300">{vpnResult.description}</div>
                              </div>

                              <div className="mt-4 grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Local Network</div>
                                  <div className="text-gray-300">{vpnResult.localNetwork}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Remote Network</div>
                                  <div className="text-gray-300">{vpnResult.remoteNetwork}</div>
                                </div>
                              </div>

                              {vpnResult.preSharedKey && (
                                <div className="mt-4">
                                  <div className="text-sm text-gray-400 mb-2">Pre-shared Key</div>
                                  <div className="font-mono text-gray-300 bg-gray-950 p-2 rounded">{vpnResult.preSharedKey}</div>
                                  <CopyButton text={vpnResult.preSharedKey} itemKey="vpn-psk" className="mt-2" />
                                </div>
                              )}
                            </div>

                            <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold text-gray-300">Generated Configuration</h4>
                                <CopyButton text={vpnResult.config} itemKey="vpn-config" />
                              </div>
                              <div className="font-mono bg-black p-4 rounded-lg border border-gray-800 text-sm whitespace-pre overflow-x-auto max-h-96">
                                {vpnResult.config}
                              </div>
                            </div>

                            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                              <div className="text-sm text-blue-400 font-semibold mb-2">💡 Implementation Notes</div>
                              <ul className="text-sm text-blue-300 space-y-1">
                                <li>• Ensure both peers have matching IKE and IPSec parameters</li>
                                <li>• Configure static routes or use routing protocols for VPN traffic</li>
                                <li>• Enable NAT-T if devices are behind NAT</li>
                                <li>• Use <code className="bg-blue-900/50 px-1 rounded">show crypto ikev2 sa</code> to verify IKE status</li>
                                <li>• Use <code className="bg-blue-900/50 px-1 rounded">show crypto ipsec sa</code> to verify IPSec tunnels</li>
                                <li>• For DMVPN/FlexVPN, ensure proper routing protocol configuration</li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </ErrorBoundary>

                {/* DNS Lookup */}
                <ErrorBoundary>
                  {activeTool === 'dns' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">DNS Lookup</h3>

                      <div className="space-y-4 sm:space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">
                              Hostname / Domain
                            </label>
                            <input
                              type="text"
                              value={dnsHostname}
                              onChange={(e) => setDnsHostname(e.target.value)}
                              placeholder="e.g., google.com, example.org"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">
                              Record Type
                            </label>
                            <select
                              value={dnsRecordType}
                              onChange={(e) => setDnsRecordType(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="A">A (IPv4)</option>
                              <option value="AAAA">AAAA (IPv6)</option>
                              <option value="CNAME">CNAME</option>
                              <option value="MX">MX (Mail)</option>
                              <option value="TXT">TXT</option>
                              <option value="NS">NS</option>
                              <option value="SOA">SOA</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                          <button
                            onClick={performDNSLookup}
                            disabled={dnsLoading}
                            className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100 transition-colors disabled:opacity-50"
                          >
                            {dnsLoading ? 'Looking up...' : 'Lookup DNS'}
                          </button>
                          <button
                            onClick={() => {
                              setDnsHostname('google.com');
                              setDnsRecordType('A');
                              setDnsResult(null);
                              setDnsError('');
                            }}
                            className="px-4 sm:px-6 py-3 bg-gray-950 hover:bg-gray-800 rounded-xl border border-gray-800 text-gray-300 touch-manipulation text-sm sm:text-base"
                          >
                            Reset
                          </button>
                        </div>
                      </div>

                      {dnsError && (
                        <div className="mt-6 p-4 bg-red-900/20 border border-red-700 rounded-xl">
                          <div className="flex items-center gap-2 text-red-400">
                            <AlertCircle className="w-5 h-5" />
                            <span>{dnsError}</span>
                          </div>
                        </div>
                      )}

                      {dnsResult && (
                        <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <div className="text-sm text-gray-400">Hostname</div>
                                <div className="text-lg font-bold text-gray-100 break-all">{dnsResult.hostname}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-400">Record Type</div>
                                <div className="text-lg font-bold text-gray-100">{dnsResult.recordType}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-400">Total Records</div>
                                <div className="text-lg font-bold text-gray-100">{dnsResult.totalRecords}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-400">Lookup Time</div>
                                <div className="text-xs font-mono text-gray-400">{dnsResult.timestamp}</div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-4">Records</h3>
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                              {dnsResult.records.map((record, idx) => (
                                <div key={idx} className="bg-gray-950/50 border border-gray-800 rounded-lg p-3 break-all">
                                  <div className="flex justify-between items-start gap-2 mb-2">
                                    <span className="text-xs font-bold uppercase text-blue-400">{record.type}</span>
                                    <span className="text-xs text-gray-500">TTL: {record.ttl}</span>
                                  </div>
                                  <div className="text-sm font-mono text-gray-300">{record.data}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <CopyButton 
                            text={JSON.stringify(dnsResult.records, null, 2)}
                            itemKey={`dns-${dnsResult.hostname}`}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Hex/ASCII Converter */}
                <ErrorBoundary>
                  {activeTool === 'hexascii' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Hex/ASCII Converter</h3>

                      <div className="space-y-4 sm:space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">
                              Conversion Mode
                            </label>
                            <select
                              value={hexAsciiMode}
                              onChange={(e) => setHexAsciiMode(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="ascii-to-hex">ASCII → Hex</option>
                              <option value="hex-to-ascii">Hex → ASCII</option>
                              <option value="base64-encode">ASCII → Base64</option>
                              <option value="base64-decode">Base64 → ASCII</option>
                              <option value="url-decode">URL Decode → ASCII</option>
                            </select>
                          </div>
                          {(hexAsciiMode === 'ascii-to-hex' || hexAsciiMode === 'hex-to-ascii') && (
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">
                                Hex Format
                              </label>
                              <select
                                value={hexAsciiHexFormat}
                                onChange={(e) => setHexAsciiHexFormat(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="lowercase">Lowercase (ab cd)</option>
                                <option value="uppercase">Uppercase (AB CD)</option>
                              </select>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">
                            Input
                          </label>
                          <textarea
                            value={hexAsciiInput}
                            onChange={(e) => setHexAsciiInput(e.target.value)}
                            placeholder={
                              hexAsciiMode === 'hex-to-ascii'
                                ? 'e.g., 0x48 0x65 0x6c 0x6c 0x6f'
                                : hexAsciiMode === 'base64-decode'
                                  ? 'e.g., SGVsbG8gV29ybGQ='
                                  : hexAsciiMode === 'url-decode'
                                    ? 'e.g., Hello%20World'
                                    : 'e.g., Hello World'
                            }
                            className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 font-mono focus:outline-none focus:ring-2 focus:ring-gray-600 min-h-24"
                          />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                          <button
                            onClick={performHexAsciiConversion}
                            className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100 transition-colors"
                          >
                            Convert
                          </button>
                          <button
                            onClick={() => {
                              setHexAsciiInput('Hello World');
                              setHexAsciiResult(null);
                              setHexAsciiError('');
                            }}
                            className="px-4 sm:px-6 py-3 bg-gray-950 hover:bg-gray-800 rounded-xl border border-gray-800 text-gray-300 touch-manipulation text-sm sm:text-base"
                          >
                            Reset
                          </button>
                        </div>
                      </div>

                      {hexAsciiError && (
                        <div className="mt-6 p-4 bg-red-900/20 border border-red-700 rounded-xl">
                          <div className="flex items-center gap-2 text-red-400">
                            <AlertCircle className="w-5 h-5" />
                            <span>{hexAsciiError}</span>
                          </div>
                        </div>
                      )}

                      {hexAsciiResult && (
                        <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                              <div>
                                <div className="text-xs text-gray-500 uppercase">Characters (code units)</div>
                                <div className="text-lg font-bold text-gray-100">{hexAsciiResult.charCount}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 uppercase">Characters (graphemes)</div>
                                <div className="text-lg font-bold text-gray-100">{hexAsciiResult.graphemeCount}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 uppercase">Bytes</div>
                                <div className="text-lg font-bold text-gray-100">{hexAsciiResult.byteCount}</div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">ASCII</h3>
                            <div className="bg-gray-950/50 border border-gray-800 rounded-lg p-3 break-all font-mono text-sm text-gray-300 min-h-16">
                              {hexAsciiResult.ascii}
                            </div>
                            <CopyButton text={hexAsciiResult.ascii} itemKey="hexascii-ascii" />
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">Hexadecimal</h3>
                            <div className="bg-gray-950/50 border border-gray-800 rounded-lg p-3 break-all font-mono text-sm text-gray-300 min-h-16">
                              {hexAsciiResult.hex}
                            </div>
                            <CopyButton text={hexAsciiResult.hex} itemKey="hexascii-hex" />
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">Binary</h3>
                            <div className="bg-gray-950/50 border border-gray-800 rounded-lg p-3 break-all font-mono text-sm text-gray-300 min-h-16">
                              {hexAsciiResult.binary}
                            </div>
                            <CopyButton text={hexAsciiResult.binary} itemKey="hexascii-binary" />
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">Base64</h3>
                            <div className="bg-gray-950/50 border border-gray-800 rounded-lg p-3 break-all font-mono text-sm text-gray-300 min-h-16">
                              {hexAsciiResult.base64}
                            </div>
                            <CopyButton text={hexAsciiResult.base64} itemKey="hexascii-base64" />
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">URL Encoded</h3>
                            <div className="bg-gray-950/50 border border-gray-800 rounded-lg p-3 break-all font-mono text-sm text-gray-300 min-h-16">
                              {hexAsciiResult.url}
                            </div>
                            <CopyButton text={hexAsciiResult.url} itemKey="hexascii-url" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* TLS/SSL Checker */}
                <ErrorBoundary>
                  {activeTool === 'ssl' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">TLS/SSL Checker</h3>
                      <p className="text-gray-400 mb-4">Powered by Mozilla TLS Observatory</p>

                      <div className="space-y-4 sm:space-y-6">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">Domain</label>
                          <input
                            type="text"
                            value={sslDomain}
                            onChange={(e) => setSslDomain(e.target.value)}
                            placeholder="example.com"
                            className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                          />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                          <button
                            onClick={performSSLCheck}
                            disabled={sslLoading}
                            className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100 transition-colors disabled:opacity-60"
                          >
                            {sslLoading ? 'Checking...' : 'Run SSL Check'}
                          </button>
                          <button
                            onClick={() => {
                              setSslDomain('google.com');
                              setSslResult(null);
                              setSslError('');
                            }}
                            className="px-4 sm:px-6 py-3 bg-gray-950 hover:bg-gray-800 rounded-xl border border-gray-800 text-gray-300 touch-manipulation text-sm sm:text-base"
                          >
                            Reset
                          </button>
                        </div>
                      </div>

                      {sslError && (
                        <div className="mt-6 p-4 bg-red-900/20 border border-red-700 rounded-xl">
                          <div className="flex items-center gap-2 text-red-400">
                            <AlertCircle className="w-5 h-5" />
                            <span>{sslError}</span>
                          </div>
                        </div>
                      )}

                      {sslResult && (
                        <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div>
                                <div className="text-xs text-gray-500 uppercase">Host</div>
                                <div className="text-lg font-bold text-gray-100 break-all">{sslResult.host}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 uppercase">IP</div>
                                <div className="text-lg font-bold text-gray-100">{sslResult.ip || '—'}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 uppercase">Status</div>
                                <div className="text-lg font-bold text-gray-100">{sslResult.status || '—'}</div>
                              </div>
                            </div>
                            {sslResult.statusMessage && (
                              <div className="mt-3 text-sm text-gray-400">{sslResult.statusMessage}</div>
                            )}
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">Protocol Support</h3>
                            {sslResult.protocols && sslResult.protocols.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {sslResult.protocols.map((proto) => (
                                  <span key={proto} className="px-3 py-1 rounded-full text-xs bg-gray-900 border border-gray-700 text-gray-300">
                                    {proto}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">No protocol data available.</div>
                            )}
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">Certificate Chain</h3>
                            {sslResult.certChain && sslResult.certChain.length > 0 ? (
                              <div className="space-y-3">
                                {sslResult.certChain.map((cert, index) => (
                                  <div key={`${cert.subject}-${index}`} className="border border-gray-800 rounded-lg p-3">
                                    <div className="text-sm text-gray-300 break-all"><span className="text-gray-500">Subject:</span> {cert.subject}</div>
                                    <div className="text-sm text-gray-300 break-all"><span className="text-gray-500">Issuer:</span> {cert.issuer}</div>
                                    <div className="text-sm text-gray-300"><span className="text-gray-500">Valid From:</span> {cert.notBefore ? new Date(cert.notBefore).toLocaleString() : '—'}</div>
                                    <div className="text-sm text-gray-300"><span className="text-gray-500">Valid To:</span> {cert.notAfter ? new Date(cert.notAfter).toLocaleString() : '—'}</div>
                                    {cert.sigAlg && (
                                      <div className="text-sm text-gray-300"><span className="text-gray-500">Signature:</span> {cert.sigAlg}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">No certificate chain data available.</div>
                            )}
                            <CopyButton text={JSON.stringify(sslResult.certChain || [], null, 2)} itemKey="ssl-certchain" />
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">Expiry</h3>
                            <div className="text-sm text-gray-300">
                              {sslResult.expiryDate ? sslResult.expiryDate.toLocaleString() : '—'}
                            </div>
                            {typeof sslResult.expiresInDays === 'number' && (
                              <div className="text-xs text-gray-500 mt-1">{sslResult.expiresInDays} days remaining</div>
                            )}
                          </div>

                          <div className="bg-black/50 rounded-xl p-4 sm:p-6 border border-gray-800">
                            <h3 className="text-lg font-bold text-gray-100 mb-3">Issues</h3>
                            {sslResult.issues && sslResult.issues.length > 0 ? (
                              <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                                {sslResult.issues.map((issue, idx) => (
                                  <li key={`${issue}-${idx}`}>{issue}</li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-sm text-gray-400">No issues reported.</div>
                            )}
                          </div>

                          <div className="text-xs text-gray-500 text-center mt-4">
                            Powered by <a href="https://certspotter.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">Certspotter</a> Certificate Transparency API
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* IP Geolocation */}
                <ErrorBoundary>
                  {activeTool === 'geolocation' && (
                    <div className="space-y-6">
                      <div className="bg-black/50 rounded-xl p-6 border border-gray-800">
                        <h3 className="text-2xl font-bold mb-4 text-gray-100">IP Geolocation Lookup</h3>
                        
                        <div className="space-y-4">
                          <input
                            type="text"
                            value={geoIP}
                            onChange={(e) => setGeoIP(e.target.value)}
                            placeholder="Enter IP address (e.g., 8.8.8.8)"
                            className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                          />
                          
                          <button
                            onClick={performGeolocation}
                            disabled={geoLoading}
                            className="w-full py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100 transition-colors disabled:opacity-50"
                          >
                            {geoLoading ? 'Looking up...' : 'Lookup IP'}
                          </button>
                        </div>

                        {geoError && (
                          <div className="mt-4 bg-red-900/30 border border-red-600 rounded-xl p-4">
                            <div className="text-red-400 font-semibold">{geoError}</div>
                          </div>
                        )}

                        {geoResult && (
                          <div className="mt-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">IP Address</div>
                                <div className="text-lg font-bold text-gray-100 break-all">{geoResult.ip}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Country</div>
                                <div className="text-lg font-bold text-gray-100">{geoResult.country || '—'}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Region</div>
                                <div className="text-lg font-bold text-gray-100">{geoResult.region || '—'}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">City</div>
                                <div className="text-lg font-bold text-gray-100">{geoResult.city || '—'}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Timezone</div>
                                <div className="text-lg font-bold text-gray-100">{geoResult.timezone || '—'}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Coordinates</div>
                                <div className="text-lg font-bold text-gray-100">{geoResult.latitude.toFixed(4)}, {geoResult.longitude.toFixed(4)}</div>
                              </div>
                            </div>

                            <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                              <div className="text-sm text-gray-400 mb-3">Network Information</div>
                              <div className="space-y-2 text-gray-300">
                                <div><span className="text-gray-400">ISP:</span> {geoResult.isp || '—'}</div>
                                <div><span className="text-gray-400">Organization:</span> {geoResult.org || '—'}</div>
                                <div><span className="text-gray-400">ASN:</span> {geoResult.asn || '—'}</div>
                              </div>
                            </div>

                            <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                              <div className="text-sm text-gray-400 mb-3">Security Flags</div>
                              <div className="space-y-2 text-gray-300">
                                <div><span className="text-gray-400">Mobile:</span> {geoResult.mobile ? '✓ Yes' : '✗ No'}</div>
                                <div><span className="text-gray-400">Proxy/VPN:</span> {geoResult.proxy ? '✓ Detected' : '✗ Not detected'}</div>
                                <div><span className="text-gray-400">Hosting:</span> {geoResult.hosting ? '✓ Yes' : '✗ No'}</div>
                              </div>
                            </div>

                            <div className="text-xs text-gray-500 text-center mt-4">
                              Powered by <a href="https://ipinfo.io" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">ipinfo.io</a>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </ErrorBoundary>

                {/* WHOIS Lookup */}
                <ErrorBoundary>
                  {activeTool === 'whois' && (
                    <div className="space-y-6">
                      <div className="bg-black/50 rounded-xl p-6 border border-gray-800">
                        <h3 className="text-2xl font-bold mb-4 text-gray-100">WHOIS Domain Lookup</h3>
                        
                        <div className="space-y-4">
                          <input
                            type="text"
                            value={whoisDomain}
                            onChange={(e) => setWhoisDomain(e.target.value)}
                            placeholder="Enter domain (e.g., google.com)"
                            className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                          />
                          
                          <button
                            onClick={performWhoisLookup}
                            disabled={whoisLoading}
                            className="w-full py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100 transition-colors disabled:opacity-50"
                          >
                            {whoisLoading ? 'Looking up...' : 'Lookup Domain'}
                          </button>
                        </div>

                        {whoisError && (
                          <div className="mt-4 bg-red-900/30 border border-red-600 rounded-xl p-4">
                            <div className="text-red-400 font-semibold">{whoisError}</div>
                          </div>
                        )}

                        {whoisResult && (
                          <div className="mt-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Domain</div>
                                <div className="text-lg font-bold text-gray-100 break-all">{whoisResult.domain}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Registrar</div>
                                <div className="text-lg font-bold text-gray-100">{whoisResult.registrar || '—'}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Registrant</div>
                                <div className="text-lg font-bold text-gray-100">{whoisResult.registrant || '—'}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Created Date</div>
                                <div className="text-lg font-bold text-gray-100">{whoisResult.createdDate || '—'}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Expiry Date</div>
                                <div className="text-lg font-bold text-gray-100">{whoisResult.expiryDate || '—'}</div>
                              </div>
                              
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400">Updated Date</div>
                                <div className="text-lg font-bold text-gray-100">{whoisResult.updatedDate || '—'}</div>
                              </div>
                            </div>

                            {whoisResult.nameservers && whoisResult.nameservers.length > 0 && (
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400 mb-3">Name Servers</div>
                                <div className="space-y-1 text-gray-300">
                                  {whoisResult.nameservers.map((ns, idx) => (
                                    <div key={idx} className="text-sm font-mono">{ns}</div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {whoisResult.status && Array.isArray(whoisResult.status) && whoisResult.status.length > 0 && (
                              <div className="bg-black/50 rounded-xl p-4 border border-gray-800">
                                <div className="text-sm text-gray-400 mb-3">Domain Status</div>
                                <div className="space-y-1 text-gray-300">
                                  {whoisResult.status.map((st, idx) => (
                                    <div key={idx} className="text-sm">{st}</div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="text-xs text-gray-500 text-center mt-4">
                              Powered by <a href="https://about.rdap.org" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">RDAP</a>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </ErrorBoundary>

                {/* NAT Configuration Generator */}
                <ErrorBoundary>
                  {activeTool === 'nat' && (
                    <div className="space-y-6">
                      <div className="bg-black/50 rounded-xl p-6 border border-gray-800">
                        <h3 className="text-2xl font-bold mb-4 text-gray-100">NAT Configuration Generator</h3>
                        
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm text-gray-300 mb-2 block">Platform</label>
                              <select 
                                value={natPlatform}
                                onChange={(e) => setNatPlatform(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="ios">Cisco IOS</option>
                                <option value="iosxe">Cisco IOS XE</option>
                                <option value="nxos">Cisco NX-OS (Nexus)</option>
                                <option value="iosxr">Cisco IOS XR</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-sm text-gray-300 mb-2 block">NAT Type</label>
                              <select 
                                value={natType}
                                onChange={(e) => setNatType(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="static">Static NAT</option>
                                <option value="dynamic">Dynamic NAT</option>
                                <option value="pat">PAT (Port Address Translation)</option>
                              </select>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm text-gray-300 mb-2 block">Inside Interface</label>
                              <input
                                type="text"
                                value={natInsideInt}
                                onChange={(e) => setNatInsideInt(e.target.value)}
                                placeholder="e.g., Ethernet0/0"
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-gray-300 mb-2 block">Outside Interface</label>
                              <input
                                type="text"
                                value={natOutsideInt}
                                onChange={(e) => setNatOutsideInt(e.target.value)}
                                placeholder="e.g., Ethernet0/1"
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              />
                            </div>
                          </div>

                          {natType === 'static' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-black/30 p-4 rounded-xl border border-gray-700">
                              <div>
                                <label className="text-sm text-gray-300 mb-2 block">Inside Local IP</label>
                                <input
                                  type="text"
                                  value={natInsideLocal}
                                  onChange={(e) => setNatInsideLocal(e.target.value)}
                                  placeholder="e.g., 192.168.1.10"
                                  className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                />
                              </div>
                              <div>
                                <label className="text-sm text-gray-300 mb-2 block">Inside Global IP</label>
                                <input
                                  type="text"
                                  value={natInsideGlobal}
                                  onChange={(e) => setNatInsideGlobal(e.target.value)}
                                  placeholder="e.g., 203.0.113.10"
                                  className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                />
                              </div>
                              <div>
                                <label className="text-sm text-gray-300 mb-2 block">Outside Local IP (optional)</label>
                                <input
                                  type="text"
                                  value={natOutsideLocal}
                                  onChange={(e) => setNatOutsideLocal(e.target.value)}
                                  placeholder="e.g., 10.0.0.1"
                                  className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                />
                              </div>
                              <div>
                                <label className="text-sm text-gray-300 mb-2 block">Outside Global IP (optional)</label>
                                <input
                                  type="text"
                                  value={natOutsideGlobal}
                                  onChange={(e) => setNatOutsideGlobal(e.target.value)}
                                  placeholder="e.g., 10.0.0.2"
                                  className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={natEnableStaticPat}
                                    onChange={(e) => setNatEnableStaticPat(e.target.checked)}
                                    className="rounded border-gray-600 bg-gray-800"
                                    id="nat-static-pat"
                                  />
                                  <label htmlFor="nat-static-pat" className="text-sm text-gray-300">
                                    Enable Static PAT (Port Forwarding)
                                  </label>
                                </div>
                                {natEnableStaticPat && (
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Protocol</label>
                                      <select
                                        value={natStaticProtocol}
                                        onChange={(e) => setNatStaticProtocol(e.target.value)}
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      >
                                        <option value="tcp">TCP</option>
                                        <option value="udp">UDP</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Inside Port</label>
                                      <input
                                        type="text"
                                        value={natStaticInsidePort}
                                        onChange={(e) => setNatStaticInsidePort(e.target.value)}
                                        placeholder="e.g., 80"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Global Port</label>
                                      <input
                                        type="text"
                                        value={natStaticGlobalPort}
                                        onChange={(e) => setNatStaticGlobalPort(e.target.value)}
                                        placeholder="e.g., 8080"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {(natType === 'dynamic' || natType === 'pat') && (
                            <div className="space-y-4 bg-black/30 p-4 rounded-xl border border-gray-700">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                  <label className="text-sm text-gray-300 mb-2 block">ACL Number</label>
                                  <input
                                    type="text"
                                    value={natAcl}
                                    onChange={(e) => setNatAcl(e.target.value)}
                                    placeholder="e.g., 10"
                                    className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  />
                                </div>
                                {natType === 'dynamic' && (
                                  <>
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Pool Name</label>
                                      <input
                                        type="text"
                                        value={natPool}
                                        onChange={(e) => setNatPool(e.target.value)}
                                        placeholder="e.g., POOL1"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                    <div />
                                  </>
                                )}
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm text-gray-300 mb-2 block">Inside Subnet</label>
                                  <input
                                    type="text"
                                    value={natInsideSubnet}
                                    onChange={(e) => setNatInsideSubnet(e.target.value)}
                                    placeholder="e.g., 192.168.1.0"
                                    className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm text-gray-300 mb-2 block">Wildcard Mask</label>
                                  <input
                                    type="text"
                                    value={natInsideWildcard}
                                    onChange={(e) => setNatInsideWildcard(e.target.value)}
                                    placeholder="e.g., 0.0.0.255"
                                    className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  />
                                </div>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={natEnableExempt}
                                    onChange={(e) => setNatEnableExempt(e.target.checked)}
                                    className="rounded border-gray-600 bg-gray-800"
                                    id="nat-exempt"
                                  />
                                  <label htmlFor="nat-exempt" className="text-sm text-gray-300">
                                    Enable NAT Exemption (No-NAT)
                                  </label>
                                </div>
                                {natEnableExempt && (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Exempt Destination</label>
                                      <input
                                        type="text"
                                        value={natExemptDest}
                                        onChange={(e) => setNatExemptDest(e.target.value)}
                                        placeholder="e.g., 10.0.0.0"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Exempt Wildcard</label>
                                      <input
                                        type="text"
                                        value={natExemptDestWildcard}
                                        onChange={(e) => setNatExemptDestWildcard(e.target.value)}
                                        placeholder="e.g., 0.255.255.255"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                              {natType === 'dynamic' && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                  <div>
                                    <label className="text-sm text-gray-300 mb-2 block">Pool Start IP</label>
                                    <input
                                      type="text"
                                      value={natPoolStart}
                                      onChange={(e) => setNatPoolStart(e.target.value)}
                                      placeholder="e.g., 203.0.113.50"
                                      className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm text-gray-300 mb-2 block">Pool End IP</label>
                                    <input
                                      type="text"
                                      value={natPoolEnd}
                                      onChange={(e) => setNatPoolEnd(e.target.value)}
                                      placeholder="e.g., 203.0.113.100"
                                      className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm text-gray-300 mb-2 block">Netmask</label>
                                    <input
                                      type="text"
                                      value={natPoolMask}
                                      onChange={(e) => setNatPoolMask(e.target.value)}
                                      placeholder="e.g., 255.255.255.0"
                                      className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="space-y-4 bg-black/30 p-4 rounded-xl border border-gray-700">
                            <div className="text-sm font-semibold text-gray-300">Advanced Features (IOS/IOS XE)</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm text-gray-300 mb-2 block">Route-Map (Policy NAT)</label>
                                <input
                                  type="text"
                                  value={natRouteMap}
                                  onChange={(e) => setNatRouteMap(e.target.value)}
                                  placeholder="e.g., RM-NAT"
                                  className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                />
                              </div>
                              <div>
                                <label className="text-sm text-gray-300 mb-2 block">VRF (optional)</label>
                                <input
                                  type="text"
                                  value={natVrf}
                                  onChange={(e) => setNatVrf(e.target.value)}
                                  placeholder="e.g., WAN-VRF"
                                  className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                />
                              </div>
                            </div>
                            {natType === 'static' && (
                              <div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={natEnableTwiceNat}
                                    onChange={(e) => setNatEnableTwiceNat(e.target.checked)}
                                    className="rounded border-gray-600 bg-gray-800"
                                    id="nat-twice"
                                  />
                                  <label htmlFor="nat-twice" className="text-sm text-gray-300">
                                    Enable Twice NAT (Source + Destination)
                                  </label>
                                </div>
                                {natEnableTwiceNat && (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Source Local</label>
                                      <input
                                        type="text"
                                        value={natTwiceSrcLocal}
                                        onChange={(e) => setNatTwiceSrcLocal(e.target.value)}
                                        placeholder="e.g., 192.168.1.10"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Source Global</label>
                                      <input
                                        type="text"
                                        value={natTwiceSrcGlobal}
                                        onChange={(e) => setNatTwiceSrcGlobal(e.target.value)}
                                        placeholder="e.g., 203.0.113.10"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Destination Local</label>
                                      <input
                                        type="text"
                                        value={natTwiceDstLocal}
                                        onChange={(e) => setNatTwiceDstLocal(e.target.value)}
                                        placeholder="e.g., 10.0.0.10"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm text-gray-300 mb-2 block">Destination Global</label>
                                      <input
                                        type="text"
                                        value={natTwiceDstGlobal}
                                        onChange={(e) => setNatTwiceDstGlobal(e.target.value)}
                                        placeholder="e.g., 198.51.100.10"
                                        className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={natEnableTimeouts}
                                  onChange={(e) => setNatEnableTimeouts(e.target.checked)}
                                  className="rounded border-gray-600 bg-gray-800"
                                  id="nat-timeouts"
                                />
                                <label htmlFor="nat-timeouts" className="text-sm text-gray-300">
                                  Enable NAT Timeouts
                                </label>
                              </div>
                              {natEnableTimeouts && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                                  <div>
                                    <label className="text-sm text-gray-300 mb-2 block">Translation Timeout</label>
                                    <input
                                      type="text"
                                      value={natTimeout}
                                      onChange={(e) => setNatTimeout(e.target.value)}
                                      placeholder="e.g., 86400"
                                      className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm text-gray-300 mb-2 block">TCP Timeout</label>
                                    <input
                                      type="text"
                                      value={natTcpTimeout}
                                      onChange={(e) => setNatTcpTimeout(e.target.value)}
                                      placeholder="e.g., 86400"
                                      className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm text-gray-300 mb-2 block">UDP Timeout</label>
                                    <input
                                      type="text"
                                      value={natUdpTimeout}
                                      onChange={(e) => setNatUdpTimeout(e.target.value)}
                                      placeholder="e.g., 300"
                                      className="w-full bg-black border border-gray-800 rounded-xl px-4 py-2 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={natEnableLogging}
                                onChange={(e) => setNatEnableLogging(e.target.checked)}
                                className="rounded border-gray-600 bg-gray-800"
                                id="nat-logging"
                              />
                              <label htmlFor="nat-logging" className="text-sm text-gray-300">
                                Enable NAT Logging
                              </label>
                            </div>
                          </div>

                          <button
                            onClick={generateNatConfig}
                            className="w-full py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100 transition-colors"
                          >
                            Generate Configuration
                          </button>
                        </div>

                        {natConfig && (
                          <div className="mt-6 space-y-4">
                            <div className="bg-black border border-gray-800 rounded-xl p-4">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-400">Configuration Output</span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(natConfig);
                                  }}
                                  className="px-3 py-1 bg-gray-800 hover:bg-gray-600 rounded-lg text-xs text-gray-100 transition-colors"
                                >
                                  Copy
                                </button>
                              </div>
                              <pre className="text-xs text-gray-300 font-mono overflow-x-auto whitespace-pre-wrap break-words max-h-96">
                                {natConfig}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </ErrorBoundary>

                {/* SNMP OID Lookup */}
                <ErrorBoundary>
                  {activeTool === 'oid' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">SNMP OID Lookup</h3>

                      <div className="mb-6">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            value={oidSearch}
                            onChange={(e) => setOidSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') lookupOID(); }}
                            placeholder="Search OID or description..."
                            className="w-full bg-black border border-gray-800 rounded-xl pl-10 pr-4 py-3 text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                          />
                        </div>
                        {oidError && <div className="text-red-400 mt-2 text-sm">{oidError}</div>}
                      </div>

                      {oidResult ? (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div>
                                <div className="text-sm text-gray-400">OID</div>
                                <div className="text-xl font-mono font-bold text-gray-100">{oidResult.oid}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-400">Name</div>
                                <div className="text-xl font-bold text-gray-100">{oidResult.name}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-400">Description</div>
                                <div className="text-gray-300">{oidResult.description}</div>
                              </div>
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Type</div>
                                  <div className="text-gray-300">{oidResult.type}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Access</div>
                                  <div className="text-gray-300">{oidResult.access}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Parent OID</div>
                                  <div className="font-mono text-gray-300">{oidResult.parent}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-800">
                                <th className="text-left py-3 px-4 text-gray-400 font-semibold">OID</th>
                                <th className="text-left py-3 px-4 text-gray-400 font-semibold">Description</th>
                                <th className="text-left py-3 px-4 text-gray-400 font-semibold">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredOIDs.map(([oid, desc], index) => (
                                <tr key={index} className="border-b border-gray-800 hover:bg-black/60">
                                  <td className="py-3 px-4">
                                    <div className="font-mono text-gray-100">{oid}</div>
                                  </td>
                                  <td className="py-3 px-4 text-gray-300">{desc}</td>
                                  <td className="py-3 px-4">
                                    <button
                                      onClick={() => {
                                        setOidSearch(oid);
                                        setTimeout(() => lookupOID(), 0);
                                      }}
                                      className="px-3 py-1 rounded-lg bg-gray-800 hover:bg-gray-600 transition-all text-gray-300"
                                    >
                                      Lookup
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <div className="mt-4 text-sm text-gray-500">
                        {oidResult ? 'OID details' : `Showing ${filteredOIDs.length} of ${Object.keys(commonOIDs).length} OIDs`}
                      </div>
                    </div>
                  )}
                </ErrorBoundary>

                {/* PoE Calculator */}
                <ErrorBoundary>
                  {activeTool === 'poe' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">PoE Calculator</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Device Type</label>
                            <select
                              value={poeDeviceType}
                              onChange={(e) => setPoeDeviceType(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="ip-camera">IP Camera</option>
                              <option value="wireless-ap">Wireless Access Point</option>
                              <option value="voip-phone">VoIP Phone</option>
                              <option value="ptz-camera">PTZ Camera</option>
                              <option value="access-point">High-power AP</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Number of Devices</label>
                            <input
                              type="number"
                              value={poeDeviceCount}
                              onChange={(e) => setPoeDeviceCount(parseInt(e.target.value) || 0)}
                              min="1"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="5"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Switch Power (Watts)</label>
                            <input
                              type="number"
                              value={poeSwitchPower}
                              onChange={(e) => setPoeSwitchPower(parseInt(e.target.value) || 0)}
                              min="1"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="370"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculatePoE}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate PoE
                        </button>
                      </div>

                      {poeResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Device Type</div>
                                  <div className="text-xl font-bold text-gray-100">{poeResult.deviceType}</div>
                                  <div className="text-sm text-gray-500">PoE Class: {poeResult.poeClass}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Number of Devices</div>
                                  <div className="text-2xl font-bold text-gray-100">{poeResult.deviceCount}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Power per Device</div>
                                  <div className="text-xl font-bold text-gray-100">{poeResult.powerPerDevice}</div>
                                  <div className="text-sm text-gray-500">Standard: {poeResult.poeStandard}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Total Power Required</div>
                                  <div className={`text-2xl font-bold ${
                                    poeResult.status.includes('✅') ? 'text-green-400' : 'text-red-400'
                                  }`}>
                                    {poeResult.totalPower}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Switch Power</div>
                                  <div className="text-2xl font-bold text-gray-100">{poeResult.switchPower}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Remaining Power</div>
                                  <div className={`text-2xl font-bold ${
                                    parseFloat(poeResult.remainingPower) >= 0 ? 'text-green-400' : 'text-red-400'
                                  }`}>
                                    {poeResult.remainingPower}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Status</div>
                                  <div className={`text-xl font-bold ${
                                    poeResult.status.includes('✅') ? 'text-green-400' : 'text-red-400'
                                  }`}>
                                    {poeResult.status}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Efficiency</div>
                                  <div className="text-xl font-bold text-gray-100">{poeResult.efficiency}%</div>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Voltage Drop</div>
                                  <div className="text-gray-300">{poeResult.voltageDrop}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Effective Power</div>
                                  <div className="text-gray-300">{poeResult.effectivePower}</div>
                                </div>
                              </div>
                              
                              <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                <div className="text-sm text-gray-400 mb-1">Recommendation</div>
                                <div className="text-gray-300">{poeResult.recommendation}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* MTU Calculator */}
                <ErrorBoundary>
                  {activeTool === 'mtu' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">MTU Calculator</h3>

                      <div className="space-y-6 mb-6">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">MTU Presets</label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <button
                              onClick={() => setMtuSize(576)}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="Minimum IPv4 MTU"
                            >
                              Min IPv4 (576)
                            </button>
                            <button
                              onClick={() => setMtuSize(1492)}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="PPPoE (DSL)"
                            >
                              PPPoE (1492)
                            </button>
                            <button
                              onClick={() => setMtuSize(1500)}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="Standard Ethernet"
                            >
                              Ethernet (1500)
                            </button>
                            <button
                              onClick={() => setMtuSize(4470)}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="VPN MTU"
                            >
                              VPN (4470)
                            </button>
                            <button
                              onClick={() => setMtuSize(9000)}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="Jumbo frames"
                            >
                              Jumbo (9000)
                            </button>
                            <button
                              onClick={() => setMtuSize(9216)}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="Cisco jumbo frames"
                            >
                              Cisco (9216)
                            </button>
                            <button
                              onClick={() => setMtuSize(65535)}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="IPv6 maximum MTU"
                            >
                              IPv6 Max (65535)
                            </button>
                            <button
                              onClick={() => setMtuSize(68)}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="Minimum MTU"
                            >
                              Minimum (68)
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-300">Common Configurations</label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <button
                              onClick={() => {
                                setMtuSize(1492);
                                setMtuProtocol('tcp');
                                setMtuFeatures({ ipv6: false, vlan: false, dot1q: true, mpls: false, mplsLabels: 1 });
                              }}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="PPPoE with TCP"
                            >
                              PPPoE + TCP
                            </button>
                            <button
                              onClick={() => {
                                setMtuSize(1500);
                                setMtuProtocol('vxlan');
                                setMtuFeatures({ ipv6: false, vlan: true, dot1q: true, mpls: false, mplsLabels: 1 });
                              }}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="VXLAN over VLAN"
                            >
                              VXLAN + VLAN
                            </button>
                            <button
                              onClick={() => {
                                setMtuSize(4470);
                                setMtuProtocol('ipsec-esp');
                                setMtuFeatures({ ipv6: false, vlan: false, dot1q: true, mpls: false, mplsLabels: 1 });
                              }}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="VPN with IPsec ESP"
                            >
                              VPN + IPsec
                            </button>
                            <button
                              onClick={() => {
                                setMtuSize(9000);
                                setMtuProtocol('mpls');
                                setMtuFeatures({ ipv6: false, vlan: false, dot1q: true, mpls: true, mplsLabels: 3 });
                              }}
                              className="px-3 py-2 bg-gray-950 hover:bg-gray-800 rounded-lg text-sm font-medium text-gray-300 border border-gray-600"
                              title="MPLS with 3 labels over jumbo frames"
                            >
                              MPLS (3 labels)
                            </button>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">MTU Size (bytes)</label>
                            <input
                              type="number"
                              value={mtuSize}
                              onChange={(e) => setMtuSize(parseInt(e.target.value) || 0)}
                              min="68"
                              max="65535"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="1500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Protocol</label>
                            <select
                              value={mtuProtocol}
                              onChange={(e) => setMtuProtocol(e.target.value)}
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            >
                              <option value="tcp">TCP</option>
                              <option value="udp">UDP</option>
                              <option value="icmp">ICMP</option>
                              <option value="gre">GRE</option>
                              <option value="ipsec-esp">IPsec ESP</option>
                              <option value="vxlan">VXLAN</option>
                              <option value="mpls">MPLS</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="ipv6"
                              checked={mtuFeatures.ipv6}
                              onChange={(e) => setMtuFeatures(prev => ({ ...prev, ipv6: e.target.checked }))}
                              className="w-4 h-4 text-gray-600 bg-black border-gray-800 rounded focus:ring-gray-600"
                            />
                            <label htmlFor="ipv6" className="text-sm font-semibold text-gray-300">IPv6</label>
                          </div>
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="vlan"
                              checked={mtuFeatures.vlan}
                              onChange={(e) => setMtuFeatures(prev => ({ ...prev, vlan: e.target.checked }))}
                              className="w-4 h-4 text-gray-600 bg-black border-gray-800 rounded focus:ring-gray-600"
                            />
                            <label htmlFor="vlan" className="text-sm font-semibold text-gray-300">VLAN</label>
                          </div>
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="mpls"
                              checked={mtuFeatures.mpls}
                              onChange={(e) => setMtuFeatures(prev => ({ ...prev, mpls: e.target.checked }))}
                              className="w-4 h-4 text-gray-600 bg-black border-gray-800 rounded focus:ring-gray-600"
                            />
                            <label htmlFor="mpls" className="text-sm font-semibold text-gray-300">MPLS</label>
                          </div>
                        </div>

                        {mtuFeatures.vlan && (
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">VLAN Tagging</label>
                              <select
                                value={mtuFeatures.dot1q ? 'single' : 'double'}
                                onChange={(e) => setMtuFeatures(prev => ({ ...prev, dot1q: e.target.value === 'single' }))}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="single">Single Tag (4 bytes)</option>
                                <option value="double">Double Tag (8 bytes)</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {mtuFeatures.mpls && (
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">MPLS Labels</label>
                              <input
                                type="number"
                                value={mtuFeatures.mplsLabels}
                                onChange={(e) => setMtuFeatures(prev => ({ ...prev, mplsLabels: parseInt(e.target.value) || 1 }))}
                                min="1"
                                max="10"
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculateMTU}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate MTU
                        </button>
                        <button
                          onClick={() => {
                            setMtuSize(1500);
                            setMtuProtocol('tcp');
                            setMtuFeatures({
                              ipv6: false,
                              vlan: false,
                              dot1q: true,
                              mpls: false,
                              mplsLabels: 1
                            });
                            setMtuResult(null);
                            setMtuError('');
                          }}
                          className="px-6 py-3 bg-gray-950 hover:bg-gray-800 rounded-xl font-semibold border border-gray-600 text-gray-300"
                        >
                          Reset
                        </button>
                      </div>

                      {mtuResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">MTU Size</div>
                                  <div className="text-2xl font-bold text-gray-100">{mtuResult.mtu} bytes</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Protocol</div>
                                  <div className="text-xl font-bold text-gray-100">{mtuResult.protocol}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Header Overhead</div>
                                  <div className="text-xl font-bold text-gray-100">{mtuResult.overhead} bytes</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Payload Size</div>
                                  <div className="text-xl font-bold text-gray-100">{mtuResult.payload} bytes</div>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Efficiency</div>
                                  <div className={`text-2xl font-bold ${
                                    parseFloat(mtuResult.efficiency) > 90 ? 'text-green-400' :
                                    parseFloat(mtuResult.efficiency) > 80 ? 'text-yellow-400' : 'text-red-400'
                                  }`}>
                                    {mtuResult.efficiency}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Max Segment Size</div>
                                  <div className="text-xl font-bold text-gray-100">{mtuResult.maxSegmentSize} bytes</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Fragments</div>
                                  <div className="text-xl font-bold text-gray-100">{mtuResult.fragments}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Throughput</div>
                                  <div className="text-xl font-bold text-gray-100">{mtuResult.throughput} Mbps</div>
                                </div>
                              </div>

                              <div className="grid md:grid-cols-2 gap-6">
                                <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                  <div className="text-sm text-gray-400 mb-2">Protocol Breakdown</div>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-300">IP Header:</span>
                                      <span className="text-gray-100">{mtuResult.breakdown.ip} bytes</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-300">Transport Header:</span>
                                      <span className="text-gray-100">{mtuResult.breakdown.transport} bytes</span>
                                    </div>
                                    {mtuResult.breakdown.encapsulation && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-300">Encapsulation:</span>
                                        <span className="text-gray-100">{mtuResult.breakdown.encapsulation} bytes</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                  <div className="text-sm text-gray-400 mb-2">Features</div>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-300">IPv6:</span>
                                      <span className="text-gray-100">{mtuResult.features.ipv6 ? 'Yes' : 'No'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-300">VLAN:</span>
                                      <span className="text-gray-100">{mtuResult.features.vlan}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-300">MPLS:</span>
                                      <span className="text-gray-100">{mtuResult.features.mpls}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                <div className="text-sm text-gray-400 mb-1">Recommendation</div>
                                <div className="text-gray-300">{mtuResult.recommendation}</div>
                                <div className="text-sm text-gray-500 mt-2">{mtuResult.pmtud}</div>
                              </div>
                            </div>
                          </div>

                          {/* Efficiency Chart */}
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="text-lg font-semibold text-gray-100 mb-4">Efficiency Comparison</div>
                            <div className="space-y-3">
                              {[576, 1492, 1500, 4470, 9000, 9216, 65535].map(size => {
                                const efficiency = ((size - mtuResult.overhead) / size * 100).toFixed(1);
                                const width = (efficiency / 100) * 100;
                                return (
                                  <div key={size} className="flex items-center space-x-3">
                                    <div className="w-16 text-sm text-gray-300">{size}</div>
                                    <div className="flex-1 bg-gray-950 rounded-full h-4">
                                      <div
                                        className={`h-4 rounded-full ${
                                          parseFloat(efficiency) > 90 ? 'bg-green-500' :
                                          parseFloat(efficiency) > 80 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${width}%` }}
                                      ></div>
                                    </div>
                                    <div className="w-12 text-sm text-gray-300 text-right">{efficiency}%</div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="text-xs text-gray-500 mt-2">Efficiency with current overhead configuration</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* Routing Metrics - FIXED calculations */}
                <ErrorBoundary>
                  {activeTool === 'metrics' && (
                    <div>
                      <h3 className="text-2xl font-bold mb-2 text-gray-100">Routing Metrics Calculator</h3>

                      <div className="space-y-6 mb-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Bandwidth (Mbps)</label>
                            <input
                              type="number"
                              value={metricBandwidth}
                              onChange={(e) => setMetricBandwidth(parseInt(e.target.value) || 0)}
                              min="1"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="100"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Delay (μs)</label>
                            <input
                              type="number"
                              value={metricDelay}
                              onChange={(e) => setMetricDelay(parseInt(e.target.value) || 0)}
                              min="0"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="100"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Load (1-255)</label>
                            <input
                              type="number"
                              value={metricLoad}
                              onChange={(e) => setMetricLoad(parseInt(e.target.value) || 0)}
                              min="1"
                              max="255"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2 text-gray-300">Reliability (1-255)</label>
                            <input
                              type="number"
                              value={metricReliability}
                              onChange={(e) => setMetricReliability(parseInt(e.target.value) || 0)}
                              min="1"
                              max="255"
                              className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              placeholder="255"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <button
                          onClick={calculateMetrics}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Calculate Metrics
                        </button>
                      </div>

                      {metricResult && (
                        <div className="mt-8 space-y-6">
                          <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">EIGRP Metric</div>
                                  <div className="text-2xl font-bold text-gray-100">{metricResult.eigrpMetric}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">OSPF Cost</div>
                                  <div className="text-2xl font-bold text-gray-100">{metricResult.ospfCost}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Route Quality</div>
                                  <div className={`text-xl font-bold ${metricResult.color}`}>
                                    {metricResult.quality}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Bandwidth</div>
                                  <div className="text-gray-300">{metricResult.bandwidth}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Delay</div>
                                  <div className="text-gray-300">{metricResult.delay}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Load</div>
                                  <div className="text-gray-300">{metricResult.load}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Reliability</div>
                                  <div className="text-gray-300">{metricResult.reliability}</div>
                                </div>
                              </div>
                              
                              <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                <div className="text-sm text-gray-400 mb-1">Note</div>
                                <div className="text-gray-300">{metricResult.note}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* BGP Tools */}
                <ErrorBoundary>
                  {activeTool === 'bgp' && (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-100">Advanced BGP Configuration Generator</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setBgpAsn('64512');
                              setBgpRouterId('1.1.1.1');
                              setBgpNeighbors([{ ip: '192.168.1.1', asn: '64513', description: 'Peer Router' }]);
                              setBgpNetworks([{ network: '192.168.0.0', mask: '255.255.0.0' }]);
                              setBgpCommunities([{ name: 'LOCAL', value: '64512:100' }]);
                              setBgpRouteMaps([{ name: 'RM-IN', action: 'permit', sequence: 10 }]);
                              setBgpPrefixLists([{ name: 'PL-IN', action: 'permit', network: '0.0.0.0/0' }]);
                              setBgpFeatures({
                                enableRouteReflector: false,
                                enableConfederation: false,
                                enableAggregation: false,
                                enableIPv6: false,
                                enableSecurity: true,
                                enableCommunities: true,
                                enableRouteMaps: false,
                                enablePrefixLists: false
                              });
                              setBgpConfederationId('64500');
                              setBgpConfederationMembers(['64512', '64513']);
                              setBgpResult(null);
                              setBgpError('');
                            }}
                            className="px-4 py-2 bg-gray-800 hover:bg-gray-600 rounded-xl text-gray-300 text-sm flex items-center gap-2"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Reset
                          </button>
                        </div>
                      </div>

                      <div className="space-y-8">
                        {/* Basic BGP Configuration */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Radio className="w-5 h-5" />
                            Basic Configuration
                          </h4>
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Local ASN</label>
                              <input
                                value={bgpAsn}
                                onChange={(e) => setBgpAsn(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                placeholder="64512"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Router ID</label>
                              <ValidatedInput
                                value={bgpRouterId}
                                onChange={setBgpRouterId}
                                validate={validateIP}
                                errorMessage="Invalid router ID (must be valid IP address)"
                                placeholder="1.1.1.1"
                              />
                            </div>
                          </div>
                        </div>

                        {/* BGP Features */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            BGP Features
                          </h4>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.entries(bgpFeatures).map(([feature, enabled]) => (
                              <label key={feature} className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={enabled}
                                  onChange={(e) => setBgpFeatures({...bgpFeatures, [feature]: e.target.checked})}
                                  className="w-4 h-4 text-gray-600 bg-black border-gray-800 rounded focus:ring-gray-600"
                                />
                                <span className="text-sm text-gray-300">
                                  {feature.replace('enable', '').replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Confederation Configuration */}
                        {bgpFeatures.enableConfederation && (
                          <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                            <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                              <Network className="w-5 h-5" />
                              Confederation Configuration
                            </h4>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-semibold mb-2 text-gray-300">Confederation ID</label>
                                <input
                                  value={bgpConfederationId}
                                  onChange={(e) => setBgpConfederationId(e.target.value)}
                                  className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  placeholder="64500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-semibold mb-2 text-gray-300">Member ASNs (comma-separated)</label>
                                <input
                                  value={bgpConfederationMembers.join(', ')}
                                  onChange={(e) => setBgpConfederationMembers(e.target.value.split(',').map(s => s.trim()))}
                                  className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                  placeholder="64512, 64513"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* BGP Neighbors */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-lg font-semibold text-gray-300 flex items-center gap-2">
                              <Server className="w-5 h-5" />
                              BGP Neighbors ({bgpNeighbors.length})
                            </h4>
                            <button
                              onClick={addBgpNeighbor}
                              className="px-3 py-1 bg-gray-800 hover:bg-gray-600 rounded-lg text-sm flex items-center gap-1"
                            >
                              <Plus className="w-4 h-4" />
                              Add Neighbor
                            </button>
                          </div>
                          <div className="space-y-4">
                            {bgpNeighbors.map((neighbor, index) => (
                              <div key={index} className="grid md:grid-cols-4 gap-4 p-4 bg-black/50 rounded-lg border border-gray-800">
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">Neighbor IP</label>
                                  <ValidatedInput
                                    value={neighbor.ip}
                                    onChange={(value) => updateBgpNeighbor(index, 'ip', value)}
                                    validate={validateIP}
                                    errorMessage="Invalid IP"
                                    placeholder="192.168.1.1"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">ASN</label>
                                  <input
                                    value={neighbor.asn}
                                    onChange={(e) => updateBgpNeighbor(index, 'asn', e.target.value)}
                                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                    placeholder="64513"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">Description</label>
                                  <input
                                    value={neighbor.description}
                                    onChange={(e) => updateBgpNeighbor(index, 'description', e.target.value)}
                                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                    placeholder="Peer Router"
                                  />
                                </div>
                                <div className="flex items-end">
                                  <button
                                    onClick={() => removeBgpNeighbor(index)}
                                    className="w-full px-3 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm flex items-center justify-center gap-1"
                                    disabled={bgpNeighbors.length === 1}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Networks */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-lg font-semibold text-gray-300 flex items-center gap-2">
                              <Globe className="w-5 h-5" />
                              Networks ({bgpNetworks.length})
                            </h4>
                            <button
                              onClick={addBgpNetwork}
                              className="px-3 py-1 bg-gray-800 hover:bg-gray-600 rounded-lg text-sm flex items-center gap-1"
                            >
                              <Plus className="w-4 h-4" />
                              Add Network
                            </button>
                          </div>
                          <div className="space-y-4">
                            {bgpNetworks.map((network, index) => (
                              <div key={index} className="grid md:grid-cols-3 gap-4 p-4 bg-black/50 rounded-lg border border-gray-800">
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">Network</label>
                                  <input
                                    value={network.network}
                                    onChange={(e) => updateBgpNetwork(index, 'network', e.target.value)}
                                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                    placeholder="192.168.0.0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">Mask</label>
                                  <input
                                    value={network.mask}
                                    onChange={(e) => updateBgpNetwork(index, 'mask', e.target.value)}
                                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                    placeholder="255.255.0.0"
                                  />
                                </div>
                                <div className="flex items-end">
                                  <button
                                    onClick={() => removeBgpNetwork(index)}
                                    className="w-full px-3 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm flex items-center justify-center gap-1"
                                    disabled={bgpNetworks.length === 1}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Communities */}
                        {bgpFeatures.enableCommunities && (
                          <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="text-lg font-semibold text-gray-300 flex items-center gap-2">
                                <Tag className="w-5 h-5" />
                                Communities ({bgpCommunities.length})
                              </h4>
                              <button
                                onClick={addBgpCommunity}
                                className="px-3 py-1 bg-gray-800 hover:bg-gray-600 rounded-lg text-sm flex items-center gap-1"
                              >
                                <Plus className="w-4 h-4" />
                                Add Community
                              </button>
                            </div>
                            <div className="space-y-4">
                              {bgpCommunities.map((community, index) => (
                                <div key={index} className="grid md:grid-cols-3 gap-4 p-4 bg-black/50 rounded-lg border border-gray-800">
                                  <div>
                                    <label className="block text-sm text-gray-400 mb-1">Name</label>
                                    <input
                                      value={community.name}
                                      onChange={(e) => updateBgpCommunity(index, 'name', e.target.value)}
                                      className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                      placeholder="LOCAL"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm text-gray-400 mb-1">Value</label>
                                    <input
                                      value={community.value}
                                      onChange={(e) => updateBgpCommunity(index, 'value', e.target.value)}
                                      className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                      placeholder="64512:100"
                                    />
                                  </div>
                                  <div className="flex items-end">
                                    <button
                                      onClick={() => removeBgpCommunity(index)}
                                      className="w-full px-3 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm flex items-center justify-center gap-1"
                                      disabled={bgpCommunities.length === 1}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Generate Button */}
                        <div className="flex gap-4">
                          <button
                            onClick={generateBGPConfig}
                            className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                          >
                            Generate BGP Configuration
                          </button>
                        </div>

                        {bgpError && (
                          <div className="bg-red-900/50 border border-red-700 rounded-xl p-4">
                            <div className="text-red-400 font-semibold">Error</div>
                            <div className="text-red-300 mt-1">{bgpError}</div>
                          </div>
                        )}

                        {/* Results */}
                        {bgpResult && (
                          <div className="space-y-6">
                            <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                              <h4 className="text-lg font-semibold text-gray-300 mb-4">Configuration Summary</h4>
                              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div>
                                  <div className="text-sm text-gray-400">Local ASN</div>
                                  <div className="text-xl font-bold text-gray-100">{bgpResult.asn}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Router ID</div>
                                  <div className="text-xl font-bold text-gray-100">{bgpResult.routerId}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Neighbors</div>
                                  <div className="text-xl font-bold text-gray-100">{bgpResult.neighbors}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-400">Networks</div>
                                  <div className="text-xl font-bold text-gray-100">{bgpResult.networks}</div>
                                </div>
                              </div>

                              {bgpResult.features.enableConfederation && bgpResult.confederation && (
                                <div className="mt-4 p-4 bg-black/60 rounded-lg border border-gray-800">
                                  <div className="text-sm text-gray-400 mb-2">Confederation</div>
                                  <div className="text-gray-300">
                                    ID: {bgpResult.confederation.id} | Members: {bgpResult.confederation.members.join(', ')}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold text-gray-300">Generated Configuration</h4>
                                <CopyButton text={bgpResult.config} itemKey="bgp-config" />
                              </div>
                              <div className="font-mono bg-black p-4 rounded-lg border border-gray-800 text-sm whitespace-pre overflow-x-auto max-h-96">
                                {bgpResult.config}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </ErrorBoundary>

                {/* ENHANCED Config Generator */}
                <ErrorBoundary>
                  {activeTool === 'config' && (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-100">Configuration Generator</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={resetConfigGenerator}
                            className="px-4 py-2 bg-gray-800 hover:bg-gray-600 rounded-xl text-gray-300 text-sm flex items-center gap-2"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Reset
                          </button>
                        </div>
                      </div>

                      <div className="space-y-8">
                        {/* Basic Configuration */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Basic Configuration
                          </h4>
                          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Device Type</label>
                              <select
                                value={configDevice}
                                onChange={(e) => setConfigDevice(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                              >
                                <option value="router">Router</option>
                                <option value="switch">Switch</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Hostname</label>
                              <input
                                value={configHostname}
                                onChange={(e) => setConfigHostname(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                placeholder="Router1"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Domain Name</label>
                              <input
                                value={configDomain}
                                onChange={(e) => setConfigDomain(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                placeholder="network.lab"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Timezone</label>
                              <select
                                value={configTimezone}
                                onChange={(e) => setConfigTimezone(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                              >
                                <option value="UTC-5">UTC-5 (EST)</option>
                                <option value="UTC-6">UTC-6 (CST)</option>
                                <option value="UTC-7">UTC-7 (MST)</option>
                                <option value="UTC-8">UTC-8 (PST)</option>
                                <option value="UTC+0">UTC+0 (GMT)</option>
                              </select>
                            </div>
                          </div>
                          
                          {configDevice === 'router' && (
                            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Routing Protocol</label>
                                <select
                                  value={configRoutingProtocol}
                                  onChange={(e) => setConfigRoutingProtocol(e.target.value)}
                                  className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                >
                                  <option value="static">Static Routing</option>
                                  <option value="ospf">OSPF</option>
                                  <option value="eigrp">EIGRP</option>
                                  <option value="bgp">BGP</option>
                                  <option value="rip">RIP</option>
                                </select>
                              </div>
                              {configRoutingProtocol === 'ospf' && (
                                <div>
                                  <label className="block text-sm text-gray-400 mb-2">OSPF Area</label>
                                  <input
                                    value={configOspfArea}
                                    onChange={(e) => setConfigOspfArea(e.target.value)}
                                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                    placeholder="0"
                                  />
                                </div>
                              )}
                              {configRoutingProtocol === 'bgp' && (
                                <div>
                                  <label className="block text-sm text-gray-400 mb-2">BGP AS Number</label>
                                  <input
                                    value={configBgpAsn || ''}
                                    onChange={(e) => setConfigBgpAsn(e.target.value)}
                                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                    placeholder="65001"
                                  />
                                </div>
                              )}
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Redundancy Protocol</label>
                                <select
                                  value={configRedundancyProtocol}
                                  onChange={(e) => setConfigRedundancyProtocol(e.target.value)}
                                  className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                >
                                  <option value="none">None</option>
                                  <option value="hsrp">HSRP</option>
                                  <option value="glbp">GLBP</option>
                                  <option value="vrrp">VRRP</option>
                                </select>
                              </div>
                              {configRedundancyProtocol !== 'none' && (
                                <div>
                                  <label className="block text-sm text-gray-400 mb-2">Virtual IP</label>
                                  <input
                                    value={configVirtualIp || ''}
                                    onChange={(e) => setConfigVirtualIp(e.target.value)}
                                    className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                    placeholder="192.168.1.1"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          
                          {configDevice === 'switch' && (
                            <div className="mt-4 grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">Primary VLAN ID</label>
                                <input
                                  value={configVlanId}
                                  onChange={(e) => setConfigVlanId(e.target.value)}
                                  className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                  placeholder="10"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-gray-400 mb-2">VLAN Name</label>
                                <input
                                  value={configVlanName}
                                  onChange={(e) => setConfigVlanName(e.target.value)}
                                  className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2 text-gray-100"
                                  placeholder="USERS"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Security Features */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5" />
                            Security Features
                          </h4>
                          <div className="grid md:grid-cols-2 gap-3">
                            <SecurityFeatureToggle
                              feature="SSH Access"
                              enabled={configSecurityFeatures.enableSSH}
                              onToggle={() => toggleSecurityFeature('enableSSH')}
                              description="Secure shell access for remote management"
                            />
                            <SecurityFeatureToggle
                              feature="HTTPS Management"
                              enabled={configSecurityFeatures.enableHTTPS}
                              onToggle={() => toggleSecurityFeature('enableHTTPS')}
                              description="Web-based management over HTTPS"
                            />
                            <SecurityFeatureToggle
                              feature="Access Control Lists"
                              enabled={configSecurityFeatures.enableACLs}
                              onToggle={() => toggleSecurityFeature('enableACLs')}
                              description="Packet filtering with ACLs"
                            />
                            <SecurityFeatureToggle
                              feature="Port Security"
                              enabled={configSecurityFeatures.enablePortSecurity}
                              onToggle={() => toggleSecurityFeature('enablePortSecurity')}
                              description="MAC-based port security"
                            />
                            <SecurityFeatureToggle
                              feature="802.1X Authentication"
                              enabled={configSecurityFeatures.enableDot1x}
                              onToggle={() => toggleSecurityFeature('enableDot1x')}
                              description="Port-based network access control"
                            />
                            <SecurityFeatureToggle
                              feature="Syslog Logging"
                              enabled={configSecurityFeatures.enableLogging}
                              onToggle={() => toggleSecurityFeature('enableLogging')}
                              description="Centralized log collection"
                            />
                            <SecurityFeatureToggle
                              feature="NTP Time Sync"
                              enabled={configSecurityFeatures.enableNTP}
                              onToggle={() => toggleSecurityFeature('enableNTP')}
                              description="Network time protocol synchronization"
                            />
                            <SecurityFeatureToggle
                              feature="SNMP Monitoring"
                              enabled={configSecurityFeatures.enableSNMP}
                              onToggle={() => toggleSecurityFeature('enableSNMP')}
                              description="Simple Network Management Protocol"
                            />
                            <SecurityFeatureToggle
                              feature="NetFlow Export"
                              enabled={configSecurityFeatures.enableNetFlow}
                              onToggle={() => toggleSecurityFeature('enableNetFlow')}
                              description="Traffic analysis and monitoring"
                            />
                            <SecurityFeatureToggle
                              feature="Storm Control"
                              enabled={configSecurityFeatures.enableStormControl}
                              onToggle={() => toggleSecurityFeature('enableStormControl')}
                              description="Broadcast/multicast storm protection"
                            />
                            {configDevice === 'switch' && (
                              <>
                                <SecurityFeatureToggle
                                  feature="VTP"
                                  enabled={configSecurityFeatures.enableVTP}
                                  onToggle={() => toggleSecurityFeature('enableVTP')}
                                  description="VLAN Trunking Protocol"
                                />
                                <SecurityFeatureToggle
                                  feature="EtherChannel"
                                  enabled={configSecurityFeatures.enableEtherChannel}
                                  onToggle={() => toggleSecurityFeature('enableEtherChannel')}
                                  description="Link aggregation and port bundling"
                                />
                              </>
                            )}
                            <SecurityFeatureToggle
                              feature="DHCP Server"
                              enabled={configSecurityFeatures.enableDHCP}
                              onToggle={() => toggleSecurityFeature('enableDHCP')}
                              description="Dynamic Host Configuration Protocol server"
                            />
                            <SecurityFeatureToggle
                              feature="QoS"
                              enabled={configSecurityFeatures.enableQoS}
                              onToggle={() => toggleSecurityFeature('enableQoS')}
                              description="Quality of Service configuration"
                            />
                            {configDevice === 'router' && (
                              <>
                                <SecurityFeatureToggle
                                  feature="Multicast Routing"
                                  enabled={configSecurityFeatures.enableMulticast}
                                  onToggle={() => toggleSecurityFeature('enableMulticast')}
                                  description="PIM multicast routing"
                                />
                                <SecurityFeatureToggle
                                  feature="VPN Support"
                                  enabled={configSecurityFeatures.enableVPN}
                                  onToggle={() => toggleSecurityFeature('enableVPN')}
                                  description="IPsec VPN configuration"
                                />
                                <SecurityFeatureToggle
                                  feature="NAT Configuration"
                                  enabled={configSecurityFeatures.enableNAT}
                                  onToggle={() => toggleSecurityFeature('enableNAT')}
                                  description="Network Address Translation"
                                />
                                <SecurityFeatureToggle
                                  feature="IPv6 Support"
                                  enabled={configSecurityFeatures.enableIPv6}
                                  onToggle={() => toggleSecurityFeature('enableIPv6')}
                                  description="IPv6 routing and addressing"
                                />
                                <SecurityFeatureToggle
                                  feature="MPLS Configuration"
                                  enabled={configSecurityFeatures.enableMPLS}
                                  onToggle={() => toggleSecurityFeature('enableMPLS')}
                                  description="Multiprotocol Label Switching"
                                />
                                <SecurityFeatureToggle
                                  feature="GRE Tunnels"
                                  enabled={configSecurityFeatures.enableGRE}
                                  onToggle={() => toggleSecurityFeature('enableGRE')}
                                  description="Generic Routing Encapsulation tunnels"
                                />
                                <SecurityFeatureToggle
                                  feature="DMVPN"
                                  enabled={configSecurityFeatures.enableDMVPN}
                                  onToggle={() => toggleSecurityFeature('enableDMVPN')}
                                  description="Dynamic Multipoint VPN"
                                />
                                <SecurityFeatureToggle
                                  feature="IP SLA"
                                  enabled={configSecurityFeatures.enableIPSLAs}
                                  onToggle={() => toggleSecurityFeature('enableIPSLAs')}
                                  description="IP Service Level Agreements"
                                />
                                <SecurityFeatureToggle
                                  feature="EEM Scripts"
                                  enabled={configSecurityFeatures.enableEEM}
                                  onToggle={() => toggleSecurityFeature('enableEEM')}
                                  description="Embedded Event Manager"
                                />
                              </>
                            )}
                            {configDevice === 'switch' && (
                              <>
                                <SecurityFeatureToggle
                                  feature="VTP"
                                  enabled={configSecurityFeatures.enableVTP}
                                  onToggle={() => toggleSecurityFeature('enableVTP')}
                                  description="VLAN Trunking Protocol"
                                />
                                <SecurityFeatureToggle
                                  feature="EtherChannel"
                                  enabled={configSecurityFeatures.enableEtherChannel}
                                  onToggle={() => toggleSecurityFeature('enableEtherChannel')}
                                  description="Link aggregation and port bundling"
                                />
                                <SecurityFeatureToggle
                                  feature="MSTP"
                                  enabled={configSecurityFeatures.enableMSTP}
                                  onToggle={() => toggleSecurityFeature('enableMSTP')}
                                  description="Multiple Spanning Tree Protocol"
                                />
                                <SecurityFeatureToggle
                                  feature="Private VLANs"
                                  enabled={configSecurityFeatures.enablePrivateVLAN}
                                  onToggle={() => toggleSecurityFeature('enablePrivateVLAN')}
                                  description="Private VLAN configuration"
                                />
                                <SecurityFeatureToggle
                                  feature="DHCP Snooping"
                                  enabled={configSecurityFeatures.enableDHCPSnooping}
                                  onToggle={() => toggleSecurityFeature('enableDHCPSnooping')}
                                  description="DHCP snooping security"
                                />
                                <SecurityFeatureToggle
                                  feature="Dynamic ARP Inspection"
                                  enabled={configSecurityFeatures.enableDAI}
                                  onToggle={() => toggleSecurityFeature('enableDAI')}
                                  description="Dynamic ARP inspection"
                                />
                                <SecurityFeatureToggle
                                  feature="IP Source Guard"
                                  enabled={configSecurityFeatures.enableIPSourceGuard}
                                  onToggle={() => toggleSecurityFeature('enableIPSourceGuard')}
                                  description="IP source address filtering"
                                />
                                <SecurityFeatureToggle
                                  feature="BPDU Guard"
                                  enabled={configSecurityFeatures.enableBPDUGuard}
                                  onToggle={() => toggleSecurityFeature('enableBPDUGuard')}
                                  description="Bridge Protocol Data Unit guard"
                                />
                                <SecurityFeatureToggle
                                  feature="Root Guard"
                                  enabled={configSecurityFeatures.enableRootGuard}
                                  onToggle={() => toggleSecurityFeature('enableRootGuard')}
                                  description="STP root guard protection"
                                />
                                <SecurityFeatureToggle
                                  feature="Loop Guard"
                                  enabled={configSecurityFeatures.enableLoopGuard}
                                  onToggle={() => toggleSecurityFeature('enableLoopGuard')}
                                  description="STP loop guard protection"
                                />
                                <SecurityFeatureToggle
                                  feature="UDLD"
                                  enabled={configSecurityFeatures.enableUDLD}
                                  onToggle={() => toggleSecurityFeature('enableUDLD')}
                                  description="Unidirectional Link Detection"
                                />
                                <SecurityFeatureToggle
                                  feature="Flex Links"
                                  enabled={configSecurityFeatures.enableFlexLinks}
                                  onToggle={() => toggleSecurityFeature('enableFlexLinks')}
                                  description="Flex Links for redundancy"
                                />
                              </>
                            )}
                          </div>
                        </div>

                        {/* Interface Configuration */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-lg font-semibold text-gray-300 flex items-center gap-2">
                              <Server className="w-5 h-5" />
                              Interface Configuration ({configInterfaces.length})
                            </h4>
                            <button
                              onClick={addConfigInterface}
                              className="px-4 py-2 bg-gray-800 hover:bg-gray-600 rounded-lg text-gray-300 flex items-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Add Interface
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            {configInterfaces.map((iface, index) => (
                              <InterfaceConfig
                                key={iface.id}
                                iface={iface}
                                index={index}
                                deviceType={configDevice}
                                isLast={index === configInterfaces.length - 1}
                                onUpdate={(field, value) => updateConfigInterface(iface.id, field, value)}
                                onRemove={() => removeConfigInterface(iface.id)}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Additional VLANs (for switches) */}
                        {configDevice === 'switch' && (
                          <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="text-lg font-semibold text-gray-300 flex items-center gap-2">
                                <Layers className="w-5 h-5" />
                                Additional VLANs
                              </h4>
                              <button
                                onClick={addAdditionalVlan}
                                className="px-4 py-2 bg-gray-800 hover:bg-gray-600 rounded-lg text-gray-300 flex items-center gap-2"
                              >
                                <Plus className="w-4 h-4" />
                                Add VLAN
                              </button>
                            </div>
                            
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-gray-800">
                                    <th className="text-left py-3 px-4 text-gray-400">VLAN ID</th>
                                    <th className="text-left py-3 px-4 text-gray-400">Name</th>
                                    <th className="text-left py-3 px-4 text-gray-400">IP Address</th>
                                    <th className="text-left py-3 px-4 text-gray-400">Subnet Mask</th>
                                    <th className="text-left py-3 px-4 text-gray-400">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {configAdditionalVlans.map((vlan) => (
                                    <tr key={vlan.id} className="border-b border-gray-800">
                                      <td className="py-3 px-4">
                                        <input
                                          value={vlan.vlanId}
                                          onChange={(e) => updateAdditionalVlan(vlan.id, 'vlanId', e.target.value)}
                                          className="w-full bg-black border border-gray-800 rounded px-2 py-1 text-gray-100"
                                        />
                                      </td>
                                      <td className="py-3 px-4">
                                        <input
                                          value={vlan.name}
                                          onChange={(e) => updateAdditionalVlan(vlan.id, 'name', e.target.value)}
                                          className="w-full bg-black border border-gray-800 rounded px-2 py-1 text-gray-100"
                                        />
                                      </td>
                                      <td className="py-3 px-4">
                                        <input
                                          value={vlan.ip}
                                          onChange={(e) => updateAdditionalVlan(vlan.id, 'ip', e.target.value)}
                                          className="w-full bg-black border border-gray-800 rounded px-2 py-1 text-gray-100"
                                          placeholder="Optional"
                                        />
                                      </td>
                                      <td className="py-3 px-4">
                                        <input
                                          value={vlan.mask}
                                          onChange={(e) => updateAdditionalVlan(vlan.id, 'mask', e.target.value)}
                                          className="w-full bg-black border border-gray-800 rounded px-2 py-1 text-gray-100"
                                          placeholder="Optional"
                                        />
                                      </td>
                                      <td className="py-3 px-4">
                                        <button
                                          onClick={() => removeAdditionalVlan(vlan.id)}
                                          className="px-2 py-1 rounded bg-red-900/30 hover:bg-red-800/30 text-red-400"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-6 flex gap-4">
                        <button
                          onClick={generateConfig}
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                        >
                          Generate Configuration
                        </button>
                      </div>

                      {configError && (
                        <div className="mt-4 p-4 bg-red-900/20 border border-red-700 rounded-xl">
                          <div className="flex items-center gap-2 text-red-400">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-semibold">Error:</span>
                            <span>{configError}</span>
                          </div>
                        </div>
                      )}

                      {configResult && (
                        <div className="mt-8 space-y-6">
                          {/* Configuration Summary */}
                          <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                            <div className="space-y-6">
                              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Device Type</div>
                                  <div className="text-xl font-bold text-gray-100">{configResult.device}</div>
                                </div>
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Hostname</div>
                                  <div className="text-xl font-bold text-gray-100">{configResult.hostname}</div>
                                </div>
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Interfaces</div>
                                  <div className="text-xl font-bold text-gray-100">{configResult.interfaces}</div>
                                </div>
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Generated</div>
                                  <div className="text-gray-300">{configResult.timestamp}</div>
                                </div>
                              </div>
                              
                              {configResult.securityFeatures && configResult.securityFeatures.length > 0 && (
                                <div>
                                  <div className="text-sm text-gray-400 mb-2">Enabled Security Features</div>
                                  <div className="flex flex-wrap gap-2">
                                    {configResult.securityFeatures.map((feature, idx) => (
                                      <span key={idx} className="px-3 py-1 bg-gray-800/50 rounded-lg text-sm text-gray-300">
                                        {feature}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              <div className="grid md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Enable Secret</div>
                                  <div className="font-mono text-sm text-gray-300">{configResult.enableSecret}</div>
                                  <CopyButton text={configResult.enableSecret} itemKey="config-secret" />
                                </div>
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">Console Password</div>
                                  <div className="font-mono text-sm text-gray-300">{configResult.consolePassword}</div>
                                  <CopyButton text={configResult.consolePassword} itemKey="config-console" />
                                </div>
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-400">VTY Password</div>
                                  <div className="font-mono text-sm text-gray-300">{configResult.vtyPassword}</div>
                                  <CopyButton text={configResult.vtyPassword} itemKey="config-vty" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Configuration Output */}
                          <div className="space-y-4">
                            <ConfigPreview
                              config={configResult.config}
                              title="Complete Configuration"
                            />
                          </div>
                          
                          {/* Export Options */}
                          <div className="flex gap-3 justify-center">
                            <CopyButton 
                              text={configResult.config} 
                              itemKey="full-config" 
                              className="px-6 py-3 bg-gray-800 hover:bg-gray-600 text-gray-100"
                            >
                              Copy Full Configuration
                            </CopyButton>
                            <button
                              onClick={() => {
                                const blob = new Blob([configResult.config], { type: 'text/plain' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${configResult.hostname}-config.txt`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              }}
                              className="px-6 py-3 bg-gray-800 hover:bg-gray-600 rounded-lg text-gray-100 flex items-center gap-2"
                            >
                              <Download className="w-4 h-4" />
                              Download Configuration
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ErrorBoundary>

                {/* STP Calculator */}
                <ErrorBoundary>
                  {activeTool === 'stp' && (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-100">STP Calculator</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setStpLinkSpeed('1000');
                              setStpPathCost('');
                              setStpHopCount('1');
                              setStpBridgePriority('32768');
                              setStpMacAddress('00:00:00:00:00:01');
                              setStpResult(null);
                            }}
                            className="px-4 py-2 bg-gray-800 hover:bg-gray-600 rounded-xl text-gray-300 text-sm flex items-center gap-2"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Reset
                          </button>
                        </div>
                      </div>

                      <div className="space-y-8">
                        {/* STP Path Cost Calculator */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Calculator className="w-5 h-5" />
                            STP Path Cost Calculator
                          </h4>
                          <div className="grid md:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Link Speed</label>
                              <select
                                value={stpLinkSpeed}
                                onChange={(e) => setStpLinkSpeed(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="10">10 Mbps (Cost: 100)</option>
                                <option value="100">100 Mbps (Cost: 19)</option>
                                <option value="1000">1 Gbps (Cost: 4)</option>
                                <option value="10000">10 Gbps (Cost: 2)</option>
                                <option value="40000">40 Gbps (Cost: 1)</option>
                                <option value="100000">100 Gbps (Cost: 1)</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Custom Cost (Optional)</label>
                              <input
                                type="number"
                                value={stpPathCost}
                                onChange={(e) => setStpPathCost(e.target.value)}
                                placeholder="Auto-calculated"
                                min="1"
                                max="200000000"
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Hops to Root</label>
                              <input
                                type="number"
                                value={stpHopCount}
                                onChange={(e) => setStpHopCount(e.target.value)}
                                min="1"
                                max="32"
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                placeholder="1"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Root Bridge Calculator */}
                        <div className="bg-black/60 p-6 rounded-xl border border-gray-800">
                          <h4 className="text-lg font-semibold text-gray-300 mb-4 flex items-center gap-2">
                            <Network className="w-5 h-5" />
                            Root Bridge Calculator
                          </h4>
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">Bridge Priority</label>
                              <select
                                value={stpBridgePriority}
                                onChange={(e) => setStpBridgePriority(e.target.value)}
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              >
                                <option value="0">0 - Root Bridge</option>
                                <option value="4096">4096 - Primary Root</option>
                                <option value="8192">8192 - Secondary Root</option>
                                <option value="12288">12288 - Tertiary Root</option>
                                <option value="16384">16384 - Backup Root</option>
                                <option value="20480">20480 - Low Priority</option>
                                <option value="24576">24576 - Low Priority</option>
                                <option value="28672">28672 - Low Priority</option>
                                <option value="32768">32768 - Default</option>
                                <option value="36864">36864 - High Priority</option>
                                <option value="40960">40960 - High Priority</option>
                                <option value="45056">45056 - High Priority</option>
                                <option value="49152">49152 - High Priority</option>
                                <option value="53248">53248 - High Priority</option>
                                <option value="57344">57344 - High Priority</option>
                                <option value="61440">61440 - High Priority</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold mb-2 text-gray-300">MAC Address</label>
                              <input
                                type="text"
                                value={stpMacAddress}
                                onChange={(e) => setStpMacAddress(e.target.value)}
                                placeholder="00:00:00:00:00:01"
                                className="w-full bg-black border border-gray-800 rounded-xl px-4 py-3 text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-600"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <button
                            onClick={calculateSTP}
                            className="flex-1 py-3 bg-gray-800 hover:bg-gray-600 rounded-xl font-semibold border border-gray-600 text-gray-100"
                          >
                            Calculate STP Values
                          </button>
                        </div>

                        {stpResult && (
                          <div className="mt-8 space-y-6">
                            <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
                              <div className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                  <div>
                                    <div className="text-sm text-gray-400">STP Path Cost</div>
                                    <div className="text-2xl font-bold text-gray-100">{stpResult.pathCost}</div>
                                    <div className="text-sm text-gray-500">{stpResult.linkSpeed} link</div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-400">Bridge ID</div>
                                    <div className="text-xl font-bold text-gray-100 font-mono">{stpResult.bridgeId}</div>
                                    <div className="text-sm text-gray-500">Priority: {stpResult.priority}</div>
                                  </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                  <div>
                                    <div className="text-sm text-gray-400">Bridge Role</div>
                                    <div className="text-lg font-bold text-green-400">{stpResult.bridgeRole}</div>
                                    <div className="text-sm text-gray-500">Based on priority</div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-400">MAC Address</div>
                                    <div className="text-lg font-bold text-gray-100 font-mono">{stpResult.formattedMac}</div>
                                    <div className="text-sm text-gray-500">System MAC</div>
                                  </div>
                                </div>

                                <div className="grid md:grid-cols-3 gap-6">
                                  <div>
                                    <div className="text-sm text-gray-400">Root Port Cost</div>
                                    <div className="text-lg font-bold text-gray-100">{stpResult.rootPortCost}</div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-400">Designated Port Cost</div>
                                    <div className="text-lg font-bold text-gray-100">{stpResult.designatedPortCost}</div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-400">Path Cost to Root</div>
                                    <div className="text-lg font-bold text-gray-100">{stpResult.totalPathCost}</div>
                                  </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                  <div>
                                    <div className="text-sm text-gray-400">Hops to Root</div>
                                    <div className="text-lg font-bold text-gray-100">{stpResult.hopCount}</div>
                                    <div className="text-sm text-gray-500">Physical links until root</div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-400">Per-Hop Cost</div>
                                    <div className="text-lg font-bold text-gray-100">{stpResult.perHopCost}</div>
                                    <div className="text-sm text-gray-500">
                                      Totals to {stpResult.totalPathCost} ({stpResult.hopCount} × {stpResult.perHopCost})
                                    </div>
                                  </div>
                                </div>

                                <div className="grid md:grid-cols-3 gap-6">
                                  <div>
                                    <div className="text-sm text-gray-400">Hello Time</div>
                                    <div className="text-lg font-bold text-gray-100">{stpResult.helloTime}s</div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-400">Max Age</div>
                                    <div className="text-lg font-bold text-gray-100">{stpResult.maxAge}s</div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-400">Forward Delay</div>
                                    <div className="text-lg font-bold text-gray-100">{stpResult.forwardDelay}s</div>
                                  </div>
                                </div>

                                <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                  <div className="text-sm text-gray-400 mb-2">STP Configuration Commands</div>
                                  <div className="font-mono text-sm text-gray-300 bg-black p-3 rounded-lg">
                                    ! Bridge Priority Configuration<br/>
                                    spanning-tree vlan 1 priority {stpResult.priority}<br/>
                                    {stpResult.priority <= 4096 ? 'spanning-tree vlan 1 root primary\n' : ''}<br/>
                                    ! Port Cost Configuration<br/>
                                    interface [port]<br/>
                                    &nbsp;&nbsp;spanning-tree cost {stpResult.pathCost}<br/>
                                    &nbsp;&nbsp;spanning-tree port-priority 128<br/>
                                    <br/>
                                    ! Path Cost Summary<br/>
                                    ! Total path cost to root: {stpResult.totalPathCost} ({stpResult.hopCount} hops × {stpResult.perHopCost})<br/>
                                    <br/>
                                    ! Timer Configuration (Optional)<br/>
                                    spanning-tree vlan 1 hello-time {stpResult.helloTime}<br/>
                                    spanning-tree vlan 1 max-age {stpResult.maxAge}<br/>
                                    spanning-tree vlan 1 forward-time {stpResult.forwardDelay}
                                  </div>
                                  <CopyButton text={`! Bridge Priority Configuration\nspanning-tree vlan 1 priority ${stpResult.priority}${stpResult.priority <= 4096 ? '\nspanning-tree vlan 1 root primary' : ''}\n\n! Port Cost Configuration\ninterface [port]\n  spanning-tree cost ${stpResult.pathCost}\n  spanning-tree port-priority 128\n\n! Path Cost Summary\n! Total path cost to root: ${stpResult.totalPathCost} (${stpResult.hopCount} hops × ${stpResult.perHopCost})\n\n! Timer Configuration (Optional)\nspanning-tree vlan 1 hello-time ${stpResult.helloTime}\nspanning-tree vlan 1 max-age ${stpResult.maxAge}\nspanning-tree vlan 1 forward-time ${stpResult.forwardDelay}`} itemKey="stp-config" />
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                  <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                    <div className="text-sm text-gray-400 mb-1">STP Standards (IEEE 802.1D)</div>
                                    <div className="text-gray-300 text-sm space-y-1">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>10 Mbps:</div><div className="font-mono">100</div>
                                        <div>100 Mbps:</div><div className="font-mono">19</div>
                                        <div>1 Gbps:</div><div className="font-mono">4</div>
                                        <div>10 Gbps:</div><div className="font-mono">2</div>
                                        <div>40/100 Gbps:</div><div className="font-mono">1</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="p-4 bg-black/60 rounded-xl border border-gray-800">
                                    <div className="text-sm text-gray-400 mb-1">Priority Ranges & Roles</div>
                                    <div className="text-gray-300 text-sm space-y-1">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>0:</div><div className="text-green-400">Root Bridge</div>
                                        <div>4096:</div><div className="text-blue-400">Primary Root</div>
                                        <div>8192:</div><div className="text-yellow-400">Secondary Root</div>
                                        <div>12288:</div><div className="text-orange-400">Tertiary Root</div>
                                        <div>16384-61440:</div><div className="text-gray-400">Regular Bridge</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </ErrorBoundary>

                {/* Default/Helper Message */}
                {!activeTool && (
                  <div className="text-center py-12">
                    <div className="inline-block p-8 bg-black/60 rounded-2xl border border-gray-800">
                      <Globe className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-300 mb-2">Select a Tool</h3>
                      <p className="text-gray-500 max-w-md">
                        Choose from {toolButtons.length} network engineering tools above. 
                        Tools are organized by category for easy navigation.
                      </p>
                      <div className="mt-6 flex flex-wrap gap-2 justify-center">
                        <div className="px-3 py-1 bg-gray-800 rounded-full text-sm text-gray-300">
                          {toolButtons.filter(t => t.category === 'ip').length} IP Tools
                        </div>
                        <div className="px-3 py-1 bg-gray-800 rounded-full text-sm text-gray-300">
                          {toolButtons.filter(t => t.category === 'security').length} Security Tools
                        </div>
                        <div className="px-3 py-1 bg-gray-800 rounded-full text-sm text-gray-300">
                          {toolButtons.filter(t => t.category === 'design').length} Design Tools
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Global Controls */}
              {!isFullscreen && (
                <div className="mt-8 flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    {filteredTools.length} of {toolButtons.length} tools shown
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={clearAllToolData}
                      className="px-4 py-2 bg-gray-950 hover:bg-gray-800 rounded-xl border border-gray-800 text-gray-300 text-sm"
                    >
                      Clear All Data
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
          opacity: 0;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        @keyframes haloGlow {
          0%, 12.5% {
            box-shadow: 0 0 15px 3px rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.5);
          }
          12.5%, 100% {
            box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
            border-color: rgb(55, 65, 81);
          }
        }
        
        .skill-card:nth-child(1) { animation: haloGlow 12s ease-in-out infinite; }
        .skill-card:nth-child(2) { animation: haloGlow 12s ease-in-out infinite; animation-delay: 1.5s; }
        .skill-card:nth-child(3) { animation: haloGlow 12s ease-in-out infinite; animation-delay: 3s; }
        .skill-card:nth-child(4) { animation: haloGlow 12s ease-in-out infinite; animation-delay: 4.5s; }
        .skill-card:nth-child(5) { animation: haloGlow 12s ease-in-out infinite; animation-delay: 6s; }
        .skill-card:nth-child(6) { animation: haloGlow 12s ease-in-out infinite; animation-delay: 7.5s; }
        .skill-card:nth-child(7) { animation: haloGlow 12s ease-in-out infinite; animation-delay: 9s; }
        .skill-card:nth-child(8) { animation: haloGlow 12s ease-in-out infinite; animation-delay: 10.5s; }
      `}</style>
    </div>
  );
}
